/*
 * AI Advisor chat widget — motionsports.de
 * Vanilla JS, no dependencies, no build step.
 *
 * Implements docs/ai-advisor/{API_CONTRACT,BEHAVIOR_REFERENCE,WIDGET_SPEC}.md:
 *   - SSE streaming of /api/chat via fetch + reader (NOT EventSource)
 *   - stable session id + conversation persistence in localStorage
 *   - the five visible tool cards (with render-nothing guards), silent tools
 *     consumed without rendering
 *   - XSS-safe markdown subset (DOM nodes, never innerHTML on model text)
 *   - product hydration via GET /api/products with an in-session cache
 *   - all error envelopes (429 / 401 / 403 / payload_too_large / 5xx / network)
 *
 * All DOM is built under one `.ms-chat-root` container appended to <body>;
 * every CSS class is prefixed `.ms-chat*` for isolation.
 */
(function () {
  'use strict';

  var CFG = window.MS_CHAT_CONFIG || {};
  var API_BASE = String(CFG.apiBase || 'https://mo.motionsports.de').replace(/\/+$/, '');
  var CHAT_KEY = CFG.chatKey || '';
  var SHOWROOM_URL = CFG.showroomUrl || 'https://motionsports.de/pages/showroom-munchen-grobenzell';

  // ---------------------------------------------------------------------------
  // Locale (docs/ai-advisor/LOCALE.md). German is the default and stays
  // byte-identical; English is opt-in on the /en storefront. Resolution order:
  // prefer Shopify's storefront localization (injected from Liquid as
  // CFG.locale, so it matches what the storefront renders), else fall back to
  // the URL path prefix. Normalised to "en"/"de", defaulting to "de"; tolerant
  // like the backend's normalizeLocale ("en", "EN", "en-GB", "en_US" → en). An
  // /en path is the backend's canonical English trigger, so it always resolves
  // to English — the widget can never serve German chrome on /en even if the
  // storefront locale signal is missing.
  //
  // The resolved locale is sent to the backend on every locale-bearing call (a
  // default x-ms-locale header on the guarded groups, plus the body `locale` on
  // the key POSTs and `?locale=` on the GETs), so Mo's replies, the
  // backend-rendered emails, the summary PDF and the served consent copy all
  // switch language together. /api/products and /api/kpi take no locale
  // (LOCALE.md §1) and are left untouched.
  // ---------------------------------------------------------------------------
  function msNormLocale(v) { return /^en/i.test(v == null ? '' : String(v)) ? 'en' : 'de'; }
  var LOCALE = (CFG.locale != null && String(CFG.locale) !== '') ? msNormLocale(CFG.locale) : 'de';
  try { if (LOCALE !== 'en' && /^\/en(\/|$|\?|#)/i.test(window.location.pathname)) LOCALE = 'en'; } catch (e) {}

  // Pick the locale variant of a THEME-OWNED user-facing value: the German one
  // (returned verbatim, so /de renders byte-identically to before) or its
  // English counterpart. Used for every widget-rendered string plus the two
  // locale-formatted helpers (euro/date). Backend-served consent/legal copy is
  // NEVER routed through this — it is fetched per-locale and rendered verbatim
  // (LOCALE.md §2).
  function L(de, en) { return LOCALE === 'en' ? en : de; }

  // ---------------------------------------------------------------------------
  // Email-capture form — widget-owned UI CHROME strings only.
  //
  // The LEGAL consent copy (checkbox labels, the shared consent footer, the
  // returning-customer hint, imprint/privacy link targets, and the
  // pre-composed `consentTextShown` audit string) is deliberately NOT
  // hard-coded here. It is served by the backend — attached to every
  // offer_email_summary tool result and available via GET /api/consent-copy —
  // and rendered verbatim, so the stored Art. 7 audit text can never diverge
  // from what was displayed, and a lawyer copy change ships as a backend
  // deploy with no widget release. See docs/{API_CONTRACT,CONSENT_FLOW}.md
  // (§2 + §7.4).
  //
  // LEGAL INVARIANTS (do not "optimise" away):
  //   * The two consents are SEPARATE — one transactional, one marketing.
  //   * BOTH checkboxes start UNCHECKED (consent copy v2 — the earlier
  //     allowance to pre-check the transactional box is revoked).
  //   * The marketing checkbox is NEVER pre-ticked, never auto-toggled by any
  //     other interaction, and never bundled into the same control/action as
  //     the transactional one.
  //   * `consentTextShown` is the backend's pre-composed string echoed back
  //     byte-for-byte — never recomposed or hard-coded by the widget.
  // ---------------------------------------------------------------------------
  var CONSENT_COPY = {
    title: 'Zusammenfassung per E-Mail',
    // Default intro for the header share-icon entry point. When the assistant
    // emits offer_email_summary it supplies its own intro (the tool `message`).
    intro: 'Ich schicke dir gerne die Zusammenfassung deiner Beratung samt Warenkorb per E-Mail.',
    emailLabel: 'E-Mail',
    emailPlaceholder: 'deine@email.de',
    submit: 'Zusammenfassung senden',
    sending: 'Wird gesendet…',
    loading: 'Einwilligungstexte werden geladen…',
    privacy: 'Wir verwenden deine E-Mail nur wie oben angegeben. Für Angebote ist eine Bestätigung über den Doppel-Opt-in-Link in der E-Mail nötig.',
    errEmail: 'Bitte gib eine gültige E-Mail-Adresse an.',
    errTransactional: 'Bitte wähle mindestens die Zusammenfassung aus.',
    errCopyLoad: 'Die Einwilligungstexte konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    errRate: 'Zu viele Anfragen — bitte kurz warten.',
    errUpstream: 'Senden gerade nicht möglich — bitte später erneut versuchen.',
    errGeneric: 'Senden fehlgeschlagen. Bitte versuch es erneut.',
    successTitle: 'Erledigt!',
    success: 'Wir haben dir die Zusammenfassung geschickt.',
    // Appended only when the response says marketing.status === "pending"
    // (API_CONTRACT.md §7.1 — the DOI mail went out and needs confirming).
    successPending: 'Bitte bestätige noch die Anmeldung über den Link in der E-Mail.',
    decline: 'Nein danke, vielleicht später',
    declined: 'Alles klar! Du findest die Option jederzeit oben unter „Per E-Mail teilen“.'
  };
  // English overlay (LOCALE.md). German above is the source of truth and stays
  // byte-identical; on /en these CHROME strings switch language. The LEGAL
  // consent copy is NOT here — it is backend-served per locale and rendered
  // verbatim.
  if (LOCALE === 'en') Object.assign(CONSENT_COPY, {
    title: 'Summary by email',
    intro: 'I\'d be happy to email you a summary of your consultation along with your cart.',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    submit: 'Send summary',
    sending: 'Sending…',
    loading: 'Loading consent text…',
    privacy: 'We only use your email as described above. For offers, confirmation via the double opt-in link in the email is required.',
    errEmail: 'Please enter a valid email address.',
    errTransactional: 'Please select at least the summary.',
    errCopyLoad: 'The consent text could not be loaded.',
    retry: 'Try again',
    errRate: 'Too many requests — please wait a moment.',
    errUpstream: 'Sending isn\'t possible right now — please try again later.',
    errGeneric: 'Sending failed. Please try again.',
    successTitle: 'Done!',
    success: 'We\'ve sent you the summary.',
    successPending: 'Please also confirm your subscription via the link in the email.',
    decline: 'No thanks, maybe later',
    declined: 'No problem! You can find the option any time above under "Share by email".'
  });

  // The marketing line of an opt-in answer — shared by the capture form
  // (POST /api/capture-email), the consent popup and the inline opt-in card
  // (POST /api/account/marketing-opt-in). Since 2026-10 the backend knows the
  // ONE consent shared with the shop, so an address that already holds it
  // answers { status:"confirmed", alreadyConfirmed:true, doiEmailSent:false }
  // and NO e-mail goes out (API_CONTRACT.md §7.1, CONSENT_FLOW.md §3.2/§4).
  // "Check your inbox" is therefore promised ONLY for a pending DOI whose mail
  // was actually sent; everything else (already subscribed, or a pending
  // record whose DOI mail could not be sent) must not send the customer
  // looking for an e-mail that never comes. UI chrome, not consent text.
  //   'already' — subscribed already (Mo DOI or the shop): nothing to do
  //   'pending' — DOI mail sent: confirm via the link
  //   'other'   — anything else: a neutral thank-you, no inbox promise
  function marketingOutcome(m) {
    if (!m || typeof m !== 'object') return 'other';
    if (m.alreadyConfirmed === true || (m.status === 'confirmed' && m.doiEmailSent !== true)) return 'already';
    if (m.status === 'pending' && m.doiEmailSent === true) return 'pending';
    return 'other';
  }
  var MKT_RESULT_COPY = {
    alreadyTitle: L('Alles erledigt', 'All set'),
    already: L('Du bist bereits für unsere Angebote angemeldet — es ist nichts weiter zu tun.',
      'You\'re already subscribed — nothing else to do.'),
    otherTitle: L('Danke!', 'Thank you!'),
    other: L('Wir haben deine Anmeldung erhalten.', 'We\'ve received your sign-up.')
  };

  // ---------------------------------------------------------------------------
  // Feedback — a small, unobtrusive way to leave a free-text comment about the
  // advisor (entry point is a quiet link beneath the composer). Available to
  // ALL tiers; no PII required. POSTs to /api/feedback (API_CONTRACT.md §9):
  // `message` is the only required field; the rest is OPTIONAL context the
  // widget already has (session id, the active thread, a coarse tier hint, the
  // current page). An `email` is attached ONLY for an already-identified user
  // (a captured email-only contact) — never collected here on purpose, and a
  // signed-in customer is linked by the session id, not by sending the address.
  // ---------------------------------------------------------------------------
  var FEEDBACK_COPY = {
    entry: 'Feedback geben',
    entryAria: 'Feedback zum Fitnessberater geben',
    title: 'Dein Feedback',
    intro: 'Wie war deine Beratung? Erzähl uns kurz, was gut lief oder was wir verbessern können.',
    placeholder: 'Dein Feedback (optional anonym) …',
    submit: 'Absenden',
    sending: 'Wird gesendet…',
    cancel: 'Abbrechen',
    errEmpty: 'Bitte schreib uns kurz, was du uns mitteilen möchtest.',
    errLong: 'Bitte fasse dich etwas kürzer (max. 4000 Zeichen).',
    errRate: 'Danke! Du hast gerade schon Feedback gesendet — bitte kurz warten.',
    errGeneric: 'Senden gerade nicht möglich — bitte später erneut versuchen.',
    thanksTitle: 'Danke!',
    thanks: 'Dein Feedback ist angekommen — das hilft uns sehr.'
  };
  if (LOCALE === 'en') Object.assign(FEEDBACK_COPY, {
    entry: 'Give feedback',
    entryAria: 'Give feedback on the fitness advisor',
    title: 'Your feedback',
    intro: 'How was your consultation? Tell us briefly what went well or what we could improve.',
    placeholder: 'Your feedback (anonymous if you like) …',
    submit: 'Submit',
    sending: 'Sending…',
    cancel: 'Cancel',
    errEmpty: 'Please write us a short note about what you\'d like to share.',
    errLong: 'Please keep it a little shorter (max. 4000 characters).',
    errRate: 'Thanks! You\'ve just sent feedback — please wait a moment.',
    errGeneric: 'Sending isn\'t possible right now — please try again later.',
    thanksTitle: 'Thank you!',
    thanks: 'Your feedback has arrived — that really helps us.'
  });

  // Fail gracefully: no secret -> log a warning, do not render the launcher.
  if (!CHAT_KEY) {
    try { console.warn('[ms-chat] ms_chat_shared_secret is empty; AI advisor widget not mounted. Paste the secret in Theme settings > AI Advisor.'); } catch (e) {}
    return;
  }
  if (window.__msChatMounted) return;
  window.__msChatMounted = true;

  // ---------------------------------------------------------------------------
  // Storage (session id + history) with silent in-memory fallback.
  // ---------------------------------------------------------------------------
  var memStore = {};
  var hasLS = (function () {
    try {
      var k = '__ms_chat_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) { return false; }
  })();
  function lsGet(k) { try { return hasLS ? window.localStorage.getItem(k) : (k in memStore ? memStore[k] : null); } catch (e) { return null; } }
  function lsSet(k, v) { try { if (hasLS) window.localStorage.setItem(k, v); else memStore[k] = v; } catch (e) { memStore[k] = v; } }
  function lsDel(k) { try { if (hasLS) window.localStorage.removeItem(k); else delete memStore[k]; } catch (e) { delete memStore[k]; } }

  // Session-scoped flags (engagement layer "once per session" caps: nudge,
  // launcher attention, chat-opened). sessionStorage so they reset when the
  // tab session ends; silent in-memory fallback degrades to "once per page
  // load" — still capped, never throws.
  var memSession = {};
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return k in memSession ? memSession[k] : null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { memSession[k] = v; } }
  function ssDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) {} delete memSession[k]; }

  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  var SID_KEY = 'ms-chat-sid';
  function getSid() {
    var sid = lsGet(SID_KEY);
    if (!sid) { sid = uuid(); lsSet(SID_KEY, sid); }
    return sid;
  }
  var sid = getSid();
  function historyKey() { return 'ms-chat-history:' + sid; }
  function convKeyStorageKey() { return 'ms-chat-convkey:' + sid; }

  // messages: UIMessage[] -> { id, role, parts: [...] }
  var messages = loadHistory();

  function loadHistory() {
    var raw = lsGet(historyKey());
    if (!raw) return [];
    try {
      var arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.slice(-40);
    } catch (e) {}
    return [];
  }
  // Another tab may have rotated the device's session (sign-out / erase /
  // new chat) while this one still holds the old sid in memory. Persisting
  // under that orphaned id would leave a transcript (possibly order status)
  // on the device that nothing ever reads or deletes again — so this tab
  // writes per-sid data only while its sid is still the device's.
  function sidIsCurrent() {
    var cur = lsGet(SID_KEY);
    return !cur || cur === sid;
  }
  function saveHistory() {
    if (!sidIsCurrent()) return;
    try { lsSet(historyKey(), JSON.stringify(messages.slice(-40))); } catch (e) {}
  }
  function rotateSession() {
    lsDel(historyKey());
    lsDel('ms-chat-cfg:' + sid); // the configurator answers of the old thread
    sid = uuid();
    lsSet(SID_KEY, sid);
    messages = [];
    // The order-attribution token is minted per session id — a rotated
    // session must not keep stamping carts with the old session's token.
    moAttrReset();
  }

  // ---------------------------------------------------------------------------
  // Fail-silent KPI telemetry. Fire-and-forget POST to /api/kpi, contract-
  // exact per API_CONTRACT.md §5: Content-Type: application/json + the
  // x-ms-session header (sendBeacon can set neither, so fetch is primary).
  // keepalive:true lets events survive page unloads (exit-intent, outbound
  // CTA clicks). Failures are swallowed and the response is never read —
  // telemetry must never make a caller care. The body carries the
  // pseudonymous session id and event names/ids only — never message text.
  // ---------------------------------------------------------------------------
  function track(event, data) {
    try {
      var payload = JSON.stringify({
        event: event,
        sessionId: sid,
        timestamp: new Date().toISOString(),
        data: data || {}
      });
      var url = API_BASE + '/api/kpi';
      if (window.fetch) {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-ms-session': sid },
          body: payload,
          keepalive: true
        }).catch(function () {});
        return;
      }
      if (navigator && typeof navigator.sendBeacon === 'function') navigator.sendBeacon(url, payload);
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Page context + browsing trail (engagement layer).
  //
  // PRIVACY POSTURE (do not change): page facts and the browsing trail are gathered
  // client-side. Context leaves the browser ONLY inside a /api/chat request the user
  // starts: the product CTA (product + trail), a nudge click (greeting; product or
  // trail), and - page facts only, NO trail - the first typed or spoken message of a
  // thread on a product (or collection) page and the first after the page changed
  // (pageContextForSend()). Never in background calls. KPI events carry ids, enums
  // and booleans only (clicked product id, pageType, samePage); never product names,
  // the browsing trail, message text, URLs or tokens.
  // TONE RULE: copy references the page or category, never the visitor's behaviour.
  // ---------------------------------------------------------------------------
  var PAGE_CTX = (function () {
    var pc = (CFG.pageContext && typeof CFG.pageContext === 'object') ? CFG.pageContext : {};
    var t = String(pc.pageType || '');
    var type = (t === 'product' || t === 'collection' || t === 'cart') ? t : (t === 'index' ? 'home' : 'other');
    return {
      type: type,
      productId: pc.productId != null ? String(pc.productId) : null,
      // The handle is the slug-shaped id — the form most likely to match the
      // backend catalog's product ids (API_CONTRACT.md §3 example ids are
      // slugs, not numeric Shopify ids). Used for context/trail ids; the
      // numeric id stays for our own KPI events.
      productHandle: pc.productHandle != null ? String(pc.productHandle) : null,
      productName: pc.productTitle != null ? String(pc.productTitle) : null,
      collectionHandle: pc.collectionHandle != null ? String(pc.collectionHandle) : null,
      // Category: the product's type on product pages, the collection title
      // on collection pages.
      category: pc.productType ? String(pc.productType) : (pc.collectionTitle ? String(pc.collectionTitle) : null)
    };
  })();

  // Lightweight browsing trail: the last few products/collections viewed
  // (ids + names + type + timestamp), capped at TRAIL_MAX, entries pruned
  // after TRAIL_TTL_MS. localStorage only — see the privacy posture above.
  var TRAIL_KEY = 'ms-chat-trail';
  var TRAIL_MAX = 5;
  var TRAIL_TTL_MS = 3 * 24 * 60 * 60 * 1000;

  function loadTrail() {
    var raw = lsGet(TRAIL_KEY);
    if (!raw) return [];
    try {
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      var now = Date.now();
      return arr.filter(function (e) {
        return e && typeof e === 'object' && typeof e.ts === 'number' && (now - e.ts) < TRAIL_TTL_MS;
      }).slice(0, TRAIL_MAX);
    } catch (e) { return []; }
  }

  function recordTrail() {
    if (PAGE_CTX.type !== 'product' && PAGE_CTX.type !== 'collection') return;
    var id = PAGE_CTX.type === 'product' ? (PAGE_CTX.productHandle || PAGE_CTX.productId) : PAGE_CTX.collectionHandle;
    var name = PAGE_CTX.type === 'product' ? PAGE_CTX.productName : PAGE_CTX.category;
    if (!id && !name) return;
    var entry = { id: id || '', name: name || '', type: PAGE_CTX.type, category: PAGE_CTX.category || '', ts: Date.now() };
    var trail = loadTrail().filter(function (e) { return !(e.type === entry.type && e.id === entry.id); });
    trail.unshift(entry);
    try { lsSet(TRAIL_KEY, JSON.stringify(trail.slice(0, TRAIL_MAX))); } catch (e) {}
  }

  // ">= 2 products viewed in one category" -> that category (the strongest
  // streak wins). Used for the comparison-offer nudge and category starters.
  function trailCategoryStreak(trail) {
    trail = trail || loadTrail();
    var counts = {};
    var best = null;
    for (var i = 0; i < trail.length; i++) {
      var e = trail[i];
      if (e.type !== 'product' || !e.category) continue;
      counts[e.category] = (counts[e.category] || 0) + 1;
      if (counts[e.category] >= 2 && (best == null || counts[e.category] > counts[best])) best = e.category;
    }
    return best;
  }

  // Trail -> the wire shape `context.recentlyViewed` accepts (API_CONTRACT.md
  // §2): { type:"product", id, name } / { type:"category", id?, name }, most
  // recent first, pre-capped to the server's own cap (3 products +
  // 2 categories — anything more is dropped server-side anyway). The backend
  // validates ids/names against the catalog and silently drops mismatches.
  // Returns null when there is nothing worth sending. Sent ONLY inside a
  // user-initiated chat request (open / first message) — never as a
  // background heartbeat.
  function recentlyViewedPayload() {
    var out = [];
    var prods = 0;
    var cats = 0;
    var trail = loadTrail();
    for (var i = 0; i < trail.length; i++) {
      var e = trail[i];
      if (e.type === 'product' && e.id && prods < 3) {
        out.push({ type: 'product', id: e.id, name: e.name || '' });
        prods++;
      } else if (e.type === 'collection' && e.name && cats < 2) {
        var c = { type: 'category', name: e.name };
        if (e.id) c.id = e.id;
        out.push(c);
        cats++;
      }
    }
    return out.length ? out : null;
  }

  // A `type: "browsing"` chat context from the trail, optionally led by a
  // specific category (categories are matched by NAME server-side, tolerant
  // of German storefront labels). Null when there is nothing to send.
  function browsingContext(leadCategory) {
    var rv = recentlyViewedPayload() || [];
    if (leadCategory) {
      rv = rv.filter(function (e) { return !(e.type === 'category' && e.name === leadCategory); });
      rv.unshift({ type: 'category', name: leadCategory });
    }
    return rv.length ? { type: 'browsing', recentlyViewed: rv } : null;
  }

  // ---------------------------------------------------------------------------
  // Tool names.
  // ---------------------------------------------------------------------------
  // suggest_replies + show_gym_plan + offer_plan_options: the
  // Home-Gym-Konfigurator's answer chips, plan and step-by-step product
  // options (the backend offers them only in a configurator thread).
  var VISIBLE_TOOLS = ['show_product', 'compare_products', 'add_to_cart', 'suggest_showroom', 'show_contact_form', 'offer_email_summary', 'suggest_replies', 'show_gym_plan', 'offer_plan_options'];
  // Background tools: kept in the local history (the backend replays them) but
  // NEVER rendered — no card, no placeholder, no error. get_order_status
  // (CHAT_ORDER_STATUS.md) carries the signed-in customer's order data in its
  // output; Mo answers in its text, so the widget only consumes it silently.
  // Any tool name NOT listed here or in VISIBLE_TOOLS resolves to null and is
  // dropped entirely (API_CONTRACT.md §2 "Tools the widget MUST NOT render").
  var SILENT_TOOLS = ['update_customer_profile', 'search_products', 'get_order_status'];
  var ALL_TOOLS = VISIBLE_TOOLS.concat(SILENT_TOOLS);

  // Exact match only: a prefix match would let a future, unknown tool such as
  // `show_product_xyz` render as a show_product card instead of nothing.
  function isToolPart(type, name) {
    return type === 'tool-' + name;
  }
  // True when a canonical part produces something visible (non-empty text or a
  // VISIBLE tool). Silent and unknown tools are invisible: they must not create
  // an (empty) assistant row or end the generating indicator.
  function isRenderablePart(part) {
    if (!part || typeof part.type !== 'string') return false;
    if (part.type === 'text') return !!(part.text || part.delta);
    var name = resolveToolName(part.type);
    return !!name && SILENT_TOOLS.indexOf(name) === -1;
  }
  function resolveToolName(type) {
    if (typeof type !== 'string' || type.indexOf('tool-') !== 0) return null;
    for (var i = 0; i < ALL_TOOLS.length; i++) {
      if (isToolPart(type, ALL_TOOLS[i])) return ALL_TOOLS[i];
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Tiny DOM helpers.
  // ---------------------------------------------------------------------------
  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v == null) continue;
        if (k === 'class') n.className = v;
        else if (k === 'text') n.textContent = v;
        else n.setAttribute(k, v);
      }
    }
    if (children != null) {
      if (!Array.isArray(children)) children = [children];
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return n;
  }

  // Trusted, internal SVG markup -> element. Never used on model output.
  var ICONS = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
    refresh: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>',
    // Desktop layout-mode toggle glyphs (the icon shows the TARGET mode):
    // `modal` = a centered window inside the viewport frame, `sidebar` = a
    // panel docked to the right edge of the viewport frame.
    modal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="8" y="8" width="8" height="8" rx="1"/></svg>',
    sidebar: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="15" y1="3" x2="15" y2="21"/></svg>',
    send: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>',
    mic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>',
    // Voice-mode toggle (equalizer waveform) — distinct from the dictate mic.
    waveform: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="4" y2="15"/><line x1="9" y1="4" x2="9" y2="20"/><line x1="14" y1="7" x2="14" y2="17"/><line x1="19.5" y1="6" x2="19.5" y2="18"/></svg>',
    // Speaking indicator — same waveform bars; CSS animates them while playing
    // (frozen under prefers-reduced-motion).
    speaking: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="4" y2="15"/><line x1="9" y1="5" x2="9" y2="19"/><line x1="14" y1="7" x2="14" y2="17"/><line x1="19.5" y1="6" x2="19.5" y2="18"/></svg>',
    external: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>',
    cart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>',
    pin: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    // Feedback entry point + card head — a speech bubble.
    feedback: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
    // Customer Account tier-3 glyphs (sign-in card + signed-in history drawer).
    user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
    history: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></svg>',
    pencil: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>',
    trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    // Download glyph — signed-in "Zusammenfassung herunterladen" header button.
    download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
    // Newsletter reward badge / sign-in teaser. Always this literal name —
    // never icon(<served value>) (an unknown name returns null).
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>'
  };
  function icon(name) {
    var tpl = document.createElement('template');
    tpl.innerHTML = ICONS[name] || '';
    return tpl.content.firstElementChild;
  }

  // The morphing blob bundle inside the brand orb (trusted, internal markup —
  // never model output). Four soft <path> blobs on a 1200x1200 viewBox; the
  // CSS rotates each <g> and tweens each path's `d` between a shared set of
  // organic shapes (the Siri churn). Colours come from the CSS palette vars;
  // blur is an SVG <feGaussianBlur> per blob (stdDeviation in viewBox units,
  // so it scales with the orb on every renderer). Each path's start `d` equals
  // its 0% keyframe, so there's no jump on first paint / where `d` can't animate.
  // FILTER IDS MUST BE UNIQUE PER INSTANCE (the __UID__ placeholder is replaced
  // on injection): url(#...) resolves to the FIRST matching id in the document,
  // and WebKit/Blink fail to paint a filter defined inside a display:none
  // subtree — so with shared ids, hiding the launcher while the panel is open
  // broke every other orb (welcome/avatar).
  var LOGO_BLOBS =
    '<svg class="ms-chat-logo-blobs" viewBox="0 0 1200 1200" aria-hidden="true" focusable="false">' +
      '<defs>' +
        '<filter id="__UID__-b1" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="26"/></filter>' +
        '<filter id="__UID__-b2" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="20"/></filter>' +
        '<filter id="__UID__-b3" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="15"/></filter>' +
        '<filter id="__UID__-b4" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="100"/></filter>' +
      '</defs>' +
      '<g class="ms-chat-blob ms-chat-blob-1"><path filter="url(#__UID__-b1)" d="M 100 600 q 0 -500, 500 -500 t 500 500 t -500 500 T 100 600 z"/></g>' +
      '<g class="ms-chat-blob ms-chat-blob-2"><path filter="url(#__UID__-b2)" d="M 100 600 q 0 -400, 500 -500 t 400 500 t -500 500 T 100 600 z"/></g>' +
      '<g class="ms-chat-blob ms-chat-blob-3"><path filter="url(#__UID__-b3)" d="M 100 600 q -50 -400, 500 -500 t 450 550 t -500 500 T 100 600 z"/></g>' +
      '<g class="ms-chat-blob ms-chat-blob-4"><path filter="url(#__UID__-b4)" d="M 150 600 q 0 -600, 500 -500 t 500 550 t -500 500 T 150 600 z"/></g>' +
    '</svg>';
  var logoBlobsSeq = 0;
  function logoBlobs() {
    var uid = 'ms-chat-lg' + (++logoBlobsSeq) + '-' + Math.random().toString(36).slice(2, 6);
    var tpl = document.createElement('template');
    tpl.innerHTML = LOGO_BLOBS.replace(/__UID__/g, uid);
    return tpl.content.firstElementChild;
  }

  // Brand logo element: the orb (CSS on .ms-chat-logo) with the morphing-blob
  // SVG inside; the variant (full motion on launcher/welcome, paused avatar)
  // is chosen per context via the extra class.
  function logoEl(extraClass) {
    var s = el('span', { class: 'ms-chat-logo ' + (extraClass || ''), 'aria-hidden': 'true' });
    s.appendChild(logoBlobs());
    return s;
  }

  // ---------------------------------------------------------------------------
  // Tiny safe Markdown -> DOM renderer. Renders the subset Mo actually emits —
  // **bold**, *italic*, `code`, [label](url), # headings, -/*/+ and 1. lists,
  // > quotes, ``` fenced code, paragraphs and soft line breaks — as real DOM
  // nodes (createElement/createTextNode), NEVER innerHTML on model text, so the
  // model can't inject HTML (XSS-safe by construction: text becomes text, and
  // only an allowlist of tags is ever created). Underscores are deliberately
  // NOT emphasis markers so identifiers like update_customer_profile read
  // intact. Streaming-safe: an incomplete trailing token (an unclosed **, *,
  // `, [, ``` or a bare block marker still being typed) is withheld until it
  // completes, so half-Markdown never flashes; the held tail appears a token
  // later. Tool/product cards are rendered separately and are untouched.
  // ---------------------------------------------------------------------------

  function safeHref(url) {
    var u = String(url || '').trim();
    try {
      var parsed = new URL(u, window.location.href);
      var p = parsed.protocol.toLowerCase();
      if (p === 'http:' || p === 'https:' || p === 'mailto:') return parsed.href;
    } catch (e) {}
    return null;
  }

  // Inline span -> DOM nodes. A marker that has no closer in `str` is rendered
  // as the literal characters (the streaming cut already withholds incomplete
  // trailing markers, so this only literalizes genuinely-unclosed Markdown).
  function appendInline(container, str) {
    var i = 0, n = str.length, textStart = 0;
    function flushText(end) {
      if (end > textStart) container.appendChild(document.createTextNode(str.slice(textStart, end)));
    }
    while (i < n) {
      var c = str.charAt(i);
      if (c === '`') {
        var close = str.indexOf('`', i + 1);
        if (close === -1) { i++; continue; }
        flushText(i);
        container.appendChild(el('code', { class: 'ms-chat-md-code', text: str.slice(i + 1, close) }));
        i = close + 1; textStart = i; continue;
      }
      if (c === '[') {
        var lm = /^\[([^\]]*)\]\(([^)\s]*)\)/.exec(str.slice(i));
        if (lm) {
          flushText(i);
          var href = safeHref(lm[2]);
          if (href) container.appendChild(el('a', { href: href, target: '_blank', rel: 'noopener noreferrer', text: lm[1] }));
          else container.appendChild(document.createTextNode(lm[0])); // unsafe scheme -> literal
          i += lm[0].length; textStart = i; continue;
        }
        i++; continue;
      }
      if (c === '*') {
        var dbl = str.charAt(i + 1) === '*';
        var marker = dbl ? '**' : '*';
        var from = i + marker.length;
        var end = str.indexOf(marker, from);
        if (end === -1 || end === from) { i++; continue; } // no closer / empty -> literal
        flushText(i);
        var wrap = el(dbl ? 'strong' : 'em');
        appendInline(wrap, str.slice(from, end));
        container.appendChild(wrap);
        i = end + marker.length; textStart = i; continue;
      }
      i++;
    }
    flushText(n);
  }

  // How much of a still-streaming string is safe to render now: withhold a
  // trailing incomplete inline token so partial Markdown never flashes.
  function inlineScanSafe(s) {
    var i = 0, n = s.length;
    while (i < n) {
      var c = s.charAt(i);
      if (c === '`') {
        var close = s.indexOf('`', i + 1);
        if (close === -1) return i; // unclosed code span
        i = close + 1; continue;
      }
      if (c === '[') {
        var lm = /^\[([^\]]*)\]\(([^)\s]*)\)/.exec(s.slice(i));
        if (!lm) return i; // link still being typed
        i += lm[0].length; continue;
      }
      if (c === '*') {
        var marker = s.charAt(i + 1) === '*' ? '**' : '*';
        var end = s.indexOf(marker, i + marker.length);
        if (end === -1) return i; // unclosed emphasis
        i = end + marker.length; continue;
      }
      i++;
    }
    return n;
  }

  function streamingSafeText(s) {
    // Inside an open fenced code block -> hold from the opening fence.
    var fences = s.match(/```/g);
    if (fences && fences.length % 2 === 1) s = s.slice(0, s.lastIndexOf('```'));
    // Hold a trailing incomplete inline span (emphasis / code / link).
    s = s.slice(0, inlineScanSafe(s));
    // Hold a trailing bare block marker still being typed ("#", "- ", ">", "1.").
    s = s.replace(/\n[ \t]*(?:#{1,6}|[-+*]|\d+[.)]|>)[ \t]*$/, '');
    return s;
  }

  var BLOCK_FENCE = /^\s*```/,
      BLOCK_HEAD = /^\s*(#{1,6})\s+(.*)$/,
      BLOCK_QUOTE = /^\s*>\s?/,
      BLOCK_UL = /^\s*[-+*]\s+/,
      BLOCK_OL = /^\s*\d+[.)]\s+/;

  function isBlockStart(line) {
    return BLOCK_FENCE.test(line) || BLOCK_HEAD.test(line) || BLOCK_QUOTE.test(line) ||
           BLOCK_UL.test(line) || BLOCK_OL.test(line);
  }

  // Block-level Markdown -> DOM nodes (recurses for blockquote contents).
  function renderBlocks(node, text) {
    var lines = String(text == null ? '' : text).split('\n');
    var i = 0;
    while (i < lines.length) {
      var line = lines[i];
      if (!line.trim()) { i++; continue; } // blank line: paragraph break

      if (BLOCK_FENCE.test(line)) {
        var code = [];
        i++;
        while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) { code.push(lines[i]); i++; }
        if (i < lines.length) i++; // skip closing fence
        node.appendChild(el('pre', { class: 'ms-chat-md-pre' }, el('code', { text: code.join('\n') })));
        continue;
      }

      var h = BLOCK_HEAD.exec(line);
      if (h) {
        var hTag = 'h' + Math.min(6, h[1].length + 2); // # -> h3 (chat-scale)
        var hn = el(hTag, { class: 'ms-chat-md-h' });
        appendInline(hn, h[2].trim());
        node.appendChild(hn);
        i++; continue;
      }

      if (BLOCK_QUOTE.test(line)) {
        var quote = [];
        while (i < lines.length && BLOCK_QUOTE.test(lines[i])) { quote.push(lines[i].replace(BLOCK_QUOTE, '')); i++; }
        var bq = el('blockquote', { class: 'ms-chat-md-quote' });
        renderBlocks(bq, quote.join('\n'));
        node.appendChild(bq);
        continue;
      }

      if (BLOCK_UL.test(line)) {
        var ul = el('ul', { class: 'ms-chat-md-list' });
        while (i < lines.length && BLOCK_UL.test(lines[i])) {
          appendInline(ul.appendChild(el('li')), lines[i].replace(BLOCK_UL, ''));
          i++;
        }
        node.appendChild(ul);
        continue;
      }

      if (BLOCK_OL.test(line)) {
        var ol = el('ol', { class: 'ms-chat-md-list' });
        while (i < lines.length && BLOCK_OL.test(lines[i])) {
          appendInline(ol.appendChild(el('li')), lines[i].replace(BLOCK_OL, ''));
          i++;
        }
        node.appendChild(ol);
        continue;
      }

      // Paragraph: gather consecutive non-blank, non-block lines. A single
      // newline inside the run is a soft break (<br>); a blank line ends it.
      var para = [];
      while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i])) { para.push(lines[i]); i++; }
      var p = el('p');
      for (var j = 0; j < para.length; j++) {
        if (j > 0) p.appendChild(el('br'));
        appendInline(p, para[j]);
      }
      node.appendChild(p);
    }
  }

  // Render `text` as formatted Markdown into `node`. When `streaming` is true,
  // an incomplete trailing token is withheld to avoid flashing broken Markdown.
  function renderMarkdownInto(node, text, streaming) {
    node.replaceChildren();
    var t = String(text == null ? '' : text);
    if (streaming) t = streamingSafeText(t);
    renderBlocks(node, t);
  }

  // ---------------------------------------------------------------------------
  // Product hydration with in-session cache.
  // ---------------------------------------------------------------------------
  var productCache = {}; // id -> product | null

  function chunk(arr, size) {
    var out = [];
    for (var i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  function hydrate(ids) {
    ids = (ids || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    var uniq = [];
    var seen = {};
    for (var i = 0; i < ids.length; i++) { if (!seen[ids[i]]) { seen[ids[i]] = 1; uniq.push(ids[i]); } }
    var need = uniq.filter(function (id) { return !(id in productCache); });

    var jobs = chunk(need, 10).map(function (batch) {
      var url = API_BASE + '/api/products?ids=' + encodeURIComponent(batch.join(','));
      return fetch(url, { method: 'GET', headers: { 'x-ms-session': sid } })
        .then(function (res) {
          // Cache ONLY what a successful response asserts. The contract
          // reserves null for UNKNOWN ids — a transient 429/network failure
          // must cache NOTHING, so a later tool call retries instead of
          // rendering nothing for the rest of the session.
          if (!res.ok) return;
          return res.json().then(function (data) {
            var prods = (data && data.products) || [];
            for (var j = 0; j < batch.length; j++) {
              productCache[batch[j]] = prods[j] != null ? prods[j] : null;
              cfgNoteRules(prods[j]);
            }
          });
        })
        .catch(function () { /* failed fetch: cache nothing -> retried later */ });
    });

    return Promise.all(jobs).then(function () {
      return ids.map(function (id) { return id in productCache ? productCache[id] : null; });
    });
  }

  // ---------------------------------------------------------------------------
  // Canonical consent copy for the capture form (API_CONTRACT.md §7.4).
  // Sourced ONLY from the backend: seeded from the offer_email_summary tool
  // result and/or fetched from GET /api/consent-copy — both serve the
  // identical payload. Held in memory for 60s (matching the endpoint's
  // Cache-Control) so a lawyer copy change reaches live widgets quickly;
  // never persisted across sessions.
  // ---------------------------------------------------------------------------
  var CONSENT_COPY_TTL_MS = 60 * 1000;
  var consentCopyCache = null;    // { copy, at }
  var consentCopyInflight = null; // de-duped GET while a fetch is pending

  function validConsentCopy(c) {
    return !!(c && typeof c === 'object' &&
      typeof c.transactionalLabel === 'string' && c.transactionalLabel &&
      typeof c.marketingLabel === 'string' && c.marketingLabel &&
      typeof c.consentTextShown === 'string' && c.consentTextShown);
  }

  function seedConsentCopy(c) {
    if (validConsentCopy(c)) consentCopyCache = { copy: c, at: Date.now() };
  }

  // Returning-customer hint (consent copy v2): backend-served AND
  // backend-gated. Returns the hint text ONLY when the backend explicitly
  // marks it enabled; an absent field (older backend) or a disabled
  // server-side switch -> null, and the caller renders NOTHING — the widget
  // carries no fallback hint copy.
  function returningHintText(c) {
    var h = c && c.returningHint;
    return (h && h.enabled === true && typeof h.text === 'string' && h.text) ? h.text : null;
  }

  function fetchConsentCopy() {
    if (consentCopyCache && (Date.now() - consentCopyCache.at) < CONSENT_COPY_TTL_MS) {
      return Promise.resolve(consentCopyCache.copy);
    }
    if (consentCopyInflight) return consentCopyInflight;
    // ?locale= switches the served consent strings (LOCALE.md §2). Query, not a
    // custom header, so the origin-allowlisted consent-copy GET keeps working
    // without a new CORS preflight allowance; de is byte-identical to before.
    consentCopyInflight = fetch(API_BASE + '/api/consent-copy?locale=' + LOCALE, { method: 'GET', headers: { 'x-ms-session': sid } })
      .then(function (res) {
        if (!res.ok) throw new Error('consent-copy ' + res.status);
        return res.json();
      })
      .then(function (data) {
        consentCopyInflight = null;
        if (!validConsentCopy(data)) throw new Error('consent-copy: invalid payload');
        consentCopyCache = { copy: data, at: Date.now() };
        return data;
      })
      .catch(function (err) {
        consentCopyInflight = null;
        throw err;
      });
    return consentCopyInflight;
  }

  // At-sign-in marketing opt-in copy (consent copy v3 — CONSENT_FLOW.md §2.1).
  // A SEPARATE payload from the capture form's: it carries no email field and
  // no transactional label (the customer is signed in, so we already hold the
  // verified email). Fetched from GET /api/consent-copy?surface=signin and held
  // in its own short-lived cache; like the capture copy it is served-only and
  // never hard-coded, so the stored Art. 7 audit text can't diverge from what
  // was shown. Same guard as the default consent-copy call (no shared secret —
  // these are public strings already shown to users).
  // Keyed by the session id (CONSENT_CONTRACT §3.1 "Cache per session"): while
  // the backend runs a framing test it assigns the variant per x-ms-session, so
  // a copy fetched under a previous sid (sign-out, rotation, another tab's sid)
  // must never be rendered or reported for the current one.
  var signInConsentCache = null;    // { sid, copy, at }
  var signInConsentInflight = null; // { sid, p } — de-duped GET while a fetch is pending

  function validSignInConsentCopy(c) {
    return !!(c && typeof c === 'object' &&
      typeof c.marketingLabel === 'string' && c.marketingLabel &&
      typeof c.consentTextShown === 'string' && c.consentTextShown);
  }

  function fetchSignInConsentCopy() {
    var reqSid = sid;
    if (signInConsentCache && signInConsentCache.sid === reqSid &&
        (Date.now() - signInConsentCache.at) < CONSENT_COPY_TTL_MS) {
      return Promise.resolve(signInConsentCache.copy);
    }
    if (signInConsentInflight && signInConsentInflight.sid === reqSid) return signInConsentInflight.p;
    var entry = { sid: reqSid, p: null };
    entry.p = fetch(API_BASE + '/api/consent-copy?surface=signin&locale=' + LOCALE, { method: 'GET', headers: { 'x-ms-session': reqSid } })
      .then(function (res) {
        if (!res.ok) throw new Error('consent-copy signin ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (signInConsentInflight === entry) signInConsentInflight = null;
        if (!validSignInConsentCopy(data)) throw new Error('consent-copy signin: invalid payload');
        signInConsentCache = { sid: reqSid, copy: data, at: Date.now() };
        return data;
      })
      .catch(function (err) {
        if (signInConsentInflight === entry) signInConsentInflight = null;
        throw err;
      });
    signInConsentInflight = entry;
    return entry.p;
  }

  // Served benefit bullets of surface=signin (CONSENT_CONTRACT §3.1): rendered
  // only when ALL are valid — an array of 1–4 strings, each non-empty after
  // trim and at most 200 characters. Anything else -> null: no list at all,
  // never a subset and never bullets of the widget's own (§0 rule 11).
  function servedBenefits(c) {
    var b = c && c.benefits;
    if (!Array.isArray(b) || b.length < 1 || b.length > 4) return null;
    for (var i = 0; i < b.length; i++) {
      if (typeof b[i] !== 'string' || !b[i].trim() || b[i].length > 200) return null;
    }
    return b.slice();
  }
  // The served framing variant id, echoed (never rendered) in the opt-in POST
  // and the consent_gate_* data; anything not matching the id pattern is left
  // out everywhere.
  function servedVariant(c) {
    var v = c && c.variant;
    return (typeof v === 'string' && /^[a-z0-9_-]{1,32}$/.test(v)) ? v : null;
  }
  // consent_gate_* KPI data: ids, enums and booleans only (API_CONTRACT §5).
  // `reward` (boolean) only when the served reward rendered on that ask —
  // otherwise the key is absent, keeping the pre-reward shape.
  function consentGateData(placement, variant, reward) {
    var d = { surface: 'signin', placement: placement };
    if (variant) d.variant = variant;
    if (reward) d.reward = true;
    return d;
  }
  // The served bullets as a list, each item via textContent. `cls` is passed
  // as a complete class-name literal by the caller (the backend fingerprints
  // 'ms-chat-optin-benefits' in the served file).
  function benefitsList(items, cls) {
    var ul = el('ul', { class: cls });
    items.forEach(function (b) {
      var li = el('li');
      li.appendChild(icon('check'));
      li.appendChild(el('span', { text: b }));
      ul.appendChild(li);
    });
    return ul;
  }

  // Served newsletter reward + value-moment lead (copy v6, optional). Like the
  // bullets: served strings only, rendered verbatim via textContent, never a
  // widget fallback. An absent / invalid field -> null -> exactly the v5 DOM.
  // On /en only once the backend marks the English copy legally reviewed.
  // Callers additionally require c.lawyerApproved === true.
  function servedStr(v, max) {
    return (typeof v === 'string' && v.trim() && v.length <= max) ? v : null;
  }
  function servedObj(c, key) {
    if (!c || (LOCALE === 'en' && c.enLegalReviewed !== true)) return null;
    var o = c[key];
    return (o && Object.prototype.toString.call(o) === '[object Object]') ? o : null;
  }
  // All-or-nothing on badge + terms; an invalid optional sub-field is dropped
  // on its own (null) and never invalidates the reward.
  function servedReward(c) {
    var r = servedObj(c, 'reward');
    if (!r) return null;
    var badge = servedStr(r.badge, 60), terms = servedStr(r.terms, 300);
    if (!badge || !terms) return null;
    // A blank string would resolve to the current page in safeHref -> require
    // a non-empty value first.
    var url = (typeof r.termsUrl === 'string' && r.termsUrl.trim()) ? safeHref(r.termsUrl) : null;
    return {
      badge: badge,
      terms: terms,
      termsUrl: url || null,
      teaser: servedStr(r.teaser, 160),
      afterAccept: servedStr(r.afterAccept, 200)
    };
  }
  function servedValueMoment(c) {
    var v = servedObj(c, 'valueMoment');
    var lead = v ? servedStr(v.lead, 200) : null;
    return lead ? { lead: lead } : null;
  }
  // Reward DOM (CONSENT_CONTRACT §3.1 extended). Served text via textContent,
  // the terms link only through safeHref (servedReward), the glyph a constant.
  function rewardBadge(r) {
    var d = el('div', { class: 'ms-chat-reward-badge' });
    d.appendChild(icon('gift'));
    d.appendChild(el('span', { text: r.badge }));
    return d;
  }
  function rewardTerms(r) {
    var d = el('div', { class: 'ms-chat-reward-terms', text: r.terms });
    if (r.termsUrl) {
      d.appendChild(document.createTextNode(' '));
      d.appendChild(el('a', { href: r.termsUrl, target: '_blank', rel: 'noopener noreferrer', text: L('Bedingungen', 'Conditions') }));
    }
    return d;
  }
  function rewardTeaser(r) {
    var d = el('div', { class: 'ms-chat-reward-teaser' });
    d.appendChild(icon('gift'));
    d.appendChild(el('span', { text: r.teaser }));
    return d;
  }
  // The teaser of the anonymous sign-in surfaces (login popup, welcome card):
  // the reward with a valid teaser from approved copy, else null.
  function signInTeaserReward(c) {
    if (!c || c.lawyerApproved !== true) return null;
    var r = servedReward(c);
    return (r && r.teaser) ? r : null;
  }

  // ---------------------------------------------------------------------------
  // Customer Account sign-in (tier 3) + signed-in conversation history.
  //
  // ADDITIVE BY DESIGN. The anonymous and email-only flows are untouched: when
  // the user never signs in, `auth.signedIn` stays false and every branch below
  // collapses to the pre-existing behaviour (the only new surface is the
  // optional sign-in card/affordance, which is the whole point of the tier).
  //
  // The widget NEVER handles OAuth tokens. Sign-in is a top-level redirect to
  // the backend's hosted flow; identity is re-hydrated via a guarded XHR to
  // /api/auth/me. The EXISTING session id (`sid`) is the opaque reference to
  // the signed-in identity — it survives the redirect unchanged, so we never
  // rotate it around sign-in (CUSTOMER_ACCOUNT.md §1). All /api/auth/me and
  // /api/account/* calls carry the same guards as the chat endpoints
  // (x-ms-chat-key + x-ms-session). Docs: docs/ai-advisor/CUSTOMER_ACCOUNT.md,
  // docs/ai-advisor/API_CONTRACT.md.
  // ---------------------------------------------------------------------------
  // UI CHROME only (German). No legal/consent copy lives here.
  var ACCOUNT_COPY = {
    signInTitle: 'Hol mehr aus deiner Beratung',
    signInIntro: 'Mit deinem motion sports Konto wird Mo zu deinem persönlichen Berater:',
    benefits: [
      'An frühere Beratungen anknüpfen',
      'Bestellungen & Adresse einbeziehen',
      'Persönliche Angebote & Aktionen zuerst sehen'
    ],
    signInBtn: 'Jetzt anmelden',
    signInHeader: 'Anmelden',
    // Reassurance under the CTA: signing in is optional, the chat works now.
    signInHint: 'Kein Konto? Einfach lostippen — Mo hilft dir sofort.',
    historyTitle: 'Deine Beratungen',
    historyAria: 'Beratungsverlauf',
    newChat: 'Neue Beratung',
    loading: 'Wird geladen…',
    loadError: 'Verlauf konnte nicht geladen werden.',
    empty: 'Noch keine gespeicherten Beratungen.',
    rename: 'Umbenennen',
    del: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    deleteConfirm: 'Dieser Chat wird gelöscht.',
    deleteYes: 'Löschen',
    msgCount: function (n) { return (n || 0) + (n === 1 ? ' Nachricht' : ' Nachrichten'); },
    logout: 'Abmelden',
    // GDPR Art. 15/20 data export (GET /api/account/export). A safe read, so —
    // unlike erase — no confirm step; `exportError` is the exact friendly copy
    // for any non-200 (401 silently drops to anonymous; 503 etc. surface here).
    exportData: 'Meine Daten herunterladen',
    exportBusy: 'Wird vorbereitet…',
    exportError: 'Download fehlgeschlagen — bitte später erneut versuchen.',
    erase: 'Alle meine Daten löschen',
    // The erase confirmation itself (heading, body, button, done/failed text)
    // is backend-served (GET /api/consent-copy?surface=erase) and never lives
    // here — its body depends on whether the shop account is erased too.
    // These are only the chrome around it.
    eraseCopyError: 'Die Löschung kann gerade nicht vorbereitet werden.',
    retry: 'Erneut versuchen',
    eraseBusy: 'Wird gelöscht…',
    eraseError: 'Löschen gerade nicht möglich — bitte später erneut versuchen.',
    eraseDoneOk: 'Schließen',
    openError: 'Diese Beratung konnte nicht geöffnet werden.'
  };
  if (LOCALE === 'en') Object.assign(ACCOUNT_COPY, {
    signInTitle: 'Get more out of your consultation',
    signInIntro: 'With your motion sports account, Mo becomes your personal advisor:',
    benefits: [
      'Pick up where past consultations left off',
      'Include your orders & address',
      'See personal offers & promotions first'
    ],
    signInBtn: 'Sign in now',
    signInHeader: 'Sign in',
    signInHint: 'No account? Just start typing — Mo helps you right away.',
    historyTitle: 'Your consultations',
    historyAria: 'Consultation history',
    newChat: 'New consultation',
    loading: 'Loading…',
    loadError: 'History could not be loaded.',
    empty: 'No saved consultations yet.',
    rename: 'Rename',
    del: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    deleteConfirm: 'This chat will be deleted.',
    deleteYes: 'Delete',
    msgCount: function (n) { return (n || 0) + (n === 1 ? ' message' : ' messages'); },
    logout: 'Sign out',
    exportData: 'Download my data',
    exportBusy: 'Preparing…',
    exportError: 'Download failed — please try again later.',
    erase: 'Delete all my data',
    eraseCopyError: 'The deletion can\'t be prepared right now.',
    retry: 'Try again',
    eraseBusy: 'Deleting…',
    eraseError: 'Deletion isn\'t possible right now — please try again later.',
    eraseDoneOk: 'Close',
    openError: 'This consultation could not be opened.'
  });

  // Device hint: set once we've confirmed a signed-in session so a later visit
  // re-probes /api/auth/me even before the storefront customer hint is read.
  // Never carries identity itself — just "worth asking the backend again".
  var SIGNED_IN_KEY = 'ms-chat-signed-in';
  // `marketing` / `optInActionable` mirror /api/auth/me (CUSTOMER_ACCOUNT.md §4):
  // optInActionable is the backend's single source of truth for "show the
  // at-sign-in opt-in" (true ⇔ no marketing decision on record yet + a verified
  // email). We never re-derive it from `status` ourselves.
  var auth = { settled: false, signedIn: false, name: null, tier: null, marketing: null, optInActionable: false };
  // The conversation currently loaded into the local view (for list highlight);
  // purely informational — the backend resolves the active thread from `sid`.
  var activeConversationId = null;
  // Active conversation THREAD key (CUSTOMER_ACCOUNT.md §7.6 / API_CONTRACT.md
  // §2): addresses WHICH thread a /api/chat turn appends to, so a signed-in
  // customer keeps several conversations under one stable session_id. NULL for
  // anonymous / email-only — we then OMIT conversationKey entirely, so the
  // backend defaults to the legacy one-thread-per-session behaviour and tiers
  // 1–2 stay byte-identical. Persisted per-session so a reload resumes the same
  // thread alongside the locally cached transcript.
  var activeConversationKey = loadConvKey();
  function loadConvKey() { return lsGet(convKeyStorageKey()) || null; }
  function saveConvKey() {
    if (!sidIsCurrent()) return;
    try { if (activeConversationKey) lsSet(convKeyStorageKey(), activeConversationKey); else lsDel(convKeyStorageKey()); } catch (e) {}
  }
  function clearConvKey() { activeConversationKey = null; try { lsDel(convKeyStorageKey()); } catch (e) {} }
  // Mint a fresh thread key for a brand-new signed-in conversation so it becomes
  // its own history row. Only mint on the FIRST turn of a fresh thread; an
  // in-progress local thread that has no key stays legacy (keyed server-side by
  // session_id) so we never fork an existing conversation mid-stream.
  function maybeMintConversationKey(isFreshThread) {
    if (!auth.signedIn || activeConversationKey || !isFreshThread) return;
    activeConversationKey = uuid();
    saveConvKey();
    updateDownloadBtn();
  }

  // Guarded account/auth XHR headers (CUSTOMER_ACCOUNT.md §4/§7).
  function accountHeaders() { return { 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE }; }

  // Best-effort, non-authoritative pre-hint: is the visitor logged into the
  // storefront? Used ONLY to decide whether the silent /api/auth/me probe is
  // worth doing — never to gate identity (CUSTOMER_ACCOUNT.md §3).
  function storefrontCustomerHint() {
    try {
      return !!(window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
                window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId);
    } catch (e) { return false; }
  }
  function shouldProbeAuth() {
    return lsGet(SIGNED_IN_KEY) === '1' || storefrontCustomerHint();
  }

  // `transient` (probeAuth only): the backend could not answer (network,
  // 429, 5xx). The UI still fails closed, but the device-side records of the
  // sign-in — the re-probe hint and the per-sid sign-in origin — are kept: a
  // blip must not turn into a lasting sign-out (no re-probe on the next open)
  // or hide "Mit Kundenkonto anmelden" from a whoami-recognised visitor once
  // the next probe succeeds (updateShopSignInBtn reads the origin record).
  function applyAuth(data, transient) {
    auth.settled = true;
    if (data && data.signedIn) {
      auth.signedIn = true;
      auth.name = (data.identity && data.identity.name) || null;
      auth.tier = (data.identity && data.identity.tier) || 3;
      // Marketing decision state drives the at-sign-in opt-in (§6.1). Trust
      // optInActionable verbatim — it is false once any decision exists.
      auth.marketing = (data.marketing && typeof data.marketing === 'object') ? data.marketing : null;
      auth.optInActionable = !!(auth.marketing && auth.marketing.optInActionable === true);
      lsSet(SIGNED_IN_KEY, '1');
    } else {
      auth.signedIn = false;
      auth.name = null;
      auth.tier = null;
      auth.marketing = null;
      auth.optInActionable = false;
      if (!transient) {
        lsDel(SIGNED_IN_KEY);
        lsDel(authViaKey()); // the origin of a sign-in that no longer exists
      }
    }
    reflectAuthState();
    flushAutoHistory(); // an async probe that resolves while the panel is open
  }

  // Ask the backend who this session belongs to (CUSTOMER_ACCOUNT.md §4).
  // Fails closed: any error => not signed in.
  var authProbed = false;
  function probeAuth(force) {
    if (authProbed && !force) return Promise.resolve(auth);
    authProbed = true;
    var url = API_BASE + '/api/auth/me?session=' + encodeURIComponent(sid);
    var transient = false;
    var reqSid = sid;
    var wasSignedIn = auth.signedIn || lsGet(SIGNED_IN_KEY) === '1';
    return fetch(url, { method: 'GET', headers: accountHeaders() })
      .then(function (r) {
        if (r.ok) return r.json();
        if (r.status === 429 || r.status >= 500) transient = true;
        return null; // 401/403 etc.: a definite "not signed in"
      })
      .then(function (data) {
        if (sid !== reqSid) return auth; // the session changed meanwhile
        applyAuth(data, transient);
        if (!transient && !(data && data.signedIn) && wasSignedIn) endedSignInCleanup();
        return auth;
      })
      .catch(function () { applyAuth(null, true); return auth; });
  }

  // The server says a session this device had signed in no longer resolves —
  // logged out (here, on another device or in the shop), expired or erased.
  // Its stored history can hold the customer's order status
  // (CHAT_ORDER_STATUS.md), so it goes exactly like on a local sign-out; the
  // next person on a shared browser must not see it. Only a DEFINITIVE answer
  // gets here (/api/auth/me signedIn:false or 401/403, an /api/account/* 401),
  // never a network error or 5xx, and never for a visitor that was anonymous.
  function endedSignInCleanup() {
    ssSet(WHOAMI_SS_KEY, '1'); // the shop must not silently re-link the fresh sid
    moBlankCart(); // before the rotation drops the cached token
    closeHistory();
    dropSessionHistory();
  }

  // Where this session's signed-in state came from, kept per sid on the
  // device: 'chat' = completed the chat's own "Anmelden" (ms_code redeemed),
  // 'shop' = recognised only through the shop's login (/apps/chat/whoami
  // linkCode redeemed). The backend gives order status (get_order_status) only
  // to the 'chat' kind (CHAT_ORDER_STATUS.md), so a 'shop' visitor keeps an
  // "Anmelden" action in the account menu. A 'chat' record is never
  // downgraded by a later shop recognition (the backend keeps it the same way).
  function authViaKey() { return 'ms-chat-auth-via:' + sid; }
  function authVia() { return lsGet(authViaKey()); }
  function setAuthVia(kind) {
    if (kind === 'shop' && authVia() === 'chat') return;
    lsSet(authViaKey(), kind);
  }

  // Complete a sign-in with its one-time code (CUSTOMER_ACCOUNT.md §2a). Since
  // 2026-10-03 a Shopify sign-in no longer links the chat session by itself —
  // the session id travels in a URL anyone can prepare, so a stranger could
  // otherwise plant their own id in a logged-in shopper's login link. The code
  // (`?ms_code=` on the return, or `linkCode` from whoami) is redeemed with
  // THIS browser's own x-ms-session — the same sid the login/whoami used — and
  // only then does /api/auth/me report signed in. Shared by both paths.
  // `kind` ('chat' | 'shop') records the sign-in origin locally on success.
  // Resolves 'ok' | 'refused' (400 etc.: expired, used, another session's
  // code — never retry, never with another sid) | 'unavailable' (503, other
  // 5xx/429, network: the backend could not decide). Never rejects.
  function redeemLinkCode(code, kind) {
    if (typeof code !== 'string' || !code) return Promise.resolve('refused');
    try {
      var h = accountHeaders();
      h['Content-Type'] = 'application/json';
      return fetch(API_BASE + '/api/auth/link', { method: 'POST', headers: h, body: JSON.stringify({ code: code }) })
        .then(function (r) {
          if (r.status === 200) {
            // 200 means the link was written; the body is only a confirmation
            // (/api/auth/me stays the authority on the identity).
            return r.json().catch(function () { return null; }).then(function (d) {
              if (d && d.signedIn === false) return 'refused';
              if (kind) setAuthVia(kind);
              return 'ok';
            });
          }
          if (r.status === 503 || r.status === 429 || r.status >= 500) return 'unavailable';
          return 'refused';
        })
        .catch(function () { return 'unavailable'; });
    } catch (e) { return Promise.resolve('unavailable'); }
  }

  // 503 on the return: the code is still unused, so keep it for ONE retry on
  // the next page load of this tab (sessionStorage — never localStorage, it is
  // a short-lived credential), only while it is younger than the code's 10 min
  // lifetime and the session id is unchanged. Deleted on read, so it can never
  // loop.
  var LINK_RETRY_KEY = 'ms-chat-link-retry';
  var LOGIN_SID_SS_KEY = 'ms-chat-login-sid';
  var LINK_RETRY_MAX_MS = 10 * 60 * 1000;
  // In-flight sign-in completion (code redeem + /api/auth/me). A panel open
  // during it waits instead of racing a second detection.
  var authLinkInflight = null;
  function retryPendingLink() {
    var raw = ssGet(LINK_RETRY_KEY);
    if (raw == null) return;
    ssDel(LINK_RETRY_KEY);
    var p = null;
    try { p = JSON.parse(raw); } catch (e) { return; }
    if (!p || typeof p.code !== 'string' || p.sid !== sid) return;
    var age = Date.now() - Number(p.at);
    if (!(age >= 0 && age < LINK_RETRY_MAX_MS)) return;
    // Silent: no notice and no second account_signin_return (the return was
    // already counted as link_failed; the backend records the link itself).
    // On anything but 'ok' the normal lazy detection runs on the next open.
    authLinkInflight = redeemLinkCode(p.code, p.kind === 'shop' ? 'shop' : 'chat').then(function (res) {
      if (res === 'ok') return probeAuth(true);
    }).then(function () { authLinkInflight = null; }, function () { authLinkInflight = null; });
  }

  // Shop-native already-signed-in detection (CUSTOMER_ACCOUNT.md §3a). Reaches
  // GET /api/auth/storefront through the Shopify App Proxy at the SAME-ORIGIN
  // path `/apps/chat/whoami` — the one channel where Shopify itself vouches the
  // logged-in customer to our cross-origin backend (it adds a signed
  // `logged_in_customer_id`). This recognises a customer who signed in via the
  // shop's OWN account icon — which /api/auth/me alone can never see. The path
  // is same-origin (no API_BASE, no x-ms-chat-key; Shopify's HMAC is the
  // guard); credentials:'include' so the shop's session cookie rides along.
  // ONCE per browser session (sessionStorage flag, set before the call so a
  // failure is never retried): a later open, visibilitychange or a sign-out in
  // this tab never re-asks the shop. The answer is used only to redeem its
  // `linkCode` — never displayed directly, logged or sent anywhere else; the
  // identity then comes from /api/auth/me like any sign-in. Resolves `true`
  // once the session is linked and probed signed-in. Everything else — a
  // storefront 404 page (proxy not set up yet), non-JSON, network error,
  // signedIn:false, linkCode null, link 400/503 — resolves `false` silently
  // and the caller falls back to the chatbot-OAuth re-hydration path.
  var WHOAMI_SS_KEY = 'ms-chat-whoami-done';
  // The one whoami -> link -> /api/auth/me chain while it runs. A second
  // detection meanwhile (visibilitychange, another open) must wait for it: it
  // would skip whoami (flag already set) and settle the visitor as anonymous
  // — login popup eligible — until the redeem lands.
  var whoamiInflight = null;
  function detectViaStorefront() {
    if (ssGet(WHOAMI_SS_KEY) === '1') return Promise.resolve(false);
    ssSet(WHOAMI_SS_KEY, '1');
    try {
      var base = String(CFG.whoamiPath || '/apps/chat/whoami');
      var askSid = sid; // the linkCode is minted for THIS session
      var url = base + (base.indexOf('?') >= 0 ? '&' : '?') + 'session=' + encodeURIComponent(askSid);
      var chain = fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' }, credentials: 'include' })
        .then(function (r) {
          if (!r.ok) return null; // not configured / storefront 404 -> no detection
          var ct = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
          if (ct.indexOf('application/json') < 0) return null; // a storefront HTML page, not the proxy
          return r.json().catch(function () { return null; });
        })
        .then(function (data) {
          // Deliberately stricter than CUSTOMER_ACCOUNT.md §3a's "linkCode
          // null => display only, stay unlinked": an unlinked session would
          // show a name while every /api/account/* call 401s (history, export,
          // erase), §6.1 forbids the consent popup before the link, and the
          // whoami answer is never displayed directly (see above). So a null
          // linkCode (or a refused redeem) is the same silent fallback as
          // signedIn:false — the visitor keeps the normal "Anmelden".
          if (!data || data.signedIn !== true || typeof data.linkCode !== 'string' || !data.linkCode) return false;
          if (sid !== askSid) return false; // session rotated meanwhile: never redeem with another id
          var linkCode = data.linkCode;
          return redeemLinkCode(linkCode, 'shop').then(function (res) {
            if (res === 'unavailable') {
              // Same rule as the sign-in return (task 1): a 503 keeps the
              // unused code for ONE retry on the next page load.
              ssSet(LINK_RETRY_KEY, JSON.stringify({ code: linkCode, sid: askSid, at: Date.now(), kind: 'shop' }));
            }
            if (res !== 'ok') return false;
            return probeAuth(true).then(function () { return auth.signedIn; });
          });
        })
        .catch(function () { return false; });
      whoamiInflight = chain;
      chain.then(function () { whoamiInflight = null; });
      return chain;
    } catch (e) { return Promise.resolve(false); }
  }

  // Resolve signed-in state on open / on becoming visible (task §1). The App
  // Proxy detection runs first (catches the shop's OWN login; a no-op after
  // its one call per browser session); only if that comes back not-signed-in
  // do we re-hydrate a chatbot-OAuth session via /api/auth/me — and then only
  // when a local hint says it's worth a call, so a pure-anonymous visitor
  // stays quiet. Either way a not-signed-in result settles the anonymous UX
  // unchanged (renders the sign-in affordance).
  function detectSignedIn(force) {
    // The first detection's own continuation below decides; never race it.
    if (whoamiInflight) return whoamiInflight.then(function () {});
    return detectViaStorefront().then(function (signedIn) {
      if (signedIn) return;
      if (shouldProbeAuth()) return probeAuth(force).then(function () {});
      applyAuth(null);
    });
  }

  // Top-level redirect into the backend's hosted sign-in, returning to THIS
  // page so the same conversation re-hydrates (CUSTOMER_ACCOUNT.md §2). We do
  // NOT use prompt=none here — this is a deliberate user click. The silent
  // prompt=none optimisation is intentionally skipped in favour of the
  // documented one-click affordance (see backend-handoff note).
  // `source` (optional) tags WHERE the click came from in the KPI so the
  // post-first-message login popup can be measured against the welcome card /
  // header button; existing callers pass nothing and send `{}` exactly as before.
  // 'login_gate' is the ONLY source value the KPI contract defines
  // (API_CONTRACT.md §5: "source only when it came from the popup") — every
  // other button (welcome card, header, account menu, link-failed notice)
  // passes nothing.
  // track() dispatches its fetch (keepalive:true) synchronously, BEFORE the
  // navigation below, so the event survives the page unload; the sid in the
  // KPI, the login URL and the later x-ms-session of /api/auth/link is the
  // same localStorage id (the admin funnel joins on it).
  function initiateLogin(source) {
    try {
      track('account_signin_started', source ? { source: source } : {});
      ssSet('ms-chat-auth-return', '1'); // re-open the panel when we come back
      // Pin the sid this login uses: the return must redeem its code with
      // exactly this x-ms-session (§2a). If another tab rotated the device's
      // session meanwhile, the code is NOT redeemed with the other id.
      ssSet(LOGIN_SID_SS_KEY, sid);
      var url = new URL(API_BASE + '/api/auth/shopify/login');
      url.searchParams.set('session', sid);
      url.searchParams.set('return_url', window.location.href);
      window.location.assign(url.toString());
    } catch (e) {
      try { console.error('[ms-chat] sign-in redirect failed', e); } catch (e2) {}
    }
  }

  // The chat sign-in could not be completed (code refused / missing, or the
  // backend unavailable): stay anonymous and say so, with "Anmelden" one tap
  // away again (it starts a NEW login with the same sid — the failed code is
  // never retried with another session id).
  function showLinkFailedNotice(unavailable) {
    try {
      if (unavailable) {
        showNotice('info', L('Die Anmeldung ist gerade nicht möglich — wir versuchen es beim nächsten Seitenaufruf noch einmal.',
          'Signing in isn\'t possible right now — we\'ll try again when you load the next page.'));
      } else {
        showNotice('info', L('Die Anmeldung ist abgelaufen — bitte melde dich erneut an.', 'Your sign-in has expired — please sign in again.'),
          ACCOUNT_COPY.signInHeader, function () { clearNotice(); initiateLogin(); });
      }
    } catch (e) {}
  }

  // Read + strip the ?ms_auth= return marker and its one-time ?ms_code=
  // (CUSTOMER_ACCOUNT.md §2/§2a). Both are stripped IMMEDIATELY on read, so a
  // reload or a copied URL never re-sends the code. With no marker, a code
  // kept from a 503 on the previous load gets its single retry.
  // The read + strip runs at the very top of init() (readAuthReturn), BEFORE
  // the shell, trail, nudges and attribution: the one-time code should leave
  // the address bar as early as this deferred script can manage, so page-view
  // collectors and Referer headers firing later never carry it. The values
  // are kept in memory only and consumed once by handleAuthReturn.
  // Earlier still: the theme's inline <head> script (layout/theme.liquid,
  // BEFORE content_for_header) moves ms_auth / ms_code / mo_c off the address
  // bar into a one-shot sessionStorage stash, so Shopify's analytics and the
  // Customer Events web pixels never record the code (or the campaign token)
  // in a page URL — this deferred script runs too late for that. The stash
  // is read once and deleted on read, and honoured only while younger than
  // the code's 10 min lifetime (a stash left on a page where this script
  // never ran must not act on a later page). The URL read stays the fallback
  // for a theme without the head script.
  var EARLY_PARAMS_KEY = 'ms-chat-early-params';
  var earlyParams = null;
  function earlyParam(name) {
    if (!earlyParams) {
      earlyParams = {};
      var raw = ssGet(EARLY_PARAMS_KEY);
      if (raw != null) {
        ssDel(EARLY_PARAMS_KEY);
        try {
          var p = JSON.parse(raw);
          var age = p ? Date.now() - Number(p.at) : -1;
          if (p && typeof p === 'object' && age >= 0 && age < LINK_RETRY_MAX_MS) earlyParams = p;
        } catch (e) {}
      }
    }
    var v = earlyParams[name];
    return typeof v === 'string' ? v : null;
  }
  var authReturnParams = null;
  function readAuthReturn() {
    if (authReturnParams) return authReturnParams;
    var marker = earlyParam('ms_auth'), code = earlyParam('ms_code');
    try {
      var u = new URL(window.location.href);
      if (marker == null && code == null) {
        marker = u.searchParams.get('ms_auth');
        code = u.searchParams.get('ms_code');
      }
      if (u.searchParams.has('ms_auth') || u.searchParams.has('ms_code')) {
        u.searchParams.delete('ms_auth');
        u.searchParams.delete('ms_code');
        window.history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
      }
    } catch (e) {}
    authReturnParams = { marker: marker, code: code };
    return authReturnParams;
  }
  function handleAuthReturn() {
    var ret = readAuthReturn();
    var marker = ret.marker, code = ret.code;
    ret.marker = null; ret.code = null; // consumed once
    if (!marker) { retryPendingLink(); return; }
    ssDel(LINK_RETRY_KEY); // a fresh return supersedes any older kept code
    var wantsOpen = ssGet('ms-chat-auth-return') === '1';
    ssDel('ms-chat-auth-return');
    authOpenHandled = true; // we own the auth state for this load
    if (marker === 'ok') {
      // Redeem BEFORE /api/auth/me. account_signin_return is sent only once
      // this step decided, so "ok" means the chat really is signed in.
      var linkSid = sid;
      var loginSid = ssGet(LOGIN_SID_SS_KEY);
      ssDel(LOGIN_SID_SS_KEY);
      // Never redeem with a session other than the one the login used (§2a:
      // "never retry with another session id") — a mismatch is link_failed.
      var redeem = (loginSid && loginSid !== sid) ? Promise.resolve('refused') : redeemLinkCode(code, 'chat');
      authLinkInflight = redeem.then(function (res) {
        if (res !== 'ok') {
          // ms_auth=ok without ms_code counts as refused (link_failed).
          if (res === 'unavailable' && code) {
            ssSet(LINK_RETRY_KEY, JSON.stringify({ code: code, sid: linkSid, at: Date.now() }));
          }
          track('account_signin_return', { result: 'link_failed' });
          // A failed redeem only means THIS code did not link the session; it
          // must not undo a sign-in the sid already had — a visitor recognised
          // through whoami ('shop') uses "Mit Kundenkonto anmelden" for the
          // chat sign-in, and that earlier link is still valid. So when there
          // is a hint of one, ask /api/auth/me (an anonymous visitor still
          // resolves to signedIn:false); otherwise settle anonymous directly.
          var settle = shouldProbeAuth() ? probeAuth(true) : Promise.resolve(applyAuth(null));
          return settle.then(function () {
            if (wantsOpen) openPanel();
            showLinkFailedNotice(res === 'unavailable');
          });
        }
        track('account_signin_return', { result: 'ok' });
        // They signed in through the chat expecting the offer: in value-moment
        // mode the popup / signin_return ask still runs this tab session
        // (SPEC B3a; read by maybeShowConsentGate).
        ssSet(SIGNIN_RETURNED_SS_KEY, '1');
        return probeAuth(true).then(function () {
          if (wantsOpen || auth.signedIn) openPanel();
          // The sign-in moment: offer the marketing opt-in. The welcome state
          // renders it in its own auth slot; present it inline only when a
          // conversation is already on screen (mid-conversation sign-in).
          if (auth.signedIn) presentSignInOptIn();
        });
      }).then(function () { authLinkInflight = null; }, function () { authLinkInflight = null; });
    } else if (marker === 'login_required') {
      // prompt=none path only: not logged in -> show the one-click affordance.
      track('account_signin_return', { result: 'login_required' });
      applyAuth(null);
      if (wantsOpen) openPanel();
    } else if (marker === 'logged_out') {
      // Back from the backend logout (§5): not a sign-in return (no KPI).
      // Signed out everywhere, and the stored history of that session goes
      // too (it can hold order status — CHAT_ORDER_STATUS.md). Like signOut():
      // whoami is not asked again in this browser session — after a degraded
      // logout (§5: the shop's own session may still be alive) its first call
      // would hand out a linkCode and silently re-link the fresh sid.
      // The marker itself proves nothing (anyone can link to ?ms_auth=
      // logged_out), so the wipe is decided by the server: probeAuth drops
      // the history only when /api/auth/me confirms that a session this
      // device had signed in has really ended (endedSignInCleanup). A
      // crafted link to a signed-in customer changes nothing.
      ssSet(WHOAMI_SS_KEY, '1');
      if (shouldProbeAuth()) probeAuth(true); else applyAuth(null);
    } else { // 'error' or anything unexpected -> stay anonymous.
      track('account_signin_return', { result: 'error' });
      applyAuth(null);
      if (wantsOpen) openPanel();
    }
  }

  // Wipe this device's chat of the current session and continue on a FRESH
  // session id — used on sign-out. The local history can hold the customer's
  // order status (get_order_status output and Mo's text), so the next person
  // on a shared browser must see nothing; a new sid also severs the old
  // session's server-side sign-in link for this device (CUSTOMER_ACCOUNT.md
  // §1: the sid IS the link). Clears the thread key + active conversation of
  // the old sid first (their storage keys are per sid), then re-renders the
  // empty welcome state. A reply still streaming is cancelled and any reply
  // being read aloud is stopped first, so nothing of the old session is drawn
  // or spoken afterwards; the in-memory traces (the captured email, the
  // loaded conversation list and its greeting) go with it — capturedEmail is
  // scoped to the chat session (API_CONTRACT.md §2) and would otherwise ride
  // on the next person's /api/chat and /api/feedback calls.
  // `adoptSid` (onSidChangedElsewhere only): another tab already rotated the
  // device's session — continue on ITS id instead of minting yet another.
  function dropSessionHistory(adoptSid) {
    if (abortActiveStream) { abortActiveStream(); abortActiveStream = null; }
    endSpeaking();
    removeTyping();
    capturedEmail = null;
    // An unsent draft (or text restored after a failed send) can hold an
    // order number or address — it belongs to the old session too.
    if (textarea) { textarea.value = ''; autoGrow(); }
    historyServerList = [];
    historyOptimistic = [];
    historyLoaded = false;
    if (historyListEl) historyListEl.replaceChildren();
    if (historyTitleEl) historyTitleEl.textContent = ACCOUNT_COPY.historyTitle;
    clearConvKey();
    activeConversationId = null;
    if (adoptSid) {
      lsDel(historyKey()); // the old sid's (the rotating tab removed it too)
      sid = adoptSid;
      messages = loadHistory(); // whatever the other tab already stored there
      // In-memory attribution only: the shared cache belongs to the tab that
      // rotated (moAttrLoad ignores another sid's token anyway).
      moAttr = null; moAttrFailed = false; moAttrConsulted = false;
    } else {
      rotateSession(); // drops history, mints a new sid, resets attribution
    }
    activeConversationKey = null;
    if (rateTimer) { clearTimeout(rateTimer); rateTimer = null; }
    state.rateLocked = false;
    state.streaming = false;
    clearNotice();
    updateInputState();
    renderAllMessages(); // empty -> welcome
  }

  // Local sign-out (the history drawer's "Abmelden"): ends the signed-in tier
  // on THIS device and removes the session's stored chat history by moving to
  // a fresh session id (dropSessionHistory). The KPI is sent first, under the
  // session that signs out. The shop's own login is not touched; whoami is not
  // asked again in this browser session, so the shop can't silently re-sign
  // the visitor in right after they chose to sign out. The flag is set HERE
  // too: after the chat's own sign-in return whoami never ran, so without it
  // the next open would make its first call and re-link the fresh sid.
  function signOut() {
    track('account_signout', {});
    ssSet(WHOAMI_SS_KEY, '1');
    moBlankCart(); // before the rotation drops the cached token
    closeHistory();
    applyAuth(null);
    dropSessionHistory();
  }

  // Sign-out, erase or "new chat" in ANOTHER tab rotated the device's session
  // id (localStorage is shared, this tab's in-memory sid is not). Without this
  // the other tab would keep the previous customer's name, drawer and
  // transcript (possibly order status) on screen — and, because sign-out is
  // local only and the old sid still resolves on the server, keep chatting as
  // them. sidIsCurrent() only stops it from WRITING. So follow the rotation:
  // stop the reply and speech, close any dialog/drawer, drop to anonymous and
  // continue on the new id with an empty view, leaving that id's storage to
  // the tab that owns it. A tab that was signed in also stops asking whoami
  // this browser session, like signOut() (the shop must not re-link it).
  function onSidChangedElsewhere(e) {
    try {
      if (!e || e.key !== SID_KEY || !e.newValue || e.newValue === sid) return;
      if (auth.signedIn) ssSet(WHOAMI_SS_KEY, '1');
      if (gateCloseFn) gateCloseFn();
      closeHistory();
      applyAuth(null);
      dropSessionHistory(e.newValue);
    } catch (er) {}
  }

  // Auth resolution on panel open (task §1). We detect promptly so a customer
  // who is already signed in — via the shop's own login OR the chatbot — sees
  // their identity, never a stale "Anmelden". Once known signed-in there's
  // nothing to redo; otherwise a later open re-hydrates via /api/auth/me (the
  // whoami call itself happens only once per browser session). An open during
  // a sign-in completion (code redeem) waits for it instead of racing it.
  var authOpenHandled = false;
  function resolveAuthOnOpen() {
    if (auth.signedIn) return;          // already known signed-in
    if (authLinkInflight) {
      authLinkInflight.then(function () {
        if (auth.settled) return;     // the completion already decided
        authOpenHandled = true;
        detectSignedIn();
      });
      return;
    }
    if (authOpenHandled) { detectSignedIn(true); return; } // re-check on a later open
    authOpenHandled = true;
    detectSignedIn();
  }

  // ---------------------------------------------------------------------------
  // Formatting helpers.
  // ---------------------------------------------------------------------------
  function euro(value) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    // en formats prices en-GB EUR ("€1,234.00"), matching the backend summary
    // email (LOCALE.md §3); de is byte-identical to before.
    return LOCALE === 'en'
      ? n.toLocaleString('en-GB', { style: 'currency', currency: 'EUR' })
      : n.toLocaleString('de-DE') + ' €';
  }

  // The prices of q units (a line total, rounded to cents) — for priceNode.
  function priceTimes(product, q) {
    var times = function (v) { var n = Number(v); return v != null && isFinite(n) ? Math.round(n * q * 100) / 100 : v; };
    return { price: times(product.price), salePrice: times(product.salePrice) };
  }

  function priceNode(product) {
    var wrap = el('div', { class: 'ms-chat-price' });
    if (product.salePrice != null && product.salePrice !== product.price) {
      wrap.appendChild(el('span', { class: 'ms-chat-price-sale', text: euro(product.salePrice) }));
      wrap.appendChild(el('span', { class: 'ms-chat-price-strike', text: euro(product.price) }));
    } else {
      wrap.appendChild(el('span', { class: 'ms-chat-price-regular', text: euro(product.price) }));
    }
    return wrap;
  }

  // Prominent primary CTA button to a product page (feature 2 / KPI driver).
  // These are the highest-value clicks, so they get the theme's primary pill
  // button instead of a subtle text link. Fires a fail-silent KPI event.
  // samePage (API_CONTRACT §5 "Product clicks"): computed at click time —
  // true only when the clicked product IS the open product page's product
  // (catalog handle vs the page's handle); always false off a product page.
  function isSamePageProduct(id) {
    return PAGE_CTX.type === 'product' && !!PAGE_CTX.productHandle && String(id) === PAGE_CTX.productHandle;
  }

  function productButton(product, label) {
    var a = el('a', { class: 'ms-chat-btn ms-chat-btn--primary ms-chat-btn--product', href: product.shopifyUrl || '#', target: '_blank', rel: 'noopener noreferrer' });
    a.appendChild(el('span', { text: label || L('Zum Produkt', 'View product') }));
    a.appendChild(icon('external'));
    a.addEventListener('click', function () { track('product_cta_clicked', { productId: product.id, samePage: isSamePageProduct(product.id) }); });
    return a;
  }

  // ---------------------------------------------------------------------------
  // Tool card builders. Each returns Promise<Element|null> (null = render nothing).
  // ---------------------------------------------------------------------------
  // Compact product card (client direction: "only what's needed"): small
  // thumb + name + price + CTA, reusing the checkout row layout. Specs, tag
  // chips, series badge, reason and delivery time live on the product page.
  function buildShowProduct(input) {
    return hydrate([input.productId]).then(function (res) {
      var p = res[0];
      if (!p) return null; // render-nothing guard
      // A product card is about to render -> the session is "consulted";
      // lazily mint the order-attribution token + stamp the live cart
      // (consent-gated, fail-silent — see the attribution block below).
      moAttrOnProductCard();
      var card = el('div', { class: 'ms-chat-card' });
      var body = el('div', { class: 'ms-chat-card-body' });

      var item = el('div', { class: 'ms-chat-checkout-item' });
      if (p.images && p.images[0]) {
        item.appendChild(el('img', { class: 'ms-chat-checkout-thumb', src: p.images[0], alt: p.name || '', loading: 'lazy' }));
      }
      var meta = el('div', { class: 'ms-chat-checkout-meta' });
      meta.appendChild(el('div', { class: 'ms-chat-prod-name', text: p.name || '' }));
      meta.appendChild(priceNode(p));
      // Sync-fresh stock flag (API_CONTRACT.md §3) — functional, not
      // decorative, so it survives the compact-card cut.
      if (p.inStock === false) meta.appendChild(el('span', { class: 'ms-chat-soldout-badge', text: L('Ausverkauft', 'Sold out') }));
      item.appendChild(meta);
      body.appendChild(item);

      // Prominent primary CTA below the product row.
      body.appendChild(productButton(p, L('Zum Produkt', 'View product')));

      card.appendChild(body);
      return card;
    });
  }

  function buildCompare(input) {
    var ids = input.productIds || [];
    return hydrate(ids).then(function (res) {
      var prods = res.filter(function (p) { return !!p; });
      if (prods.length < 2) return null; // need >= 2

      var wrap = el('div', { class: 'ms-chat-card' });
      var body = el('div', { class: 'ms-chat-card-body' });
      if (input.comparisonContext) body.appendChild(el('div', { class: 'ms-chat-compare-context', text: input.comparisonContext }));

      // Spec keys present in >= 2 products (or all keys when exactly 2 products).
      var keyCount = {};
      var keyOrder = [];
      prods.forEach(function (p) {
        Object.keys(p.specifications || {}).forEach(function (k) {
          if (!(k in keyCount)) { keyCount[k] = 0; keyOrder.push(k); }
          keyCount[k]++;
        });
      });
      var specKeys = keyOrder.filter(function (k) { return prods.length === 2 ? true : keyCount[k] >= 2; });

      var scroll = el('div', { class: 'ms-chat-compare-scroll' });
      var table = el('table', { class: 'ms-chat-compare-table' });

      // Header row.
      var thead = el('thead');
      var hrow = el('tr');
      hrow.appendChild(el('th'));
      prods.forEach(function (p) {
        var th = el('th');
        if (p.images && p.images[0]) th.appendChild(el('img', { class: 'ms-chat-compare-img', src: p.images[0], alt: p.name || '', loading: 'lazy' }));
        th.appendChild(el('div', { class: 'ms-chat-compare-name', text: p.name || '' }));
        hrow.appendChild(th);
      });
      thead.appendChild(hrow);
      table.appendChild(thead);

      var tbody = el('tbody');
      function row(label, cellFn) {
        var tr = el('tr');
        tr.appendChild(el('td', { class: 'ms-chat-compare-rowlabel', text: label }));
        prods.forEach(function (p) { tr.appendChild(cellFn(p)); });
        tbody.appendChild(tr);
      }

      row(L('Preis', 'Price'), function (p) { var td = el('td'); td.appendChild(priceNode(p)); return td; });
      specKeys.forEach(function (k) {
        row(k, function (p) {
          var v = (p.specifications && p.specifications[k] != null) ? String(p.specifications[k]) : '—';
          return el('td', { text: v });
        });
      });
      row(L('Lieferzeit', 'Delivery time'), function (p) { return el('td', { text: p.deliveryTime || '—' }); });
      row('', function (p) { var td = el('td'); td.appendChild(productButton(p, L('Zum Produkt', 'View product'))); return td; });

      table.appendChild(tbody);
      scroll.appendChild(table);
      body.appendChild(scroll);
      wrap.appendChild(body);
      return wrap;
    });
  }

  // ---------------------------------------------------------------------------
  // Storefront cart sync (BUGFIX).
  //
  // The add_to_cart card's "Zur Kasse" button opens the combined cart permalink
  // in a NEW tab (target="_blank"); the add happens server-side over there. THIS
  // (chat) tab keeps its stale, server-rendered cart UI — the header count
  // (#CartBubble) and the cart drawer (<cart-modal>) — until a manual reload.
  //
  // Fix: when this tab regains focus / becomes visible, re-fetch the live
  // Shopify cart (/cart.js) and reconcile the theme's own cart UI in place — no
  // full page reload. DISPLAY-ONLY: we only GET /cart.js and re-render cart
  // sections, never POST, so it can never double-add, and the chat
  // conversation/state is never touched.
  //
  // We reuse the theme's own primitives: the #CartBubble badge (same hidden/
  // empty semantics the theme's updater uses) and <cart-modal>.reloadContent()
  // (the Section Rendering API call the theme runs after its OWN adds). The cart
  // PAGE (.section-main-cart, if the shopper is sitting on /cart) is re-rendered
  // the same way the theme does it.
  // ---------------------------------------------------------------------------
  var cartCountKnown = null;       // last reconciled item_count (null = not read yet)
  var cartRefreshInFlight = false; // single-flight guard across focus/visibility/poll

  // Prefer the theme's locale-aware cart route; fall back to the canonical path.
  function cartJsUrl() {
    try { if (window.routes && window.routes.cart_url) return window.routes.cart_url; } catch (e) {}
    return '/cart.js';
  }

  // The count the page was rendered with, so the first refresh can tell whether
  // the cart changed in another tab. Empty/hidden badge == 0 items.
  function readBubbleCount() {
    try {
      var b = document.getElementById('CartBubble');
      if (!b || b.classList.contains('hidden')) return 0;
      var n = parseInt((b.textContent || '').replace(/[^\d]/g, ''), 10);
      return isNaN(n) ? 0 : n;
    } catch (e) { return 0; }
  }

  // Mirror the theme's header-badge update: reveal + set the count, or hide when
  // empty. textContent (never innerHTML) — the badge only ever holds a number.
  // The redesigned header renders TWO badges (topbar = desktop, mobile icons =
  // mobile) and only ONE carries #CartBubble (the one the theme JS needs; see
  // sections/header.liquid). Update every [data-fh-cart-bubble] too, so the
  // badge the shopper can actually see is never stale after a focus refresh.
  // readBubbleCount() can keep reading #CartBubble — that badge always renders.
  function setCartBubble(count) {
    try {
      var list = document.querySelectorAll('#CartBubble, [data-fh-cart-bubble]');
      for (var i = 0; i < list.length; i++) {
        var b = list[i];
        if (count > 0) { b.classList.remove('hidden'); b.textContent = String(count); }
        else { b.classList.add('hidden'); b.textContent = ''; }
      }
    } catch (e) {}
  }

  // Re-render the cart PAGE section in place via the Section Rendering API,
  // exactly as the theme does after its own cart changes. No-op off /cart.
  function reloadCartPageSection() {
    try {
      var sec = document.querySelector('.section-main-cart');
      if (!sec || !sec.id) return;
      var id = sec.id.replace(/^shopify-section-/, '');
      var url = window.location.origin + window.location.pathname + '?section_id=' + encodeURIComponent(id);
      fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r && r.ok ? r.text() : null; })
        .then(function (html) {
          if (html == null) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#' + sec.id);
          if (fresh) sec.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    } catch (e) {}
  }

  // Refresh the drawer contents WITHOUT opening it. <cart-modal> exposes
  // reloadContent() (the same Section Rendering fetch the theme uses), so calling
  // it directly refreshes the drawer silently — no notification, no auto-open,
  // the shopper isn't interrupted.
  function reloadCartDrawer() {
    try {
      var modal = document.querySelector('cart-modal');
      if (modal && typeof modal.reloadContent === 'function') {
        var p = modal.reloadContent();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    } catch (e) {}
  }

  // Re-fetch the live cart and reconcile the storefront cart UI. Idempotent and
  // display-only: safe to call on every focus/visibility/poll tick. The heavier
  // drawer/page re-render runs ONLY when item_count actually changed, so an open
  // drawer is never disturbed unless its contents really differ.
  function refreshCartUI() {
    if (cartRefreshInFlight) return;
    if (cartCountKnown === null) cartCountKnown = readBubbleCount();
    cartRefreshInFlight = true;
    fetch(cartJsUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
        var changed = count !== cartCountKnown;
        cartCountKnown = count;
        setCartBubble(count);            // always reconcile the badge (cheap, idempotent)
        if (changed) { reloadCartDrawer(); reloadCartPageSection(); }
      })
      .catch(function () {})
      .then(function () { cartRefreshInFlight = false; });
  }

  // After the shopper clicks "Zur Kasse" the add happens in the OTHER tab and we
  // can't observe exactly when it lands. Focus/visibility return covers the usual
  // case; this bounded poll is the belt-and-braces path for when THIS tab keeps
  // focus (permalink opened in a background tab / popup blocked). Each tick is
  // just a /cart.js GET, gated by the change check in refreshCartUI().
  function pollCartAfterCheckout() {
    [1200, 2500, 4500, 7000].forEach(function (d) { setTimeout(refreshCartUI, d); });
  }

  // ---------------------------------------------------------------------------
  // Order attribution — stamp the LIVE storefront cart with the session's
  // opaque marker (backend repo: docs/ORDER_ATTRIBUTION.md + API_CONTRACT.md
  // §10). Once stamped, WHATEVER lands in the cart — Mo's checkout button or a
  // product the shopper found via manual search — the resulting order carries
  // the "_mo" note attribute, and the backend's orders webhook can attribute
  // it to this consultation (tiers "Beraten & gekauft" / "Beraten, anderes
  // gekauft").
  //
  // INVARIANTS (do not weaken):
  //   * CONSENT-GATED: nothing here runs unless Shopify's Customer Privacy API
  //     reports analytics processing is allowed. No consent -> silent no-op.
  //   * OPAQUE TOKEN ONLY: the raw session id NEVER goes into a URL or a cart
  //     attribute — only the server-minted token from /api/attribution/token.
  //   * FAIL-SILENT + NON-BLOCKING: every call is fire-and-forget; the
  //     shopping flow never waits on (or breaks because of) attribution.
  //   * LAZY: the token is minted only once the session is "consulted" (a
  //     product card rendered) or the shopper clicks Mo's checkout button —
  //     never on mere page load. Minting is idempotent per session
  //     (repeat POSTs return the same token).
  //
  // Re-stamping matters: a completed checkout clears the cart's attributes,
  // so we stamp (a) right after the token is minted, (b) before every Mo
  // checkout-link click, (c) once per page load when a cached token exists.
  // /cart/update.js merges attributes, so repeat stamps are idempotent.
  // ---------------------------------------------------------------------------
  var MO_ATTR_KEY = 'ms-mo-attr';
  var moAttr = null;             // { sid, token, cartAttributes } in-memory cache
  var moAttrInflight = false;    // single-flight guard on the token mint
  var moAttrFailed = false;      // mint failed -> give up silently for THIS page view
  var moAttrConsulted = false;   // a product card rendered (session is "consulted")
  var moAttrPageStamped = false; // render/page-load paths stamp at most once per load
  var moAttrRenewed = false;     // live-consultation renewal: at most once per page view
  var moAttrRenewing = false;    // a renewal POST is in flight (render stamps wait for it)
  var moAttrStampDeferred = false; // a render stamp was skipped during the renewal

  function moAnalyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' &&
        cp.analyticsProcessingAllowed() === true);
    } catch (e) { return false; }
  }

  function moAttrValid(c) {
    return !!(c && typeof c === 'object' && typeof c.token === 'string' && c.token &&
      c.cartAttributes && typeof c.cartAttributes === 'object' && !Array.isArray(c.cartAttributes));
  }

  // Cached token, memory-first then localStorage. The stored copy is keyed to
  // the sid it was minted for: after a session rotation a stale entry must not
  // stamp carts with the OLD session's token, so it's dropped.
  function moAttrLoad() {
    if (moAttr) return moAttr;
    var raw = lsGet(MO_ATTR_KEY);
    if (!raw) return null;
    try {
      var c = JSON.parse(raw);
      if (moAttrValid(c) && c.sid === sid) { moAttr = c; return c; }
    } catch (e) {}
    lsDel(MO_ATTR_KEY); // stale / another session's cache
    return null;
  }

  function moAttrReset() {
    moAttr = null;
    moAttrFailed = false;
    moAttrConsulted = false;
    lsDel(MO_ATTR_KEY);
  }

  // The actual stamp: same-origin, fire-and-forget, consent re-checked at call
  // time (consent can be withdrawn mid-page). keepalive so a stamp fired right
  // before a navigation still lands.
  function moStampCart() {
    var c = moAttrLoad();
    if (!c || !moAnalyticsAllowed()) return;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: c.cartAttributes }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // Ensure a token exists, then stamp. viaRender=true is the card-render /
  // page-load path and stamps at most once per page load (history re-renders
  // several product cards at once — one stamp is enough); the checkout-click
  // path passes false and ALWAYS re-stamps (a completed checkout clears the
  // attributes). 401/403/429/5xx/network: give up silently for this page view.
  function moAttrEnsure(viaRender) {
    if (!moAnalyticsAllowed()) return;
    if (moAttrLoad()) {
      if (viaRender && moAttrPageStamped) return;
      // A renewal may replace the token any moment: a render stamp issued now
      // could land AFTER the renewal's stamp and put the old token back. The
      // renewal stamps (or, unchanged/failed, catches this stamp up) itself.
      if (viaRender && moAttrRenewing) { moAttrStampDeferred = true; return; }
      moAttrPageStamped = true;
      moStampCart();
      return;
    }
    if (moAttrInflight || moAttrFailed) return;
    moAttrInflight = true;
    var reqSid = sid;
    try {
      fetch(API_BASE + '/api/attribution/token', {
        method: 'POST',
        headers: { 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': reqSid }
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          moAttrInflight = false;
          // The session rotated meanwhile (a new chat, a configurator handover,
          // sign-out): this token belongs to the old session — drop it, and mint
          // for the new one only once that session itself was consulted (LAZY).
          if (sid !== reqSid) { if (moAttrConsulted) moAttrEnsure(viaRender); return; }
          if (!data || data.ok !== true || !moAttrValid(data)) { moAttrFailed = true; return; }
          var c = { sid: reqSid, token: data.token, cartAttributes: data.cartAttributes };
          moAttr = c;
          try { lsSet(MO_ATTR_KEY, JSON.stringify(c)); } catch (e) {}
          moAttrPageStamped = true;
          moStampCart();
        })
        .catch(function () { moAttrInflight = false; moAttrFailed = true; });
    } catch (e) { moAttrInflight = false; moAttrFailed = true; }
  }

  // Renewal after a LIVE product consultation (API_CONTRACT §10 "Lifetime and
  // renewal"): the endpoint returns the same token while it exists and a new
  // one once the backend deleted it (retention, erasure) — without this a
  // device keeps stamping a dead token until its sid rotates. Called only from
  // finalizeStream() of a streamed turn (never restored history, never page
  // load), only with a cached token for this sid and analytics consent, at
  // most once per page view. Any failure keeps the cached token and does NOT
  // set moAttrFailed (the checkout path keeps stamping it). A reply for a sid
  // that rotated meanwhile is dropped.
  function moAttrRenew() {
    if (moAttrRenewed || moAttrInflight) return;
    if (!moAnalyticsAllowed()) return;
    var cached = moAttrLoad();
    if (!cached) return; // no token yet -> the card render mints as before
    moAttrRenewed = true;
    moAttrInflight = true;
    moAttrRenewing = true;
    var reqSid = sid;
    function done(stamped) {
      moAttrInflight = false;
      moAttrRenewing = false;
      var catchUp = moAttrStampDeferred && !stamped;
      moAttrStampDeferred = false;
      // A render stamp skipped during the renewal still owes this page its stamp.
      if (catchUp && sid === reqSid && !moAttrPageStamped && moAttrLoad()) {
        moAttrPageStamped = true;
        moStampCart();
      }
    }
    try {
      fetch(API_BASE + '/api/attribution/token', {
        method: 'POST',
        headers: { 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': reqSid }
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (sid !== reqSid) { done(false); return; } // rotated meanwhile: drop
          if (!data || data.ok !== true || !moAttrValid(data)) { done(false); return; }
          var cur = moAttrLoad();
          if (cur && cur.token === data.token) { done(false); return; } // unchanged
          var c = { sid: reqSid, token: data.token, cartAttributes: data.cartAttributes };
          moAttr = c;
          try { lsSet(MO_ATTR_KEY, JSON.stringify(c)); } catch (e) {}
          moAttrPageStamped = true;
          moStampCart(); // consent re-checked inside
          done(true);
        })
        .catch(function () { done(false); });
    } catch (e) { done(false); }
  }

  // API_CONTRACT §10 "Ending the marker": when the session ends (sign-out,
  // erase, a sign-in the server ended) or analytics consent is withdrawn, set
  // every key of the cached cartAttributes to "" so the cart stops carrying
  // this session's marker. Not consent-gated (it removes a marker and sends
  // nothing for analytics); same-origin, fire-and-forget. Without a cached
  // entry (none minted, or another tab rotated first) it skips — the widget
  // never hard-codes the attribute key.
  function moBlankCart() {
    var c = moAttrLoad();
    if (!c) return;
    var blanked = {};
    for (var k in c.cartAttributes) {
      if (Object.prototype.hasOwnProperty.call(c.cartAttributes, k)) blanked[k] = '';
    }
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: blanked }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // The backend's consultation tools (API_CONTRACT §10): a finished turn with
  // one of these is a live product consultation.
  var MO_CONSULT_PARTS = { 'tool-show_product': 1, 'tool-compare_products': 1, 'tool-add_to_cart': 1, 'tool-suggest_showroom': 1, 'tool-show_gym_plan': 1, 'tool-offer_plan_options': 1 };
  function hasConsultationPart(parts) {
    for (var i = 0; parts && i < parts.length; i++) {
      if (parts[i] && MO_CONSULT_PARTS[parts[i].type] === 1) return true;
    }
    return false;
  }

  // First product card rendered -> the session is "consulted"; mint lazily.
  function moAttrOnProductCard() {
    moAttrConsulted = true;
    moAttrEnsure(true);
  }

  function initAttribution() {
    // Consent may arrive AFTER page load (banner interaction): start stamping
    // the moment Shopify announces it.
    try {
      document.addEventListener('visitorConsentCollected', function () {
        if (!moAnalyticsAllowed()) { moBlankCart(); return; } // withdrawn: end the marker
        // Cached token -> re-stamp; consulted but tokenless -> mint now.
        if (moAttrLoad() || moAttrConsulted) moAttrEnsure(true);
      });
    } catch (e) {}
    // Once per page load: re-stamp if a cached token exists and consent
    // allows (a checkout in between cleared the attributes). Shopify loads
    // customerPrivacy async, so retry briefly before giving up — the consent
    // event above stays armed either way. Never mints here.
    var tries = 0;
    (function tick() {
      if (moAnalyticsAllowed()) {
        if (moAttrLoad()) moAttrEnsure(true);
        return;
      }
      var cpLoaded = !!(window.Shopify && window.Shopify.customerPrivacy);
      if (cpLoaded || ++tries > 5) return; // consent denied, or API never loaded
      setTimeout(tick, tries * 1000);
    })();
  }

  // tool-add_to_cart -> direct-checkout CTA. Per API_CONTRACT.md the tool id is
  // unchanged but a single call can now cover ONE *or* SEVERAL products in one
  // combined cart: read `productIds` when present, else fall back to the single
  // `productId`. We render ONE card listing every resolved product and ONE
  // checkout button pointing at the response's top-level `cartUrl` — a single
  // permalink that puts all variants in one cart (never stitched client-side).
  function buildAddToCart(input) {
    var ids = (input.productIds && input.productIds.length)
      ? input.productIds.slice()
      : (input.productId != null ? [input.productId] : []);
    ids = ids.map(function (s) { return String(s).trim(); }).filter(Boolean);
    var seen = {}, uniq = [];
    for (var i = 0; i < ids.length; i++) { if (!seen[ids[i]]) { seen[ids[i]] = 1; uniq.push(ids[i]); } }
    ids = uniq;
    if (!ids.length) return Promise.resolve(null);

    // Dedicated fetch (not the per-id hydrate cache) so we get the response's
    // top-level `cartUrl` — the one combined permalink for exactly these ids.
    var url = API_BASE + '/api/products?ids=' + encodeURIComponent(ids.join(','));
    return fetch(url, { method: 'GET', headers: { 'x-ms-session': sid } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        var prods = (data && data.products) || [];
        var cartUrl = (data && data.cartUrl) || null;
        // Warm the shared cache opportunistically (unknown ids come back
        // null) — but only from a successful response; a failed fetch must
        // not mark the ids unknown for the rest of the session.
        if (data) {
          for (var j = 0; j < ids.length; j++) {
            if (!(ids[j] in productCache)) productCache[ids[j]] = prods[j] != null ? prods[j] : null;
            cfgNoteRules(prods[j]);
          }
        }
        var resolved = prods.filter(function (p) { return !!p; });
        if (!resolved.length) return null; // nothing resolvable -> render nothing
        var multi = resolved.length > 1;

        var card = el('div', { class: 'ms-chat-card' });
        var body = el('div', { class: 'ms-chat-card-body' });
        if (input.message) body.appendChild(el('div', { class: 'ms-chat-card-msg', text: input.message }));

        // One row per product (thumb + name + price), so the shopper sees exactly
        // what the single checkout click buys. Since 2026-10-27 the cartUrl
        // carries each product's rule minimum (/api/products: resolveProductRules
        // ().min — a pair of plates goes in as 2): such a row names the quantity
        // („2× …“, „Paarweise“) and its price is the line total.
        resolved.forEach(function (p) {
          var q = p.rules && typeof p.rules.min === 'number' && p.rules.min > 1 ? Math.floor(p.rules.min) : 1;
          var item = el('div', { class: 'ms-chat-checkout-item' });
          if (p.images && p.images[0]) {
            item.appendChild(el('img', { class: 'ms-chat-checkout-thumb', src: p.images[0], alt: p.name || '', loading: 'lazy' }));
          }
          var meta = el('div', { class: 'ms-chat-checkout-meta' });
          meta.appendChild(el('span', { class: 'ms-chat-checkout-name', text: (q > 1 ? q + '× ' : '') + (p.name || L('Produkt', 'Product')) }));
          if (q > 1 && p.rules.step === 2) meta.appendChild(el('span', { class: 'ms-chat-checkout-unit', text: L('Paarweise', 'In pairs') }));
          meta.appendChild(priceNode(q > 1 ? priceTimes(p, q) : p));
          // The server-built cartUrl EXCLUDES sold-out products — mark them so
          // the listed rows match what the checkout click actually contains.
          if (p.inStock === false) meta.appendChild(el('span', { class: 'ms-chat-soldout-note', text: L('Ausverkauft — nicht im Warenkorb', 'Sold out — not in cart') }));
          item.appendChild(meta);
          body.appendChild(item);
        });

        if (cartUrl) {
          // ONE button -> the combined cart covering every resolved product.
          // Labeled "Zur Kasse": the permalink lands in checkout, so an
          // add-to-cart label would be misleading. Brand-blue checkout
          // styling makes it the unmistakable primary action.
          var btn = el('a', { class: 'ms-chat-btn ms-chat-btn--checkout', href: cartUrl, target: '_blank', rel: 'noopener noreferrer', 'aria-label': L('Zur Kasse', 'Checkout') });
          btn.appendChild(icon('cart'));
          btn.appendChild(el('span', { text: L('Zur Kasse', 'Checkout') }));
          btn.addEventListener('click', function () {
            // Re-stamp the live cart BEFORE the permalink opens (a completed
            // checkout clears cart attributes). Fire-and-forget: mints the
            // token first if this session doesn't have one yet, never blocks
            // or delays the navigation.
            moAttrEnsure(false);
            track('add_to_cart_clicked', { productId: resolved[0].id, productIds: resolved.map(function (p) { return p.id; }) });
            // The permalink populates the cart in the new tab; poll so this tab's
            // cart UI catches up even if it keeps focus (re-fetch only, no re-add).
            pollCartAfterCheckout();
          });
          body.appendChild(btn);
          body.appendChild(el('div', { class: 'ms-chat-caption', text: L('Direkt zur sicheren Kasse bei motionsports.de', 'Straight to secure checkout at motionsports.de') }));
        } else {
          // No resolvable variant -> degrade to product-page link(s), never a
          // broken checkout link.
          resolved.forEach(function (p) {
            if (!p.shopifyUrl) return;
            var link = el('a', { class: 'ms-chat-btn ms-chat-btn--secondary', href: p.shopifyUrl, target: '_blank', rel: 'noopener noreferrer' });
            link.appendChild(el('span', { text: multi ? (p.name || L('Zum Produkt', 'View product')) : L('Zum Produkt', 'View product') }));
            link.appendChild(icon('external'));
            link.addEventListener('click', function () { track('product_cta_clicked', { productId: p.id, samePage: isSamePageProduct(p.id) }); });
            body.appendChild(link);
          });
        }

        card.appendChild(body);
        return card;
      })
      .catch(function () { return null; });
  }

  // suggest_showroom (since 2026-10-27): only the products that stand in the
  // showroom — the tool result's inShowroom, or showroom: true from
  // /api/products — and nothing when none does (the result's ok: false).
  // A backend whose showroom list is unknown right now (showroomKnown:
  // false; or products with rules but no showroom flag) gets a card without
  // product names — the team confirms what is on display.
  // A backend before that (no showroom field, no rules, a result of just
  // { ok }): every product, as before. The result follows the call in the
  // same stream; a live card waits for it (at most 3 s, or until the turn
  // ends), a restored one has it stored with the turn.
  var showroomOut = {};   // toolCallId -> the call's result (null: none / an error)
  var showroomWait = {};  // toolCallId -> callbacks of cards waiting for it
  function showroomResult(id, out) {
    if (!id) return;
    showroomOut[id] = out && typeof out === 'object' ? out : null;
    var w = showroomWait[id];
    delete showroomWait[id];
    for (var i = 0; w && i < w.length; i++) w[i]();
  }
  // A finished (or broken) turn: no result comes any more.
  function showroomTurnOver() {
    for (var id in showroomWait) {
      if (Object.prototype.hasOwnProperty.call(showroomWait, id)) showroomResult(id, showroomOut[id]);
    }
  }
  function showroomResultFor(id, live) {
    if (!id || Object.prototype.hasOwnProperty.call(showroomOut, id) || !live || !state.streaming) return Promise.resolve(id ? showroomOut[id] : undefined);
    return new Promise(function (resolve) {
      var done = false;
      var fin = function () { if (!done) { done = true; resolve(showroomOut[id]); } };
      (showroomWait[id] = showroomWait[id] || []).push(fin);
      setTimeout(fin, 3000);
    });
  }
  // The backend's showroom list is unknown right now: its result says so (Mo
  // was told not to promise a particular product), or — no list in the
  // result (none came, or a restored turn of a backend before) — the
  // products carry rules (a backend since 2026-10-27 sends them on every
  // product) but no showroom flag (sent only while the list is known).
  function showroomUnknown(prods, res) {
    if (res && res.showroomKnown === false) return true;
    if (res && (res.showroomKnown === true || Array.isArray(res.inShowroom))) return false;
    if (prods.some(function (p) { return typeof p.showroom === 'boolean'; })) return false;
    return prods.some(function (p) { return !!p.rules && typeof p.rules === 'object'; });
  }
  function showroomPick(prods, out) {
    var res = out && typeof out === 'object' ? out : null;
    if (res && res.ok === false) return [];
    var listed = res && Array.isArray(res.inShowroom) ? res.inShowroom : null;
    // /api/products sends showroom: true / false only while the list is known.
    var known = !!listed || prods.some(function (p) { return typeof p.showroom === 'boolean'; });
    if (!known) return prods;
    var inList = {};
    (listed || []).forEach(function (x) { if (typeof x === 'string') inList[x.trim().split('~')[0]] = 1; });
    return prods.filter(function (p) {
      return p.showroom === true || inList[String(p.id != null ? p.id : '').split('~')[0]] === 1;
    });
  }

  function buildShowroom(input, key, live) {
    var ids = Array.isArray(input.productIds) ? input.productIds : [];
    return hydrate(ids).then(function (res) {
      var found = res.filter(function (p) { return !!p; });
      if (found.length < 1) return null;
      return showroomResultFor(key, live).then(function (out) {
        var res = out && typeof out === 'object' ? out : null;
        if (res && res.ok === false) return null;
        if (showroomUnknown(found, res)) return showroomCard(found, true);
        return showroomCard(showroomPick(found, res));
      });
    });
  }
  // anon: the showroom list is unknown — the card names no product.
  function showroomCard(prods, anon) {
    if (prods.length < 1) return null;
    var names = prods.map(function (p) { return p.name; }).join(', ');

    var card = el('div', { class: 'ms-chat-card' });
    var body = el('div', { class: 'ms-chat-card-body' });
    var head = el('div', { class: 'ms-chat-card-head' });
    head.appendChild(icon('pin'));
    head.appendChild(el('span', { text: L('Showroom in Gröbenzell bei München', 'Showroom in Gröbenzell near Munich') }));
    body.appendChild(head);
    body.appendChild(el('div', { class: 'ms-chat-card-text', text: anon
      ? L('Besuche unseren Showroom – welche Geräte dort ausgestellt sind, bestätigt dir das Team bei der Terminvereinbarung.', 'Visit our showroom – the team confirms which equipment is on display when you book your visit.')
      : L('Möchtest du ', 'Would you like to test ') + names + L(' vor dem Kauf testen? Besuche unseren Showroom!', ' before buying? Visit our showroom!') }));

    var btn = el('a', { class: 'ms-chat-btn ms-chat-btn--primary', href: SHOWROOM_URL, target: '_blank', rel: 'noopener noreferrer' });
    btn.appendChild(el('span', { text: L('Showroom ansehen', 'View showroom') }));
    btn.appendChild(icon('external'));
    btn.addEventListener('click', function () { track('showroom_clicked', { productIds: prods.map(function (p) { return p.id; }) }); });
    body.appendChild(btn);
    body.appendChild(el('div', { class: 'ms-chat-caption', text: L('Terminvereinbarung erforderlich', 'Appointment required') }));
    card.appendChild(body);
    return card;
  }

  var REASON_LABELS = {
    studio_consultation: { title: 'Persönliche Studio-Beratung', sub: 'Ein Studio-Spezialist meldet sich für ein individuelles Konzept.' },
    public_sector_quote: { title: 'Formelles Angebot anfordern', sub: 'Mit Kauf auf Rechnung, Zahlungsziel und CE-Doku.' },
    physio_consultation: { title: 'Physio- / Reha-Beratung', sub: 'Persönliche Beratung zu Reha-Einsatz und Medizinprodukten.' },
    bulk_discount: { title: 'Mengenrabatt anfragen', sub: 'Wir erstellen ein individuelles Angebot.' },
    leasing: { title: 'Leasing-Anfrage', sub: 'Flexible Finanzierung für gewerbliche Kunden.' },
    maintenance: { title: 'Wartungsvertrag', sub: 'Langfristige Wartung und Ersatzteilversorgung.' },
    // Customer-service escalation (CONTACT_FORM_ORDER_SUPPORT.md).
    order_support: { title: 'Kontakt zum motion sports Team', sub: 'Bestellstatus, Retoure/Rückgabe, Stornierung oder Reklamation — das Team kümmert sich.' },
    general: { title: 'Persönliche Beratung', sub: 'Wir helfen dir gerne weiter.' }
  };
  if (LOCALE === 'en') Object.assign(REASON_LABELS, {
    studio_consultation: { title: 'Personal studio consultation', sub: 'A studio specialist will get in touch for an individual concept.' },
    public_sector_quote: { title: 'Request a formal quote', sub: 'With purchase on account, payment terms and CE documentation.' },
    physio_consultation: { title: 'Physio / rehab consultation', sub: 'Personal advice on rehab use and medical devices.' },
    bulk_discount: { title: 'Request a volume discount', sub: 'We\'ll prepare an individual quote.' },
    leasing: { title: 'Leasing enquiry', sub: 'Flexible financing for business customers.' },
    maintenance: { title: 'Maintenance contract', sub: 'Long-term maintenance and spare-parts supply.' },
    order_support: { title: 'Contact the motion sports team', sub: 'Order status, return, cancellation or complaint — the team will take care of it.' },
    general: { title: 'Personal consultation', sub: 'We\'re happy to help.' }
  });

  function buildContactForm(input) {
    var reason = input.reason || 'general';
    var labels = REASON_LABELS[reason] || REASON_LABELS.general;
    var orgRequired = (reason === 'studio_consultation' || reason === 'public_sector_quote');

    var card = el('div', { class: 'ms-chat-card' });
    var body = el('div', { class: 'ms-chat-card-body' });

    var head = el('div', { class: 'ms-chat-card-head' });
    head.appendChild(icon('mail'));
    head.appendChild(el('span', { text: labels.title }));
    body.appendChild(head);
    body.appendChild(el('div', { class: 'ms-chat-card-text', text: input.message || labels.sub }));

    var refsEl = null;
    if (input.productIds && input.productIds.length) {
      refsEl = el('div', { class: 'ms-chat-card-refs' });
      body.appendChild(refsEl);
      hydrate(input.productIds).then(function (res) {
        var names = res.filter(function (p) { return !!p; }).map(function (p) { return p.name; });
        if (names.length) {
          refsEl.replaceChildren();
          refsEl.appendChild(el('span', { text: L('Im Bezug: ', 'Regarding: ') }));
          refsEl.appendChild(el('b', { text: names.join(', ') }));
        }
      });
    }

    var form = el('form', { class: 'ms-chat-form', novalidate: 'novalidate' });

    function field(labelText, inputEl) {
      var f = el('div', { class: 'ms-chat-field' });
      var id = 'msc-' + Math.random().toString(36).slice(2, 8);
      inputEl.id = id;
      var lbl = el('label', { text: labelText });
      lbl.setAttribute('for', id);
      f.appendChild(lbl);
      f.appendChild(inputEl);
      return f;
    }

    var nameInput = el('input', { type: 'text', name: 'name', required: 'required', autocomplete: 'name' });
    var emailInput = el('input', { type: 'email', name: 'email', required: 'required', autocomplete: 'email' });
    var orgInput = el('input', { type: 'text', name: 'organization', autocomplete: 'organization' });
    if (orgRequired) orgInput.setAttribute('required', 'required');
    var phoneInput = el('input', { type: 'tel', name: 'phone', autocomplete: 'tel' });
    var msgInput = el('textarea', { name: 'message', required: 'required', placeholder: reason === 'order_support'
      ? L('Bestellnummer + kurz dein Anliegen…', 'Order number + briefly your request…')
      : L('Beschreibe kurz dein Anliegen…', 'Briefly describe your request…') });

    form.appendChild(field('Name *', nameInput));
    form.appendChild(field(L('E-Mail *', 'Email *'), emailInput));
    form.appendChild(field(orgRequired ? L('Organisation / Studio *', 'Organization / Studio *') : L('Organisation', 'Organization'), orgInput));
    form.appendChild(field(L('Telefon', 'Phone'), phoneInput));
    form.appendChild(field(L('Nachricht *', 'Message *'), msgInput));

    var errEl = el('div', { class: 'ms-chat-form-error', style: 'display:none' });
    form.appendChild(errEl);

    var submit = el('button', { type: 'submit', class: 'ms-chat-btn ms-chat-btn--primary' }, [L('Anfrage senden', 'Send request')]);
    form.appendChild(submit);
    body.appendChild(form);

    body.appendChild(el('div', { class: 'ms-chat-caption', text: L('Wir melden uns innerhalb von 1-2 Werktagen. Deine Daten werden nur für die Bearbeitung deiner Anfrage verwendet.', 'We\'ll get back to you within 1-2 business days. Your data is used only to process your request.') }));

    function showError(msg) { errEl.textContent = msg; errEl.style.display = 'block'; }
    function clearError() { errEl.textContent = ''; errEl.style.display = 'none'; }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      clearError();

      var payload = {
        reason: reason,
        name: nameInput.value.trim(),
        email: emailInput.value.trim(),
        organization: orgInput.value.trim(),
        phone: phoneInput.value.trim(),
        message: msgInput.value.trim(),
        // The backend keys its contact_form_submitted KPI row on the BODY's
        // sessionId (API_CONTRACT §5) — the header alone leaves it unjoined.
        sessionId: sid
      };
      if (input.productIds && input.productIds.length) payload.productIds = input.productIds;

      if (!payload.name) { showError(L('Bitte gib deinen Namen an.', 'Please enter your name.')); return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email)) { showError(L('Bitte gib eine gültige E-Mail-Adresse an.', 'Please enter a valid email address.')); return; }
      if (orgRequired && !payload.organization) { showError(L('Bitte gib deine Organisation / dein Studio an.', 'Please enter your organization / studio.')); return; }
      if (!payload.message) { showError(L('Bitte beschreibe kurz dein Anliegen.', 'Please briefly describe your request.')); return; }

      submit.disabled = true;
      submit.textContent = L('Wird gesendet…', 'Sending…');

      fetch(API_BASE + '/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (res.ok) {
          var ok = el('div', { class: 'ms-chat-form-success' });
          ok.appendChild(icon('check'));
          ok.appendChild(el('h3', { text: L('Vielen Dank!', 'Thank you!') }));
          ok.appendChild(el('p', { text: L('Wir haben deine Anfrage erhalten und melden uns innerhalb von 1-2 Werktagen.', 'We\'ve received your request and will get back to you within 1-2 business days.') }));
          body.replaceChildren(head, ok);
          return;
        }
        return res.json().catch(function () { return null; }).then(function (data) {
          var code = data && data.error && data.error.code;
          var msg = (data && data.error && data.error.message) || '';
          if (res.status === 429 || code === 'rate_limited') {
            // Mirror the chat path: German hint + submit locked for the
            // Retry-After window (same 30s default when the header is
            // missing/unreadable).
            var retry = parseInt(res.headers.get('Retry-After'), 10);
            if (!isFinite(retry) || retry <= 0) retry = 30;
            showError(L('Zu viele Anfragen — bitte kurz warten.', 'Too many requests — please wait a moment.'));
            submit.textContent = L('Anfrage senden', 'Send request');
            setTimeout(function () { submit.disabled = false; }, retry * 1000);
            return;
          }
          if (code === 'upstream_unavailable' || res.status === 502) {
            msg = L('Senden gerade nicht möglich — bitte später erneut versuchen.', 'Sending isn\'t possible right now — please try again later.');
          } else if (!msg) {
            msg = L('Senden fehlgeschlagen. Bitte versuch es erneut.', 'Sending failed. Please try again.');
          }
          showError(msg);
          submit.disabled = false;
          submit.textContent = L('Anfrage senden', 'Send request');
        });
      }).catch(function () {
        showError(L('Senden gerade nicht möglich — bitte später erneut versuchen.', 'Sending isn\'t possible right now — please try again later.'));
        submit.disabled = false;
        submit.textContent = L('Anfrage senden', 'Send request');
      });
    });

    card.appendChild(body);
    return Promise.resolve(card);
  }

  // ---------------------------------------------------------------------------
  // GDPR email-capture form. Shared by BOTH entry points:
  //   (a) the assistant's offer_email_summary tool part (inline in the chat),
  //   (b) the header share icon (proactive, on demand).
  // Returns the card Element synchronously; the consent block fills in once
  // the CANONICAL backend copy resolves (submit stays disabled until then —
  // the audit string must be the served text, never a widget fallback). The
  // two consents are SEPARATE; BOTH boxes start UNCHECKED (v2) and the
  // marketing box is never bundled with the transactional one. POSTs to
  // /api/capture-email
  // (API_CONTRACT.md §7) echoing the backend's `consentTextShown` verbatim.
  // opts.productIds is advisory cart-preview only — the backend resolves the
  // real products. opts.trigger is the offer's value moment, echoed back on
  // submit (telemetry only).
  // ---------------------------------------------------------------------------
  // Returning-customer memory (API_CONTRACT.md §2 "customer"): the email the
  // user captured IN THIS PAGE'S chat session, attached to subsequent
  // /api/chat requests so the backend can inject its memory block. PRIVACY
  // GATE (do not change): in-memory ONLY — never localStorage/cookies, never
  // auto-attached on a fresh widget open; a navigation resets it. The backend
  // independently verifies the capture came from this same x-ms-session, so
  // a forged value resolves to no memory anyway.
  var capturedEmail = null;

  function buildCaptureCard(opts) {
    opts = opts || {};
    var card = el('div', { class: 'ms-chat-card' });
    var body = el('div', { class: 'ms-chat-card-body' });

    var head = el('div', { class: 'ms-chat-card-head' });
    head.appendChild(icon('mail'));
    head.appendChild(el('span', { text: CONSENT_COPY.title }));
    body.appendChild(head);

    if (opts.message) body.appendChild(el('div', { class: 'ms-chat-card-text', text: opts.message }));

    if (opts.productIds && opts.productIds.length) {
      var refsEl = el('div', { class: 'ms-chat-card-refs' });
      body.appendChild(refsEl);
      hydrate(opts.productIds).then(function (res) {
        var names = res.filter(function (p) { return !!p; }).map(function (p) { return p.name; });
        if (names.length) {
          refsEl.replaceChildren();
          refsEl.appendChild(el('span', { text: L('Im Warenkorb: ', 'In cart: ') }));
          refsEl.appendChild(el('b', { text: names.join(', ') }));
        }
      });
    }

    var form = el('form', { class: 'ms-chat-form ms-chat-capture', novalidate: 'novalidate' });

    // Returning-customer hint (backend-served, v2) directly ABOVE the email
    // field — the "why give my email" answer where the decision happens.
    // Filled in renderConsent ONLY when the backend marks it enabled;
    // informational UI copy, NOT part of the consent block or of
    // `consentTextShown`. No fallback text lives in the widget.
    var returnHintEl = el('div', { class: 'ms-chat-returning-hint ms-chat-returning-hint--form', style: 'display:none' });
    form.appendChild(returnHintEl);

    // Email field (real <label> tied to the input via for/id).
    var emailId = 'msc-' + Math.random().toString(36).slice(2, 8);
    var emailInput = el('input', { type: 'email', name: 'email', required: 'required', autocomplete: 'email', placeholder: CONSENT_COPY.emailPlaceholder, id: emailId });
    var emailField = el('div', { class: 'ms-chat-field' });
    var emailLbl = el('label', { text: CONSENT_COPY.emailLabel + ' *' });
    emailLbl.setAttribute('for', emailId);
    emailField.appendChild(emailLbl);
    emailField.appendChild(emailInput);
    form.appendChild(emailField);

    // Consent block — populated with the CANONICAL backend-served copy once it
    // resolves (loadConsent below). Nothing legal is hard-coded here.
    var consentArea = el('div', { class: 'ms-chat-consent-area' });
    form.appendChild(consentArea);

    var errEl = el('div', { class: 'ms-chat-form-error', style: 'display:none' });
    form.appendChild(errEl);

    var submit = el('button', { type: 'submit', class: 'ms-chat-btn ms-chat-btn--primary' }, [CONSENT_COPY.submit]);
    submit.disabled = true; // enabled only once the served consent copy is rendered
    form.appendChild(submit);
    body.appendChild(form);

    body.appendChild(el('div', { class: 'ms-chat-caption', text: CONSENT_COPY.privacy }));

    // Imprint/Privacy links next to the form (compliance requirement —
    // CONSENT_FLOW.md). Targets are backend-served; filled in renderConsent.
    var legalEl = el('div', { class: 'ms-chat-legal-links' });
    body.appendChild(legalEl);

    // Decline affordance (API_CONTRACT.md §2/§5): the backend cannot observe
    // a dismissal, so the widget reports it — ONE email_capture_declined KPI
    // event (trigger only; no email, no text), then the card collapses to a
    // short note. Removed automatically on submit success (body is replaced).
    var declineBtn = el('button', { type: 'button', class: 'ms-chat-capture-decline', text: CONSENT_COPY.decline });
    declineBtn.addEventListener('click', function () {
      track('email_capture_declined', opts.trigger ? { trigger: opts.trigger } : {});
      body.replaceChildren(head, el('div', { class: 'ms-chat-card-text', text: CONSENT_COPY.declined }));
      // Unblock the header entry point: a declined card must not be "reused".
      var row = card.closest ? card.closest('.ms-chat-row') : null;
      if (row && row === lastCaptureRow) lastCaptureRow = null;
    });
    body.appendChild(declineBtn);

    // Two SEPARATE consent controls. Each is a real <label> wrapping a real
    // <input type=checkbox> (keyboard-usable, clicking the text toggles it) with
    // the full consent text always visible (never truncated). v2: EVERY box
    // starts UNCHECKED — there is deliberately no way to construct a
    // pre-ticked row here.
    function consentRow(labelText) {
      var row = el('label', { class: 'ms-chat-consent' });
      var input = el('input', { type: 'checkbox' });
      row.appendChild(input);
      var text = el('span', { class: 'ms-chat-consent-text' }, [labelText]);
      row.appendChild(text);
      return { row: row, input: input };
    }

    // The backend-served copy currently rendered. Submitting is impossible
    // until this is set: `consentTextShown` must be the served audit string
    // verbatim (Art. 7 proof), so there is no client-side fallback text.
    var copy = null;
    var txn = null;
    var mkt = null;
    var consentGroup = null;
    var attnActive = false;

    // Unchecked-transactional attention treatment: accent outline + a brief
    // shake on the checkbox group (the shake is disabled via CSS under
    // prefers-reduced-motion) plus ONE inline hint line. No request is sent.
    // Cleared as soon as the user checks a box.
    function flagConsentAttention() {
      showError(CONSENT_COPY.errTransactional);
      attnActive = true;
      if (consentGroup) {
        consentGroup.classList.remove('ms-chat-consent-group--attn');
        void consentGroup.offsetWidth; // restart the shake on repeat clicks
        consentGroup.classList.add('ms-chat-consent-group--attn');
      }
      if (txn) { try { txn.input.focus(); } catch (e) {} }
    }
    function clearConsentAttention() {
      if (!attnActive) return;
      attnActive = false;
      if (consentGroup) consentGroup.classList.remove('ms-chat-consent-group--attn');
      clearError();
    }

    function renderConsent(c) {
      copy = c;
      consentArea.replaceChildren();
      // BOTH boxes start UNCHECKED (consent copy v2 — API_CONTRACT.md §2/§7.4;
      // the v1 allowance to pre-check the transactional box is revoked).
      // TRANSACTIONAL box: required to submit, but the user actively ticks it
      // — the submit button stays clickable and an unchecked submit triggers
      // the attention treatment instead of a request.
      txn = consentRow(c.transactionalLabel);
      // MARKETING box: PROMINENT (own accent-edged block) but ALWAYS
      // UNCHECKED and never auto-toggled by any other interaction.
      // DELIBERATE LEGAL DECISION — never pre-check this box: pre-ticked
      // marketing consent is invalid under the GDPR's clear-affirmative-act
      // requirement (CJEU C-673/17 "Planet49") and a German UWG Abmahnung
      // trigger. The opt-in is won through placement and copy, never through
      // a pre-tick. Do not "optimise" this.
      mkt = consentRow(c.marketingLabel);
      mkt.row.classList.add('ms-chat-consent--marketing');
      consentGroup = el('div', { class: 'ms-chat-consent-group' });
      consentGroup.appendChild(txn.row);
      consentGroup.appendChild(mkt.row);
      consentArea.appendChild(consentGroup);
      // Checking a box resolves the unchecked-transactional highlight.
      txn.input.addEventListener('change', function () { if (txn.input.checked) clearConsentAttention(); });
      mkt.input.addEventListener('change', function () { if (mkt.input.checked) clearConsentAttention(); });
      // Shared footer line beneath BOTH checkboxes (v2) — backend-served,
      // rendered verbatim (part of the consentTextShown audit text).
      if (typeof c.consentFooter === 'string' && c.consentFooter) {
        consentArea.appendChild(el('div', { class: 'ms-chat-consent-footer', text: c.consentFooter }));
      }
      // Returning-customer hint above the email field — backend-gated; an
      // absent field or enabled:false renders nothing.
      var rh = returningHintText(c);
      returnHintEl.textContent = rh || '';
      returnHintEl.style.display = rh ? '' : 'none';

      legalEl.replaceChildren();
      var impHref = safeHref(c.imprintUrl);
      var privHref = safeHref(c.privacyUrl);
      if (impHref) legalEl.appendChild(el('a', { href: impHref, target: '_blank', rel: 'noopener noreferrer', text: L('Impressum', 'Imprint') }));
      if (privHref) legalEl.appendChild(el('a', { href: privHref, target: '_blank', rel: 'noopener noreferrer', text: L('Datenschutz', 'Privacy') }));

      submit.disabled = false;
    }

    function loadConsent() {
      consentArea.replaceChildren(el('div', { class: 'ms-chat-consent-loading', text: CONSENT_COPY.loading }));
      fetchConsentCopy().then(renderConsent).catch(function () {
        // Without the served copy the form must not submit — offer a retry.
        consentArea.replaceChildren();
        consentArea.appendChild(el('div', { class: 'ms-chat-form-error', text: CONSENT_COPY.errCopyLoad }));
        var retry = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary' }, [CONSENT_COPY.retry]);
        retry.addEventListener('click', loadConsent);
        consentArea.appendChild(retry);
      });
    }
    loadConsent();

    function showError(msg) { errEl.textContent = msg; errEl.style.display = 'block'; }
    function clearError() { errEl.textContent = ''; errEl.style.display = 'none'; }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      clearError();
      if (!copy || !txn || !mkt) return; // canonical copy not loaded -> cannot submit

      // Client-side validation before sending.
      var email = emailInput.value.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { showError(CONSENT_COPY.errEmail); try { emailInput.focus(); } catch (e) {} return; }
      // v2: the transactional box starts unchecked. An unchecked submit sends
      // NOTHING — it highlights the checkbox group with one inline hint.
      if (!txn.input.checked) { flagConsentAttention(); return; }

      var marketing = !!mkt.input.checked;
      var payload = {
        sessionId: sid,
        email: email,
        transactionalConsent: true,
        marketingConsent: marketing,
        // The backend's pre-composed audit string (labels + shared footer),
        // echoed back VERBATIM — byte-for-byte the text rendered above. Never
        // recomposed or hard-coded client-side (Art. 7 proof of consent).
        consentTextShown: copy.consentTextShown,
        // Stored with the consent record and carried to the summary + DOI
        // emails. MUST match the locale the consent copy was served in
        // (LOCALE.md §1) — both derive from the page LOCALE, so they agree.
        locale: LOCALE
      };
      // Echo of the offer's value moment (telemetry only — opt-in funnel split).
      if (opts.trigger) payload.trigger = opts.trigger;

      submit.disabled = true;
      submit.textContent = CONSENT_COPY.sending;
      // No widget-side "submitted" KPI event: email_capture_submitted is
      // recorded server-side by /api/capture-email (API_CONTRACT.md §5).

      fetch(API_BASE + '/api/capture-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (res.ok) {
          // Unlock returning-customer memory for the rest of this page's
          // session (in-memory only — see the capturedEmail privacy gate).
          capturedEmail = email;
          // A marketing tick here is the same decision the consent gate asks
          // for — remember it (device-local UX memory) so the gate stays quiet.
          if (marketing) recordMktDecision('accepted');
          // Branch the success copy on the DOI state (API_CONTRACT.md §7.1).
          // The transactional summary is sent either way, so its confirmation
          // always stays; only the marketing line varies, and only when the
          // box was ticked (an unticked submit on a confirmed address also
          // reads alreadyConfirmed — not news the customer asked for):
          // pending + DOI mail sent -> "confirm the link"; already subscribed
          // (Mo or shop, no DOI mail) -> "nothing else to do"; else nothing.
          return res.json().catch(function () { return null; }).then(function (data) {
            var outcome = marketing ? marketingOutcome(data && data.marketing) : 'other';
            var line = CONSENT_COPY.success;
            if (outcome === 'pending') line += ' ' + CONSENT_COPY.successPending;
            else if (outcome === 'already') line += ' ' + MKT_RESULT_COPY.already;
            var ok = el('div', { class: 'ms-chat-form-success' });
            ok.appendChild(icon('check'));
            ok.appendChild(el('h3', { text: CONSENT_COPY.successTitle }));
            ok.appendChild(el('p', { text: line }));
            body.replaceChildren(head, ok);
            scrollToBottom();
          });
        }
        // Error: keep the form populated for retry.
        return res.json().catch(function () { return null; }).then(function (data) {
          var code = data && data.error && data.error.code;
          var msg = (data && data.error && data.error.message) || '';
          if (code === 'transactional_consent_required') {
            // Defensive: the widget already blocks an unchecked submit
            // client-side; if the backend still rejects (§7.1), surface the
            // same targeted checkbox attention instead of a generic error.
            submit.disabled = false;
            submit.textContent = CONSENT_COPY.submit;
            flagConsentAttention();
            return;
          }
          if (res.status === 429 || code === 'rate_limited') {
            // Honor Retry-After: keep submit locked for the window (mirrors
            // the chat path; same 30s default when the header is unreadable)
            // instead of allowing an instant re-submission.
            var retry = parseInt(res.headers.get('Retry-After'), 10);
            if (!isFinite(retry) || retry <= 0) retry = 30;
            showError(CONSENT_COPY.errRate);
            submit.textContent = CONSENT_COPY.submit;
            setTimeout(function () { submit.disabled = false; }, retry * 1000);
            return;
          }
          if (res.status === 502 || res.status === 503 || code === 'upstream_unavailable') {
            msg = CONSENT_COPY.errUpstream;
          } else if (!msg) {
            msg = CONSENT_COPY.errGeneric;
          }
          showError(msg);
          submit.disabled = false;
          submit.textContent = CONSENT_COPY.submit;
        });
      }).catch(function () {
        showError(CONSENT_COPY.errUpstream);
        submit.disabled = false;
        submit.textContent = CONSENT_COPY.submit;
      });
    });

    card.appendChild(body);
    return card;
  }

  function buildToolCard(name, input, key, live) {
    switch (name) {
      case 'suggest_replies': return buildReplies(input);
      case 'show_gym_plan': return buildGymPlan(input, key);
      case 'offer_plan_options': return buildOffer(input, key);
      case 'show_product': return buildShowProduct(input);
      case 'compare_products': return buildCompare(input);
      case 'add_to_cart': return buildAddToCart(input);
      case 'suggest_showroom': return buildShowroom(input, key, live);
      case 'show_contact_form': return buildContactForm(input);
      case 'offer_email_summary':
        // Signed-in (tier 3): the email comes from the account, so never show
        // the "type your email" capture (task §5 — no double-ask). The backend
        // normally won't emit this for a signed-in customer; suppress defensively.
        if (auth.signedIn) return Promise.resolve(null);
        return Promise.resolve(buildCaptureCard({ message: input.message, productIds: input.productIds, trigger: input.trigger }));
      default: return Promise.resolve(null);
    }
  }

  // ---------------------------------------------------------------------------
  // Home-Gym-Konfigurator (Men's Health / Women's Health cooperation).
  //
  // The configurator page (sections/ms-home-gym-configurator.liquid) lets the
  // visitor pick a few basic answers and hands the conversation to Mo through
  // window.MS_CHAT.configurator.start(). From then on:
  //   * the panel is DOCKED into the page's [data-ms-chat-dock] host (no
  //     launcher, no backdrop, no page shift, no scroll lock — see DOCK);
  //   * every /api/chat request of that thread carries
  //     context { type: "configurator", configurator: {answers…, plan} }
  //     (backend API_CONTRACT §2) — the page's own form state, so a changed
  //     answer or an edited plan reaches Mo with the next message;
  //   * Mo's suggest_replies renders answer chips, show_gym_plan a plan card
  //     in the chat AND the editable plan in the page's [data-ms-chat-plan]
  //     host, with one „Alles in den Warenkorb“ (POST /cart/add.js).
  // The page follows along through `ms-chat:configurator` CustomEvents on
  // document (type: ready | restored | started | stage | plan | reply | cart |
  // reset). KPI: ids, enums, counts and booleans only (§0 rule 13).
  // The thread's answers live in localStorage under the session id
  // (ms-chat-cfg:<sid>) and are active only while the same thread is open —
  // a new chat (rotated sid / new conversationKey) ends them.
  // ---------------------------------------------------------------------------
  var DOCK = null;                    // [data-ms-chat-dock] host on the configurator page
  var dockRoot = null;                // .ms-chat-root inside the host
  var CFG_STORE_PREFIX = 'ms-chat-cfg:';
  var CFG_FIELDS = {
    // Mo's own customer personas (the ids the backend derives for every
    // customer); they replace the former „Ich will …“ goal.
    persona: ['pragmatic_beginner', 'ambitious_home_athlete', 'strength_focused', 'cardio_focused', 'design_lifestyle', 'weight_loss'],
    location: ['house', 'apartment', 'garage'],
    space: ['xs', 's', 'm', 'l'],
    level: ['rookie', 'intermediate', 'pro'],
    budget: ['b1', 'b2', 'b3', 'b4', 'b5']
  };
  // Answers of the first page version (stored threads, cached pages): its
  // personas and the removed goal map onto today's personas (backend:
  // configurator-core.mjs personaAnswer).
  var CFG_LEGACY_PERSONA = { busy: 'pragmatic_beginner', ambitious: 'ambitious_home_athlete', health: 'cardio_focused', family: 'pragmatic_beginner', best_ager: 'cardio_focused' };
  var CFG_LEGACY_GOAL = { muscle: 'strength_focused', fitness: 'weight_loss', health: 'cardio_focused', endurance: 'cardio_focused', athletic: 'ambitious_home_athlete' };
  var CFG_MULTI = {
    training: ['free_weights', 'machines', 'cardio', 'functional'],
    wishes: ['assembly', 'fast_delivery', 'quiet', 'no_drilling', 'foldable', 'floor_protection', 'showroom']
  };
  var CFG_ROLES = {
    base: L('Basisgerät', 'Base unit'),
    accessory: L('Zubehör', 'Accessory'),
    weights: L('Gewichte', 'Weights'),
    cardio: 'Cardio',
    flooring: L('Bodenschutz', 'Floor protection'),
    extra: 'Extra'
  };
  var CFG_REPLY_TOPICS = ['budget', 'wishes', 'frequency', 'space', 'equipment', 'other'];
  var CFG_FEATURES = ['options', 'rules'];
  var cfgPlans = {};          // toolCallId -> plan (this thread's rendered plans)
  var cfgPlanSeq = 0;
  var cfgOfferGroups = {};    // toolCallId -> option group (this thread's offer_plan_options)
  var cfgOfferSeq = 0;
  var cfgActivePlanKey = null;
  var cfgStage = 'start';     // start | questions | plan | cart
  var cfgCartBusy = false;

  function cfgStorageKey() { return CFG_STORE_PREFIX + sid; }
  // Plan prices always with cents (euro() trims them for product cards).
  function cfgMoney(value) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    return LOCALE === 'en'
      ? n.toLocaleString('en-GB', { style: 'currency', currency: 'EUR' })
      : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }
  function cfgEmit(type, detail) {
    try {
      var d = detail || {};
      d.type = type;
      document.dispatchEvent(new CustomEvent('ms-chat:configurator', { detail: d }));
    } catch (e) {}
  }

  // Keep only known ids (the vocabulary is the backend's configurator-core.mjs).
  // The visitor's own note from the page's optional step 04 („Gibt es etwas,
  // das wir in deinem Fall beachten sollten?“) rides along as `note` — free
  // text, never an answer of its own: a note alone is no configurator state.
  function cfgSanitize(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var out = {};
    var known = 0;
    var k, i, v;
    for (k in CFG_FIELDS) {
      if (!Object.prototype.hasOwnProperty.call(CFG_FIELDS, k)) continue;
      v = typeof raw[k] === 'string' ? raw[k] : '';
      if (k === 'persona' && CFG_FIELDS.persona.indexOf(v) === -1) {
        v = CFG_LEGACY_PERSONA[v] || (typeof raw.goal === 'string' && CFG_LEGACY_GOAL[raw.goal]) || '';
      }
      if (CFG_FIELDS[k].indexOf(v) !== -1) { out[k] = v; if (k !== 'budget') known++; }
    }
    for (k in CFG_MULTI) {
      if (!Object.prototype.hasOwnProperty.call(CFG_MULTI, k)) continue;
      var list = Array.isArray(raw[k]) ? raw[k] : [];
      var picked = [];
      for (i = 0; i < CFG_MULTI[k].length; i++) {
        if (list.indexOf(CFG_MULTI[k][i]) !== -1) picked.push(CFG_MULTI[k][i]);
      }
      out[k] = picked;
      if (k === 'training' && picked.length) known++;
    }
    var note = cfgCleanNote(raw.note);
    if (note) out.note = note;
    return known ? out : null;
  }

  // The note as Mo gets it (context.configurator.note; the backend's
  // sanitizeConfiguratorNote and the page's noteClean are the same rule):
  // plain text, control and invisible format characters out — the joiners
  // U+200C / U+200D stay (emoji and scripts need them) — spaces, tabs and
  // U+2028 / U+2029 collapsed to one space, line breaks kept but single, at
  // most CFG_NOTE_MAX characters; empty → ''.
  var CFG_NOTE_MAX = 500;
  function cfgCleanNote(v) {
    if (typeof v !== 'string') return '';
    var t = v.replace(/\r\n?/g, '\n')
      .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u00ad\u200b\u200e\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g, '')
      .replace(/[^\S\n]+/g, ' ')
      .replace(/ ?\n ?/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .trim();
    if (t.length > CFG_NOTE_MAX) {
      t = t.slice(0, CFG_NOTE_MAX);
      // Never half a surrogate pair (an emoji cut in two).
      if (/[\ud800-\udbff]$/.test(t)) t = t.slice(0, -1);
      t = t.trim();
    }
    return t;
  }

  function cfgLoad() {
    var raw = lsGet(cfgStorageKey());
    if (!raw) return null;
    try {
      var c = JSON.parse(raw);
      if (c && c.v === 1 && c.answers && typeof c.answers === 'object') return c;
    } catch (e) {}
    return null;
  }
  function cfgSave(c) {
    if (!sidIsCurrent()) return;
    try { lsSet(cfgStorageKey(), JSON.stringify(c)); } catch (e) {}
  }
  // The configurator state of the OPEN thread, or null (another thread, or none).
  function cfgActive() {
    var c = cfgLoad();
    if (!c) return null;
    if ((c.convKey || null) !== (activeConversationKey || null)) return null;
    return c;
  }

  // context { type: "configurator" } for /api/chat — the answers plus the plan
  // as it stands on the page (ids + quantities only).
  function cfgContextForSend() {
    var c = cfgActive();
    if (!c) return null;
    var a = c.answers || {};
    var conf = { v: 1 };
    for (var k in a) { if (Object.prototype.hasOwnProperty.call(a, k)) conf[k] = a[k]; }
    if (c.partner === 'mh' || c.partner === 'wh') conf.partner = c.partner;
    var plan = cfgPlanForContext();
    if (plan.length) conf.plan = plan;
    // What this widget renders beyond the first version: "options" = the
    // step-by-step product choice (offer_plan_options); "rules" (since
    // 2026-10-27) = it normalises Mo's quantities by the product rules and
    // sizes one floor product to his floor area, as the backend reports in
    // `adjusted`. Without them (an old cached widget) the backend keeps the
    // one-plan guide / tells Mo to put the final quantities into his input.
    conf.features = CFG_FEATURES.slice();
    return { type: 'configurator', source: 'configurator', configurator: conf };
  }

  // Hold the one-per-session sign-in / opt-in popup in a configurator thread
  // until Mo has shown a plan — the guided questions (and the step-by-step
  // choice, whose plan is the visitor's own) are not interrupted.
  function cfgGateHold() {
    return !!(cfgActive() && (!cfgActivePlanKey || cfgActivePlanKey === CFG_OWN_KEY));
  }

  function cfgSetStage(stage) {
    if (cfgStage === stage) return;
    cfgStage = stage;
    cfgEmit('stage', { stage: stage });
  }

  // Starts a configurator thread with the page's answers. A thread in
  // progress is ended first exactly like „Neuen Chat starten“ (anonymous: new
  // session id). Returns false when Mo cannot start right now (rate limit).
  function cfgStart(rawAnswers, meta) {
    var answers = cfgSanitize(rawAnswers);
    if (!answers) return false;
    if (state.rateLocked) return false;
    meta = meta || {};
    if (messages.length || state.streaming) startNewChat(true);
    var partner = meta.partner === 'mh' || meta.partner === 'wh' ? meta.partner : null;
    // Signed in, startNewChat already minted the new thread's key; otherwise
    // the first turn mints it (updated below).
    cfgSave({ v: 1, answers: answers, partner: partner, convKey: activeConversationKey || null, edits: {}, startedAt: Date.now() });
    // note: whether the visitor wrote something in step 04 („written“) or
    // went on without („skipped“, also an old page without the step) — never
    // the text itself (personal data, it lives only in the request context).
    track('configurator_handover', {
      persona: answers.persona || '',
      location: answers.location || '',
      space: answers.space || '',
      training: (answers.training || []).join(','),
      level: answers.level || '',
      partner: partner || '',
      prefilled: !!meta.prefilled,
      note: answers.note && meta.note !== 'skipped' ? 'written' : 'skipped'
    });
    openPanel(true);
    cfgApplyChrome();
    sendContextGreeting(cfgContextForSend());
    // A signed-in visitor's first turn minted the thread key just now.
    var c = cfgLoad();
    if (c) { c.convKey = activeConversationKey || null; cfgSave(c); }
    cfgStage = 'start';
    cfgSetStage('questions');
    // The page's plan box shows its waiting state right away (not only after
    // a reload) — the step-by-step choice fills it pick by pick.
    cfgRenderHost();
    cfgEmit('started', { answers: answers, partner: partner });
    return true;
  }

  // Changed answers after the handover: stored for the next turns and told to
  // Mo as a visible user message (the page composes the text).
  function cfgUpdate(rawAnswers, text) {
    var c = cfgActive();
    var answers = cfgSanitize(rawAnswers);
    if (!c || !answers) return false;
    if (state.streaming || state.rateLocked) return false;
    c.answers = answers;
    cfgSave(c);
    var msg = typeof text === 'string' ? text.trim().slice(0, 600) : '';
    if (msg) sendMessage(msg, null);
    return true;
  }

  // The dock's footer: the „·“ between the disclaimer and „Feedback geben“
  // only while all three sit on one line.
  function cfgFooterWrap() {
    var footer = panel && panel.querySelector('.ms-chat-footer');
    if (!footer) return;
    var disc = footer.querySelector('.ms-chat-disclaimer');
    var sep = footer.querySelector('.ms-chat-footer-sep');
    var link = footer.querySelector('.ms-chat-feedback-link');
    if (!disc || !sep || !link) return;
    footer.classList.remove('ms-chat-footer--wrapped');
    var d = disc.getBoundingClientRect();
    var s = sep.getBoundingClientRect();
    var k = link.getBoundingClientRect();
    if (!s.width) return;
    var sameLine = function (a, b) { return a.top < b.bottom - 1 && b.top < a.bottom - 1; };
    footer.classList.toggle('ms-chat-footer--wrapped', !sameLine(d, s) || !sameLine(s, k));
  }
  function cfgApplyChrome() {
    var on = !!cfgActive();
    if (DOCK) DOCK.classList.toggle('ms-chat-dock--live', state.open);
    if (DOCK) cfgFooterWrap();
    if (textarea) {
      // The composer is one row high: where the docked panel is too narrow
      // for the long placeholder (phones, the tablet's chat column) it would
      // wrap and be cut in half, so the short one there.
      var longText = L('Antwort auswählen oder eigene Nachricht schreiben …', 'Pick an answer or write your own message …');
      var shortText = L('Antwort wählen oder schreiben …', 'Pick an answer or write …');
      textarea.placeholder = !on
        ? L('Wie kann ich dir helfen?', 'How can I help you?')
        : !DOCK || cfgFitsOneRow(longText)
          ? longText
          : cfgFitsOneRow(shortText)
            ? shortText
            : L('Antwort schreiben …', 'Write …');
    }
  }
  var cfgMeasure = null;
  function cfgFitsOneRow(text) {
    try {
      var cs = window.getComputedStyle(textarea);
      var box = textarea.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      if (!(box > 0)) return isDesktop();
      if (!cfgMeasure) cfgMeasure = document.createElement('canvas').getContext('2d');
      cfgMeasure.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      return cfgMeasure.measureText(text).width + 4 <= box;
    } catch (e) { return isDesktop(); }
  }

  // Docked mode boot: a restored configurator thread opens in the dock right
  // away (reload, back navigation); otherwise the page's intro stays visible
  // until the handover.
  function cfgInitDock() {
    var c = cfgActive();
    var restored = !!(DOCK && c && messages.length);
    // Rotation / resize: the placeholder follows the composer's width — and
    // once the web font has loaded (measured with the fallback font before,
    // which is narrower).
    if (DOCK) {
      var chromeTimer = null;
      window.addEventListener('resize', function () {
        if (chromeTimer) clearTimeout(chromeTimer);
        chromeTimer = setTimeout(function () { chromeTimer = null; cfgApplyChrome(); cfgOfferViews(); }, 150);
      });
      try {
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { cfgApplyChrome(); });
      } catch (e) {}
    }
    if (restored) {
      openPanel(true);
      cfgApplyChrome();
      cfgSetStage(cfgActivePlanKey ? 'plan' : 'questions');
    }
    cfgRenderHost();
    cfgEmit(restored ? 'restored' : 'ready', {
      answers: restored ? c.answers : null,
      partner: restored ? (c.partner || null) : null,
      stage: cfgStage,
      dock: !!DOCK
    });
  }

  // KPI from the page: only configurator_* names, only flat ids/enums/numbers.
  function cfgTrackFromPage(event, data) {
    if (typeof event !== 'string' || !/^configurator_[a-z_]{1,40}$/.test(event)) return;
    if (event === 'configurator_chat_started' || event === 'configurator_plan_shown') return; // server-only
    var safe = {};
    if (data && typeof data === 'object') {
      for (var k in data) {
        if (!Object.prototype.hasOwnProperty.call(data, k) || !/^[a-z_]{1,30}$/.test(k)) continue;
        var v = data[k];
        if (typeof v === 'boolean' || (typeof v === 'number' && isFinite(v))) safe[k] = v;
        else if (typeof v === 'string' && /^[a-z0-9_,.-]{0,80}$/i.test(v)) safe[k] = v;
      }
    }
    track(event, safe);
  }

  function cfgRestart() {
    startNewChat();
    return true;
  }

  // ----- suggest_replies → answer chips --------------------------------------
  function buildReplies(input) {
    var opts = Array.isArray(input && input.options) ? input.options : [];
    var options = [];
    for (var i = 0; i < opts.length && options.length < 8; i++) {
      var o = typeof opts[i] === 'string' ? opts[i].trim() : '';
      if (o && o.length <= 80 && options.indexOf(o) === -1) options.push(o);
    }
    if (options.length < 2) return Promise.resolve(null);
    var multi = input.multiSelect === true;
    var topic = CFG_REPLY_TOPICS.indexOf(input.topic) !== -1 ? input.topic : 'other';
    var wrap = el('div', { class: 'ms-chat-replies' + (multi ? ' ms-chat-replies--multi' : ''), role: 'group', 'aria-label': L('Antwortvorschläge', 'Suggested answers') });
    var picked = [];
    var confirm = null;
    function send(values) {
      if (wrap.classList.contains('ms-chat-replies--used')) return;
      if (state.streaming || state.rateLocked) return;
      wrap.classList.add('ms-chat-replies--used');
      track('configurator_chip_clicked', { topic: topic, multi: multi, count: values.length });
      cfgEmit('reply', { topic: topic, values: values.slice() });
      var text = values.length > 1
        ? values.slice(0, -1).join(', ') + L(' und ', ' and ') + values[values.length - 1]
        : values[0];
      sendMessage(text, null);
    }
    options.forEach(function (label) {
      var b = el('button', { class: 'ms-chat-reply', type: 'button', text: label });
      if (multi) b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () {
        if (wrap.classList.contains('ms-chat-replies--used')) return;
        if (!multi) { send([label]); return; }
        var idx = picked.indexOf(label);
        if (idx === -1) picked.push(label); else picked.splice(idx, 1);
        b.setAttribute('aria-pressed', idx === -1 ? 'true' : 'false');
        b.classList.toggle('ms-chat-reply--on', idx === -1);
        if (confirm) confirm.disabled = picked.length === 0;
      });
      wrap.appendChild(b);
    });
    if (multi) {
      confirm = el('button', { class: 'ms-chat-reply ms-chat-reply--confirm', type: 'button', text: L('Übernehmen', 'Confirm') });
      confirm.disabled = true;
      confirm.addEventListener('click', function () {
        if (!picked.length) return;
        // Keep the order Mo offered them in.
        send(options.filter(function (o) { return picked.indexOf(o) !== -1; }));
      });
      wrap.appendChild(confirm);
    }
    return Promise.resolve(wrap).then(function (node) { setTimeout(cfgSyncReplies, 0); return node; });
  }

  // Chips are answerable only until the visitor wrote the next message.
  function cfgSyncReplies() {
    if (!messagesEl) return;
    try {
      var userRows = messagesEl.querySelectorAll('.ms-chat-row--user');
      var lastUser = userRows.length ? userRows[userRows.length - 1] : null;
      var groups = messagesEl.querySelectorAll('.ms-chat-replies');
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i];
        var before = lastUser && (g.compareDocumentPosition(lastUser) & 4);
        if (before) g.classList.add('ms-chat-replies--used');
      }
    } catch (e) {}
  }

  // ----- show_gym_plan → plan card + the page's plan view ----------------------
  // A plan line: { uid, ref, qty, role, note, removed, variantId, product, added }.
  // Mo's lines carry uid = ref (edits stored before lines had uids keep
  // working); lines the visitor added himself („+ Produkt hinzufügen“,
  // „+ Gewichtsstufe“) carry uid 'a<n>' and added: true. The DOM and the stored
  // edits key on uid, never on ref — the same product may sit in the plan
  // twice with different variants (weight steps). A line the visitor chose
  // from Mo's options (offer_plan_options) also carries pick = the option
  // group's toolCallId and pickStep = its step, so a later choice in the same
  // group (or, for one-thing steps, in another group of the step) replaces it.
  // Before Mo has shown a plan the picks live in the visitor's own plan
  // (key CFG_OWN_KEY: no Mo lines, no card in the chat); a plan from Mo then
  // takes over exactly like any newer plan.
  var CFG_MAX_LINES = 20;
  var CFG_OWN_KEY = '#own';
  var CFG_ROLE_ORDER = ['base', 'accessory', 'weights', 'cardio', 'flooring', 'extra'];
  var CFG_ROLES_PLURAL = {
    base: L('Basisgeräte', 'Base units'),
    accessory: L('Zubehör', 'Accessories'),
    weights: L('Gewichte', 'Weights'),
    cardio: 'Cardio',
    flooring: L('Bodenschutz', 'Floor protection'),
    extra: 'Extras'
  };
  var cfgFocusNext = null;    // { uid, key } — focus target after the next host render

  function cfgResetPlans() {
    var own = cfgPlans[CFG_OWN_KEY];
    if (own && own.timer) { clearTimeout(own.timer); own.timer = null; }
    cfgPlans = {};
    cfgPlanSeq = 0;
    cfgActivePlanKey = null;
    cfgOfferGroups = {};
    cfgOfferSeq = 0;
  }

  function cfgVariantOf(p, line) {
    var vs = Array.isArray(p && p.variants) ? p.variants : [];
    if (!vs.length) return null;
    var want = line.variantId || (p.selectedVariantId != null ? String(p.selectedVariantId) : null);
    var i;
    if (want) { for (i = 0; i < vs.length; i++) { if (vs[i] && String(vs[i].id) === String(want)) return vs[i]; } }
    for (i = 0; i < vs.length; i++) { if (vs[i] && vs[i].isDefault && vs[i].available) return vs[i]; }
    for (i = 0; i < vs.length; i++) { if (vs[i] && vs[i].available) return vs[i]; }
    return vs[0];
  }
  // The name and PDP link a line shows. /api/products answers a pinned ref
  // (handle~variantId) with the product projected onto THAT variant: name
  // „<name> – <variant>“, shopifyUrl ?variant=<id>, selectedVariantId. A line
  // whose own variant differs (a weight step added from it, a changed select)
  // must never show that projection, so the projection is stripped and the
  // line's own variant appended whenever the line names one.
  function cfgSelectedVariant(p) {
    if (!p || p.selectedVariantId == null) return null;
    var vs = Array.isArray(p.variants) ? p.variants : [];
    for (var i = 0; i < vs.length; i++) { if (vs[i] && String(vs[i].id) === String(p.selectedVariantId)) return vs[i]; }
    return null;
  }
  function cfgBaseName(p) {
    var name = String((p && p.name) || '');
    var sv = cfgSelectedVariant(p);
    var title = sv && typeof sv.title === 'string' ? sv.title.trim() : '';
    var suffix = ' – ' + title;
    if (title && name.length > suffix.length && name.slice(-suffix.length) === suffix) return name.slice(0, -suffix.length);
    return name;
  }
  function cfgBaseUrl(p) {
    return String((p && p.shopifyUrl) || '').replace(/([?&])variant=[^&#]*(&?)/, function (m, sep, amp) { return amp ? sep : ''; });
  }
  function cfgLineView(p, l) {
    var projected = !!(p && p.selectedVariantId != null);
    var v = cfgVariantOf(p, l);
    if ((!l.variantId && !projected) || !v || cfgVariants(p).length < 2) {
      return projected ? { name: cfgBaseName(p), url: cfgBaseUrl(p) } : { name: String(p.name || ''), url: String(p.shopifyUrl || '') };
    }
    var title = typeof v.title === 'string' ? v.title.trim() : '';
    var url = cfgBaseUrl(p);
    var vid = v.id != null ? String(v.id) : '';
    if (url && /^\d+$/.test(vid)) url += (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + vid;
    return { name: cfgBaseName(p) + (title ? ' – ' + title : ''), url: url };
  }
  function cfgUnitPrice(p, v) {
    if (v) return (typeof v.salePrice === 'number' && v.salePrice > 0) ? v.salePrice : Number(v.price) || 0;
    return p.salePrice != null ? Number(p.salePrice) || 0 : Number(p.price) || 0;
  }
  function cfgLineAvailable(p, v) {
    if (!p) return false;
    if (v) return v.available !== false && v.id != null;
    return p.inStock !== false;
  }
  // The product a line stands for (variant suffix stripped).
  function cfgBaseId(l) {
    var id = l.product && l.product.id != null ? String(l.product.id) : String(l.ref || '');
    return id.split('~')[0];
  }
  // Variants a visitor can pick (the ones with a cart id).
  function cfgVariants(p) {
    return Array.isArray(p && p.variants) ? p.variants.filter(function (x) { return x && x.id != null; }) : [];
  }
  function cfgDefaultVariant(p) {
    var vs = cfgVariants(p);
    var i;
    for (i = 0; i < vs.length; i++) { if (vs[i].isDefault && vs[i].available !== false) return vs[i]; }
    for (i = 0; i < vs.length; i++) { if (vs[i].available !== false) return vs[i]; }
    return vs[0] || null;
  }

  // ----- product rules (since 2026-10-27) --------------------------------------
  // /api/products and the picker's cards carry the quantity rules the backend
  // checks Mo's plans against (product-rules.mjs): rules: { step?, min?, max?,
  // coverageM2? } — only what differs from the default (step 1, min 1, no
  // max, no coverage; {} = one by one) — and showroom: true / false while the
  // backend knows its showroom list. A coverageM2 means „sized to the room“.
  // The plan follows them: lines from Mo's input and from his options are
  // normalised (up to the step, at least min, at most max — Mo's text names
  // the same quantities, the tool result told him), the stepper moves by the
  // step and never below min, „+ Produkt hinzufügen“ adds min, the cart gets
  // the same quantities, and a plan stored before is normalised when it
  // comes back. A backend before that sends neither field: quantities stay
  // as they are (1–20) and only the round-4 rule of thumb (cfgSoldInPairs)
  // decides what „Hinzufügen“ adds.
  // cfgRulesOn: a product or card of this page load carried one of the
  // fields — the backend knows rules, a product without them has none.
  // A plan line holds at most CFG_MAX_QTY units, a floor product (one with
  // coverage) CFG_FLOOR_MAX_QTY — a whole room in small tiles (the backend's
  // RULE_LIMITS lineMax / floorLineMax). Raw quantities (Mo's input, the
  // stored plan) are clamped to CFG_FLOOR_MAX_QTY first and normalised by
  // their product's rules (cfgNormQty) before they are shown or carted.
  var CFG_MAX_QTY = 20;
  var CFG_FLOOR_MAX_QTY = 200;
  // A variant that is no tile — a connector, tape, a ramp, an edge piece of a
  // puzzle system — covers nothing (byte for byte the backend's
  // product-rules.mjs NON_TILE_VARIANT).
  var CFG_NON_TILE = /verbinder|frog|clip|connector|kleber|klebeband|\btape\b|rampe|\brechts\b|\blinks\b|\bgerade\b|abschluss|kantenleiste/i;
  var cfgRulesOn = false;
  function cfgNoteRules(p) {
    if (cfgRulesOn || !p || typeof p !== 'object') return;
    if ((p.rules && typeof p.rules === 'object') || typeof p.showroom === 'boolean') cfgRulesOn = true;
  }
  function cfgWhole(v, lo, hi) {
    var n = typeof v === 'number' ? v : NaN;
    return isFinite(n) && Math.floor(n) === n && n >= lo && n <= hi ? n : 0;
  }
  // { step, min, max, cover, known } the way the backend resolves and
  // normalises (product-rules.mjs normalizeQuantity) — for variant v of the
  // product when given (a line's chosen variant, an option item's ref): min
  // and max are multiples of step within a plan line's 1–20 (a floor
  // product's 1–200), the smaller quantity wins a conflict; cover = m² one
  // unit covers (0: none — also for a variant that is no tile); known = the
  // backend sent rules (or knows them and this product has none).
  function cfgRulesOf(p, v) {
    var r = p && p.rules && typeof p.rules === 'object' && !Array.isArray(p.rules) ? p.rules : null;
    var cover = r && typeof r.coverageM2 === 'number' && isFinite(r.coverageM2) && r.coverageM2 > 0 && r.coverageM2 <= 10000 ? r.coverageM2 : 0;
    if (cover && v && CFG_NON_TILE.test(String(v.title || ''))) cover = 0;
    var line = cover ? CFG_FLOOR_MAX_QTY : CFG_MAX_QTY;
    var step = Math.min((r && cfgWhole(r.step, 1, 100000)) || 1, line);
    var max = (r && cfgWhole(r.max, 1, 100000)) || 0;
    var cap = max ? Math.min(max, line) : line;
    if (step > cap) step = 1;
    var min = (r && cfgWhole(r.min, 1, 100000)) || step;
    var lo = Math.min(Math.ceil(min / step) * step, Math.max(1, Math.floor(cap / step) * step));
    var hi = Math.max(lo, Math.floor(cap / step) * step);
    return { step: step, min: lo, max: hi, cover: cover, known: !!r || cfgRulesOn };
  }
  // A quantity as the rules of the product (its variant v) allow it (no
  // rules: 1–20 as before).
  function cfgNormQty(p, q, v) {
    var r = cfgRulesOf(p, v);
    var n = Math.floor(Number(q));
    if (!isFinite(n) || n < 1) n = r.min;
    n = Math.ceil(n / r.step) * r.step;
    return Math.min(r.max, Math.max(r.min, n));
  }
  // The rules of a plan line or an option item: its product with the line's
  // variant (an item: its ref's, else the default one).
  function cfgItemRules(x) { return cfgRulesOf(x.product, cfgVariantOf(x.product, x)); }
  // A floor product sized to Mo's floor area (areaM2 / floorAreaM2) like the
  // backend's validateLines (rule „Raumfläche“): only when exactly ONE of the
  // items covers an area — it gets ceil(area / coverage) units (its step /
  // min / max respected). With two (a drop zone under the rack plus mats for
  // the rest) Mo computes the pieces himself and nothing is resized.
  // items: plan lines or option items ({ product, qty[, variantId] }).
  function cfgSizeTiles(items, area) {
    if (!(area > 0)) return;
    var tiles = items.filter(function (x) { return x.product && cfgItemRules(x).cover > 0; });
    if (tiles.length !== 1) return;
    var t = tiles[0];
    var needed = Math.max(1, Math.ceil(area / cfgItemRules(t).cover - 1e-9));
    t.qty = cfgNormQty(t.product, needed, cfgVariantOf(t.product, t));
  }
  // A floor area from Mo's input as the backend accepts it (m², > 0, ≤ 500,
  // two decimals), else 0.
  function cfgArea(v) {
    var n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v.replace(',', '.')) : NaN);
    return isFinite(n) && n > 0 && n <= 500 ? Math.round(n * 100) / 100 : 0;
  }
  // „12“ / „2,5“ (en: „2.5“) — one decimal at most.
  function cfgM2(n) {
    var x = Math.round(n * 10) / 10;
    var s = x % 1 === 0 ? String(x) : x.toFixed(1);
    return (LOCALE === 'en' ? s : s.replace('.', ',')) + '\u00a0m²';
  }
  // „deckt ca. 12 m²“ for qty units of a product (its variant v) with
  // coverage, else ''.
  function cfgCoverText(p, qty, v) {
    var cover = cfgRulesOf(p, v).cover;
    return cover && qty > 0 ? L('deckt ca. ', 'covers approx. ') + cfgM2(cover * qty) : '';
  }
  // The rule a plan line explains next to its stepper: „Paarweise“, „In 3er-
  // Schritten“, „Mindestens 4“ — and the area its quantity covers (none for
  // a chosen variant that is no tile: v).
  function cfgRuleText(p, qty, v) {
    var r = cfgRulesOf(p, v);
    var parts = [];
    if (r.step === 2) parts.push(L('Paarweise', 'In pairs'));
    else if (r.step > 2) parts.push(L('In ' + r.step + 'er-Schritten', 'In steps of ' + r.step));
    if (r.min > r.step) parts.push(L('mindestens ' + r.min, 'at least ' + r.min));
    var cover = cfgCoverText(p, qty, v);
    if (cover) parts.push(cover);
    var t = parts.join(' · ');
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  }
  // The first available variant of the product that no (kept) line of the
  // plan uses yet — the next weight step — or null.
  // The next free variant of a product. With a source line („+ Gewichtsstufe“
  // on the 10 kg line) the search starts after that line's variant — the next
  // heavier step first, lighter ones only when nothing heavier is free.
  function cfgNextVariant(plan, p, from) {
    var base = String(p && p.id != null ? p.id : '').split('~')[0];
    var used = {};
    plan.lines.forEach(function (l) {
      if (l.removed || !l.product || cfgBaseId(l) !== base) return;
      var v = cfgVariantOf(l.product, l);
      if (v && v.id != null) used[String(v.id)] = 1;
    });
    var vs = cfgVariants(p);
    var start = 0;
    if (from && from.product) {
      var cur = cfgVariantOf(from.product, from);
      for (var k = 0; cur && k < vs.length; k++) {
        if (String(vs[k].id) === String(cur.id)) { start = k + 1; break; }
      }
    }
    for (var n = 0; n < vs.length; n++) {
      var v2 = vs[(start + n) % vs.length];
      if (v2.available !== false && !used[String(v2.id)]) return v2;
    }
    return null;
  }
  function cfgLineByUid(lines, uid) {
    for (var i = 0; i < lines.length; i++) { if (lines[i].uid === uid) return lines[i]; }
    return null;
  }
  // The plan's cap counts the lines in the plan: a line of Mo's that the
  // visitor removed stays visible (to add it back) but frees its slot.
  function cfgKeptCount(lines) {
    var n = 0;
    for (var i = 0; i < lines.length; i++) { if (!lines[i].removed) n++; }
    return n;
  }
  function cfgPlanFull(plan) { return cfgKeptCount(plan.lines) >= CFG_MAX_LINES; }
  function cfgNewUid(lines) {
    var n = 0;
    lines.forEach(function (l) {
      var m = /^a(\d{1,6})$/.exec(l.uid);
      if (m) n = Math.max(n, parseInt(m[1], 10));
    });
    var uid;
    do { n++; uid = 'a' + n; } while (cfgLineByUid(lines, uid));
    return uid;
  }
  // A visitor's line sits right after the last line of the same product
  // (weight steps stay together), otherwise at the end. The restore uses the
  // same rule, so the order survives a reload.
  function cfgInsertLine(lines, line) {
    var base = cfgBaseId(line);
    var at = -1;
    for (var i = 0; i < lines.length; i++) { if (cfgBaseId(lines[i]) === base) at = i; }
    if (at === -1) lines.push(line); else lines.splice(at + 1, 0, line);
  }

  // A product ref in Mo's tool input (a catalog id or handle~variantId) as
  // the backend takes it, after trim(): byte for byte configurator-core.mjs
  // PRODUCT_REF_RE — 1 to 255 characters (CONFIGURATOR_LIMITS.productIdMax),
  // no whitespace, no comma. A ref the backend drops from the plan
  // (normalizePlanItems) and the options (normalizeOfferOptions) is no line
  // or item here either, so the page holds exactly the lines Mo's tool result
  // names; a comma would also split /api/products' ids and shift the answers
  // of the whole batch.
  var CFG_REF_RE = /^[^\s,]{1,255}$/;

  // The plan from the tool input, with the visitor's edits and added lines
  // of this thread.
  function cfgMakePlan(input, key) {
    var items = Array.isArray(input && input.items) ? input.items : [];
    var lines = [];
    var seen = {};
    // Mo's lines: up to 20 (the backend's PLAN_MAX_ITEMS, the same as the
    // page plan's cap — a full plan from Mo leaves no room until the visitor
    // removes a line). Two lines of one ref are one line, their quantities
    // summed (the backend's normalizePlanItems; the product's rules apply
    // once it is known — cfgNormLine). inQty keeps that input quantity: the
    // floor area sizes from it again when a late line answers (cfgSizePlan).
    for (var i = 0; i < items.length && lines.length < CFG_MAX_LINES; i++) {
      var it = items[i];
      if (!it || typeof it.productId !== 'string') continue;
      var ref = it.productId.trim();
      if (!CFG_REF_RE.test(ref)) continue;
      var q = parseInt(it.quantity, 10);
      q = isFinite(q) && q >= 1 ? Math.min(q, CFG_FLOOR_MAX_QTY) : 1;
      if (Object.prototype.hasOwnProperty.call(seen, ref)) { seen[ref].qty = seen[ref].inQty = Math.min(CFG_FLOOR_MAX_QTY, seen[ref].inQty + q); continue; }
      lines.push(seen[ref] = {
        uid: ref,
        ref: ref,
        qty: q,
        inQty: q,
        role: CFG_ROLES[it.role] ? it.role : 'extra',
        note: typeof it.note === 'string' ? it.note.slice(0, 160) : '',
        removed: false,
        variantId: null,
        product: null,
        added: false
      });
    }
    var c = cfgLoad();
    // edits[key][uid] = { qty, variantId, removed } — Mo's lines (uid = ref,
    // the shape stored before uids existed). Something stored means the
    // visitor changed this plan (only his edits and picks store it —
    // cfgSaveEdits): its quantities (his, or the ones shown then) are never
    // sized to the floor area again, only normalised (cfgNormLine; up to 200
    // for a floor product).
    var edits = c && c.edits && c.edits[key];
    var edited = false;
    if (edits && typeof edits === 'object') {
      lines.forEach(function (l) {
        var e = edits[l.uid];
        if (!e || typeof e !== 'object') return;
        edited = true;
        var eq = parseInt(e.qty, 10);
        if (eq >= 1 && eq <= CFG_FLOOR_MAX_QTY) l.qty = eq;
        if (typeof e.variantId === 'string') l.variantId = e.variantId;
        l.removed = e.removed === true;
      });
    }
    // added[key] = [{ uid, ref, role, qty, variantId, pick? }] — the visitor's
    // lines, after Mo's (no dedupe against Mo's lines: two lines of one
    // product with different variants are fine).
    var added = c && c.added && c.added[key];
    if (lines.length) cfgRestoreAdded(lines, added);
    return {
      key: key,
      seq: ++cfgPlanSeq,
      title: typeof input.title === 'string' ? input.title.slice(0, 120) : '',
      summary: typeof input.summary === 'string' ? input.summary.slice(0, 500) : '',
      tips: (Array.isArray(input.tips) ? input.tips : []).filter(function (t) { return typeof t === 'string' && t.trim(); }).slice(0, 3),
      // The floor area Mo sized the flooring for (since 2026-10-27).
      area: cfgArea(input.floorAreaM2),
      // The visitor changed the plan (stored edits or lines of his own; from
      // now on set by cfgSaveEdits): Mo's quantities are no longer sized.
      edited: edited || lines.some(function (l) { return l.added; }),
      lines: lines,
      ready: false,
      settled: false // its products have answered (or not) — see cfgPlansPending
    };
  }

  // A line's quantity once its product is known follows the rules of the
  // product with the line's variant — Mo's input, the visitor's stored lines
  // and edits of an older widget alike.
  function cfgNormLine(l) {
    if (l.product) l.qty = cfgNormQty(l.product, l.qty, cfgVariantOf(l.product, l));
  }
  // Mo's floor tiles sized to his floor area like the backend's validateLines
  // on his input: each of his lines from his input quantity (by the rules of
  // its product and variant), then exactly one coverage line sized — with
  // two or more none is. Runs when his products answer and again when late
  // lines come back (cfgPlanRetry) with every line known by then, so a tile
  // sized while a second one was still missing is not sized any more. Never
  // in a plan the visitor changed (plan.edited): its quantities stay.
  function cfgSizePlan(plan) {
    if (!plan.area || plan.edited) return;
    var mo = plan.lines.filter(function (l) { return !l.added && !!l.product; });
    mo.forEach(function (l) { l.qty = cfgNormQty(l.product, l.inQty, cfgVariantOf(l.product, l)); });
    cfgSizeTiles(mo, plan.area);
  }

  // The visitor's stored lines into `lines` (up to the cap).
  function cfgRestoreAdded(lines, added) {
    if (!Array.isArray(added)) return;
    added.forEach(function (a) {
      if (cfgKeptCount(lines) >= CFG_MAX_LINES || !a || typeof a.ref !== 'string') return;
      var aref = a.ref.trim();
      if (!aref || aref.length > 300) return;
      var aq = parseInt(a.qty, 10);
      var line = {
        uid: typeof a.uid === 'string' && /^a\d{1,6}$/.test(a.uid) && !cfgLineByUid(lines, a.uid) ? a.uid : cfgNewUid(lines),
        ref: aref,
        qty: aq >= 1 && aq <= CFG_FLOOR_MAX_QTY ? aq : 1,
        role: CFG_ROLES[a.role] ? a.role : 'extra',
        note: '',
        removed: false,
        variantId: typeof a.variantId === 'string' ? a.variantId : null,
        product: null,
        added: true
      };
      if (typeof a.pick === 'string' && a.pick && a.pick.length <= 200) {
        line.pick = a.pick;
        if (typeof a.step === 'string' && Object.prototype.hasOwnProperty.call(CFG_OFFER_STEPS, a.step)) line.pickStep = a.step;
        // The option it was picked as (lines stored before had none).
        if (typeof a.opt === 'number' && a.opt >= 0 && a.opt <= 3 && Math.floor(a.opt) === a.opt) line.pickOpt = a.opt;
      }
      cfgInsertLine(lines, line);
    });
  }

  // Only the active plan's edits are kept — the stored object stays small.
  // Two exceptions, both a load gap (/api/products did not answer): the
  // plan's own lines still waiting for their products (plan.missing) are
  // stored as they were, and a newer plan of Mo's that could not load keeps
  // what is stored for it — both come back once the products answer
  // (cfgPlanRetry); an edit or a pick meanwhile must not erase them.
  // Only the visitor's edits and picks store a plan (a late answer only one
  // he changed already): what is stored is his, never sized again.
  function cfgSaveEdits(plan) {
    plan.edited = true;
    var c = cfgActive();
    if (!c) return;
    var e = {};
    var added = [];
    var lines = plan.failed && plan.missing ? plan.lines.concat(plan.missing) : plan.lines;
    lines.forEach(function (l) {
      if (l.added) {
        var a = { uid: l.uid, ref: l.ref, role: l.role, qty: l.qty, variantId: l.variantId || null };
        if (l.pick) {
          a.pick = l.pick;
          if (l.pickStep) a.step = l.pickStep;
          if (typeof l.pickOpt === 'number') a.opt = l.pickOpt;
        }
        added.push(a);
      }
      else e[l.uid] = { qty: l.qty, variantId: l.variantId, removed: l.removed };
    });
    var prevEdits = c.edits && typeof c.edits === 'object' ? c.edits : {};
    var prevAdded = c.added && typeof c.added === 'object' ? c.added : {};
    c.edits = {};
    c.edits[plan.key] = e;
    c.added = {};
    if (added.length) c.added[plan.key] = added;
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k) || k === plan.key) continue;
      if (!cfgPlans[k].failed || cfgPlans[k].seq <= plan.seq) continue;
      if (prevEdits[k]) c.edits[k] = prevEdits[k];
      if (prevAdded[k]) c.added[k] = prevAdded[k];
    }
    cfgSave(c);
  }

  function cfgPlanTotals(plan) {
    var total = 0, units = 0, count = 0, unavailable = 0;
    plan.lines.forEach(function (l) {
      if (l.removed || !l.product) return;
      var v = cfgVariantOf(l.product, l);
      if (!cfgLineAvailable(l.product, v)) { unavailable++; return; }
      total += cfgUnitPrice(l.product, v) * l.qty;
      units += l.qty;
      count++;
    });
    return { total: total, units: units, count: count, unavailable: unavailable };
  }

  // The plan as Mo should see it: [{ id, qty, added?: true }]. Two lines that
  // resolve to the same id are one item (quantities summed, at most what one
  // line of that product holds — 20, a floor product 200); an item is
  // `added` only when every line behind it is the visitor's own.
  function cfgPlanForContext() {
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    if (!plan) return [];
    var out = [];
    var byId = {};
    plan.lines.forEach(function (l) {
      if (l.removed || !l.product) return;
      var v = cfgVariantOf(l.product, l);
      var base = l.ref.split('~')[0];
      var id = v && v.id != null && Array.isArray(l.product.variants) && l.product.variants.length > 1 ? base + '~' + v.id : base;
      var hit = byId[id];
      if (hit) {
        hit.qty = Math.min(cfgRulesOf(l.product, v).max, hit.qty + l.qty);
        if (!l.added) delete hit.added;
        return;
      }
      var item = { id: id, qty: l.qty };
      if (l.added) item.added = true;
      byId[id] = item;
      out.push(item);
    });
    return out.slice(0, 20);
  }

  function cfgSetActivePlan(key) {
    if (!cfgPlans[key]) return;
    cfgActivePlanKey = key;
    cfgRenderHost();
    cfgRefreshPlanCards();
    cfgOwnPlanCard();
    cfgOfferReconcile();
    cfgOfferWaitOver();
    if (cfgStage !== 'cart') cfgSetStage('plan');
    var t = cfgPlanTotals(cfgPlans[key]);
    cfgEmit('plan', { items: t.count, units: t.units, total: Math.round(t.total) });
  }

  // A plan whose products have not answered yet. With moOnly, only Mo's
  // plans (show_gym_plan) count, not the visitor's own plan.
  function cfgPlansPending(moOnly) {
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k)) continue;
      if (moOnly && k === CFG_OWN_KEY) continue;
      if (!cfgPlans[k].settled) return true;
    }
    return false;
  }
  // Nothing active once every plan of Mo's has answered (the newest one had
  // no products, a restore): the newest plan of Mo's that has lines, else the
  // visitor's own plan. Never while one of Mo's is still loading — the own
  // plan would flash up empty (or stale) before Mo's newer plan.
  function cfgPlanFallback() {
    if (cfgActivePlanKey || cfgPlansPending(true)) return;
    var best = null;
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k) || k === CFG_OWN_KEY) continue;
      if (cfgPlans[k].ready && (!best || cfgPlans[k].seq > best.seq)) best = cfgPlans[k];
    }
    if (best) cfgSetActivePlan(best.key);
    else if (cfgPlans[CFG_OWN_KEY] && cfgPlans[CFG_OWN_KEY].ready) cfgSetActivePlan(CFG_OWN_KEY);
  }

  function buildGymPlan(input, key) {
    key = key || ('plan:' + JSON.stringify(input).length + ':' + cfgPlanSeq);
    var plan = cfgMakePlan(input || {}, key);
    if (!plan.lines.length) return Promise.resolve(null);
    var previous = cfgPlans[key];
    if (previous) plan.seq = previous.seq;
    cfgPlans[key] = plan;
    var card = el('div', { class: 'ms-chat-card ms-chat-plan', 'data-plan-key': key });
    // Mo's lines and the visitor's restored lines in one go (hydrate answers
    // per requested id, duplicates included).
    var refs = plan.lines.map(function (l) { return l.ref; });
    return hydrate(refs).then(function (res) {
      plan.settled = true;
      // Products that did not answer (429, network): the plan lacks lines it
      // has — no measure for the stored choices (cfgPlansFailed). Those lines
      // wait aside (plan.missing, unknown ids are dropped): they stay stored
      // (cfgSaveEdits) and go back in once their products answer.
      plan.failed = cfgUnresolved(refs);
      for (var i = 0; i < plan.lines.length; i++) {
        plan.lines[i].product = res[i] || null;
        cfgNormLine(plan.lines[i]);
      }
      cfgSizePlan(plan);
      plan.all = plan.lines;
      plan.missing = plan.lines.filter(function (l) { return !l.product && !(String(l.ref).trim() in productCache); });
      plan.lines = plan.lines.filter(function (l) { return !!l.product; });
      plan.card = card;
      if (plan.failed) cfgStallWatch();
      if (!plan.lines.length) {
        if (cfgPlans[key] === plan) { cfgPlanFallback(); cfgOfferReconcile(); cfgOfferWaitOver(); }
        if (!plan.failed) return null;
        // None of its products answered: an empty slot in the chat, the card
        // goes in once they do (cfgPlanRetry).
        plan.slot = el('div', { class: 'ms-chat-plan-slot', hidden: 'hidden' });
        return plan.slot;
      }
      return cfgPlanShow(plan);
    });
  }

  // A plan of Mo's with its products: shown, and active when it is the
  // newest of the thread.
  function cfgPlanShow(plan) {
    var key = plan.key;
    plan.ready = true;
    // A plan is a product consultation (attribution token, consent-gated).
    moAttrOnProductCard();
    // The newest plan of the thread is the active one — decided when its
    // products arrive, since restored plans hydrate in any order.
    if (cfgPlans[key] !== plan) return null; // superseded by a re-render
    var newest = true;
    for (var k in cfgPlans) {
      if (Object.prototype.hasOwnProperty.call(cfgPlans, k) && cfgPlans[k].seq > plan.seq) newest = false;
    }
    if (newest) cfgSetActivePlan(key);
    else {
      if (cfgActivePlanKey !== key) cfgRefreshPlanCards();
      cfgPlanFallback();
      cfgOfferReconcile();
      cfgOfferWaitOver();
    }
    fillPlanCard(plan.card, plan);
    return plan.card;
  }

  // A plan of Mo's whose products did not all answer asks again for the
  // missing ones (cfgStalledRetry: every 30 s, back online, the tab visible
  // again, a pick that had to wait). Once they answer, the lines go back in
  // place — edits made meanwhile stay — and a plan that had none shows up.
  // A plan the visitor has not changed is then sized again from Mo's input
  // with every line it has now (cfgSizePlan), and nothing of it is stored: a
  // late answer never turns Mo's quantities into the visitor's edits.
  function cfgPlanRetry(plan) {
    if (!plan.failed || plan.busy || cfgPlans[plan.key] !== plan) return;
    var refs = plan.missing.map(function (l) { return l.ref; });
    plan.busy = true;
    hydrate(refs).then(function (res) {
      plan.busy = false;
      if (!plan.failed || cfgPlans[plan.key] !== plan) return;
      if (cfgUnresolved(refs)) { cfgStallWatch(); return; }
      var missing = plan.missing;
      plan.missing = [];
      plan.failed = false;
      for (var i = 0; i < missing.length; i++) {
        missing[i].product = res[i] || null;
        cfgNormLine(missing[i]);
        if (missing[i].product) cfgPlanPutBack(plan, missing[i]);
      }
      cfgSizePlan(plan);
      if (!plan.ready) {
        var card = plan.lines.length ? cfgPlanShow(plan) : null;
        if (card && plan.slot && plan.slot.parentNode) plan.slot.parentNode.replaceChild(card, plan.slot);
        if (!card) { cfgPlanFallback(); cfgOfferReconcile(); cfgOfferWaitOver(); }
        return;
      }
      if (plan.key === cfgActivePlanKey) {
        if (plan.edited) cfgSaveEdits(plan);
        cfgRenderHost();
        var t = cfgPlanTotals(plan);
        cfgEmit('plan', { items: t.count, units: t.units, total: Math.round(t.total) });
      }
      if (plan.card) fillPlanCard(plan.card, plan);
      cfgOfferReconcile();
      cfgOfferWaitOver();
    });
  }
  // A line that waited for its product back into the plan: the visitor's
  // next to its product (cfgInsertLine, up to the cap), Mo's right after his
  // nearest line before it.
  function cfgPlanPutBack(plan, l) {
    if (l.added) {
      if (cfgKeptCount(plan.lines) >= CFG_MAX_LINES) return;
      if (cfgLineByUid(plan.lines, l.uid)) l.uid = cfgNewUid(plan.lines);
      cfgInsertLine(plan.lines, l);
      return;
    }
    var at = 0;
    for (var i = plan.all.indexOf(l) - 1; i >= 0; i--) {
      var j = plan.all[i].added ? -1 : plan.lines.indexOf(plan.all[i]);
      if (j === -1) continue;
      var base = cfgBaseId(plan.lines[j]);
      at = j + 1;
      while (at < plan.lines.length && plan.lines[at].added && cfgBaseId(plan.lines[at]) === base) at++;
      break;
    }
    plan.lines.splice(at, 0, l);
  }

  function cfgRefreshPlanCards() {
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k)) continue;
      var p = cfgPlans[k];
      if (p.ready && p.card) fillPlanCard(p.card, p);
    }
  }

  function planHost() {
    try { return document.querySelector('[data-ms-chat-plan]'); } catch (e) { return null; }
  }

  // The compact plan card in the chat.
  function fillPlanCard(card, plan) {
    var active = plan.key === cfgActivePlanKey;
    var host = planHost();
    card.replaceChildren();
    card.classList.toggle('ms-chat-plan--old', !active);
    var body = el('div', { class: 'ms-chat-card-body' });
    var head = el('div', { class: 'ms-chat-plan-eyebrow', text: active ? L('Dein Home-Gym-Plan', 'Your home-gym plan') : L('Früherer Plan', 'Earlier plan') });
    body.appendChild(head);
    if (plan.title) body.appendChild(el('div', { class: 'ms-chat-plan-title', text: plan.title }));
    if (plan.summary && !host) body.appendChild(el('div', { class: 'ms-chat-card-text', text: plan.summary }));
    var list = el('div', { class: 'ms-chat-plan-lines' });
    plan.lines.forEach(function (l) {
      var p = l.product;
      var v = cfgVariantOf(p, l);
      var row = el('div', { class: 'ms-chat-plan-line' + (l.removed ? ' ms-chat-plan-line--removed' : '') + (l.added ? ' ms-chat-plan-line--added' : '') });
      if (p.images && p.images[0]) row.appendChild(el('img', { class: 'ms-chat-plan-thumb', src: p.images[0], alt: '', loading: 'lazy' }));
      var meta = el('div', { class: 'ms-chat-plan-meta' });
      meta.appendChild(el('div', { class: 'ms-chat-plan-name', text: cfgLineView(p, l).name }));
      var sub = CFG_ROLES[l.role] + (v && v.title && p.variants && p.variants.length > 1 ? ' · ' + v.title : '') + (l.added ? ' · ' + (l.pick ? L('von dir gewählt', 'your choice') : L('von dir ergänzt', 'added by you')) : '');
      meta.appendChild(el('div', { class: 'ms-chat-plan-sub', text: sub }));
      row.appendChild(meta);
      var avail = cfgLineAvailable(p, v);
      row.appendChild(el('div', { class: 'ms-chat-plan-price', text: !avail ? L('ausverkauft', 'sold out') : (l.qty > 1 ? l.qty + '× ' : '') + cfgMoney(cfgUnitPrice(p, v)) }));
      list.appendChild(row);
    });
    body.appendChild(list);
    var t = cfgPlanTotals(plan);
    var tot = el('div', { class: 'ms-chat-plan-total' });
    tot.appendChild(el('span', { text: L('Gesamt', 'Total') + ' · ' + t.units + ' ' + (t.units === 1 ? L('Artikel', 'item') : L('Artikel', 'items')) }));
    tot.appendChild(el('strong', { text: cfgMoney(Math.round(t.total * 100) / 100) }));
    body.appendChild(tot);
    if (active) {
      var add = el('button', { class: 'ms-chat-btn ms-chat-btn--checkout', type: 'button' });
      add.appendChild(icon('cart'));
      add.appendChild(el('span', { text: L('Alles in den Warenkorb', 'Add everything to cart') }));
      add.disabled = !t.count || cfgCartBusy;
      add.addEventListener('click', function () { cfgAddToCart(plan, add); });
      body.appendChild(add);
      if (host) {
        var view = el('button', { class: 'ms-chat-btn ms-chat-btn--secondary', type: 'button', text: L('Plan ansehen & anpassen', 'View & adjust plan') });
        view.addEventListener('click', function () {
          try { host.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) {}
        });
        body.appendChild(view);
      }
    } else {
      var use = el('button', { class: 'ms-chat-btn ms-chat-btn--secondary', type: 'button', text: L('Diesen Plan übernehmen', 'Use this plan') });
      use.addEventListener('click', function () { cfgSetActivePlan(plan.key); });
      body.appendChild(use);
    }
    card.appendChild(body);
  }

  // ----- offer_plan_options → Mo's options, picked into the plan --------------
  // Mo goes through the product types one by one (base, accessory, bench,
  // weights, flooring, cardio, extra): one card per option with price, pros
  // and cons. „Auswählen“ — for accessories several + „Übernehmen“ — puts the
  // option's items into the page plan as the visitor's own lines (tag „Von dir
  // gewählt“) and tells Mo („Ich nehme: …“) with the updated plan in the
  // context. A later choice in the same group replaces that group's lines
  // („Ich nehme stattdessen: …“). „Überspringen“ / „Andere Vorschläge“ are
  // answer chips. The thread's choices are stored with its answers
  // (c.picks: toolCallId -> option indices), so a reload shows them again.
  // KPI: configurator_option_picked / _skipped / _more (ids, enums, numbers).
  var CFG_OFFER_STEPS = { base: 'base', accessory: 'accessory', bench: 'accessory', weights: 'weights', flooring: 'flooring', cardio: 'cardio', extra: 'extra' };

  function cfgClip(v, max) {
    if (typeof v !== 'string') return '';
    var t = v.replace(/\s+/g, ' ').trim();
    return t.length > max ? t.slice(0, max - 1).trim() + '…' : t;
  }
  function cfgClipList(list, n, max) {
    var out = [];
    for (var i = 0; Array.isArray(list) && i < list.length && out.length < n; i++) {
      var t = cfgClip(list[i], max);
      if (t) out.push(t);
    }
    return out;
  }

  // The tool input as the widget renders it (the backend's limits).
  function cfgOfferInput(input) {
    var rec = input && typeof input === 'object' ? input : {};
    var step = typeof rec.step === 'string' && Object.prototype.hasOwnProperty.call(CFG_OFFER_STEPS, rec.step) ? rec.step : 'extra';
    var raw = Array.isArray(rec.options) ? rec.options : [];
    var options = [];
    for (var i = 0; i < raw.length && options.length < 4; i++) {
      var o = raw[i];
      if (!o || typeof o !== 'object' || !Array.isArray(o.items)) continue;
      var items = [];
      var seen = {};
      for (var j = 0; j < o.items.length && items.length < 6; j++) {
        var it = o.items[j];
        if (!it || typeof it.productId !== 'string') continue;
        var ref = it.productId.trim();
        if (!CFG_REF_RE.test(ref)) continue;
        // Clamped like the backend's normalizeOfferOptions (200: it does not
        // know the product); its rules apply once it is known (buildOffer).
        var q = parseInt(it.quantity, 10);
        q = isFinite(q) && q >= 1 ? Math.min(q, CFG_FLOOR_MAX_QTY) : 1;
        if (Object.prototype.hasOwnProperty.call(seen, ref)) { seen[ref].qty = Math.min(CFG_FLOOR_MAX_QTY, seen[ref].qty + q); continue; }
        seen[ref] = { ref: ref, qty: q, product: null, name: '', url: '' };
        items.push(seen[ref]);
      }
      if (!items.length) continue;
      options.push({
        idx: options.length,
        rank: i + 1, // Mo's own position (KPI), whatever is left out on the way
        items: items,
        label: cfgClip(o.label, 60),
        badge: cfgClip(o.badge, 24),
        pros: cfgClipList(o.pros, 3, 90),
        cons: cfgClipList(o.cons, 2, 90)
      });
    }
    return {
      step: step,
      role: CFG_OFFER_STEPS[step],
      title: cfgClip(rec.title, 80),
      intro: cfgClip(rec.intro, 240),
      multi: rec.multiSelect === true,
      // The floor area the flooring options are sized for (since 2026-10-27).
      area: cfgArea(rec.areaM2),
      options: options
    };
  }

  // Product + variant of a line or an option item (a weight step is its own).
  function cfgItemIdent(p, line) {
    var v = cfgVariantOf(p, line);
    return String(p && p.id != null ? p.id : '').split('~')[0] + '~' + (v && v.id != null ? String(v.id) : '');
  }

  function cfgOfferByIdx(g, ix) {
    for (var i = 0; i < g.shown.length; i++) { if (g.shown[i].idx === ix) return g.shown[i]; }
    return null;
  }

  // Ids /api/products did not answer (a 429 or network error caches nothing;
  // an unknown id is cached as null).
  function cfgUnresolved(refs) {
    for (var i = 0; i < refs.length; i++) { if (!(String(refs[i]).trim() in productCache)) return true; }
    return false;
  }
  // hydrate(), asked again after each pause in `waits` while an id is
  // unresolved — a short outage must not drop an option or a stored choice.
  function cfgHydrateRetry(refs, waits) {
    return hydrate(refs).then(function (res) {
      if (!waits.length || !cfgUnresolved(refs)) return res;
      return new Promise(function (resolve) { setTimeout(resolve, waits[0]); }).then(function () {
        return cfgHydrateRetry(refs, waits.slice(1));
      });
    });
  }
  // A group that shows nothing because /api/products did not answer (not
  // because its items are unknown or sold out) keeps an empty slot in the
  // chat and is built again into it once the products answer (cfgStallWatch).
  function cfgOfferStalled(g, input, refs) {
    if (!cfgUnresolved(refs)) return null;
    g.input = input;
    g.slot = el('div', { class: 'ms-chat-offer-slot', hidden: 'hidden' });
    cfgStallWatch();
    return g.slot;
  }
  function cfgOfferRetry(g) {
    var slot = g.slot;
    g.slot = null;
    buildOffer(g.input, g.key).then(function (node) {
      if (node && slot.parentNode) slot.parentNode.replaceChild(node, slot);
    }).catch(function () { /* render nothing on failure */ });
  }

  function buildOffer(input, key) {
    var g = cfgOfferInput(input);
    if (!g.options.length) return Promise.resolve(null);
    g.key = key || ('offer:' + JSON.stringify(input).length);
    // Every group of the thread is registered in the order of the
    // conversation (a re-render of the same tool call keeps its place): a
    // choice is checked against the plan, a swap looks at the other groups of
    // the same step, the floating widget's plan card sits under the latest.
    var prev = cfgOfferGroups[g.key];
    g.seq = prev ? prev.seq : ++cfgOfferSeq;
    g.chosen = [];
    g.pending = [];
    cfgOfferGroups[g.key] = g;
    var refs = [];
    g.options.forEach(function (o) {
      o.items.forEach(function (it) { if (refs.indexOf(it.ref) === -1) refs.push(it.ref); });
    });
    return cfgHydrateRetry(refs, [1500, 4000]).then(function (res) {
      var byRef = {};
      for (var i = 0; i < refs.length; i++) byRef[refs[i]] = res[i] || null;
      // An option with an unknown or sold-out item is left out.
      g.shown = [];
      g.options.forEach(function (o) {
        var ok = true;
        var total = 0;
        var regular = 0;
        var cover = 0;
        o.items.forEach(function (it) {
          var p = byRef[it.ref];
          var v = p ? cfgVariantOf(p, { variantId: null }) : null;
          if (!p || p.inStock === false || !cfgLineAvailable(p, v)) { ok = false; return; }
          var view = cfgLineView(p, { variantId: null });
          it.product = p;
          it.name = view.name;
          it.url = view.url;
          // The quantities the plan will hold — by the rules of the product
          // and its ref's variant (pairs …), one floor product sized to Mo's
          // floor area: the card's price and item list, the pick and the
          // cart all use them.
          it.qty = cfgNormQty(p, it.qty, v);
        });
        if (!ok) return;
        cfgSizeTiles(o.items, g.area);
        o.items.forEach(function (it) {
          var v = cfgVariantOf(it.product, { variantId: null });
          cover += cfgRulesOf(it.product, v).cover * it.qty;
          total += cfgUnitPrice(it.product, v) * it.qty;
          regular += (v ? Number(v.price) || 0 : Number(it.product.price) || 0) * it.qty;
        });
        o.name = o.label || o.items[0].name;
        o.total = Math.round(total * 100) / 100;
        o.regular = Math.round(regular * 100) / 100;
        o.cover = cover;
        g.shown.push(o);
      });
      if (!g.shown.length) return cfgOfferStalled(g, input, refs);
      // Product options are a product consultation (attribution token,
      // consent-gated) — in the step flow Mo may never show a plan.
      moAttrOnProductCard();
      var c = cfgActive();
      // A thread whose configurator run has ended (an older conversation from
      // the history) shows the options, but nothing can be chosen any more.
      g.live = !!c;
      var stored = c && c.picks && typeof c.picks === 'object' ? c.picks[g.key] : null;
      g.chosen = [];
      if (Array.isArray(stored)) {
        stored.forEach(function (ix) { if (typeof ix === 'number' && cfgOfferByIdx(g, ix) && g.chosen.indexOf(ix) === -1) g.chosen.push(ix); });
      }
      g.pending = g.chosen.slice();
      // A stored choice whose lines left the plan meanwhile is not „Gewählt“.
      if (c && !cfgPlansPending() && !cfgPlansFailed() && cfgOfferReconcileGroup(g, cfgOfferPlanLines(), c)) cfgSave(c);
      return cfgOfferRender(g);
    });
  }

  function cfgOfferPriceEl(o) {
    var box = el('div', { class: 'ms-chat-offer-price' });
    if (o.regular > o.total + 0.004) {
      box.appendChild(el('span', { class: 'ms-chat-offer-sale', text: cfgMoney(o.total) }));
      box.appendChild(el('s', { class: 'ms-chat-offer-strike', text: cfgMoney(o.regular) }));
    } else {
      box.appendChild(el('span', { text: cfgMoney(o.total) }));
    }
    return box;
  }

  function cfgOfferRender(g) {
    // ms-chat-offer is the fingerprint marker of the option group.
    var wrap = el('div', { class: 'ms-chat-offer' + (g.multi ? ' ms-chat-offer--multi' : ''), role: 'group', 'aria-label': g.title || L('Vorschläge von Mo', 'Suggestions from Mo'), 'data-step': g.step });
    if (g.title || g.intro) {
      var head = el('div', { class: 'ms-chat-offer-head' });
      if (g.title) head.appendChild(el('div', { class: 'ms-chat-offer-title', text: g.title }));
      if (g.intro) head.appendChild(el('div', { class: 'ms-chat-offer-intro', text: g.intro }));
      wrap.appendChild(head);
    }
    var list = el('ul', { class: 'ms-chat-offer-list' });
    g.cards = [];
    g.shown.forEach(function (o) {
      var first = o.items[0];
      var p = first.product;
      var li = el('li', { class: 'ms-chat-offer-card' });
      var thumb = el('span', { class: 'ms-chat-offer-thumb', 'aria-hidden': 'true' });
      if (p.images && p.images[0]) thumb.appendChild(el('img', { src: p.images[0], alt: '', loading: 'lazy' }));
      li.appendChild(thumb);
      var info = el('div', { class: 'ms-chat-offer-info' });
      if (o.badge) info.appendChild(el('span', { class: 'ms-chat-offer-badge', text: o.badge }));
      if (o.items.length === 1 && first.url) {
        var link = el('a', { class: 'ms-chat-offer-name', href: first.url, target: '_blank', rel: 'noopener noreferrer', text: o.name });
        link.addEventListener('click', function () { track('product_cta_clicked', { productId: p.id, samePage: false }); });
        info.appendChild(link);
      } else {
        info.appendChild(el('span', { class: 'ms-chat-offer-name', text: o.name }));
      }
      info.appendChild(cfgOfferPriceEl(o));
      // Flooring: the area the option covers („Deckt ca. 12 m²“).
      if (o.cover > 0) info.appendChild(el('span', { class: 'ms-chat-offer-cover', text: L('Deckt ca. ', 'Covers approx. ') + cfgM2(o.cover) }));
      li.appendChild(info);
      // A bundle (weights: one line per step) names its items.
      if (o.items.length > 1 || first.qty > 1) {
        var items = el('ul', { class: 'ms-chat-offer-items', 'aria-label': L('Enthält', 'Includes') });
        o.items.forEach(function (it) { items.appendChild(el('li', { text: it.qty + '× ' + it.name })); });
        li.appendChild(items);
      }
      if (o.pros.length || o.cons.length) {
        var pts = el('ul', { class: 'ms-chat-offer-points' });
        o.pros.forEach(function (t) { pts.appendChild(el('li', { class: 'ms-chat-offer-pro', text: t })); });
        o.cons.forEach(function (t) { pts.appendChild(el('li', { class: 'ms-chat-offer-con', text: t })); });
        li.appendChild(pts);
      }
      var act = el('div', { class: 'ms-chat-offer-act' });
      var btn = el('button', { class: 'ms-chat-btn ms-chat-btn--primary ms-chat-offer-choose', type: 'button' });
      btn.addEventListener('click', function () { cfgOfferChoose(g, o); });
      act.appendChild(btn);
      li.appendChild(act);
      list.appendChild(li);
      g.cards.push({ o: o, li: li, btn: btn });
    });
    wrap.appendChild(list);
    if (g.multi) {
      g.confirm = el('button', { class: 'ms-chat-btn ms-chat-btn--primary ms-chat-offer-confirm', type: 'button' });
      g.confirm.addEventListener('click', function () { cfgOfferConfirm(g); });
      wrap.appendChild(g.confirm);
    }
    g.noteEl = el('div', { class: 'ms-chat-offer-note', role: 'status', 'aria-live': 'polite' });
    wrap.appendChild(g.noteEl);
    // Phones / tablets: the page plan sits below the chat — a chosen option
    // says it is in the plan and leads there (cfgOfferView).
    if (planHost()) {
      g.viewEl = el('button', { class: 'ms-chat-btn ms-chat-btn--secondary ms-chat-offer-view', type: 'button', text: L('Im Plan ✓ · Plan ansehen', 'In your plan ✓ · View plan') });
      g.viewEl.hidden = true;
      g.viewEl.addEventListener('click', function () {
        var host = planHost();
        try { if (host) host.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) {}
      });
      wrap.appendChild(g.viewEl);
    }
    // Skip / other suggestions: answer chips, spent with the next message.
    var chips = el('div', { class: 'ms-chat-replies ms-chat-offer-replies', role: 'group', 'aria-label': L('Weiter mit Mo', 'Continue with Mo') });
    [
      [L('Überspringen', 'Skip'), L('Diesen Schritt überspringe ich.', 'I\'ll skip this step.'), 'configurator_option_skipped'],
      [L('Andere Vorschläge', 'Other suggestions'), L('Zeig mir bitte andere Vorschläge für diesen Schritt.', 'Please show me other options for this step.'), 'configurator_option_more']
    ].forEach(function (d) {
      var b = el('button', { class: 'ms-chat-reply', type: 'button', text: d[0] });
      b.addEventListener('click', function () {
        if (chips.classList.contains('ms-chat-replies--used')) return;
        if (state.streaming || state.rateLocked) return;
        chips.classList.add('ms-chat-replies--used');
        track(d[2], { step: g.step });
        sendMessage(d[1], null);
      });
      chips.appendChild(b);
    });
    wrap.appendChild(chips);
    g.el = wrap;
    if (!g.live) chips.classList.add('ms-chat-replies--used');
    cfgOfferSync(g);
    cfgOwnPlanCard();
    setTimeout(cfgSyncReplies, 0);
    return wrap;
  }

  // Button labels and states from g.chosen (in the plan) / g.pending (multi:
  // toggled). Toggled but not taken over yet („Übernehmen“) has its own look —
  // only „Gewählt ✓“ is the filled blue.
  function cfgOfferSync(g) {
    g.cards.forEach(function (cd) {
      var ix = cd.o.idx;
      var chosen = g.chosen.indexOf(ix) !== -1;
      var on = g.multi ? g.pending.indexOf(ix) !== -1 : chosen;
      var label = !on ? L('Auswählen', 'Choose')
        : chosen ? L('Gewählt', 'Chosen')
        : L('Ausgewählt', 'Selected');
      cd.li.classList.toggle('ms-chat-offer-card--on', on && chosen);
      cd.li.classList.toggle('ms-chat-offer-card--pending', on && !chosen);
      cd.btn.classList.toggle('ms-chat-offer-choose--on', on && chosen);
      cd.btn.classList.toggle('ms-chat-offer-choose--pending', on && !chosen);
      cd.btn.textContent = on ? label + ' ✓' : label;
      cd.btn.setAttribute('aria-label', label + ': ' + cd.o.name);
      if (g.multi) cd.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      else if (chosen) cd.btn.setAttribute('aria-disabled', 'true');
      else cd.btn.removeAttribute('aria-disabled');
      cd.btn.disabled = !g.live;
    });
    var waiting = false;
    if (g.confirm) {
      var n = g.pending.length;
      var same = n === g.chosen.length && g.pending.every(function (ix) { return g.chosen.indexOf(ix) !== -1; });
      g.confirm.textContent = L('Übernehmen', 'Confirm') + (n ? ' (' + n + ')' : '');
      g.confirm.disabled = !n || same || !g.live;
      // Nothing changed since the last „Übernehmen“: nothing to confirm.
      g.confirm.hidden = same && g.chosen.length > 0;
      // Toggled but not in the plan yet: the group says so until
      // „Übernehmen“ (or the toggle is undone) — a warning stays first. The
      // button itself stays in view meanwhile (sticky, CSS).
      var adding = g.pending.some(function (ix) { return g.chosen.indexOf(ix) === -1; });
      var removing = g.chosen.some(function (ix) { return g.pending.indexOf(ix) === -1; });
      waiting = g.live && (adding || removing);
      if (waiting && (g.notePending || !g.noteEl.textContent)) {
        cfgOfferNote(g, '', adding
          ? L('Noch nicht im Plan – tippe auf „Übernehmen“.', 'Not in your plan yet – tap “Confirm”.')
          : L('Noch nicht übernommen – tippe auf „Übernehmen“.', 'Not applied yet – tap “Confirm”.'));
        g.notePending = true;
      } else if (!waiting && g.notePending) {
        cfgOfferNote(g, '', '');
      }
    }
    g.viewOn = g.live && g.chosen.length > 0 && !waiting;
    cfgOfferView(g);
  }

  function cfgOfferNote(g, kind, text) {
    g.noteWait = false;
    g.notePending = false;
    if (!g.noteEl) return;
    g.noteEl.className = 'ms-chat-offer-note' + (kind ? ' ms-chat-offer-note--' + kind : '');
    g.noteEl.textContent = text || '';
  }

  // „Im Plan ✓ · Plan ansehen“ under a group with a choice, while the page
  // plan sits below the chat (the stacked layout of phones and tablets —
  // read from the page, so it follows its breakpoints and a rotation).
  function cfgOfferView(g) {
    if (g.viewEl) g.viewEl.hidden = !(g.viewOn && cfgPlanStacked());
  }
  function cfgPlanStacked() {
    var host = planHost();
    if (!host || !DOCK) return false;
    try {
      var h = host.getBoundingClientRect();
      var d = DOCK.getBoundingClientRect();
      // In the chat's column, below it (side by side the plan has a column
      // of its own).
      return h.width > 0 && d.height > 0 && h.left < d.right && h.right > d.left && h.top >= d.bottom - 1;
    } catch (e) { return false; }
  }
  function cfgOfferViews() {
    for (var k in cfgOfferGroups) {
      if (Object.prototype.hasOwnProperty.call(cfgOfferGroups, k) && cfgOfferGroups[k].viewEl) cfgOfferView(cfgOfferGroups[k]);
    }
  }

  function cfgOfferChoose(g, o) {
    if (g.multi) {
      var i = g.pending.indexOf(o.idx);
      if (i === -1) g.pending.push(o.idx); else g.pending.splice(i, 1);
      cfgOfferNote(g, '', '');
      cfgOfferSync(g);
      return;
    }
    if (g.chosen.indexOf(o.idx) !== -1) return;
    cfgOfferPick(g, [o.idx]);
  }

  function cfgOfferConfirm(g) {
    // In the order Mo offered them.
    var idxs = [];
    g.shown.forEach(function (o) { if (g.pending.indexOf(o.idx) !== -1) idxs.push(o.idx); });
    if (idxs.length) cfgOfferPick(g, idxs);
  }

  // The picked options into the active plan — or, before Mo has shown one,
  // into the visitor's own plan (created here, shown on the page).
  //
  // What a pick replaces: a re-pick in the same group replaces that group's
  // earlier choice. In a single-select group of a step that holds one thing
  // (base, bench, weights, flooring, cardio) it also replaces the earlier
  // picks of the same step from every other group — Mo's „other suggestions“
  // and a swap he offers arrive as a new group („Ich nehme stattdessen: …“,
  // the same item set again is no swap). Accessories and extras, and
  // multiSelect groups, add next to the picks of other groups. A picked line
  // whose item is wanted again stays as it is (the visitor's quantity and
  // variant). Every picked line notes its group, step and option (pickOpt).
  function cfgOfferPick(g, idxs) {
    if (state.streaming || state.rateLocked) return false;
    var c = cfgActive();
    if (!c) return false;
    var opts = [];
    idxs.forEach(function (ix) { var o = cfgOfferByIdx(g, ix); if (o) opts.push(o); });
    if (!opts.length) return false;
    var plan = cfgPickTarget();
    if (cfgPickWaits(plan)) {
      // The thread's plan is still loading (a reload whose products have not
      // answered yet) or could not load (a load gap): say so, and ask for its
      // products again right away — the pick must not land in a stand-in.
      cfgOfferNote(g, 'warn', L('Dein Plan wird gerade noch geladen – bitte versuch es gleich noch einmal.',
        'Your plan is still loading – please try again in a moment.'));
      g.noteWait = true;
      cfgStalledRetry();
      return false;
    }
    var fresh = !plan;
    if (fresh) plan = { key: CFG_OWN_KEY, seq: 0, own: true, title: '', summary: '', tips: [], lines: [], ready: true, settled: true };
    var cross = !g.multi && g.step !== 'accessory' && g.step !== 'extra';
    var scope = [g];
    if (cross) {
      for (var k in cfgOfferGroups) {
        if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k)) continue;
        var og = cfgOfferGroups[k];
        if (og !== g && og.cards && og.live && og.step === g.step) scope.push(og);
      }
    }
    // A visitor's line from an earlier choice this pick replaces.
    var inScope = function (l) {
      if (!l.added || !l.pick) return false;
      if (l.pick === g.key) return true;
      if (!cross) return false;
      var from = cfgOfferGroups[l.pick];
      return (l.pickStep || (from ? from.step : '')) === g.step;
    };
    // The items of the new choice -> { o, it }: the first chosen option (in
    // Mo's order) that holds the item.
    var wanted = {};
    var wants = function (id) { return Object.prototype.hasOwnProperty.call(wanted, id); };
    opts.forEach(function (o) {
      o.items.forEach(function (it) {
        var id = cfgItemIdent(it.product, { variantId: null });
        if (!wants(id)) wanted[id] = { o: o, it: it };
      });
    });
    // The earlier choices go. Where Mo's plan holds one of them as his own
    // line (he took the picks over, or it was there already), that line is
    // marked removed — unless the new choice includes it again. „Ich nehme
    // stattdessen“ only when something goes: another option of the group (a
    // bigger set too), another item set from a group of the same step, an
    // item that is not wanted again.
    var replacing = false;
    var before = {};
    scope.forEach(function (sg) {
      sg.chosen.forEach(function (ix) {
        var po = cfgOfferByIdx(sg, ix);
        if (!po) return;
        if (sg === g ? idxs.indexOf(ix) === -1 : !cfgOfferSameItems(po, wanted)) replacing = true;
        po.items.forEach(function (it) {
          var id = cfgItemIdent(it.product, { variantId: null });
          before[id] = 1;
          if (!wants(id)) replacing = true;
        });
      });
    });
    // `have`: the items the plan holds outside the earlier picks (Mo's lines
    // that go are marked removed) — an item it holds is not added twice, Mo
    // would read a doubled quantity.
    var drop = [];
    var have = {};
    plan.lines.forEach(function (l) {
      if (inScope(l) || l.removed || !l.product) return;
      var id = cfgItemIdent(l.product, l);
      if (!l.added && before[id] === 1 && !wants(id)) drop.push(l);
      else have[id] = 1;
    });
    // The earlier picked lines whose item is wanted again (and not held
    // otherwise) stay, with the visitor's quantity and variant (multiSelect:
    // also the line of an option that stays chosen when the visitor changed
    // its variant); `got` = the items they stand for. The others go.
    var stay = [];
    var got = {};
    var stays = function (l) {
      for (var i = 0; i < stay.length; i++) { if (stay[i].l === l) return true; }
      return false;
    };
    plan.lines.forEach(function (l) {
      if (l.removed || !l.product || !inScope(l)) return;
      var id = cfgItemIdent(l.product, l);
      if (wants(id) && !got[id] && have[id] !== 1) { got[id] = 1; stay.push({ l: l, w: wanted[id] }); }
    });
    if (g.multi) {
      plan.lines.forEach(function (l) {
        if (l.removed || !l.product || l.pick !== g.key || stays(l)) return;
        var from = typeof l.pickOpt === 'number' ? [l.pickOpt] : g.chosen;
        for (var i = 0; i < from.length; i++) {
          var o = g.chosen.indexOf(from[i]) !== -1 && idxs.indexOf(from[i]) !== -1 ? cfgOfferByIdx(g, from[i]) : null;
          var it = o ? cfgOfferItemFor(o, l, got) : null;
          if (!it || have[cfgItemIdent(it.product, { variantId: null })] === 1) continue;
          got[cfgItemIdent(it.product, { variantId: null })] = 1;
          stay.push({ l: l, w: { o: o, it: it } });
          return;
        }
      });
    } else {
      // Single-select: the same item set picked again from a newer group of
      // the step keeps the line with the variant and quantity the visitor
      // set (no swap).
      plan.lines.forEach(function (l) {
        if (l.removed || !l.product || !inScope(l) || stays(l)) return;
        var from = cfgOfferGroups[l.pick];
        var po = from && typeof l.pickOpt === 'number' ? cfgOfferByIdx(from, l.pickOpt) : null;
        if (!po || !cfgOfferSameItems(po, wanted)) return;
        for (var i = 0; i < opts.length; i++) {
          var it = cfgOfferItemFor(opts[i], l, got);
          if (!it || have[cfgItemIdent(it.product, { variantId: null })] === 1) continue;
          got[cfgItemIdent(it.product, { variantId: null })] = 1;
          stay.push({ l: l, w: { o: opts[i], it: it } });
          return;
        }
      });
    }
    plan.lines.forEach(function (l) {
      if (!l.removed && l.product && inScope(l) && !stays(l) && !wants(cfgItemIdent(l.product, l))) replacing = true;
    });
    // A picked line still waiting for its product (a load gap) stays or goes
    // the same way, by its ref.
    var missing = [];
    (plan.missing || []).forEach(function (l) {
      if (!inScope(l)) { missing.push(l); return; }
      for (var i = 0; i < opts.length; i++) {
        for (var j = 0; j < opts[i].items.length; j++) {
          var it = opts[i].items[j];
          var id = cfgItemIdent(it.product, { variantId: null });
          if (it.ref !== l.ref || got[id] || have[id] === 1) continue;
          got[id] = 1;
          stay.push({ l: l, w: { o: opts[i], it: it } });
          missing.push(l);
          return;
        }
      }
      // multiSelect: its option may be left out of the cards during the gap
      // — the line stays unless the visitor took that option off.
      if (g.multi && l.pick === g.key) {
        var off = typeof l.pickOpt === 'number' && cfgOfferByIdx(g, l.pickOpt) && g.chosen.indexOf(l.pickOpt) !== -1 && idxs.indexOf(l.pickOpt) === -1;
        if (!off) { missing.push(l); return; }
      }
      replacing = true;
    });
    var keep = plan.lines.filter(function (l) { return !inScope(l) || stays(l); });
    stay.forEach(function (s) { if (s.l.product) have[cfgItemIdent(s.l.product, s.l)] = 1; });
    for (var gid in got) { if (Object.prototype.hasOwnProperty.call(got, gid)) have[gid] = 1; }
    var add = [];
    opts.forEach(function (o) {
      o.items.forEach(function (it) {
        var id = cfgItemIdent(it.product, { variantId: null });
        if (have[id] === 1) return;
        have[id] = 1;
        add.push({ o: o, it: it });
      });
    });
    if (cfgKeptCount(keep) - drop.length + add.length > CFG_MAX_LINES) {
      cfgOfferNote(g, 'warn', L('Dafür ist in deinem Plan kein Platz mehr (höchstens ' + CFG_MAX_LINES + ' Positionen) – entferne zuerst eine Position.',
        'There is no room for this in your plan (' + CFG_MAX_LINES + ' items at most) – remove an item first.'));
      return false;
    }
    // What the message names and the KPI counts: a multiSelect „Übernehmen“
    // that only adds names the options new in this action; one that takes
    // something off names the whole new choice („Ich nehme stattdessen“).
    var named = g.multi && !replacing ? opts.filter(function (o) { return g.chosen.indexOf(o.idx) === -1; }) : opts;
    if (!named.length) named = opts;
    plan.lines = keep;
    if (plan.missing) plan.missing = missing;
    drop.forEach(function (l) { l.removed = true; });
    stay.forEach(function (s) {
      // A quantity the earlier option set (not changed by the visitor)
      // follows the new option (by the rules of the line's variant).
      var src = cfgOfferSourceItem(s.l);
      if (src && src.qty === s.l.qty) s.l.qty = s.l.product ? cfgNormQty(s.l.product, s.w.it.qty, cfgVariantOf(s.l.product, s.l)) : s.w.it.qty;
      s.l.pick = g.key;
      s.l.pickStep = g.step;
      s.l.pickOpt = s.w.o.idx;
    });
    add.forEach(function (a) {
      cfgInsertLine(plan.lines, {
        uid: cfgNewUid(plan.lines),
        ref: a.it.ref,
        qty: a.it.qty,
        role: g.role,
        note: '',
        removed: false,
        variantId: null,
        product: a.it.product,
        added: true,
        pick: g.key,
        pickStep: g.step,
        pickOpt: a.o.idx
      });
    });
    c.picks = c.picks && typeof c.picks === 'object' ? c.picks : {};
    // The other groups of the step show their options as „Auswählen“ again.
    scope.forEach(function (sg) {
      if (sg === g) return;
      sg.chosen = [];
      sg.pending = [];
      delete c.picks[sg.key];
      cfgOfferNote(sg, '', '');
      cfgOfferSync(sg);
    });
    c.picks[g.key] = idxs.slice();
    if (fresh) c.ownPlan = true;
    cfgSave(c);
    if (fresh) cfgPlans[CFG_OWN_KEY] = plan;
    g.chosen = idxs.slice();
    g.pending = idxs.slice();
    cfgOfferNote(g, '', '');
    cfgOfferSync(g);
    cfgSaveEdits(plan);
    if (cfgActivePlanKey !== plan.key) {
      cfgSetActivePlan(plan.key);
    } else {
      cfgRenderHost();
      if (plan.card) fillPlanCard(plan.card, plan);
      cfgOwnPlanCard();
      cfgOfferReconcile();
      var t = cfgPlanTotals(plan);
      cfgEmit('plan', { items: t.count, units: t.units, total: Math.round(t.total) });
    }
    // rank: the option's position in Mo's list (1 = his first option).
    var count = 0;
    named.forEach(function (o) { count += o.items.length; });
    track('configurator_option_picked', { step: g.step, rank: named[0].rank, items: count, multi: g.multi, count: named.length });
    var names = named.map(function (o) { return o.name.replace(/[.\s]+$/, ''); }).join(', ');
    sendMessage((replacing ? L('Ich nehme stattdessen: ', 'Instead I\'ll take: ') : L('Ich nehme: ', 'I\'ll take: ')) + names + '.', null);
    return true;
  }
  // The option holds exactly the items of `wanted` (product and variant).
  function cfgOfferSameItems(o, wanted) {
    var seen = {};
    var n = 0;
    for (var i = 0; i < o.items.length; i++) {
      if (!o.items[i].product) return false;
      var id = cfgItemIdent(o.items[i].product, { variantId: null });
      if (!Object.prototype.hasOwnProperty.call(wanted, id)) return false;
      if (!seen[id]) { seen[id] = 1; n++; }
    }
    for (var k in wanted) { if (Object.prototype.hasOwnProperty.call(wanted, k)) n--; }
    return n === 0;
  }
  // The item of option o with the product of line l (any variant) that is
  // not taken yet (got).
  function cfgOfferItemFor(o, l, got) {
    var base = cfgBaseId(l);
    for (var i = 0; i < o.items.length; i++) {
      var it = o.items[i];
      if (!it.product || String(it.product.id != null ? it.product.id : it.ref).split('~')[0] !== base) continue;
      if (got && got[cfgItemIdent(it.product, { variantId: null })]) continue;
      return it;
    }
    return null;
  }
  // The option item a picked line came from: the option it notes (pickOpt)
  // — a line stored before options were noted: a chosen option of its group
  // — the same item first, else the same product.
  function cfgOfferSourceItem(l) {
    var og = cfgOfferGroups[l.pick];
    if (!og || !og.shown) return null;
    var from = typeof l.pickOpt === 'number' ? [l.pickOpt] : og.chosen;
    var ident = l.product ? cfgItemIdent(l.product, l) : null;
    var i, j, o;
    for (i = 0; ident && i < from.length; i++) {
      o = cfgOfferByIdx(og, from[i]);
      for (j = 0; o && j < o.items.length; j++) {
        if (o.items[j].product && cfgItemIdent(o.items[j].product, { variantId: null }) === ident) return o.items[j];
      }
    }
    for (i = 0; i < from.length; i++) {
      o = cfgOfferByIdx(og, from[i]);
      var it = o ? cfgOfferItemFor(o, l, null) : null;
      if (it) return it;
    }
    return null;
  }

  // ----- a choice follows the plan -------------------------------------------
  // An option is „Gewählt ✓“ only while the active plan still holds one of
  // its items: a line picked from this group (any variant — the visitor may
  // have changed it), or any other line of the same product and variant (Mo
  // took the pick over into his plan, or it was there already). „Entfernen“,
  // a newer plan of Mo's without it, or a swap make it choosable again — a
  // fresh „Ich nehme: …“. Checked only once every plan of the thread has its
  // products (until then the stored choices stand).
  function cfgOfferPlanLines() {
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    return plan && plan.ready ? plan.lines : [];
  }
  function cfgOfferInPlan(g, o, lines) {
    for (var i = 0; i < o.items.length; i++) {
      var it = o.items[i];
      if (!it.product) continue;
      var base = String(it.product.id != null ? it.product.id : it.ref).split('~')[0];
      var ident = cfgItemIdent(it.product, { variantId: null });
      for (var j = 0; j < lines.length; j++) {
        var l = lines[j];
        if (l.removed || !l.product) continue;
        if (l.added && l.pick === g.key && cfgBaseId(l) === base) return true;
        if (cfgItemIdent(l.product, l) === ident) return true;
      }
    }
    return false;
  }
  // The other way round: an option not chosen whose every item the plan
  // holds (same product and variant), at least one as a line picked from
  // this group — an older plan taken back („Diesen Plan übernehmen“) still
  // holds that pick, so it reads „Gewählt ✓“ again.
  function cfgOfferPickedHere(g, o, lines) {
    var mine = false;
    var idents = {};
    for (var i = 0; i < o.items.length; i++) {
      var it = o.items[i];
      if (!it.product) return false;
      var ident = cfgItemIdent(it.product, { variantId: null });
      idents[ident] = 1;
      var hit = false;
      for (var j = 0; j < lines.length; j++) {
        var l = lines[j];
        if (l.removed || !l.product || cfgItemIdent(l.product, l) !== ident) continue;
        hit = true;
        if (l.added && l.pick === g.key) mine = true;
      }
      if (!hit) return false;
    }
    // Single-select: every line picked from this group is one of the
    // option's items — a smaller set inside the bigger one picked is not.
    if (mine && !g.multi) {
      for (var m = 0; m < lines.length; m++) {
        var pl = lines[m];
        if (pl.added && !pl.removed && pl.product && pl.pick === g.key && idents[cfgItemIdent(pl.product, pl)] !== 1) return false;
      }
    }
    return mine;
  }
  // The group's choices follow the active plan: those that left it are
  // dropped, those its picked lines hold again come back (stored in c.picks
  // too); true when something changed.
  function cfgOfferReconcileGroup(g, lines, c) {
    if (!g.live) return false;
    var next = [];
    g.shown.forEach(function (o) {
      if (g.chosen.indexOf(o.idx) !== -1 && cfgOfferInPlan(g, o, lines)) next.push(o.idx);
    });
    // Options come back only when none of the earlier choices is left (an
    // older plan taken back): exactly the options the plan's lines picked
    // from this group were picked as — single-select at most one. Lines
    // stored before the option was noted on them: every option the plan
    // holds as picked here (cfgOfferPickedHere).
    if (!next.length) {
      var named = {};
      var noted = true;
      lines.forEach(function (l) {
        if (!l.added || l.removed || !l.product || l.pick !== g.key) return;
        if (typeof l.pickOpt === 'number') named[l.pickOpt] = 1; else noted = false;
      });
      g.shown.forEach(function (o) {
        if ((g.multi || !next.length) && (noted ? named[o.idx] === 1 : cfgOfferPickedHere(g, o, lines))) next.push(o.idx);
      });
    }
    var gone = g.chosen.filter(function (ix) { return next.indexOf(ix) === -1; });
    var back = next.filter(function (ix) { return g.chosen.indexOf(ix) === -1; });
    if (!gone.length && !back.length) return false;
    g.chosen = next;
    // A taken-over option that left the plan is no longer toggled either;
    // options toggled but not taken over yet stay toggled.
    g.pending = g.pending.filter(function (ix) { return gone.indexOf(ix) === -1; });
    back.forEach(function (ix) { if (g.pending.indexOf(ix) === -1) g.pending.push(ix); });
    c.picks = c.picks && typeof c.picks === 'object' ? c.picks : {};
    if (next.length) c.picks[g.key] = next.slice(); else delete c.picks[g.key];
    return true;
  }
  // A plan of Mo's whose products did not all answer is no measure: while
  // the active plan is one, or stands in for a newer one that failed (the
  // fallback — an older plan or the visitor's own), the choices stand.
  function cfgPlansFailed() {
    var cur = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k)) continue;
      var p = cfgPlans[k];
      if (p.failed && (!cur || p === cur || p.seq > cur.seq)) return true;
    }
    return false;
  }
  // „Entfernen“ is the visitor's own word: the option the removed line came
  // from is choosable again right away — also while a plan of Mo's could not
  // load all its products (then cfgOfferReconcile leaves the choices alone,
  // a missing line may only be a load gap).
  function cfgOfferForget(l) {
    if (!l || !l.product || !cfgPlansFailed()) return;
    var c = cfgActive();
    if (!c) return;
    var lines = cfgOfferPlanLines();
    var ident = cfgItemIdent(l.product, l);
    var base = cfgBaseId(l);
    var dirty = false;
    for (var k in cfgOfferGroups) {
      if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k)) continue;
      var g = cfgOfferGroups[k];
      if (!g.cards || !g.live || !g.chosen.length) continue;
      var next = g.chosen.filter(function (ix) {
        var o = cfgOfferByIdx(g, ix);
        if (!o) return false;
        var hit = o.items.some(function (it) {
          if (!it.product) return false;
          if (cfgItemIdent(it.product, { variantId: null }) === ident) return true;
          return l.added && l.pick === g.key && String(it.product.id != null ? it.product.id : it.ref).split('~')[0] === base;
        });
        return !hit || cfgOfferInPlan(g, o, lines);
      });
      if (next.length === g.chosen.length) continue;
      var gone = g.chosen.filter(function (ix) { return next.indexOf(ix) === -1; });
      g.chosen = next;
      g.pending = g.pending.filter(function (ix) { return gone.indexOf(ix) === -1; });
      c.picks = c.picks && typeof c.picks === 'object' ? c.picks : {};
      if (next.length) c.picks[g.key] = next.slice(); else delete c.picks[g.key];
      cfgOfferSync(g);
      dirty = true;
    }
    if (dirty) cfgSave(c);
  }
  function cfgOfferReconcile() {
    if (cfgPlansPending() || cfgPlansFailed()) return;
    var c = cfgActive();
    if (!c) return;
    var lines = cfgOfferPlanLines();
    var dirty = false;
    for (var k in cfgOfferGroups) {
      if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k)) continue;
      var g = cfgOfferGroups[k];
      if (!g.cards || !cfgOfferReconcileGroup(g, lines, c)) continue;
      dirty = true;
      cfgOfferSync(g);
    }
    if (dirty) cfgSave(c);
  }

  // The floating widget (a configurator thread continued on another shop
  // page) has no plan host: the visitor's own plan gets a chat card — lines,
  // total, „Alles in den Warenkorb“ — under the latest option group, from
  // the first pick on (and again after a reload). On the configurator page
  // the plan host shows it instead. The card follows the group instead of
  // sitting in it (the group's role=group label is not the plan's); a group
  // just rendered is not in the chat yet — then it waits in the group and
  // moves out right after (late).
  function cfgOwnPlanCard(late) {
    var plan = cfgPlans[CFG_OWN_KEY];
    if (!plan || !plan.ready || cfgActivePlanKey !== CFG_OWN_KEY || planHost()) return;
    if (!plan.card && !cfgKeptCount(plan.lines)) return;
    var last = null;
    for (var k in cfgOfferGroups) {
      if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k)) continue;
      var g = cfgOfferGroups[k];
      if (g.el && g.live && (!last || g.seq > last.seq)) last = g;
    }
    if (!last) return;
    if (!plan.card) plan.card = el('div', { class: 'ms-chat-card ms-chat-plan ms-chat-offer-plan', 'data-plan-key': CFG_OWN_KEY });
    fillPlanCard(plan.card, plan);
    var at = last.el.parentNode;
    if (at) {
      if (plan.card.previousSibling !== last.el) at.insertBefore(plan.card, last.el.nextSibling);
      return;
    }
    if (plan.card.parentNode !== last.el) last.el.appendChild(plan.card);
    if (late !== true) setTimeout(function () { cfgOwnPlanCard(true); }, 0);
  }

  // The visitor's own plan of a restored thread (reload): its stored lines,
  // active unless a plan of Mo's is (a newer plan of Mo's always wins — and
  // while one of Mo's is still loading, the own plan waits instead of
  // flashing up first).
  function cfgRestoreOwnPlan() {
    var c = cfgActive();
    if (!c) return;
    var added = c.added && c.added[CFG_OWN_KEY];
    if (c.ownPlan !== true && !(Array.isArray(added) && added.length)) return;
    var plan = { key: CFG_OWN_KEY, seq: 0, own: true, title: '', summary: '', tips: [], lines: [], ready: false, settled: false, tries: 0, timer: null, busy: false };
    cfgRestoreAdded(plan.lines, added);
    cfgPlans[CFG_OWN_KEY] = plan;
    cfgOwnPlanHydrate(plan);
  }
  // Its products. An id /api/products did not answer (429, network) is asked
  // again with a short backoff, then every 30 s and whenever the browser is
  // back online or the tab visible again (cfgStallWatch): the plan is never
  // activated without its lines — the next pick or edit would store the
  // emptied plan and the picks would be lost. Unknown ids (null) are dropped.
  var CFG_OWN_RETRY = [1500, 4000, 10000];
  function cfgOwnPlanHydrate(plan) {
    var refs = plan.lines.map(function (l) { return l.ref; });
    plan.busy = true;
    hydrate(refs).then(function (res) {
      plan.busy = false;
      if (cfgPlans[CFG_OWN_KEY] !== plan || plan.ready) return;
      if (cfgUnresolved(refs)) {
        if (plan.timer) return;
        if (plan.tries < CFG_OWN_RETRY.length) {
          plan.timer = setTimeout(function () { plan.timer = null; cfgOwnPlanHydrate(plan); }, CFG_OWN_RETRY[plan.tries]);
          plan.tries++;
        } else cfgStallWatch();
        return;
      }
      for (var i = 0; i < plan.lines.length; i++) {
        plan.lines[i].product = res[i] || null;
        cfgNormLine(plan.lines[i]);
      }
      plan.lines = plan.lines.filter(function (l) { return !!l.product; });
      plan.ready = true;
      plan.settled = true;
      cfgOfferWaitOver();
      if (!cfgActivePlanKey && !cfgPlansPending(true)) cfgSetActivePlan(CFG_OWN_KEY);
      else cfgOfferReconcile();
      // Option groups that gave up meanwhile try again now.
      cfgStalledRetry();
    });
  }
  // The plan a pick goes into: the active one, else the visitor's own (null:
  // a new own plan).
  function cfgPickTarget() {
    var cur = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    var own = cfgPlans[CFG_OWN_KEY];
    return cur && cur.ready ? cur : (own && own.ready ? own : null);
  }
  // A pick waits while the thread's plan is not there yet: the own plan
  // still without its products, or a plan of Mo's newer than the target
  // still loading or without any of its products (a load gap) — the pick
  // would land in a stand-in and be lost once that plan is back.
  function cfgPickWaits(target) {
    if (!target && cfgPlans[CFG_OWN_KEY]) return true;
    for (var k in cfgPlans) {
      if (!Object.prototype.hasOwnProperty.call(cfgPlans, k) || k === CFG_OWN_KEY) continue;
      var p = cfgPlans[k];
      if (p !== target && p.seq > (target ? target.seq : -1) && (!p.settled || (p.failed && !p.ready))) return true;
    }
    return false;
  }
  // „Dein Plan wird gerade noch geladen“ (a pick blocked by a loading plan)
  // is over once a pick could go through — the own plan or one of Mo's has
  // its products, or nothing is loading any more.
  function cfgOfferWaitOver() {
    if (cfgPickWaits(cfgPickTarget())) return;
    for (var k in cfgOfferGroups) {
      if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k) || !cfgOfferGroups[k].noteWait) continue;
      cfgOfferNote(cfgOfferGroups[k], '', '');
      // A toggle not taken over yet says so again.
      cfgOfferSync(cfgOfferGroups[k]);
    }
  }
  // After a longer outage of /api/products: the own plan still waiting for
  // its products, plans of Mo's that did not get them all (cfgPlanRetry) and
  // option groups that rendered nothing (cfgOfferStalled) are asked again
  // every 30 s, when the browser is back online and when the tab becomes
  // visible — not only with the next pick or a reload.
  var cfgStall = { on: false, timer: null };
  function cfgStallWatch() {
    if (!cfgStall.on) {
      cfgStall.on = true;
      window.addEventListener('online', cfgStalledRetry);
      document.addEventListener('visibilitychange', function () { if (!document.hidden) cfgStalledRetry(); });
    }
    if (!cfgStall.timer) cfgStall.timer = setTimeout(cfgStalledRetry, 30000);
  }
  function cfgStalledRetry() {
    if (cfgStall.timer) { clearTimeout(cfgStall.timer); cfgStall.timer = null; }
    var own = cfgPlans[CFG_OWN_KEY];
    if (own && !own.ready) cfgOwnPlanRetry(own);
    for (var p in cfgPlans) {
      if (Object.prototype.hasOwnProperty.call(cfgPlans, p) && p !== CFG_OWN_KEY && cfgPlans[p].failed) cfgPlanRetry(cfgPlans[p]);
    }
    for (var k in cfgOfferGroups) {
      if (!Object.prototype.hasOwnProperty.call(cfgOfferGroups, k)) continue;
      var g = cfgOfferGroups[k];
      if (g.slot && g.slot.parentNode) cfgOfferRetry(g);
    }
  }
  // A pick while the own plan still waits for its products: ask right now.
  function cfgOwnPlanRetry(plan) {
    if (plan.ready || plan.busy) return;
    if (plan.timer) { clearTimeout(plan.timer); plan.timer = null; }
    cfgOwnPlanHydrate(plan);
  }

  // The weights hint above the plan lines: Mo names the kind of weights, the
  // visitor decides the steps (Mo cannot know which plates he already owns).
  function cfgWeightsHint(plan) {
    var names = '';
    var byMo = false;
    var steps = false;
    plan.lines.forEach(function (l) {
      if (l.removed || l.role !== 'weights' || !l.product) return;
      names += ' ' + (l.product.name || '');
      if (!l.added) byMo = true;
      if (cfgVariants(l.product).length > 1) steps = true;
    });
    if (!names) return null;
    var word = /scheibe|plate/i.test(names) ? L('Hantelscheiben', 'weight plates')
      : /hantel|dumbbell/i.test(names) ? L('Hanteln', 'dumbbells')
      : /kettlebell/i.test(names) ? 'Kettlebells'
      : L('Gewichte', 'weights');
    var text = (byMo ? L('Mo empfiehlt zu deinem Setup ' + word + '. ', 'Mo recommends ' + word + ' for your setup. ') : '') +
      (steps
        ? L('Bitte prüfe eigenständig, welche Gewichtsstufen du benötigst – weitere Stufen ergänzt du mit „+\u00a0Gewichtsstufe“.',
          'Please check for yourself which weight steps you need – add more steps with “+\u00a0Weight\u00a0step”.')
        : L('Bitte prüfe eigenständig, welche Gewichte du benötigst.', 'Please check for yourself which weights you need.'));
    return el('p', { class: 'ms-chat-planhost-hint', role: 'note', text: text });
  }

  // The editable plan in the page's [data-ms-chat-plan] host. The product
  // picker (cfgPick.area) survives every re-render — search text, filter,
  // scroll position and focus stay — everything else is rebuilt around it.
  function cfgRenderHost() {
    var host = planHost();
    if (!host) return;
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    var wrap = host.querySelector('.ms-chat-planhost');
    if (!plan || !plan.ready || !cfgActive()) {
      if (wrap) wrap.parentNode.removeChild(wrap);
      cfgPick.open = false;
      cfgPickRender();
      host.setAttribute('data-state', cfgActive() ? 'waiting' : 'empty');
      return;
    }
    if (!wrap) {
      wrap = el('div', { class: 'ms-chat-root ms-chat-planhost' });
      host.appendChild(wrap);
    }
    host.setAttribute('data-state', 'ready');

    // Focus inside a line (stepper, variant, remove …) comes back to the same
    // control of the rebuilt line.
    var focus = cfgFocusNext;
    cfgFocusNext = null;
    var area = cfgPickArea();
    try {
      var ae = document.activeElement;
      if (!focus && ae && ae !== document.body && wrap.contains(ae) && !area.contains(ae)) {
        var owner = ae.closest ? ae.closest('.ms-chat-planhost-line') : null;
        if (owner && ae.getAttribute('data-focus')) focus = { uid: owner.getAttribute('data-uid'), key: ae.getAttribute('data-focus') };
      }
    } catch (e) {}

    var kids = Array.prototype.slice.call(wrap.childNodes);
    for (var i = 0; i < kids.length; i++) { if (kids[i] !== area) wrap.removeChild(kids[i]); }
    if (area.parentNode !== wrap) wrap.appendChild(area);
    function before(n) { wrap.insertBefore(n, area); }

    var head = el('div', { class: 'ms-chat-planhost-head' });
    if (plan.own) {
      // The visitor's own plan from the step-by-step choice: no Mo title, no
      // summary.
      head.appendChild(el('h3', { class: 'ms-chat-planhost-title', text: L('Dein Home-Gym-Plan', 'Your home-gym plan') }));
    } else {
      head.appendChild(el('div', { class: 'ms-chat-plan-eyebrow', text: L('Dein Home-Gym-Plan von Mo', 'Your home-gym plan by Mo') }));
      if (plan.title) head.appendChild(el('h3', { class: 'ms-chat-planhost-title', text: plan.title }));
      if (plan.summary) head.appendChild(el('p', { class: 'ms-chat-planhost-summary', text: plan.summary }));
    }
    before(head);

    var hint = cfgWeightsHint(plan);
    if (hint) before(hint);

    var list = el('ul', { class: 'ms-chat-planhost-lines' });
    plan.lines.forEach(function (l) { list.appendChild(cfgHostLine(plan, l)); });
    // The visitor's own plan can be emptied (its lines are all his own).
    if (!plan.lines.length) list.appendChild(el('li', { class: 'ms-chat-planhost-empty', text: L('Noch nichts im Plan – wähle bei Mo aus oder füge ein Produkt hinzu.', 'Nothing in the plan yet – choose with Mo or add a product.') }));
    before(list);

    cfgPickRender();

    if (plan.tips.length) {
      wrap.appendChild(el('div', { class: 'ms-chat-planhost-section', 'aria-hidden': 'true', text: L('Tipps von Mo', 'Tips from Mo') }));
      var tips = el('ul', { class: 'ms-chat-planhost-tips', 'aria-label': L('Tipps von Mo', 'Tips from Mo') });
      plan.tips.forEach(function (tip) { tips.appendChild(el('li', { text: tip })); });
      wrap.appendChild(tips);
    }

    var t = cfgPlanTotals(plan);
    var foot = el('div', { class: 'ms-chat-planhost-foot' });
    var sum = el('div', { class: 'ms-chat-planhost-sum' });
    sum.appendChild(el('span', { class: 'ms-chat-planhost-sum-label', text: L('Gesamt', 'Total') }));
    sum.appendChild(el('span', { class: 'ms-chat-planhost-sum-value', text: cfgMoney(Math.round(t.total * 100) / 100) }));
    foot.appendChild(sum);
    foot.appendChild(el('div', { class: 'ms-chat-planhost-legal', text: t.units + ' ' + L('Artikel · inkl. MwSt., zzgl. Versand', 'items · incl. VAT, plus shipping') + (t.unavailable ? L(' · nicht lieferbare Positionen bleiben draußen', ' · unavailable items are left out') : '') }));
    var add = el('button', { class: 'ms-chat-btn ms-chat-btn--checkout ms-chat-planhost-add', type: 'button' });
    add.appendChild(icon('cart'));
    add.appendChild(el('span', { text: L('Alles in den Warenkorb', 'Add everything to cart') }));
    add.disabled = !t.count || cfgCartBusy;
    add.addEventListener('click', function () { cfgAddToCart(plan, add); });
    foot.appendChild(add);
    var status = el('div', { class: 'ms-chat-planhost-status', role: 'status', 'aria-live': 'polite' });
    foot.appendChild(status);
    plan.statusEl = status;
    wrap.appendChild(foot);

    if (focus) cfgRestoreFocus(list, focus);
  }

  function cfgRestoreFocus(list, focus) {
    try {
      if (focus.uid === '#add') {
        if (cfgPick.openBtn && !cfgPick.openBtn.hidden) cfgPick.openBtn.focus();
        return;
      }
      var rows = list.querySelectorAll('.ms-chat-planhost-line');
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute('data-uid') !== focus.uid) continue;
        var ctrls = rows[i].querySelectorAll('[data-focus]');
        var target = null;
        for (var j = 0; j < ctrls.length; j++) {
          if (ctrls[j].getAttribute('data-focus') === focus.key && !ctrls[j].disabled) target = ctrls[j];
        }
        for (j = 0; !target && j < ctrls.length; j++) { if (!ctrls[j].disabled) target = ctrls[j]; }
        if (target) target.focus();
        return;
      }
    } catch (e) {}
  }

  function cfgHostLine(plan, l) {
    var p = l.product;
    var v = cfgVariantOf(p, l);
    var avail = cfgLineAvailable(p, v);
    var view = cfgLineView(p, l);
    var baseName = cfgBaseName(p);
    var li = el('li', { class: 'ms-chat-planhost-line' + (l.removed ? ' ms-chat-planhost-line--removed' : '') + (!avail ? ' ms-chat-planhost-line--na' : '') + (l.added ? ' ms-chat-planhost-line--added' : ''), 'data-uid': l.uid });
    var img = el('a', { class: 'ms-chat-planhost-thumb', href: view.url || '#', target: '_blank', rel: 'noopener noreferrer', 'aria-hidden': 'true', tabindex: '-1' });
    if (p.images && p.images[0]) img.appendChild(el('img', { src: p.images[0], alt: '', loading: 'lazy' }));
    img.addEventListener('click', function () { track('product_cta_clicked', { productId: p.id, samePage: false }); });
    li.appendChild(img);

    var main = el('div', { class: 'ms-chat-planhost-main' });
    var tags = el('div', { class: 'ms-chat-planhost-tags' });
    tags.appendChild(el('span', { class: 'ms-chat-planhost-role ms-chat-planhost-role--' + l.role, text: CFG_ROLES[l.role] }));
    if (l.added) tags.appendChild(el('span', { class: 'ms-chat-planhost-tag', text: l.pick ? L('Von dir gewählt', 'Your choice') : L('Von dir ergänzt', 'Added by you') }));
    main.appendChild(tags);
    var name = el('a', { class: 'ms-chat-planhost-name', href: view.url || '#', target: '_blank', rel: 'noopener noreferrer', text: view.name });
    name.addEventListener('click', function () { track('product_cta_clicked', { productId: p.id, samePage: false }); });
    main.appendChild(name);
    if (l.note) main.appendChild(el('div', { class: 'ms-chat-planhost-note', text: l.note }));
    // Why the stepper jumps by two („Paarweise“) / what the flooring covers
    // (by the chosen variant: a connector or an edge piece covers nothing).
    var rule = l.removed ? '' : cfgRuleText(p, l.qty, v);
    if (rule) main.appendChild(el('div', { class: 'ms-chat-planhost-rule', text: rule }));
    if (!avail) main.appendChild(el('span', { class: 'ms-chat-soldout-badge', text: L('Aktuell nicht lieferbar', 'Currently unavailable') }));

    var vs = cfgVariants(p);
    if (vs.length > 1 && !l.removed) {
      var opts = el('div', { class: 'ms-chat-planhost-opts' });
      var sel = el('select', { class: 'ms-chat-planhost-variant', 'data-focus': 'variant', 'aria-label': L('Variante von ', 'Variant of ') + baseName });
      vs.forEach(function (x) {
        var opt = el('option', { value: String(x.id), text: (x.title || '—') + ' · ' + cfgMoney(cfgUnitPrice(p, x)) + (x.available === false ? L(' (ausverkauft)', ' (sold out)') : '') });
        if (x.available === false) opt.disabled = true;
        if (v && String(v.id) === String(x.id)) opt.selected = true;
        sel.appendChild(opt);
      });
      sel.addEventListener('change', function () {
        l.variantId = sel.value;
        // A floor tile's line holds up to 200, a connector's 20.
        l.qty = cfgNormQty(p, l.qty, cfgVariantOf(p, l));
        cfgPlanEdited(plan, 'variant');
      });
      opts.appendChild(sel);
      // Weights: another step of the same plates / dumbbells (the next
      // variant not yet in the plan). A full plan keeps the button usable:
      // the click explains the limit (cfgAddStep).
      if (l.role === 'weights') {
        var next = cfgNextVariant(plan, p, l);
        var step = el('button', { class: 'ms-chat-planhost-step', type: 'button', 'data-focus': 'step' });
        step.appendChild(el('span', { 'aria-hidden': 'true', text: '+' }));
        step.appendChild(el('span', { text: L('Gewichtsstufe', 'Weight step') }));
        step.setAttribute('aria-label', next
          ? L('Gewichtsstufe hinzufügen: ', 'Add weight step: ') + baseName
          : L('Gewichtsstufe – alle Stufen sind schon im Plan', 'Weight step – every step is already in the plan'));
        step.disabled = !next;
        step.addEventListener('click', function () { cfgAddStep(plan, l); });
        opts.appendChild(step);
      }
      main.appendChild(opts);
    }
    li.appendChild(main);

    var side = el('div', { class: 'ms-chat-planhost-side' });
    if (!l.removed) {
      // The product's rules: steps of `step` (pairs: 2), never below its
      // minimum or above its maximum — up to 200 for a floor tile (no rules:
      // 1–20 as before).
      var rr = cfgRulesOf(p, v);
      var qty = el('div', { class: 'ms-chat-qty', role: 'group', 'aria-label': L('Menge', 'Quantity') });
      var minus = el('button', { class: 'ms-chat-qty-btn', type: 'button', text: '−', 'data-focus': 'minus', 'aria-label': rr.step > 1 ? L(rr.step + ' weniger', rr.step + ' fewer') : L('Weniger', 'Fewer') });
      var val = el('span', { class: 'ms-chat-qty-val', text: String(l.qty), 'aria-live': 'polite' });
      var plus = el('button', { class: 'ms-chat-qty-btn', type: 'button', text: '+', 'data-focus': 'plus', 'aria-label': rr.step > 1 ? L(rr.step + ' mehr', rr.step + ' more') : L('Mehr', 'More') });
      minus.disabled = l.qty - rr.step < rr.min;
      plus.disabled = l.qty + rr.step > rr.max;
      minus.addEventListener('click', function () { if (l.qty - rr.step >= rr.min) { l.qty = cfgNormQty(p, l.qty - rr.step, v); cfgPlanEdited(plan, 'qty'); } });
      plus.addEventListener('click', function () { if (l.qty + rr.step <= rr.max) { l.qty = cfgNormQty(p, l.qty + rr.step, v); cfgPlanEdited(plan, 'qty'); } });
      qty.appendChild(minus); qty.appendChild(val); qty.appendChild(plus);
      side.appendChild(qty);
      side.appendChild(el('div', { class: 'ms-chat-planhost-price', text: avail ? cfgMoney(Math.round(cfgUnitPrice(p, v) * l.qty * 100) / 100) : '—' }));
    }
    // Mo's lines toggle (remove / add back); the visitor's own lines are
    // simply deleted.
    var toggle = el('button', { class: 'ms-chat-planhost-toggle', type: 'button', 'data-focus': 'toggle', text: l.removed ? L('Wieder hinzufügen', 'Add back') : L('Entfernen', 'Remove') });
    toggle.addEventListener('click', function () {
      if (l.added) {
        var idx = plan.lines.indexOf(l);
        if (idx === -1) return;
        var after = plan.lines[idx + 1] || plan.lines[idx - 1] || null;
        plan.lines.splice(idx, 1);
        cfgFocusNext = after ? { uid: after.uid, key: 'toggle' } : { uid: '#add' };
        cfgPlanEdited(plan, 'remove', l);
        return;
      }
      if (l.removed && cfgPlanFull(plan)) { cfgLimitNote(plan, l); return; }
      l.removed = !l.removed;
      cfgPlanEdited(plan, l.removed ? 'remove' : 'restore', l);
    });
    side.appendChild(toggle);
    li.appendChild(side);
    return li;
  }

  function cfgLimitText() {
    return L('Dein Plan hat schon ' + CFG_MAX_LINES + ' Positionen – entferne eine, um etwas hinzuzufügen.',
      'Your plan already has ' + CFG_MAX_LINES + ' items – remove one to add something.');
  }
  // The limit message where the visitor clicked (a full plan is long, the
  // status line at its foot is out of view) — read out by the status line.
  function cfgLimitNote(plan, l) {
    cfgPlanStatus(plan, 'warn', cfgLimitText());
    try {
      var host = planHost();
      var rows = host ? host.querySelectorAll('.ms-chat-planhost-line') : [];
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute('data-uid') !== l.uid) continue;
        var main = rows[i].querySelector('.ms-chat-planhost-main');
        if (!main || main.querySelector('.ms-chat-planhost-limit')) return;
        main.appendChild(el('div', { class: 'ms-chat-planhost-limit', 'aria-hidden': 'true', text: cfgLimitText() }));
        return;
      }
    } catch (e) {}
  }

  // „+ Gewichtsstufe“: the same product once more with the next free variant.
  function cfgAddStep(plan, src) {
    if (!src.product || plan !== cfgPlans[cfgActivePlanKey]) return;
    if (cfgPlanFull(plan)) { cfgLimitNote(plan, src); return; }
    var next = cfgNextVariant(plan, src.product, src);
    if (!next) return;
    var line = {
      uid: cfgNewUid(plan.lines),
      ref: cfgBaseId(src),
      qty: cfgNormQty(src.product, src.qty, next),
      role: 'weights',
      note: '',
      removed: false,
      variantId: String(next.id),
      product: src.product,
      added: true
    };
    cfgInsertLine(plan.lines, line);
    cfgFocusNext = { uid: line.uid, key: 'variant' };
    cfgPlanEdited(plan, 'add_step');
  }

  function cfgPlanEdited(plan, action, line) {
    cfgSaveEdits(plan);
    track('configurator_plan_edited', { action: action });
    cfgRenderHost();
    if (plan.card) fillPlanCard(plan.card, plan);
    // „Entfernen“ of a chosen option's line makes it choosable again.
    if (action === 'remove') cfgOfferForget(line);
    cfgOfferReconcile();
    var t = cfgPlanTotals(plan);
    cfgEmit('plan', { items: t.count, units: t.units, total: Math.round(t.total) });
  }

  // ----- „+ Produkt hinzufügen“: the curated assortment as an inline picker ----
  // GET /api/configurator/assortment (origin-guarded like /api/products) once
  // per page load; a failed load can be retried. Cards: { id, name, category,
  // role, price, salePrice?, priceMin?, priceMax?, images, shopifyUrl, inStock,
  // variants? } sorted by role, then name.
  var cfgAsm = { state: 'idle', products: [], promise: null };
  var cfgPick = { area: null, open: false, query: '', role: 'all', rows: [], built: false };

  function cfgCleanCard(c) {
    if (!c || typeof c !== 'object' || typeof c.id !== 'string' || !c.id.trim() || typeof c.name !== 'string') return null;
    var num = function (x) { var n = Number(x); return typeof x === 'number' && isFinite(n) ? n : undefined; };
    cfgNoteRules(c);
    // The card's product rules (since 2026-10-27; see cfgRulesOf).
    var rules = c.rules && typeof c.rules === 'object' && !Array.isArray(c.rules)
      ? { step: num(c.rules.step), min: num(c.rules.min), max: num(c.rules.max), coverageM2: num(c.rules.coverageM2) }
      : undefined;
    var vs = Array.isArray(c.variants) ? c.variants.filter(function (v) { return v && typeof v === 'object'; }).slice(0, 100).map(function (v) {
      return {
        id: v.id != null && v.id !== '' ? String(v.id) : null,
        title: typeof v.title === 'string' ? v.title : '',
        price: Number(v.price) || 0,
        salePrice: num(v.salePrice),
        available: v.available !== false,
        isDefault: v.isDefault === true
      };
    }) : [];
    return {
      id: c.id.trim(),
      name: c.name.slice(0, 200),
      category: typeof c.category === 'string' ? c.category : '',
      role: CFG_ROLES[c.role] ? c.role : 'extra',
      price: Number(c.price) || 0,
      salePrice: num(c.salePrice),
      priceMin: num(c.priceMin),
      priceMax: num(c.priceMax),
      images: (Array.isArray(c.images) ? c.images : []).filter(function (s) { return typeof s === 'string' && s; }).slice(0, 1),
      shopifyUrl: typeof c.shopifyUrl === 'string' ? c.shopifyUrl : '',
      inStock: c.inStock !== false,
      variants: vs,
      rules: rules,
      showroom: typeof c.showroom === 'boolean' ? c.showroom : undefined
    };
  }

  function cfgLoadAssortment() {
    if (cfgAsm.state === 'ok') return Promise.resolve(cfgAsm.products);
    if (cfgAsm.promise) return cfgAsm.promise;
    cfgAsm.state = 'loading';
    cfgPickRender();
    cfgAsm.promise = fetch(API_BASE + '/api/configurator/assortment', { method: 'GET', headers: { 'x-ms-session': sid } })
      .then(function (res) {
        if (!res.ok) throw new Error('assortment ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || data.ok !== true || !Array.isArray(data.products)) throw new Error('assortment: invalid payload');
        var out = [];
        for (var i = 0; i < data.products.length && out.length < 400; i++) {
          var card = cfgCleanCard(data.products[i]);
          if (card) out.push(card);
        }
        cfgAsm.products = out;
        cfgAsm.state = 'ok';
        cfgAsm.promise = null;
        cfgPickRender();
        cfgPickSettled();
        return out;
      })
      .catch(function () {
        cfgAsm.state = 'error';
        cfgAsm.promise = null;
        cfgPickRender();
        cfgPickSettled();
        return [];
      });
    return cfgAsm.promise;
  }

  // The product as a plan line needs it, when /api/products cannot answer.
  function cfgCardProduct(card) {
    return {
      id: card.id,
      name: card.name,
      category: card.category,
      price: card.price,
      salePrice: card.salePrice,
      priceMin: card.priceMin,
      priceMax: card.priceMax,
      images: card.images.slice(),
      shopifyUrl: card.shopifyUrl,
      inStock: card.inStock,
      variants: card.variants.slice(),
      rules: card.rules,
      showroom: card.showroom
    };
  }

  function cfgCardPriceEl(card) {
    var box = el('div', { class: 'ms-chat-planpick-price' });
    var min = card.priceMin, max = card.priceMax;
    if (typeof min === 'number' && typeof max === 'number' && min > 0 && max > min) {
      box.appendChild(el('span', { text: L('ab ', 'from ') + cfgMoney(min) }));
      return box;
    }
    var sale = typeof card.salePrice === 'number' && card.salePrice > 0 && card.salePrice < card.price ? card.salePrice : null;
    if (sale != null) {
      box.appendChild(el('span', { class: 'ms-chat-planpick-sale', text: cfgMoney(sale) }));
      box.appendChild(el('s', { class: 'ms-chat-planpick-strike', text: cfgMoney(card.price) }));
    } else {
      box.appendChild(el('span', { text: cfgMoney(card.price) }));
    }
    return box;
  }

  // The persistent „+ Produkt hinzufügen“ area of the plan host. Its search
  // is always there: an empty query shows the curated assortment, 2+
  // characters search the whole shop (cfgFind).
  function cfgPickArea() {
    if (cfgPick.area) return cfgPick.area;
    var area = el('div', { class: 'ms-chat-planhost-addarea' });
    var openBtn = el('button', { class: 'ms-chat-planhost-addbtn', type: 'button', 'aria-expanded': 'false', 'aria-controls': 'ms-chat-planpick' });
    openBtn.appendChild(el('span', { class: 'ms-chat-planhost-addbtn-plus', 'aria-hidden': 'true', text: '+' }));
    openBtn.appendChild(el('span', { text: L('Produkt hinzufügen', 'Add a product') }));
    openBtn.addEventListener('click', cfgPickOpen);
    area.appendChild(openBtn);

    var pick = el('div', { class: 'ms-chat-planpick', id: 'ms-chat-planpick', role: 'region', 'aria-label': L('Produkt zum Plan hinzufügen', 'Add a product to the plan') });
    pick.hidden = true;
    var head = el('div', { class: 'ms-chat-planpick-head' });
    var headText = el('div', { class: 'ms-chat-planpick-headtext' });
    headText.appendChild(el('div', { class: 'ms-chat-planpick-kicker', text: L('Produkt hinzufügen', 'Add a product') }));
    var titleEl = el('div', { class: 'ms-chat-planpick-title', text: L('Aus dem Home-Gym-Sortiment', 'From the home-gym range') });
    headText.appendChild(titleEl);
    head.appendChild(headText);
    var done = el('button', { class: 'ms-chat-planpick-done', type: 'button', text: L('Fertig', 'Done') });
    done.addEventListener('click', cfgPickClose);
    head.appendChild(done);
    pick.appendChild(head);

    var tools = el('div', { class: 'ms-chat-planpick-tools' });
    var search = el('input', { class: 'ms-chat-planpick-search', type: 'search', placeholder: L('Im ganzen Shop suchen …', 'Search the whole shop …'), 'aria-label': L('Produkte im ganzen Shop suchen', 'Search products in the whole shop'), autocomplete: 'off', enterkeyhint: 'search', maxlength: '80' });
    search.addEventListener('input', function () { cfgPick.query = search.value; cfgPickQueryChanged(); });
    search.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
    tools.appendChild(search);
    var roles = el('div', { class: 'ms-chat-planpick-roles', role: 'group', 'aria-label': L('Nach Kategorie filtern', 'Filter by category') });
    roles.hidden = true;
    tools.appendChild(roles);
    pick.appendChild(tools);

    var stateEl = el('div', { class: 'ms-chat-planpick-state', role: 'status', 'aria-live': 'polite' });
    pick.appendChild(stateEl);
    var note = el('div', { class: 'ms-chat-planpick-note', role: 'status', 'aria-live': 'polite' });
    pick.appendChild(note);
    var list = el('ul', { class: 'ms-chat-planpick-list', 'aria-label': L('Produkte', 'Products') });
    list.hidden = true;
    pick.appendChild(list);
    var slist = el('ul', { class: 'ms-chat-planpick-list ms-chat-planpick-list--search', 'aria-label': L('Suchergebnisse', 'Search results') });
    slist.hidden = true;
    pick.appendChild(slist);
    pick.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); cfgPickClose(); }
    });
    area.appendChild(pick);

    cfgPick.area = area;
    cfgPick.openBtn = openBtn;
    cfgPick.panel = pick;
    cfgPick.titleEl = titleEl;
    cfgPick.tools = tools;
    cfgPick.search = search;
    cfgPick.rolesEl = roles;
    cfgPick.stateEl = stateEl;
    cfgPick.noteEl = note;
    cfgPick.listEl = list;
    cfgPick.slistEl = slist;
    return area;
  }

  function cfgPickOpen() {
    cfgPick.open = true;
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    if (plan && cfgPlanFull(plan)) cfgPickNote('warn', cfgLimitText());
    else cfgPickNote('', '');
    cfgPickRender();
    cfgLoadAssortment();
    cfgPickFocus();
  }
  // Focus in the open picker: the search — it works even while the range
  // loads, failed or is empty.
  function cfgPickFocus() {
    try { cfgPick.search.focus(); } catch (e) {}
  }
  // A load settled (range or search). A visitor who had pressed „Erneut
  // versuchen“ (focus parked on the search meanwhile) gets the new retry
  // button when it failed again. Focus elsewhere stays.
  function cfgPickSettled() {
    if (!cfgPick.area || !cfgPick.open || !cfgPick.retryFocus) return;
    if (cfgAsm.state === 'loading' || cfgFind.state === 'loading') return;
    cfgPick.retryFocus = false;
    try {
      if (document.activeElement !== cfgPick.search) return;
      var retry = cfgPick.stateEl.querySelector('.ms-chat-planpick-retry');
      if (retry) retry.focus();
    } catch (e) {}
  }
  function cfgPickClose() {
    cfgPick.open = false;
    cfgPickRender();
    try { cfgPick.openBtn.focus(); } catch (e) {}
  }
  function cfgPickNote(kind, text) {
    if (!cfgPick.noteEl) return;
    cfgPick.noteEl.className = 'ms-chat-planpick-note' + (kind ? ' ms-chat-planpick-note--' + kind : '');
    cfgPick.noteEl.textContent = text || '';
  }

  // ----- whole-shop search (GET /api/configurator/search) ----------------------
  // 2–80 characters, debounced ~250 ms; every query change bumps cfgFind.seq,
  // so only the latest request's answer is ever shown (an older one is
  // aborted where AbortController exists, ignored otherwise).
  var cfgFind = { q: '', state: 'idle', results: [], rows: [], seq: 0, timer: null, ctrl: null };

  function cfgPickTerm() {
    return String(cfgPick.query || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  }
  function cfgFindAbort() {
    if (!cfgFind.ctrl) return;
    try { cfgFind.ctrl.abort(); } catch (e) {}
    cfgFind.ctrl = null;
  }
  function cfgPickQueryChanged() {
    var q = cfgPickTerm();
    if (q.length < 2) q = '';
    if (q === cfgFind.q && cfgFind.state !== 'error') { cfgPickRender(); return; }
    if (cfgFind.timer) { clearTimeout(cfgFind.timer); cfgFind.timer = null; }
    cfgFindAbort();
    var seq = ++cfgFind.seq;
    cfgFind.q = q;
    if (!q) { cfgFind.state = 'idle'; cfgPickRender(); return; }
    cfgFind.state = 'loading';
    cfgPickRender();
    cfgFind.timer = setTimeout(function () { cfgFind.timer = null; cfgFindRun(q, seq); }, 250);
  }
  function cfgFindRetry() {
    if (!cfgFind.q) return;
    cfgFindAbort();
    var seq = ++cfgFind.seq;
    cfgFind.state = 'loading';
    cfgPickRender();
    cfgFindRun(cfgFind.q, seq);
  }
  function cfgFindRun(q, seq) {
    if (seq !== cfgFind.seq) return;
    var ctrl = null;
    try { ctrl = typeof AbortController === 'function' ? new AbortController() : null; } catch (e) { ctrl = null; }
    cfgFind.ctrl = ctrl;
    var init = { method: 'GET', headers: { 'x-ms-session': sid } };
    if (ctrl) init.signal = ctrl.signal;
    fetch(API_BASE + '/api/configurator/search?q=' + encodeURIComponent(q) + '&limit=12', init)
      .then(function (res) {
        if (!res.ok) throw new Error('search ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (seq !== cfgFind.seq) return;
        if (!data || data.ok !== true || !Array.isArray(data.products)) throw new Error('search: invalid payload');
        var out = [];
        for (var i = 0; i < data.products.length && out.length < 12; i++) {
          var card = cfgCleanCard(data.products[i]);
          if (card) out.push(card);
        }
        cfgFind.ctrl = null;
        cfgFind.results = out;
        cfgFind.state = 'ok';
        cfgFindBuild();
        cfgPickRender();
        cfgPickSettled();
      })
      .catch(function () {
        if (seq !== cfgFind.seq) return; // superseded (or aborted) — the newer query decides
        cfgFind.ctrl = null;
        cfgFind.state = 'error';
        cfgPickRender();
        cfgPickSettled();
      });
  }
  function cfgFindBuild() {
    if (!cfgPick.slistEl) return;
    cfgFind.rows.forEach(function (r) { if (r.timer) clearTimeout(r.timer); });
    cfgPick.slistEl.replaceChildren();
    cfgFind.rows = cfgFind.results.map(function (card) {
      var row = cfgPickRow(card);
      cfgPick.slistEl.appendChild(row.li);
      return row;
    });
  }

  function cfgPickRender() {
    if (!cfgPick.area) return;
    cfgPick.openBtn.hidden = cfgPick.open;
    cfgPick.openBtn.setAttribute('aria-expanded', cfgPick.open ? 'true' : 'false');
    cfgPick.panel.hidden = !cfgPick.open;
    if (!cfgPick.open) return;
    // A search covers the whole shop, not only the curated range — and so
    // does the picker whose range is empty or could not load (only the
    // search is left there).
    var wholeShop = !!cfgFind.q || cfgAsm.state === 'error' || (cfgAsm.state === 'ok' && !cfgAsm.products.length);
    if (cfgPick.titleEl) cfgPick.titleEl.textContent = wholeShop ? L('Aus dem ganzen Shop', 'From the whole shop') : L('Aus dem Home-Gym-Sortiment', 'From the home-gym range');
    if (cfgAsm.state === 'ok' && cfgAsm.products.length && !cfgPick.built) cfgPickBuild();
    cfgPickChips();
    cfgPickSync();
    cfgPickFilter();
  }

  // One curated row per card — built once, filtering only shows / hides rows.
  function cfgPickBuild() {
    cfgPick.built = true;
    cfgPick.listEl.replaceChildren();
    cfgPick.rows = cfgAsm.products.map(function (card) {
      var row = cfgPickRow(card);
      cfgPick.listEl.appendChild(row.li);
      return row;
    });
  }

  // A picker row (curated assortment or search result).
  function cfgPickRow(card) {
    var li = el('li', { class: 'ms-chat-planpick-row' + (card.inStock ? '' : ' ms-chat-planpick-row--na') });
    var thumb = el('span', { class: 'ms-chat-planpick-thumb', 'aria-hidden': 'true' });
    if (card.images[0]) thumb.appendChild(el('img', { src: card.images[0], alt: '', loading: 'lazy' }));
    li.appendChild(thumb);
    var meta = el('div', { class: 'ms-chat-planpick-meta' });
    meta.appendChild(el('span', { class: 'ms-chat-planpick-role', text: CFG_ROLES[card.role] }));
    meta.appendChild(el('span', { class: 'ms-chat-planpick-name', text: card.name }));
    meta.appendChild(cfgCardPriceEl(card));
    li.appendChild(meta);
    var act = el('div', { class: 'ms-chat-planpick-act' });
    var inPlan = el('span', { class: 'ms-chat-planpick-inplan', text: L('Im Plan', 'In plan') });
    inPlan.hidden = true;
    act.appendChild(inPlan);
    var row = { card: card, li: li, inPlan: inPlan, btn: null, busy: false, timer: null };
    if (card.inStock) {
      var btn = el('button', { class: 'ms-chat-planpick-add', type: 'button', 'aria-label': L('Hinzufügen: ', 'Add: ') + card.name });
      btn.appendChild(el('span', { class: 'ms-chat-planpick-add-label', text: L('Hinzufügen', 'Add') }));
      btn.addEventListener('click', function () { cfgPickAdd(row); });
      act.appendChild(btn);
      row.btn = btn;
    } else {
      act.appendChild(el('span', { class: 'ms-chat-planpick-soldout', text: L('Ausverkauft', 'Sold out') }));
    }
    li.appendChild(act);
    return row;
  }

  // Role chips (Alle + the roles of the list that is shown: the range, or
  // while search results are shown only theirs) — rebuilt only when that set
  // changes; a chosen role the shown list does not have goes back to „Alle“,
  // so a chip picked in the range never hides every search result. While a
  // search loads, chips and role stay — the rows still shown belong to the
  // previous query, the new results decide.
  function cfgPickChips() {
    if (cfgFind.state === 'loading' && cfgPick.chipSig != null) return;
    var present = {};
    if (cfgPickShowFound()) cfgFind.results.forEach(function (c) { present[c.role] = 1; });
    else if (cfgAsm.state === 'ok') cfgAsm.products.forEach(function (c) { present[c.role] = 1; });
    var chips = [{ id: 'all', label: L('Alle', 'All') }];
    CFG_ROLE_ORDER.forEach(function (r) { if (present[r]) chips.push({ id: r, label: CFG_ROLES_PLURAL[r] }); });
    if (!present[cfgPick.role]) cfgPick.role = 'all';
    var sig = chips.map(function (ch) { return ch.id; }).join(',');
    if (sig === cfgPick.chipSig) return;
    cfgPick.chipSig = sig;
    cfgPick.rolesEl.replaceChildren();
    cfgPick.chips = [];
    if (chips.length > 2) {
      chips.forEach(function (ch) {
        var b = el('button', { class: 'ms-chat-planpick-chip', type: 'button', 'data-role': ch.id, 'aria-pressed': ch.id === cfgPick.role ? 'true' : 'false', text: ch.label });
        b.addEventListener('click', function () { cfgPick.role = ch.id; cfgPickFilter(); });
        cfgPick.rolesEl.appendChild(b);
        cfgPick.chips.push(b);
      });
    }
    cfgPick.rolesEl.hidden = chips.length <= 2;
  }

  // Search results are shown (also the previous ones while the next query
  // loads), not the curated range.
  function cfgPickShowFound() {
    return cfgPickTerm().length >= 2 && (cfgFind.state === 'ok' || (cfgFind.state === 'loading' && cfgFind.rows.length > 0));
  }
  // Shows the curated list (filtered by role and a 1-character query; while
  // a search is pending also by its words — an instant preview) or the search
  // results (filtered by role), and the state line.
  function cfgPickFilter() {
    if (!cfgPick.area) return;
    var term = cfgPickTerm();
    var searching = term.length >= 2;
    var showFound = cfgPickShowFound();
    var rows = showFound ? cfgFind.rows : cfgPick.rows;
    var words = showFound ? [] : term.toLowerCase().split(/\s+/).filter(Boolean);
    var shown = 0;
    rows.forEach(function (r) {
      var ok = cfgPick.role === 'all' || r.card.role === cfgPick.role;
      for (var i = 0; ok && i < words.length; i++) { if (r.card.name.toLowerCase().indexOf(words[i]) === -1) ok = false; }
      r.li.hidden = !ok;
      if (ok) shown++;
    });
    cfgPick.listEl.hidden = showFound || !shown;
    cfgPick.slistEl.hidden = !showFound || !shown;
    (cfgPick.chips || []).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-role') === cfgPick.role ? 'true' : 'false');
    });

    var stateEl = cfgPick.stateEl;
    // A focused „Erneut versuchen“ is about to be rebuilt: keep the focus in
    // the picker instead of losing it to <body>.
    var refocus = false;
    try { refocus = stateEl.contains(document.activeElement); } catch (e) {}
    stateEl.replaceChildren();
    stateEl.className = 'ms-chat-planpick-state';
    var st = cfgAsm.state;
    var retry = null;
    if (searching && cfgFind.state === 'loading') {
      cfgPickStateText(stateEl, 'loading', L('Suche …', 'Searching …'));
    } else if (searching && cfgFind.state === 'error') {
      cfgPickStateText(stateEl, 'error', L('Die Suche hat gerade nicht geklappt.', 'The search did not work right now.'));
      retry = cfgFindRetry;
    } else if (searching) {
      if (!shown) cfgPickStateText(stateEl, '', L('Nichts gefunden – versuch einen anderen Begriff oder frag Mo.', 'Nothing found – try another word or ask Mo.'));
    } else if (st === 'idle' || st === 'loading') {
      cfgPickStateText(stateEl, 'loading', L('Sortiment wird geladen …', 'Loading the range …'));
    } else if (st === 'error') {
      cfgPickStateText(stateEl, 'error', L('Das Sortiment konnte gerade nicht geladen werden.', 'The range could not be loaded right now.'));
      retry = function () { cfgLoadAssortment(); };
    } else if (!cfgAsm.products.length) {
      cfgPickStateText(stateEl, '', L('Suche nach Produkten aus dem ganzen Shop.', 'Search for products from the whole shop.'));
    } else if (!shown) {
      cfgPickStateText(stateEl, '', L('Keine passenden Produkte gefunden.', 'No matching products found.'));
    }
    if (retry) {
      var rb = el('button', { class: 'ms-chat-planpick-retry', type: 'button', text: L('Erneut versuchen', 'Try again') });
      rb.addEventListener('click', retry);
      stateEl.appendChild(rb);
    }
    if (refocus) {
      try {
        var nb = stateEl.querySelector('.ms-chat-planpick-retry');
        if (nb) nb.focus();
        else { cfgPick.retryFocus = true; cfgPick.search.focus(); }
      } catch (e) {}
    }
  }
  function cfgPickStateText(stateEl, kind, text) {
    if (kind) stateEl.classList.add('ms-chat-planpick-state--' + kind);
    if (kind === 'loading') stateEl.appendChild(el('span', { class: 'ms-chat-planpick-spinner', 'aria-hidden': 'true' }));
    stateEl.appendChild(el('span', { text: text }));
  }

  // "Im Plan" on the rows whose product the active plan holds.
  function cfgPickSync() {
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    var inPlan = {};
    if (plan) plan.lines.forEach(function (l) { if (!l.removed && l.product) inPlan[cfgBaseId(l)] = 1; });
    var mark = function (r) { r.inPlan.hidden = !inPlan[r.card.id] || !!r.timer; };
    cfgPick.rows.forEach(mark);
    cfgFind.rows.forEach(mark);
  }

  // A weights product in single weight steps — every variant one weight
  // („5 kg“, „2,5 kg“, „10 lb“, also after an option like „ATX / 5 kg“; a
  // bare „8“ when the name gives the unit) and no set or pair in its name
  // (Hantelsatz, Komplettsatz, Hantelset, … Paar): a step is bought twice.
  var CFG_SET_RE = /s[aä]tz|\bsets?\b|hantelset|komplett|paar/i;
  var CFG_ONE_WEIGHT_RE = /^\d+(?:[.,]\d+)?\s*(kg|lbs?)?(?:\s*[-–]\s*[^\d]+)?$/i;
  function cfgSoldInPairs(p) {
    var name = String(p && p.name || '');
    var vs = cfgVariants(p);
    if (vs.length < 2 || CFG_SET_RE.test(name)) return false;
    var unit = /\d\s*(kg|lbs?)\b/i.test(name);
    for (var i = 0; i < vs.length; i++) {
      var m = String(vs[i].title || '').split('/').pop().replace(/^\s+|\s+$/g, '').match(CFG_ONE_WEIGHT_RE);
      if (!m || (!m[1] && !unit)) return false;
    }
    return true;
  }

  function cfgPickAdd(row) {
    var plan = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
    if (!plan || !plan.ready || row.busy) return;
    if (cfgPlanFull(plan)) { cfgPickNote('warn', cfgLimitText()); return; }
    var card = row.card;
    row.busy = true;
    row.btn.setAttribute('aria-busy', 'true');
    row.li.classList.add('ms-chat-planpick-row--busy');
    hydrate([card.id]).then(function (res) { return res && res[0] ? res[0] : null; }, function () { return null; }).then(function (prod) {
      row.busy = false;
      row.btn.removeAttribute('aria-busy');
      row.li.classList.remove('ms-chat-planpick-row--busy');
      var cur = cfgActivePlanKey && cfgPlans[cfgActivePlanKey];
      if (!cur || !cur.ready) return;
      if (cfgPlanFull(cur)) { cfgPickNote('warn', cfgLimitText()); return; }
      var p = prod || cfgCardProduct(card);
      var weights = card.role === 'weights';
      // What „Hinzufügen“ adds: the product's minimum by its rules (pairs: 2)
      // and one more step for a line already there. A backend without rules:
      // the round-4 rule of thumb — plates and dumbbells in steps come in
      // pairs; a single product filed under weights (a set, an adjustable
      // kettlebell, a machine) once.
      var v = weights ? (cfgNextVariant(cur, p) || cfgDefaultVariant(p)) : cfgDefaultVariant(p);
      var vid = v && v.id != null ? String(v.id) : null;
      var rules = cfgRulesOf(p, v);
      var pairs = !rules.known && weights && cfgSoldInPairs(p);
      var first = rules.known ? rules.min : (pairs ? 2 : 1);
      var more = rules.known ? rules.step : (pairs ? 2 : 1);
      // The same product and variant the visitor already added: one more of
      // it instead of a second identical row.
      var same = null;
      cur.lines.forEach(function (l) {
        if (same || !l.added || l.removed || !l.product || l.ref !== card.id) return;
        var lv = cfgVariantOf(l.product, l);
        if (String(lv && lv.id != null ? lv.id : '') === String(vid || '')) same = l;
      });
      if (same) {
        // One more step, up to what the line holds (20, a floor tile 200).
        same.qty = cfgNormQty(p, same.qty + more, v);
      } else {
        cfgInsertLine(cur.lines, {
          uid: cfgNewUid(cur.lines),
          ref: card.id,
          qty: cfgNormQty(p, first, v),
          role: card.role,
          note: '',
          removed: false,
          variantId: vid,
          product: p,
          added: true
        });
      }
      cfgPlanEdited(cur, 'add');
      // Feedback on the row; the picker stays open for the next product.
      var label = row.btn.querySelector('.ms-chat-planpick-add-label');
      row.li.classList.add('ms-chat-planpick-row--added');
      if (label) label.textContent = L('✓ hinzugefügt', '✓ added');
      cfgPickNote('ok', L('„' + card.name + '“ ist jetzt in deinem Plan.', '“' + card.name + '” is now in your plan.'));
      if (row.timer) clearTimeout(row.timer);
      row.timer = setTimeout(function () {
        row.timer = null;
        row.li.classList.remove('ms-chat-planpick-row--added');
        if (label) label.textContent = L('Hinzufügen', 'Add');
        cfgPickSync();
      }, 2400);
      cfgPickSync();
    });
  }

  function cfgPlanStatus(plan, kind, text) {
    if (!plan.statusEl) return;
    plan.statusEl.className = 'ms-chat-planhost-status' + (kind ? ' ms-chat-planhost-status--' + kind : '');
    plan.statusEl.textContent = text || '';
  }

  // POST /cart/add.js with the plan's available lines (theme route, locale-
  // aware), then refresh the header badge and open the theme's cart drawer.
  // A line Shopify refuses (stock) fails the whole batch — then line by line,
  // so everything addable lands and the rest is named.
  function cfgCartAddUrl() {
    try { if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url.replace(/\.js$/, '') + '.js'; } catch (e) {}
    return '/cart/add.js';
  }
  function cfgPostAdd(items) {
    return fetch(cfgCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }
  function cfgAddToCart(plan, btn) {
    if (cfgCartBusy) return;
    var items = [];
    var names = {};
    var prices = {};
    var byId = {};
    // Mo's lines and the visitor's own; two lines of the same variant are one
    // cart line.
    plan.lines.forEach(function (l) {
      if (l.removed || !l.product) return;
      var v = cfgVariantOf(l.product, l);
      if (!cfgLineAvailable(l.product, v) || !v) return;
      var id = parseInt(v.id, 10);
      if (!isFinite(id)) return;
      // The quantity the line shows — by the rules of its product and
      // variant (pairs …, a floor tile up to 200).
      var q = cfgNormQty(l.product, l.qty, v);
      if (byId[id]) { byId[id].quantity += q; return; }
      byId[id] = { id: id, quantity: q };
      items.push(byId[id]);
      names[id] = cfgLineView(l.product, l).name;
      prices[id] = cfgUnitPrice(l.product, v) || 0;
    });
    if (!items.length) { cfgPlanStatus(plan, 'warn', L('Keine lieferbaren Produkte im Plan.', 'No available products in the plan.')); return; }
    cfgCartBusy = true;
    if (btn) btn.disabled = true;
    cfgPlanStatus(plan, '', L('Wird in den Warenkorb gelegt …', 'Adding to cart …'));
    // The plan is a Mo consultation: stamp the cart's attribution marker
    // (consent-gated, fail-silent) before the add.
    moAttrConsulted = true;
    moAttrEnsure(false);
    cfgPostAdd(items).then(function (ok) {
      if (ok) return { added: items, failed: [] };
      var added = [], failed = [];
      return items.reduce(function (p, it) {
        return p.then(function () {
          return cfgPostAdd([it]).then(function (one) { (one ? added : failed).push(it); });
        });
      }, Promise.resolve()).then(function () { return { added: added, failed: failed }; });
    }).then(function (r) {
      cfgCartBusy = false;
      if (btn) btn.disabled = false;
      if (!r.added.length) {
        cfgPlanStatus(plan, 'warn', L('Das hat leider nicht geklappt — bitte versuch es gleich nochmal.', 'That did not work — please try again in a moment.'));
        return;
      }
      var units = r.added.reduce(function (n, it) { return n + it.quantity; }, 0);
      // value = whole EUR of the lines that actually landed (not the plan total).
      var value = r.added.reduce(function (n, it) { return n + prices[it.id] * it.quantity; }, 0);
      track('configurator_cart_added', { items: r.added.length, units: units, value: Math.round(value), partial: r.failed.length > 0 });
      if (r.failed.length) {
        cfgPlanStatus(plan, 'warn', L('Im Warenkorb — bis auf: ', 'In your cart — except: ') + r.failed.map(function (it) { return names[it.id]; }).join(', ') + L(' (gerade nicht in dieser Menge verfügbar).', ' (not available in this quantity right now).'));
      } else {
        cfgPlanStatus(plan, 'ok', L('Alles im Warenkorb. ', 'Everything is in your cart. ') + L('Viel Spaß mit deinem Home Gym!', 'Enjoy your home gym!'));
      }
      cfgSetStage('cart');
      cfgEmit('cart', { items: r.added.length, units: units, partial: r.failed.length > 0 });
      try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
      refreshCartUI();
      try {
        var modal = document.querySelector('cart-modal');
        if (modal && typeof modal.reloadContent === 'function') {
          var pr = modal.reloadContent();
          var show = function () { try { if (typeof modal.show === 'function') modal.show(); } catch (e) {} };
          if (pr && typeof pr.then === 'function') pr.then(show, show); else show();
        }
      } catch (e) {}
    });
  }

  // ---------------------------------------------------------------------------
  // Widget shell.
  // ---------------------------------------------------------------------------
  var root, launcher, panel, backdrop, modeBtn, shareBtn, downloadBtn, messagesEl, textarea, sendBtn, micBtn, vmBtn, speakingBtn, noticeEl, welcomeEl;
  // Customer Account tier-3 chrome (built in buildShell / buildWelcome).
  var signInBtn, accountBtn, accountNameEl, historyEl, historyListEl, historyTitleEl, welcomeAuthEl;
  // Desktop layout mode, persisted across page loads. 'sidebar' = docked
  // right-edge sidebar (page makes room, site stays interactive); 'modal' =
  // centered near-fullscreen modal over the blurred backdrop. Mobile ignores
  // this entirely (true fullscreen, see the ≤640px CSS + syncMobileViewport).
  var VIEW_MODE_KEY = 'ms-chat-view-mode';
  function loadViewMode() {
    var v = lsGet(VIEW_MODE_KEY);
    if (v === 'sidebar' || v === 'modal') return v;
    // Migration: the old enlarged state was the focused/large layout, which
    // maps to the modal; everyone else starts in the default sidebar.
    return lsGet('ms-chat-expanded') === '1' ? 'modal' : 'sidebar';
  }
  var state = { open: false, streaming: false, rateLocked: false, viewMode: loadViewMode() };
  var typingEl = null;
  var rateTimer = null;

  // Desktop/mobile branch point — matches the CSS breakpoint (641px). All
  // desktop-mode side effects (page shift, backdrop) and all mobile side
  // effects (scroll lock, visual-viewport sizing) are gated on this so the
  // two parts can never bleed into each other.
  var desktopMq = window.matchMedia ? window.matchMedia('(min-width: 641px)') : null;
  function isDesktop() { return desktopMq ? desktopMq.matches : true; }

  function buildShell() {
    root = el('div', { class: 'ms-chat-root' });

    // The launcher is just the brand orb — no badge/pill on top of it.
    launcher = el('button', { class: 'ms-chat-launcher', type: 'button', 'aria-label': L('Chat öffnen', 'Open chat') });
    launcher.appendChild(logoEl('ms-chat-launcher-logo'));
    launcher.addEventListener('click', togglePanel);

    backdrop = el('div', { class: 'ms-chat-backdrop', 'aria-hidden': 'true' });
    backdrop.addEventListener('click', closePanel);

    // Docked on the configurator page Mo is the home-gym advisor (the
    // client's wording); everywhere else the fitness advisor as before.
    panel = el('div', { class: 'ms-chat-panel', role: 'dialog', 'aria-label': DOCK ? L('KI-Home-Gym-Berater', 'AI home-gym advisor') : L('AI Fitnessberater', 'AI fitness advisor'), 'aria-modal': 'false' });

    var header = el('div', { class: 'ms-chat-header' });
    // The "Mo" wordmark is intentionally omitted from the header so the action
    // buttons get the full width (the welcome state still shows the animated
    // brand orb). With no left title, the actions row is the sole flex child.
    // Exception: docked on the configurator page the panel is part of the
    // page and introduces itself (orb + name + status line).
    if (DOCK) {
      var dockTitle = el('div', { class: 'ms-chat-docktitle' });
      dockTitle.appendChild(logoEl('ms-chat-docktitle-logo'));
      var dockText = el('div', { class: 'ms-chat-docktitle-text' });
      dockText.appendChild(el('div', { class: 'ms-chat-docktitle-name' }, ['Mo', el('span', { class: 'ms-chat-docktitle-long', text: L(' – Dein Home-Gym-Berater', ' – your home-gym advisor') })]));
      dockText.appendChild(el('div', { class: 'ms-chat-docktitle-status', text: L('Online · KI-Home-Gym-Berater', 'Online · AI home-gym advisor') }));
      dockTitle.appendChild(dockText);
      header.appendChild(dockTitle);
    }
    var actions = el('div', { class: 'ms-chat-header-actions' });
    // Feature 7: "Per E-Mail teilen" text button (replaces the share icon).
    // Same action as before — opens the email-capture form (same form the
    // assistant's offer_email_summary tool renders inline). Hidden in a fresh
    // conversation; updateShareBtn() reveals it once the first user message is
    // sent (or a history with messages is restored).
    shareBtn = el('button', { class: 'ms-chat-share', type: 'button', text: L('Per E-Mail teilen', 'Share by email'), 'aria-label': L('Zusammenfassung per E-Mail teilen', 'Share summary by email'), title: L('Zusammenfassung per E-Mail', 'Summary by email') });
    shareBtn.addEventListener('click', function () { openCaptureForm(); });
    actions.appendChild(shareBtn);
    // Signed-in (tier 3): download the branded PDF summary of the ACTIVE thread
    // (CUSTOMER_ACCOUNT.md §11) — replaces the email-share path that's suppressed
    // for signed-in customers. Same quiet-pill chrome as the share button;
    // updateDownloadBtn() reveals it once a downloadable thread exists.
    downloadBtn = el('button', { class: 'ms-chat-download', type: 'button', 'aria-label': L('Zusammenfassung herunterladen', 'Download summary'), title: L('Zusammenfassung herunterladen', 'Download summary') });
    downloadBtn.appendChild(icon('download'));
    downloadBtn.appendChild(el('span', { text: L('Zusammenfassung', 'Summary') }));
    downloadBtn.addEventListener('click', downloadSummary);
    actions.appendChild(downloadBtn);
    // Customer Account (tier 3): the "Anmelden" pill (shown only when the auth
    // state is settled + NOT signed in) and the signed-in identity pill (the
    // customer's name, shown only when signed in — it opens the conversation
    // history). Both hidden by default; reflectAuthState() toggles them so a
    // signed-in customer never sees "Anmelden" and an anonymous one never sees
    // a name. The Anmelden button INITIATES the sign-in flow directly (the same
    // top-level redirect the in-chat sign-in card triggers, task §2) — it used
    // to merely scroll to an already-visible card, which read as "nothing
    // happened".
    signInBtn = el('button', { class: 'ms-chat-signin-btn', type: 'button', text: ACCOUNT_COPY.signInHeader, 'aria-label': L('Mit Konto anmelden', 'Sign in with account') });
    signInBtn.addEventListener('click', function () { initiateLogin(); });
    actions.appendChild(signInBtn);
    accountBtn = el('button', { class: 'ms-chat-account-btn', type: 'button', 'aria-label': ACCOUNT_COPY.historyAria, title: ACCOUNT_COPY.historyAria });
    accountBtn.appendChild(icon('user'));
    accountNameEl = el('span', { class: 'ms-chat-account-name' });
    accountBtn.appendChild(accountNameEl);
    accountBtn.addEventListener('click', openHistory);
    actions.appendChild(accountBtn);
    // Feature 6 (reworked): desktop layout-mode toggle, sidebar ⇄ modal.
    // Hidden on mobile via CSS (.ms-chat-mode); icon/labels set by
    // applyViewMode().
    modeBtn = el('button', { class: 'ms-chat-iconbtn ms-chat-mode', type: 'button' });
    modeBtn.addEventListener('click', toggleViewMode);
    actions.appendChild(modeBtn);
    var newBtn = el('button', { class: 'ms-chat-iconbtn', type: 'button', 'aria-label': L('Neuen Chat starten', 'Start new chat'), title: L('Neuen Chat starten', 'Start new chat') });
    newBtn.appendChild(icon('refresh'));
    newBtn.addEventListener('click', function () { startNewChat(); });
    var closeBtn = el('button', { class: 'ms-chat-iconbtn ms-chat-close', type: 'button', 'aria-label': L('Chat schließen', 'Close chat') });
    closeBtn.appendChild(icon('close'));
    closeBtn.addEventListener('click', closePanel);
    actions.appendChild(newBtn);
    actions.appendChild(closeBtn);
    header.appendChild(actions);
    panel.appendChild(header);

    messagesEl = el('div', { class: 'ms-chat-messages' });
    panel.appendChild(messagesEl);

    var inputbar = el('div', { class: 'ms-chat-inputbar' });
    noticeEl = el('div', { style: 'display:none' });
    inputbar.appendChild(noticeEl);

    // Unified composer: textarea (top, borderless) + control row (bottom,
    // right-aligned mic + send) inside ONE bordered rounded surface.
    var composer = el('div', { class: 'ms-chat-composer' });
    textarea = el('textarea', { class: 'ms-chat-textarea', rows: '1', placeholder: L('Wie kann ich dir helfen?', 'How can I help you?'), 'aria-label': L('Nachricht', 'Message') });
    textarea.addEventListener('input', autoGrow);
    textarea.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
    });
    composer.appendChild(textarea);
    var controls = el('div', { class: 'ms-chat-input-controls' });
    sendBtn = el('button', { class: 'ms-chat-send ms-chat-send--hidden', type: 'button', 'aria-label': L('Senden', 'Send') });
    sendBtn.appendChild(icon('send'));
    sendBtn.addEventListener('click', onSend);
    // Voice input: only render the mic button when the browser supports the Web
    // Speech API (Chrome/Edge/Android). On unsupported browsers (Firefox, some
    // iOS) it's simply absent — typed input is unaffected.
    if (voiceSupported()) {
      micBtn = el('button', { class: 'ms-chat-mic', type: 'button', 'aria-label': L('Spracheingabe starten', 'Start voice input'), 'aria-pressed': 'false', title: L('Spracheingabe', 'Voice input') });
      micBtn.appendChild(icon('mic'));
      micBtn.addEventListener('click', toggleVoice);
      controls.appendChild(micBtn);
      // Voice mode (hands-free conversation loop) toggle — NEXT TO the dictate
      // mic, gated on the same recognition capability check. The dictate mic
      // above is unchanged.
      vmBtn = el('button', { class: 'ms-chat-voicemode', type: 'button', 'aria-label': L('Sprachmodus', 'Voice mode'), 'aria-pressed': 'false', title: L('Sprachmodus', 'Voice mode') });
      vmBtn.appendChild(icon('waveform'));
      vmBtn.addEventListener('click', toggleVoiceMode);
      controls.appendChild(vmBtn);
      // Speaking indicator — shown only while a reply is being read aloud;
      // tapping it stops playback immediately and resumes listening.
      speakingBtn = el('button', { class: 'ms-chat-speaking', type: 'button', 'aria-label': L('Sprachausgabe stoppen', 'Stop voice output'), title: L('Sprachausgabe stoppen', 'Stop voice output'), style: 'display:none' });
      speakingBtn.appendChild(icon('speaking'));
      speakingBtn.addEventListener('click', function () {
        if (!isSpeaking) return;
        endSpeaking();
        if (voiceMode) restartVoiceLoop();
      });
      controls.appendChild(speakingBtn);
    }
    controls.appendChild(sendBtn);
    composer.appendChild(controls);
    // The whole surface reads as the input — clicking its padding (not the
    // buttons) focuses the textarea.
    composer.addEventListener('click', function (e) {
      if (e.target === composer || e.target === controls) { try { textarea.focus(); } catch (err) {} }
    });
    inputbar.appendChild(composer);
    // Footer line: the AI disclaimer plus a subtle, always-available Feedback
    // entry point. Deliberately quiet (a small text link) so it never competes
    // with the composer; opens the short feedback card in the message area.
    var footer = el('div', { class: 'ms-chat-footer' });
    footer.appendChild(el('span', { class: 'ms-chat-disclaimer', text: DOCK ? L('KI-Home-Gym-Berater – Antworten können Fehler enthalten', 'AI home-gym advisor – answers may contain errors') : L('KI-Fitnessberater – Antworten können Fehler enthalten', 'AI fitness advisor – answers may contain errors') }));
    footer.appendChild(el('span', { class: 'ms-chat-footer-sep', 'aria-hidden': 'true', text: '·' }));
    // The dock names the home-gym advisor like its header and disclaimer.
    var feedbackAria = DOCK ? L('Feedback zum Home-Gym-Berater geben', 'Give feedback on the home-gym advisor') : FEEDBACK_COPY.entryAria;
    var feedbackLink = el('button', { class: 'ms-chat-feedback-link', type: 'button', text: FEEDBACK_COPY.entry, title: feedbackAria, 'aria-label': feedbackAria });
    feedbackLink.addEventListener('click', function () { openFeedbackCard(); });
    footer.appendChild(feedbackLink);
    inputbar.appendChild(footer);
    panel.appendChild(inputbar);

    welcomeEl = buildWelcome();

    // Signed-in conversation-history drawer (overlays the panel; inert until a
    // signed-in customer opens it).
    buildHistoryDrawer();

    root.appendChild(launcher);
    root.appendChild(backdrop);
    if (DOCK) {
      // Configurator page: the panel lives in the page's dock host (its own
      // .ms-chat-root carries the tokens); launcher + backdrop stay unused.
      dockRoot = el('div', { class: 'ms-chat-root ms-chat-root--dock' });
      panel.classList.add('ms-chat-panel--docked');
      panel.setAttribute('role', 'region');
      dockRoot.appendChild(panel);
      DOCK.appendChild(dockRoot);
      launcher.classList.add('ms-chat-launcher--hidden');
    } else {
      root.appendChild(panel);
    }
    document.body.appendChild(root);

    applyViewMode();

    // Mobile keyboard handling: the visual viewport shrinks/scrolls when the
    // on-screen keyboard opens — re-pin the panel to the visible area so the
    // input stays just above the keyboard (no-ops while closed / on desktop).
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncMobileViewport);
      window.visualViewport.addEventListener('scroll', syncMobileViewport);
    }
    // Crossing the desktop/mobile breakpoint while open: re-sync the page
    // shift, backdrop and scroll lock for the new branch.
    if (desktopMq) {
      if (desktopMq.addEventListener) desktopMq.addEventListener('change', syncChrome);
      else if (desktopMq.addListener) desktopMq.addListener(syncChrome);
    }
    // Voice mode is hands-free and audible — auto-disable it when the tab is
    // backgrounded so it never keeps the mic open / speaks off-screen.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { if (voiceMode) disableVoiceMode(); return; }
      // Became visible again: re-fetch the cart so a quick-checkout completed in
      // the new tab is reflected here (header count + drawer) without a reload.
      refreshCartUI();
      // Became visible again (task §1): if the chat is open and we're not signed
      // in, re-run detection so a sign-in done on the storefront in another tab
      // is picked up promptly and the "Anmelden" affordance never goes stale.
      // /api/auth/me only — whoami is never re-asked (once per browser
      // session) — and never while a sign-in code is being redeemed.
      if (state.open && !auth.signedIn && !authLinkInflight) detectSignedIn(true);
    });
    // Complementary cart-refresh triggers: window focus catches returning from
    // another window/app even when the Visibility API didn't flip; pageshow
    // catches back/forward navigations restored from the bfcache.
    window.addEventListener('focus', refreshCartUI);
    window.addEventListener('pageshow', refreshCartUI);
  }

  // Feature 7: the share button is hidden until the conversation has at least
  // one message; once shown it stays for the rest of the conversation (the CSS
  // animates the reveal). Re-evaluated on send, restore, new chat and rollback.
  function updateShareBtn() {
    if (!shareBtn) return;
    // Signed-in customers never see the "type your email" capture entry point —
    // their email comes from the account (task §5). Anonymous/email-only keep
    // the exact previous behaviour (auth.signedIn is always false for them).
    shareBtn.classList.toggle('ms-chat-share--visible', messages.length > 0 && !auth.signedIn);
    updateDownloadBtn();
  }

  // The signed-in download button (task §2): shown only once signed in AND the
  // active thread is addressable (a conversationKey + at least one turn), so we
  // never offer a download that the summary endpoint would 404. Hidden entirely
  // for anonymous / email-only — keeping their header byte-identical.
  function updateDownloadBtn() {
    if (!downloadBtn) return;
    var canDownload = !!(auth.signedIn && activeConversationKey && messages.length > 0);
    downloadBtn.classList.toggle('ms-chat-download--visible', canDownload);
  }

  // Keep exactly one desktop mode class on the panel and the toggle button's
  // icon/labels in sync (the icon shows the TARGET mode, so it reads as a
  // layout switch, not a zoom).
  function applyViewMode() {
    if (!panel) return;
    if (DOCK) { panel.classList.remove('ms-chat-panel--sidebar', 'ms-chat-panel--modal'); syncChrome(); return; }
    panel.classList.toggle('ms-chat-panel--sidebar', state.viewMode === 'sidebar');
    panel.classList.toggle('ms-chat-panel--modal', state.viewMode === 'modal');
    panel.setAttribute('aria-modal', state.viewMode === 'modal' ? 'true' : 'false');
    if (modeBtn) {
      var toModal = state.viewMode === 'sidebar';
      modeBtn.replaceChildren(icon(toModal ? 'modal' : 'sidebar'));
      modeBtn.title = toModal ? L('Als zentriertes Fenster öffnen', 'Open as centered window') : L('Als Seitenleiste andocken', 'Dock as sidebar');
      modeBtn.setAttribute('aria-label', modeBtn.title);
    }
    syncChrome();
  }

  function toggleViewMode() {
    // The relayout changes the message area's width AND height — preserve the
    // reading position by keeping the distance from the bottom constant.
    var fromBottom = messagesEl
      ? (messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight)
      : 0;
    state.viewMode = state.viewMode === 'sidebar' ? 'modal' : 'sidebar';
    lsSet(VIEW_MODE_KEY, state.viewMode);
    applyViewMode();
    requestAnimationFrame(function () {
      if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight - messagesEl.clientHeight - fromBottom;
    });
  }

  // Everything OUTSIDE the panel that depends on open-state + mode + viewport:
  // the modal backdrop, the desktop page shift, the mobile scroll lock and the
  // mobile visual-viewport sizing. Single place, so open/close/toggle/resize
  // can never leave a stray backdrop, offset or lock behind.
  function syncChrome() {
    if (DOCK) {
      // Docked into the page: no backdrop, no page shift, no scroll lock.
      if (backdrop) backdrop.classList.remove('ms-chat-backdrop--open');
      setPageShift(false);
      document.documentElement.classList.remove('ms-chat-mobile-open');
      syncMobileViewport();
      if (DOCK) DOCK.classList.toggle('ms-chat-dock--live', state.open);
      return;
    }
    var desktop = isDesktop();
    if (backdrop) backdrop.classList.toggle('ms-chat-backdrop--open', state.open && desktop && state.viewMode === 'modal');
    setPageShift(state.open && desktop && state.viewMode === 'sidebar');
    document.documentElement.classList.toggle('ms-chat-mobile-open', state.open && !desktop);
    syncMobileViewport();
  }

  // Desktop sidebar "make room": margin-right on <html> reflows the storefront
  // next to the docked panel. The -anim class only lives around the change so
  // the page never keeps a permanent transition (or offset) once closed.
  var pageShiftTimer = null;
  function setPageShift(on) {
    var de = document.documentElement;
    if (de.classList.contains('ms-chat-page-shift') === on) return;
    de.classList.add('ms-chat-page-anim');
    de.classList.toggle('ms-chat-page-shift', on);
    if (pageShiftTimer) clearTimeout(pageShiftTimer);
    pageShiftTimer = setTimeout(function () { de.classList.remove('ms-chat-page-anim'); pageShiftTimer = null; }, 420);
  }

  // Mobile keyboard handling: size the OPEN panel to the visual viewport so
  // the input row sits just above the on-screen keyboard and the message list
  // shrinks to fit. translateY(offsetTop) re-pins the fixed panel when iOS
  // scrolls the layout viewport to reveal the focused input. On desktop (or
  // closed) all inline styles are cleared so the CSS modes rule alone.
  function syncMobileViewport() {
    if (!panel) return;
    var vv = window.visualViewport;
    if (state.open && !isDesktop() && vv && !DOCK) {
      var pinned = messagesEl && (messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight) < 120;
      panel.style.height = Math.round(vv.height) + 'px';
      panel.style.transform = 'translateY(' + Math.round(vv.offsetTop) + 'px)';
      // Keyboard opening shrinks the list — keep the latest message + input
      // in view if the user was already reading the bottom.
      if (pinned && messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight;
    } else {
      panel.style.height = '';
      panel.style.transform = '';
    }
  }

  // Welcome state: the animated brand orb is the hero (an empty chat has
  // nothing to read, so full motion is fine here — reduced-motion still
  // freezes it via CSS). The prompt line lives in the composer placeholder
  // ("Wie kann ich dir helfen?"). The starter prompt chips were removed
  // (they saw no use and pushed the sign-in surface below the fold on
  // mobile); beneath the orb sits ONLY the auth slot — the sign-in card for
  // settled-anonymous visitors, so it is visible without scrolling. Signed-in
  // customers see the orb alone (owner request: a clean welcome); their
  // marketing opt-in is asked once, by the first-message popup.
  function buildWelcome() {
    var w = el('div', { class: 'ms-chat-welcome' });
    w.appendChild(logoEl('ms-chat-welcome-logo'));
    // Sign-in benefits card slot (filled by updateWelcomeAuth when the auth
    // state settles as not-signed-in; empty otherwise).
    welcomeAuthEl = el('div', { class: 'ms-chat-welcome-auth' });
    w.appendChild(welcomeAuthEl);
    return w;
  }

  // Auto-grow up to the cap (must match the CSS max-height), then the textarea
  // scrolls internally — the composer/panel never grow past it. Also the single
  // sync point for the appear-on-type send button: every path that changes the
  // input's value (typing, voice dictation, send-clear, error-restore) already
  // calls autoGrow().
  function autoGrow() {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    if (sendBtn) sendBtn.classList.toggle('ms-chat-send--hidden', !textarea.value.trim());
  }

  function togglePanel() { state.open ? closePanel() : openPanel(); }
  function openPanel(noFocus) {
    if (state.open) return;
    state.open = true;
    // Engagement layer: an opened chat ends the nudge's eligibility for this
    // session.
    ssSet(SS_CHAT_OPENED, '1');
    removeNudge();
    // Customer Account: resolve signed-in state on first open (lazy — no
    // network for pure-anonymous visitors; see resolveAuthOnOpen). Arm the
    // history auto-surface (task §1); it flushes once auth has settled (now if
    // already settled, else when the probe resolves via applyAuth).
    autoHistoryArmed = true;
    resolveAuthOnOpen();
    flushAutoHistory();
    panel.classList.add('ms-chat-panel--open');
    launcher.classList.add('ms-chat-launcher--hidden');
    syncChrome(); // backdrop (modal) / page shift (sidebar) / mobile lock+size
    // Re-measure the textarea now that the panel is visible — the init-time
    // autoGrow() ran on a hidden panel (scrollHeight 0), which left the field
    // mis-sized (internally overflowing) on first open.
    autoGrow();
    scrollToBottom();
    // Docked: never pull the page (or the mobile keyboard) to the input.
    if (!DOCK && noFocus !== true) setTimeout(function () { try { textarea.focus(); } catch (e) {} }, 50);
    track('chat_opened', {});
  }
  function closePanel() {
    if (!state.open || DOCK) return; // the dock has no close
    state.open = false;
    if (voiceMode) disableVoiceMode(); // stop playback + mic, return to normal
    panel.classList.remove('ms-chat-panel--open');
    launcher.classList.remove('ms-chat-launcher--hidden');
    syncChrome(); // restores the page: no leftover offset, backdrop or lock
    track('chat_closed', {});
  }

  function scrollToBottom() {
    requestAnimationFrame(function () { messagesEl.scrollTop = messagesEl.scrollHeight; });
  }
  // The element's top at the top of the message list (or the bottom, when
  // everything below it fits).
  function scrollToStart(node) {
    requestAnimationFrame(function () {
      try {
        var top = messagesEl.scrollTop + node.getBoundingClientRect().top - messagesEl.getBoundingClientRect().top - 12;
        messagesEl.scrollTop = Math.min(top, messagesEl.scrollHeight - messagesEl.clientHeight);
      } catch (e) { messagesEl.scrollTop = messagesEl.scrollHeight; }
    });
  }

  function updateInputState() {
    var disabled = state.streaming || state.rateLocked;
    if (textarea) textarea.disabled = disabled;
    if (sendBtn) sendBtn.disabled = disabled;
    if (micBtn) micBtn.disabled = disabled;
    if (disabled) stopVoice();
  }

  // ---------------------------------------------------------------------------
  // Voice input (Web Speech API). Dictates into the textarea in German; the
  // mic button is only created when the API exists (see buildShell). Audio is
  // handled by the browser's own speech service — no audio touches our backend.
  // ---------------------------------------------------------------------------
  var SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
  var recognition = null;
  var recognizing = false;

  // Voice mode (hands-free conversation loop) state. ALL additive: while
  // voiceMode stays false every branch below is skipped and dictation behaves
  // exactly as before.
  var voiceMode = false;        // the loop is active
  var ttsAudio = null;          // reused <audio>, unlocked on the enabling gesture
  var isSpeaking = false;       // a reply is currently being read aloud
  var ttsBackoffUntil = 0;      // epoch ms; while in the future, skip /api/tts (429 Retry-After)
  var ttsHintShown = false;     // "Sprachausgabe nicht verfügbar" shown once
  var voiceRestartTimer = null; // debounce for re-arming the mic
  var currentBlobUrl = null;    // object URL of the playing MP3 (revoked on stop)
  var speakSeq = 0;             // guards async TTS callbacks against a newer reply
  var streamTts = null;         // active streaming-TTS session (per-sentence queue), or null

  function voiceSupported() { return !!SpeechRec; }

  function setMicState(on) {
    recognizing = on;
    if (!micBtn) return;
    micBtn.classList.toggle('ms-chat-mic--recording', on);
    micBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    micBtn.setAttribute('aria-label', on ? L('Spracheingabe beenden', 'Stop voice input') : L('Spracheingabe starten', 'Start voice input'));
    micBtn.title = on ? L('Spracheingabe beenden', 'Stop voice input') : L('Spracheingabe', 'Voice input');
  }

  function stopVoice() {
    if (recognition && recognizing) { try { recognition.stop(); } catch (e) {} }
  }

  function startVoice() {
    if (!SpeechRec || recognizing) return;
    if (state.streaming || state.rateLocked) return;
    recognition = new SpeechRec();
    recognition.lang = 'de-DE';
    recognition.interimResults = true;
    recognition.continuous = false;
    // Dictate-once appends to whatever is already typed; voice mode listens
    // fresh (and never clobbers the textarea, so the user can still type).
    var base = voiceMode ? '' : (textarea.value.trim() ? textarea.value.trim() + ' ' : '');
    var finalT = '';
    recognition.onresult = function (e) {
      var interim = '';
      for (var i = e.resultIndex; i < e.results.length; i++) {
        var seg = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalT += seg;
        else interim += seg;
      }
      if (!voiceMode) {
        textarea.value = base + finalT + interim;
        autoGrow();
      }
    };
    recognition.onerror = function (e) {
      setMicState(false);
      if (e && (e.error === 'not-allowed' || e.error === 'service-not-allowed')) {
        showNotice('warn', L('Mikrofonzugriff wurde blockiert. Bitte erlaube ihn in den Browser-Einstellungen.', 'Microphone access was blocked. Please allow it in your browser settings.'));
        if (voiceMode) disableVoiceMode(); // can't run the loop without the mic
      }
    };
    recognition.onend = function () {
      setMicState(false);
      if (voiceMode) {
        // Recognition end / final result -> send the transcript via the normal
        // send path. Silence (no transcript) just re-arms the mic to keep the
        // hands-free loop alive.
        var said = finalT.trim();
        if (said) voiceSubmit(said);
        else restartVoiceLoop();
        return;
      }
      try { textarea.focus(); } catch (e) {}
    };
    try {
      recognition.start();
      setMicState(true);
      clearNotice();
    } catch (e) {
      setMicState(false);
      if (voiceMode) restartVoiceLoop();
    }
  }

  function toggleVoice() {
    if (recognizing) stopVoice();
    else startVoice();
  }

  // ---------------------------------------------------------------------------
  // Voice mode — hands-free conversation loop. Reuses the recognition above and
  // the existing send path; adds TTS playback of the completed reply. The loop:
  //   listen -> send final transcript -> on stream complete, speak the reply's
  //   plain text (markdown stripped; tool cards never read) -> on playback end,
  //   listen again. Toggling off, closing the panel, or the tab going hidden
  //   all stop it (handled elsewhere).
  //
  // iOS AUTOPLAY: programmatic audio is only allowed once unlocked by a user
  // gesture. The <audio> element is created AND played (a tiny silent clip)
  // inside the toggle's click handler (enableVoiceMode -> unlockAudio), then
  // reused for every later reply — so playback fired when a stream completes
  // (not itself a gesture) is permitted. This is the real iOS risk; without it
  // the first reply would be silently blocked.
  // ---------------------------------------------------------------------------

  // Minimal valid silent WAV built in code (so no opaque base64 blob is
  // shipped) — used ONLY to unlock the <audio> element on the enabling gesture.
  function silentWavUrl() {
    try {
      var len = 8, buf = new ArrayBuffer(44 + len), dv = new DataView(buf);
      function s(o, str) { for (var i = 0; i < str.length; i++) dv.setUint8(o + i, str.charCodeAt(i)); }
      s(0, 'RIFF'); dv.setUint32(4, 36 + len, true); s(8, 'WAVE');
      s(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
      dv.setUint32(24, 8000, true); dv.setUint32(28, 8000, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
      s(36, 'data'); dv.setUint32(40, len, true);
      for (var i = 0; i < len; i++) dv.setUint8(44 + i, 128); // 8-bit silence
      var bytes = new Uint8Array(buf), bin = '';
      for (var j = 0; j < bytes.length; j++) bin += String.fromCharCode(bytes[j]);
      return 'data:audio/wav;base64,' + btoa(bin);
    } catch (e) { return ''; }
  }

  function unlockAudio() {
    try {
      if (!ttsAudio) {
        ttsAudio = new Audio();
        ttsAudio.preload = 'auto';
        ttsAudio.addEventListener('ended', onPlaybackEnded);
      }
      var u = silentWavUrl();
      if (!u) return;
      ttsAudio.src = u;
      var p = ttsAudio.play();
      if (p && p.then) p.then(function () { try { ttsAudio.pause(); ttsAudio.currentTime = 0; } catch (e) {} }).catch(function () {});
    } catch (e) {}
  }

  function setVmState(on) {
    if (!vmBtn) return;
    vmBtn.classList.toggle('ms-chat-voicemode--on', on);
    vmBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    vmBtn.setAttribute('aria-label', on ? L('Sprachmodus beenden', 'Stop voice mode') : L('Sprachmodus', 'Voice mode'));
    vmBtn.title = L('Sprachmodus', 'Voice mode');
  }

  function toggleVoiceMode() {
    if (voiceMode) disableVoiceMode();
    else enableVoiceMode();
  }

  function enableVoiceMode() {
    if (voiceMode) return;
    voiceMode = true;
    unlockAudio(); // MUST run inside this click gesture (iOS autoplay unlock)
    setVmState(true);
    ttsHintShown = false;
    track('voice_mode_on', {}); // fail-silent KPI, no transcript content
    clearNotice();
    if (!state.streaming && !state.rateLocked && !isSpeaking) startVoice();
  }

  function disableVoiceMode() {
    if (!voiceMode) return;
    voiceMode = false;
    setVmState(false);
    if (voiceRestartTimer) { clearTimeout(voiceRestartTimer); voiceRestartTimer = null; }
    endSpeaking(); // stop any playback
    stopVoice();   // stop the mic
    track('voice_mode_off', {});
  }

  // Re-arm the mic after a small debounce, gated so it never fights streaming,
  // rate locks, an in-flight reply, or a closed panel.
  function restartVoiceLoop() {
    if (!voiceMode) return;
    if (voiceRestartTimer) clearTimeout(voiceRestartTimer);
    voiceRestartTimer = setTimeout(function () {
      voiceRestartTimer = null;
      if (voiceMode && !recognizing && !isSpeaking && !state.streaming && !state.rateLocked && state.open) startVoice();
    }, 350);
  }

  // Send a spoken transcript through the SAME path a typed message uses.
  function voiceSubmit(text) {
    if (state.streaming || state.rateLocked || !text) return;
    endSpeaking();          // a new turn supersedes any current playback
    sendMessage(text, pageContextForSend());
  }

  function showSpeakingIndicator() {
    if (!speakingBtn) return;
    speakingBtn.style.display = '';
    speakingBtn.classList.add('ms-chat-speaking--on');
  }
  function hideSpeakingIndicator() {
    if (!speakingBtn) return;
    speakingBtn.style.display = 'none';
    speakingBtn.classList.remove('ms-chat-speaking--on');
  }

  function startSpeaking() { isSpeaking = true; showSpeakingIndicator(); }

  // Stop playback (MP3 or speechSynthesis) and tidy up — does NOT re-arm the
  // mic on its own (callers decide whether the loop continues).
  function endSpeaking() {
    isSpeaking = false;
    hideSpeakingIndicator();
    // Tear down any streaming-TTS session and revoke its buffered clips. An
    // interrupt (tap / text-send / new voice turn / disable) must stop the
    // queue cleanly — nothing buffered keeps playing.
    if (streamTts) {
      var s = streamTts;
      streamTts = null;
      for (var k in s.clips) { if (s.clips[k] && s.clips[k].url) { try { URL.revokeObjectURL(s.clips[k].url); } catch (e) {} } }
    }
    try { if (ttsAudio) ttsAudio.pause(); } catch (e) {}
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) {}
    if (currentBlobUrl) { try { URL.revokeObjectURL(currentBlobUrl); } catch (e) {} currentBlobUrl = null; }
  }

  // Natural end of one played clip. When a streaming-TTS queue is driving
  // playback, advance to the next clip in seq order instead of ending the turn;
  // otherwise (single-shot path) this is the end of the reply -> resume listening.
  function onPlaybackEnded() {
    if (streamTts && !streamTts.aborted && streamTts.playing) { streamTtsAdvance(); return; }
    if (!isSpeaking) return;
    endSpeaking();
    if (voiceMode) restartVoiceLoop();
  }

  // After Mo's reply stream completes.
  //
  // Streaming-TTS path: if a per-sentence session is running, audio is ALREADY
  // playing (it started ~1 s into the stream). Flush the trailing partial as a
  // final chunk and let the queue drain — playback finishing re-arms the mic.
  // If that session already fell back, hand the unspoken remainder to the
  // single-shot path. Otherwise (tool-only reply, TTS backoff at stream start,
  // or no text) fall back to the existing play-after-complete behavior.
  function voiceAfterReply(parts, errored) {
    if (!voiceMode) return;
    if (streamTts) {
      if (errored) { endSpeaking(); restartVoiceLoop(); return; }
      streamTts.done = true;
      if (streamTts.aborted) { streamTtsDoFallback(); return; }
      streamTtsDrain(true); // flush the held trailing partial as the last chunk
      if (streamTts && !streamTts.playing && streamTts.play >= streamTts.emitted) {
        streamTtsFinish(); // nothing buffered and nothing pending -> done now
      } else if (streamTts) {
        streamTtsPump();   // ensure the queue is progressing
      }
      return;
    }
    if (errored) { restartVoiceLoop(); return; }
    var speech = plainTextFromParts(parts);
    if (speech) speakReply(speech);
    else restartVoiceLoop();
  }

  // Assistant message -> spoken text: text parts only (tool cards are NEVER
  // read aloud), with the markdown subset stripped.
  function plainTextFromParts(parts) {
    var out = '';
    parts = parts || [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] && parts[i].type === 'text') out += parts[i].text || '';
    }
    return stripMarkdownForSpeech(out);
  }

  function stripMarkdownForSpeech(s) {
    s = String(s == null ? '' : s);
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1'); // [label](url) -> label
    s = s.replace(/\*\*([^*]+)\*\*/g, '$1');          // **bold** -> bold
    s = s.replace(/\*([^*]+)\*/g, '$1');              // *italic* -> italic
    s = s.replace(/[*_`#>]/g, '');                    // stray markdown artifacts
    return s.replace(/\s+/g, ' ').trim();
  }

  function pickGermanVoice(synth) {
    try {
      var vs = synth.getVoices() || [];
      for (var i = 0; i < vs.length; i++) {
        if (vs[i] && vs[i].lang && String(vs[i].lang).toLowerCase().indexOf('de') === 0) return vs[i];
      }
    } catch (e) {}
    return null;
  }

  // POST /api/tts (standard auth headers) and play the MP3. On any non-2xx
  // (incl. 502 upstream_unavailable, the documented "use the local voice"
  // signal) or a play failure -> browser speechSynthesis fallback. 429 records
  // a Retry-After backoff so we stop hitting the endpoint for the window and go
  // straight to the local voice meanwhile.
  function speakReply(text) {
    var seq = ++speakSeq;
    startSpeaking();
    if (Date.now() < ttsBackoffUntil) { speakViaSynthesis(text, seq); return; }
    fetch(API_BASE + '/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
      body: JSON.stringify({ text: text })
    }).then(function (res) {
      if (seq !== speakSeq || !voiceMode || !isSpeaking) return; // superseded / cancelled
      if (!res.ok) {
        if (res.status === 429) {
          var ra = parseInt(res.headers.get('Retry-After'), 10);
          if (!isFinite(ra) || ra <= 0) ra = 300; // tts bucket window
          ttsBackoffUntil = Date.now() + ra * 1000;
        }
        speakViaSynthesis(text, seq);
        return;
      }
      return res.blob().then(function (blob) {
        if (seq !== speakSeq || !voiceMode || !isSpeaking) return;
        playBlob(blob, text, seq);
      });
    }).catch(function () {
      if (seq === speakSeq && isSpeaking) speakViaSynthesis(text, seq);
    });
  }

  function playBlob(blob, text, seq) {
    try {
      if (currentBlobUrl) { try { URL.revokeObjectURL(currentBlobUrl); } catch (e) {} }
      currentBlobUrl = URL.createObjectURL(blob);
      ttsAudio.src = currentBlobUrl;
      var p = ttsAudio.play();
      if (p && p.then) {
        p.then(function () { if (seq === speakSeq) track('voice_reply_played', {}); })
         .catch(function () { if (seq === speakSeq && isSpeaking) speakViaSynthesis(text, seq); });
      } else {
        track('voice_reply_played', {});
      }
    } catch (e) {
      if (seq === speakSeq && isSpeaking) speakViaSynthesis(text, seq);
    }
  }

  function speakViaSynthesis(text, seq) {
    try {
      var synth = window.speechSynthesis;
      if (!synth || typeof window.SpeechSynthesisUtterance === 'undefined') { ttsUnavailable(); return; }
      var u = new window.SpeechSynthesisUtterance(text);
      u.lang = 'de-DE';
      var v = pickGermanVoice(synth);
      if (v) u.voice = v;
      u.onstart = function () { if (seq === speakSeq) track('voice_reply_played', {}); };
      u.onend = function () { if (seq === speakSeq) onPlaybackEnded(); };
      u.onerror = function () { if (seq === speakSeq) onPlaybackEnded(); };
      synth.cancel();
      synth.speak(u);
    } catch (e) { ttsUnavailable(); }
  }

  // Neither remote TTS nor local speech worked: show a small hint once, keep
  // the mic loop running (input-only).
  function ttsUnavailable() {
    if (!ttsHintShown) { ttsHintShown = true; showNotice('warn', L('Sprachausgabe nicht verfügbar.', 'Voice output unavailable.')); }
    onPlaybackEnded();
  }

  // ---------------------------------------------------------------------------
  // Streaming voice mode — audio WHILE the text streams (API_CONTRACT.md §8).
  //
  // As chat tokens arrive we split them into sentences, POST each to
  // `/api/tts` with `{ stream: true, seq }` and push the returned MP3 into a
  // playback QUEUE that plays clips strictly in seq order — so speech begins
  // ~1 s into the stream instead of after the whole answer. Requests complete
  // out of order; we buffer later-but-ready clips and only advance when the
  // next-in-order clip has arrived. The same unlocked `ttsAudio` element is
  // reused, so iOS autoplay keeps working and clips never overlap.
  //
  // This is layered ON TOP of the existing play-after-complete path, which
  // stays the fallback: any chunk failure (429 / 502 / network / play error)
  // stops the per-sentence path and hands the unspoken remainder to
  // speakReply() (single-shot full message -> speechSynthesis), unchanged.
  // ---------------------------------------------------------------------------

  function startStreamTts() {
    ++speakSeq; // supersede any single-shot TTS callback still in flight
    streamTts = {
      next: 0,        // next seq to assign
      emitted: 0,     // count of chunks emitted (== highest seq + 1)
      pending: '',    // text accumulated but not yet emitted as a chunk
      clips: {},      // seq -> { status:'loading'|'ready', url, text }
      play: 0,        // seq we are waiting to play next
      playing: false, // a clip is currently playing
      done: false,    // chat stream finished -> pending flushed, no more chunks
      aborted: false, // fell back to the single-shot / synthesis path
      tracked: false  // KPI fired once per spoken reply
    };
    isSpeaking = true; // block the listen loop while this reply will be spoken
  }

  // Feed one chat text-delta into the streaming session (voice mode only).
  function streamTtsFeed(text) {
    if (!voiceMode || !text) return;
    if (!streamTts) {
      // In a TTS backoff window: don't open a streaming session — let
      // voiceAfterReply() fall back to the single-shot path (which goes
      // straight to speechSynthesis while backed off).
      if (Date.now() < ttsBackoffUntil) return;
      startStreamTts();
    }
    if (!streamTts) return;
    streamTts.pending += text;
    // After a fallback we keep accumulating the rest of the text so the
    // single-shot fallback can speak everything not yet heard; no more chunks.
    if (streamTts.aborted) return;
    streamTtsDrain(false);
  }

  // German abbreviations whose trailing "." must NOT end a sentence (so "z. B.",
  // "usw.", "ca." never split a thought into a choppy clip). Any SINGLE-letter
  // token before a dot (initials, the "z."/"B." of "z. B.") is treated the same.
  // Matched case-insensitively against the word right before the dot.
  var TTS_ABBR = {
    usw: 1, etc: 1, ca: 1, bzw: 1, ggf: 1, inkl: 1, evtl: 1, max: 1, min: 1,
    nr: 1, tel: 1, vgl: 1, sog: 1, bspw: 1, mind: 1, ggfs: 1, zzgl: 1, abzgl: 1
  };

  // Does the terminator at index i in `s` actually END a sentence? "\n ! ? …"
  // always do; "." does NOT when it's a decimal (3.5), a mid-token dot
  // (google.com) or part of a German abbreviation (z. B., usw.).
  function isTtsSentenceEnd(s, i) {
    var c = s.charAt(i);
    if (c === '\n' || c === '!' || c === '?' || c === '…') return true;
    if (c !== '.') return false;
    var prev = i > 0 ? s.charAt(i - 1) : '';
    var next = i + 1 < s.length ? s.charAt(i + 1) : '';
    if (next >= '0' && next <= '9' && prev >= '0' && prev <= '9') return false; // 3.5
    // A real end is followed by whitespace / buffer-end / a closing mark; a
    // letter or digit right after means we're still inside a token (google.com).
    if (next && !/\s/.test(next) && '.!?…)]»"’”'.indexOf(next) === -1) return false;
    var m = /([A-Za-zÄÖÜäöüß]+)$/.exec(s.slice(i > 12 ? i - 12 : 0, i));
    if (m) { var w = m[1].toLowerCase(); if (w.length === 1 || TTS_ABBR[w]) return false; }
    return true;
  }

  // Mirror of the backend's canonical splitIntoTtsChunks() (API_CONTRACT.md §8.3 /
  // src/lib/tts-text.mjs) so the widget's chunk boundaries match what the server
  // synthesizes. Emit the moment a sentence terminator (. ! ? …) or newline
  // completes a sentence; COALESCE fragments shorter than minChars (40, measured
  // after Markdown stripping) into the next sentence so we never synthesize a
  // one-word clip; FORCE-CUT any run longer than maxChars (220) at the last clause
  // boundary (, ; : – —) or space, so a long opening sentence can't stall the
  // first audio (the ~1 s start). Returns { chunks, rest }: emitted chunks (raw
  // substrings, in stream order) and the unterminated tail to carry into the next
  // delta. With { flush:true } (stream end) the tail is emitted too.
  function splitIntoTtsChunks(buf, opts) {
    var MIN = 40, MAX = 220, CLAUSE = ',;:–—';
    var flush = !!(opts && opts.flush);
    var work = String(buf == null ? '' : buf);
    var chunks = [];
    for (;;) {
      if (!work) break;
      var cut = -1, lastClause = -1, lastSpace = -1;
      for (var i = 0; i < work.length; i++) {
        var ch = work.charAt(i);
        if (isTtsSentenceEnd(work, i)) {
          while (i + 1 < work.length && isTtsSentenceEnd(work, i + 1)) i++; // absorb "...", "?!"
          var end = i + 1;
          if (stripMarkdownForSpeech(work.slice(0, end)).length >= MIN) { cut = end; break; }
          continue; // sentence too short so far -> coalesce into the next one
        }
        if (CLAUSE.indexOf(ch) !== -1) lastClause = i;
        else if (ch === ' ' || ch === '\t') lastSpace = i;
        if (i + 1 >= MAX) { // long run, no usable sentence end yet -> force-cut
          cut = lastClause >= 0 ? lastClause + 1 : (lastSpace >= 0 ? lastSpace + 1 : i + 1);
          break;
        }
      }
      if (cut <= 0) break; // nothing emittable yet -> hold the whole rest
      chunks.push(work.slice(0, cut));
      work = work.slice(cut);
    }
    if (flush && work) { chunks.push(work); work = ''; }
    return { chunks: chunks, rest: work };
  }

  // Drive the splitter over the accumulated `pending` text and fire one TTS
  // request per complete chunk, in stream order. `flush` (stream end) drains the
  // held trailing partial too. The unterminated tail stays in `pending` for the
  // next delta.
  function streamTtsDrain(flush) {
    var s = streamTts;
    if (!s || s.aborted) return;
    var r = splitIntoTtsChunks(s.pending, { flush: flush });
    s.pending = r.rest;
    for (var i = 0; i < r.chunks.length; i++) {
      streamTtsEmit(r.chunks[i]);
      if (!streamTts || streamTts.aborted) return; // a chunk failed -> fell back
    }
  }

  // Request one chunk's audio and slot it into the queue at its seq.
  function streamTtsEmit(text) {
    var s = streamTts;
    if (!s) return;
    var clean = stripMarkdownForSpeech(text);
    if (!clean) return; // nothing speakable (pure markdown / whitespace)
    var seq = s.next++;
    s.emitted = s.next;
    s.clips[seq] = { status: 'loading', url: null, text: clean };
    fetch(API_BASE + '/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
      body: JSON.stringify({ text: clean, stream: true, seq: seq })
    }).then(function (res) {
      if (streamTts !== s || s.aborted) return; // superseded / already fell back
      if (!res.ok) {
        if (res.status === 429) {
          var ra = parseInt(res.headers.get('Retry-After'), 10);
          if (!isFinite(ra) || ra <= 0) ra = 300; // tts-stream bucket window
          ttsBackoffUntil = Date.now() + ra * 1000;
        }
        streamTtsFallback(); // any non-2xx -> stop per-sentence path
        return;
      }
      return res.blob().then(function (blob) {
        if (streamTts !== s || s.aborted) return;
        var clip = s.clips[seq];
        if (!clip) return;
        clip.url = URL.createObjectURL(blob);
        clip.status = 'ready';
        streamTtsPump(); // may be the clip we were waiting on
      });
    }).catch(function () {
      if (streamTts === s && !s.aborted) streamTtsFallback();
    });
  }

  // Play the next-in-order clip if it's ready and nothing is currently playing.
  function streamTtsPump() {
    var s = streamTts;
    if (!s || s.aborted || s.playing) return;
    var clip = s.clips[s.play];
    if (!clip || clip.status !== 'ready') return; // not emitted yet / still loading -> wait
    s.playing = true;
    if (currentBlobUrl) { try { URL.revokeObjectURL(currentBlobUrl); } catch (e) {} }
    currentBlobUrl = clip.url; clip.url = null; // ownership moves to currentBlobUrl
    try {
      ttsAudio.src = currentBlobUrl;
      var p = ttsAudio.play();
      if (!s.tracked) { s.tracked = true; showSpeakingIndicator(); track('voice_reply_played', {}); }
      if (p && p.then) p.catch(function () { if (streamTts === s) streamTtsFallback(); });
    } catch (e) { streamTtsFallback(); }
  }

  // One clip finished -> revoke it and advance to the next seq.
  function streamTtsAdvance() {
    var s = streamTts;
    if (!s) return;
    s.playing = false;
    if (currentBlobUrl) { try { URL.revokeObjectURL(currentBlobUrl); } catch (e) {} currentBlobUrl = null; }
    if (s.clips[s.play]) delete s.clips[s.play];
    s.play++;
    streamTtsPump(); // play the next clip if it's already buffered
    if (streamTts === s && !s.playing && s.done && s.play >= s.emitted) streamTtsFinish();
  }

  // Whole reply spoken: tear down and resume listening.
  function streamTtsFinish() {
    streamTts = null;
    isSpeaking = false;
    hideSpeakingIndicator();
    if (voiceMode) restartVoiceLoop();
  }

  // A chunk failed: stop the per-sentence path. Move the text of every
  // emitted-but-unplayed clip back in front of `pending` (in order) so the
  // fallback speaks everything not yet heard.
  function streamTtsFallback() {
    var s = streamTts;
    if (!s || s.aborted) return;
    s.aborted = true;
    try { ttsAudio.pause(); } catch (e) {}
    if (currentBlobUrl) { try { URL.revokeObjectURL(currentBlobUrl); } catch (e) {} currentBlobUrl = null; }
    var unplayed = '';
    for (var k = s.play; k < s.emitted; k++) {
      if (s.clips[k]) {
        if (s.clips[k].url) { try { URL.revokeObjectURL(s.clips[k].url); } catch (e) {} }
        unplayed += (s.clips[k].text || '') + ' ';
      }
    }
    s.clips = {};
    s.playing = false;
    s.pending = unplayed + s.pending;
    // If the chat stream already finished, speak the remainder now; otherwise
    // voiceAfterReply() calls streamTtsDoFallback() once the rest has streamed.
    if (s.done) streamTtsDoFallback();
  }

  // Hand the unspoken remainder to the existing single-shot path, which already
  // handles 429-backoff -> speechSynthesis and 502 -> speechSynthesis.
  function streamTtsDoFallback() {
    var s = streamTts;
    if (!s) return;
    var text = stripMarkdownForSpeech(s.pending);
    streamTts = null;
    if (!text) { isSpeaking = false; hideSpeakingIndicator(); if (voiceMode) restartVoiceLoop(); return; }
    speakReply(text);
  }

  function showWelcome() {
    messagesEl.replaceChildren(welcomeEl);
  }
  function clearWelcome() {
    if (welcomeEl && welcomeEl.parentNode === messagesEl) messagesEl.removeChild(welcomeEl);
  }

  // ---------------------------------------------------------------------------
  // Notices.
  // ---------------------------------------------------------------------------
  function showNotice(kind, text, actionLabel, actionFn) {
    noticeEl.className = 'ms-chat-notice ' + (kind === 'warn' ? 'ms-chat-notice--warn' : 'ms-chat-notice--info');
    noticeEl.replaceChildren();
    noticeEl.appendChild(el('div', { text: text }));
    if (actionLabel) {
      var b = el('button', { class: 'ms-chat-btn ms-chat-btn--secondary', type: 'button' }, [actionLabel]);
      b.addEventListener('click', actionFn);
      noticeEl.appendChild(b);
    }
    noticeEl.style.display = 'block';
  }
  function clearNotice() { noticeEl.style.display = 'none'; noticeEl.replaceChildren(); }

  // Build an assistant row: brand avatar + a stacked content column. Returns
  // both so callers append bubbles/cards into `.content`.
  function assistantRow() {
    var row = el('div', { class: 'ms-chat-row ms-chat-row--assistant' });
    row.appendChild(logoEl('ms-chat-avatar'));
    var content = el('div', { class: 'ms-chat-asst-content' });
    row.appendChild(content);
    return { row: row, content: content };
  }

  // An assistant-area error line that lives in the message list.
  function showMessageError(text) {
    clearWelcome();
    var ar = assistantRow();
    ar.content.appendChild(el('div', { class: 'ms-chat-bubble ms-chat-bubble--assistant', text: text }));
    messagesEl.appendChild(ar.row);
    scrollToBottom();
  }

  // ---------------------------------------------------------------------------
  // Rendering: user + assistant messages.
  // ---------------------------------------------------------------------------
  function renderUserMessage(msg) {
    clearWelcome();
    var text = textOfMessage(msg);
    var row = el('div', { class: 'ms-chat-row ms-chat-row--user' });
    row.appendChild(el('div', { class: 'ms-chat-bubble ms-chat-bubble--user', text: text }));
    messagesEl.appendChild(row);
    cfgSyncReplies(); // answer chips above this message are spent
    return row;
  }

  function textOfMessage(msg) {
    var parts = msg.parts || [];
    var out = '';
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] && parts[i].type === 'text') out += parts[i].text || '';
    }
    return out;
  }

  // ctx-based incremental renderer, reused for streaming and restore.
  function newAssistantCtx() {
    clearWelcome();
    var ar = assistantRow();
    messagesEl.appendChild(ar.row);
    return { row: ar.row, content: ar.content, activeText: null, tools: {} };
  }

  function renderPartIntoCtx(ctx, part) {
    if (!part || typeof part.type !== 'string') return;
    var type = part.type;

    if (type === 'text') {
      var t = part.text != null ? part.text : (part.delta != null ? part.delta : '');
      if (!t) return;
      if (!ctx.activeText) {
        var node = el('div', { class: 'ms-chat-bubble ms-chat-bubble--assistant' });
        ctx.content.appendChild(node);
        ctx.activeText = { str: '', node: node };
      }
      ctx.activeText.str += t;
      // While the reply is still streaming, withhold any incomplete trailing
      // Markdown token so half-syntax never flashes; a completed run (text-end /
      // finalize) and restored history both render in full.
      renderMarkdownInto(ctx.activeText.node, ctx.activeText.str, state.streaming);
      scrollToBottom();
      return;
    }

    var name = resolveToolName(type);
    if (!name) return;
    if (SILENT_TOOLS.indexOf(name) !== -1) return; // consume silently
    var input = part.input != null ? part.input : part.args;
    if (input == null) return; // wait until args have streamed

    ctx.activeText = null; // a tool card ends the current text run

    var key = part.toolCallId || (name + ':' + JSON.stringify(input));
    var inputKey = JSON.stringify(input);
    // A restored showroom card decides with its stored result (showroomPick).
    if (name === 'suggest_showroom' && part.output !== undefined) showroomResult(key, part.output);
    var holder = ctx.tools[key];
    if (holder) {
      if (holder.inputKey === inputKey) return; // unchanged -> no re-render
      holder.inputKey = inputKey;
    } else {
      holder = { node: el('div'), inputKey: inputKey };
      ctx.tools[key] = holder;
      ctx.content.appendChild(holder.node);
    }
    buildToolCard(name, input, key, !!ctx.live).then(function (cardEl) {
      holder.node.replaceChildren();
      if (!cardEl) return;
      holder.node.appendChild(cardEl);
      // A live option group is often taller than the chat: start at Mo's
      // reply (text, step title, first option) instead of its last card.
      if (ctx.live && name === 'offer_plan_options') scrollToStart(ctx.row);
      else scrollToBottom();
    }).catch(function () { /* render nothing on failure */ });
  }

  function renderRestoredAssistant(msg) {
    var parts = msg.parts || [];
    // A stored turn made only of background/unknown tool parts (e.g. a lone
    // get_order_status call) has nothing to show — don't leave an empty
    // avatar row behind.
    var visible = false;
    for (var j = 0; j < parts.length; j++) { if (isRenderablePart(parts[j])) { visible = true; break; } }
    if (!visible) return;
    var ctx = newAssistantCtx();
    for (var i = 0; i < parts.length; i++) renderPartIntoCtx(ctx, parts[i]);
  }

  function renderAllMessages() {
    messagesEl.replaceChildren();
    updateShareBtn();
    cfgResetPlans(); // the thread's plans register again as their cards rebuild
    if (!messages.length) { showWelcome(); cfgRenderHost(); return; }
    for (var i = 0; i < messages.length; i++) {
      var m = messages[i];
      if (m.role === 'user') renderUserMessage(m);
      else if (m.role === 'assistant') renderRestoredAssistant(m);
    }
    cfgRestoreOwnPlan();
    scrollToBottom();
  }

  // ---------------------------------------------------------------------------
  // Generating indicator. Instead of the old three bouncing dots, the
  // assistant-slot avatar itself animates while a reply is pending (calm wave
  // motion + a gentle breathing pulse — the .ms-chat-row--gen CSS). When the
  // first tokens arrive this row is removed and the real assistant row takes
  // the same position, so the orb reads as settling into the static avatar
  // beside the streaming text. Reduced motion freezes it via CSS.
  // ---------------------------------------------------------------------------
  function showTyping() {
    removeTyping();
    var ar = assistantRow();
    ar.row.classList.add('ms-chat-row--gen');
    ar.row.setAttribute('role', 'status');
    ar.row.setAttribute('aria-label', L('Mo antwortet', 'Mo is replying'));
    typingEl = ar.row;
    messagesEl.appendChild(typingEl);
    scrollToBottom();
  }
  function removeTyping() {
    if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
    typingEl = null;
  }

  // ---------------------------------------------------------------------------
  // Wire helpers + history accumulation for the streamed assistant message.
  // ---------------------------------------------------------------------------
  function accumulatePart(parts, part) {
    var type = part.type;
    if (type === 'text') {
      var t = part.text != null ? part.text : (part.delta != null ? part.delta : '');
      if (!t) return;
      var last = parts[parts.length - 1];
      if (last && last.type === 'text') last.text += t;
      else parts.push({ type: 'text', text: t });
      return;
    }
    var name = resolveToolName(type);
    if (!name) return;
    var input = part.input != null ? part.input : part.args;
    // Keep ALL tool parts (incl. silent ones) in history so the backend can
    // replay update_customer_profile etc. on subsequent turns.
    var id = part.toolCallId || null;
    var existing = null;
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].toolCallId && parts[i].toolCallId === id) { existing = parts[i]; break; }
    }
    if (existing) {
      if (input != null) { existing.input = input; existing.state = 'output-available'; }
      // Persist the tool RESULT (tool-output-available) too, so restored
      // histories carry { state: "output-available", input, output } like a
      // genuine AI-SDK history. Never rendered.
      if (part.output !== undefined) { existing.output = part.output; existing.state = 'output-available'; }
    } else {
      // An output without any accumulated call to attach it to: ignore
      // (outputs only ever follow tool-input-available on the wire).
      if (input == null && part.output !== undefined) return;
      parts.push({ type: 'tool-' + name, toolCallId: id, state: input != null ? 'output-available' : 'input-streaming', input: input != null ? input : undefined, output: part.output !== undefined ? part.output : undefined });
    }
  }

  function toWire(msgs) {
    return msgs.map(function (m) { return { id: m.id, role: m.role, parts: m.parts }; });
  }

  // ---------------------------------------------------------------------------
  // Send + SSE stream consumption.
  // ---------------------------------------------------------------------------
  // Page context on typed / spoken turns (API_CONTRACT §2 "Typed turns",
  // source:"page"): the open page's facts — never the trail — ride along with
  // the first user message of a thread on a product (or collection) page and
  // with the first message after the page changed. "Already sent for this
  // page in this session" is sessionStorage['ms-chat-ctx-last'] =
  // '<sid>|p:<handle>' / '<sid>|c:<collectionHandle>', written by
  // finalizeStream() only for a finished, saved, non-cancelled turn of that
  // sid (a failed / cancelled / rate-limited turn re-sends it next time).
  function pageCtxKey(ctx) {
    if (!ctx) return null;
    if (ctx.type === 'product' && ctx.productId) return 'p:' + String(ctx.productId);
    if (ctx.type === 'browsing' && ctx.source === 'page' && ctx.recentlyViewed && ctx.recentlyViewed[0] && ctx.recentlyViewed[0].id) return 'c:' + String(ctx.recentlyViewed[0].id);
    return null;
  }
  function pageContextForSend() {
    try {
      var ctx;
      if (PAGE_CTX.type === 'product' && PAGE_CTX.productHandle) {
        ctx = { type: 'product', productId: PAGE_CTX.productHandle, source: 'page' };
        if (PAGE_CTX.productName) ctx.productTitle = PAGE_CTX.productName;
      } else if (PAGE_CTX.type === 'collection' && PAGE_CTX.collectionHandle && PAGE_CTX.category) {
        // Exactly one category entry, no trail, no products — the only
        // browsing shape the backend accepts with source:"page".
        ctx = { type: 'browsing', recentlyViewed: [{ type: 'category', id: PAGE_CTX.collectionHandle, name: PAGE_CTX.category }], source: 'page' };
      } else {
        return undefined; // home, search, content, account … never send context
      }
      var hasUserMsg = messages.some(function (m) { return m && m.role === 'user'; });
      if (hasUserMsg && ssGet(CTX_LAST_KEY) === sid + '|' + pageCtxKey(ctx)) return undefined;
      return ctx;
    } catch (e) { return undefined; }
  }

  function onSend() {
    if (state.streaming || state.rateLocked) return;
    stopVoice();
    // A text send stops any current playback immediately but does NOT exit
    // voice mode — the upcoming reply is still spoken (the loop continues).
    if (voiceMode) endSpeaking();
    var text = textarea.value.trim();
    if (!text) return;
    sendMessage(text, pageContextForSend());
  }

  function sendMessage(text, context) {
    clearNotice();
    // Signed-in: the first turn of a fresh thread mints its own conversationKey
    // so it becomes a discrete history entry (no-op for anonymous / a thread
    // that already has a key). Decided BEFORE the user message is pushed.
    maybeMintConversationKey(messages.length === 0);

    var userMsg = { id: 'u-' + uuid(), role: 'user', parts: [{ type: 'text', text: text }] };
    messages.push(userMsg);
    var userRow = renderUserMessage(userMsg);
    saveHistory();
    updateShareBtn(); // first user message reveals the share button
    track('message_sent', {}); // event only — never the message text

    textarea.value = '';
    autoGrow();

    startStream({ userMsg: userMsg, userRow: userRow, restoreText: text, context: context || null });

    // First-message gate: presented after the user's first message of the
    // session, while the reply streams in behind it (the reply is never
    // conditional on the choice). Anonymous visitors get the sign-in
    // motivation popup, signed-in customers the marketing opt-in. All
    // frequency/eligibility rules live in loginGateEligible() /
    // consentGateEligible(); the short delay lets the sent message visibly
    // land first so the dialog reads as an interlude, not a wall.
    setTimeout(function () { maybeShowConsentGate(0, userMsg); }, 700);
  }

  // Fresh-open contextual greeting (API_CONTRACT.md §2): POST the context
  // with an EMPTY messages array — the backend streams a natural,
  // context-aware greeting as the first assistant message. No fake user
  // primer enters the history. Only valid for a fresh conversation; a stream
  // error rolls back to the welcome state via the normal error handling.
  function sendContextGreeting(context) {
    if (!context || messages.length || state.streaming || state.rateLocked) return;
    clearNotice();
    maybeMintConversationKey(true); // fresh thread (messages is empty here)
    clearWelcome();
    startStream({ userMsg: null, userRow: null, restoreText: '', context: context });
  }

  // Cancels the /api/chat turn currently in flight (set by startStream). Used
  // by dropSessionHistory: after a sign-out / erase the late reply — it can be
  // the previous customer's order status — must never be drawn, spoken or
  // saved, and must not flip state.streaming under the next person's turn.
  var abortActiveStream = null;

  function startStream(opts) {
    var userMsg = opts.userMsg;
    var userRow = opts.userRow;
    var restoreText = opts.restoreText || '';

    state.streaming = true;
    updateInputState();
    showTyping();

    var asstParts = [];
    var ctx = null;
    var gotContent = false;
    var toolNames = {};      // toolCallId -> toolName (from tool-input-start / -available)
    var finished = false;
    var streamErrored = false;
    // The network failed after partial content (catch below): finalized to
    // keep the partial answer, but not a clean finish (SPEC C, HARDENING-1).
    var streamBroken = false;
    // The session this turn belongs to. A sign-out / erase mid-stream rotates
    // the session and wipes the stored history — the late reply (it may carry
    // order data) must not be written into the NEXT person's fresh session.
    var streamSid = sid;
    // Session-bound cancel: once set, every later callback of THIS turn (fetch
    // resolution, pump, events, finalize, error paths) returns without
    // touching the DOM, history, voice or state.streaming.
    var cancelled = false;
    var streamReader = null;
    var aborter = null;
    try { if (typeof AbortController === 'function') aborter = new AbortController(); } catch (e) {}
    function cancelStream() {
      if (cancelled) return;
      cancelled = true;
      finished = true; // finalizeStream is a no-op from here on
      try { if (aborter) aborter.abort(); } catch (e) {}
      try { if (streamReader) streamReader.cancel(); } catch (e) {}
    }
    abortActiveStream = cancelStream;

    // Creating the real assistant row is the pending->streaming transition:
    // drop the generating placeholder FIRST so its avatar never coexists with
    // the new row's own avatar (a tool-first reply removed typing only after an
    // async card build, briefly showing two avatars).
    function ensureCtx() { if (!ctx) { removeTyping(); ctx = newAssistantCtx(); ctx.live = true; } return ctx; }

    function rollback() {
      // Remove optimistic user message + assistant scaffolding; restore input.
      if (userMsg) {
        var idx = messages.indexOf(userMsg);
        if (idx !== -1) messages.splice(idx, 1);
        if (userRow && userRow.parentNode) userRow.parentNode.removeChild(userRow);
      }
      if (ctx && ctx.row && ctx.row.parentNode) ctx.row.parentNode.removeChild(ctx.row);
      removeTyping();
      saveHistory();
      updateShareBtn();
      if (!messages.length) showWelcome();
      if (restoreText) { textarea.value = restoreText; autoGrow(); }
    }

    // Idempotent: called on the `finish` event, on `[DONE]`, and on socket
    // close (r.done) — whichever comes first wins.
    function finalizeStream() {
      if (finished) return;
      finished = true;
      removeTyping();
      // A showroom card still waiting for its result decides now.
      showroomTurnOver();
      // Flush any Markdown token withheld during streaming, in case the stream
      // closed without a trailing text-end (the run is complete now).
      if (ctx && ctx.activeText && ctx.activeText.node) {
        renderMarkdownInto(ctx.activeText.node, ctx.activeText.str, false);
      }
      // Stream produced nothing visible -> drop the empty assistant row.
      if (ctx && ctx.row && !gotContent && ctx.content && !ctx.content.childNodes.length && ctx.row.parentNode) {
        ctx.row.parentNode.removeChild(ctx.row);
      }
      if (asstParts.length && sid === streamSid) {
        messages.push({ id: 'a-' + uuid(), role: 'assistant', parts: asstParts });
        saveHistory();
        // This turn's context (typed page / CTA / nudge) is now part of a
        // saved answer of this session: don't attach it again on this page.
        var sentCtxKey = pageCtxKey(chatBody.context);
        if (sentCtxKey) ssSet(CTX_LAST_KEY, streamSid + '|' + sentCtxKey);
      }
      state.streaming = false;
      updateInputState();
      if (streamErrored) {
        // With partial content already rendered the notice is appended AFTER
        // it (showMessageError adds a new row at the end) — the partial
        // answer is kept, never discarded. With nothing rendered it is the
        // whole response, as before.
        showMessageError(L('Es gab ein Problem. Bitte versuch es gleich nochmal.', 'Something went wrong. Please try again in a moment.'));
      }
      // Voice mode: speak the completed reply, then resume listening.
      if (voiceMode) voiceAfterReply(asstParts, streamErrored);
      // A live product consultation finished cleanly: renew the attribution
      // token once per page view (API_CONTRACT §10; fail-silent, consent-gated).
      // A broken stream (network error after partial content) is no clean
      // finish, even though streamErrored is still false there (SPEC C).
      if (!streamBroken && !streamErrored && sid === streamSid && hasConsultationPart(asstParts)) moAttrRenew();
      // Value moment (SPEC B3b): a clean product turn may carry the opt-in
      // ask. Dormant unless the served copy has `valueMoment`. Guarded so a
      // throw can never reach the catch below and add a false error row.
      if (!streamBroken && !streamErrored && sid === streamSid) {
        try { maybeValueMomentAsk(asstParts, streamSid, opts); } catch (e) {}
      }
    }

    // Feed a part in the widget's canonical shape into history + the renderer.
    // (renderPartIntoCtx / accumulatePart understand {type:'text',text} and
    // {type:'tool-<name>', toolCallId, input} — unchanged from before.)
    function feedCanonical(part) {
      if (cancelled) return;
      accumulatePart(asstParts, part);
      // Silent / unknown tools: history only. Creating the assistant row here
      // would end the generating indicator and leave an empty avatar row while
      // the background tool (search_products, get_order_status …) runs.
      if (!isRenderablePart(part)) return;
      var c = ensureCtx().content;
      var before = c.childNodes.length;
      renderPartIntoCtx(ctx, part);
      if (c.childNodes.length > 0 || before !== c.childNodes.length) gotContent = true;
    }

    // Body carries the full history each turn; `context` (when present) primes
    // the assistant with the current product / browsing trail per
    // API_CONTRACT.md §2.
    // `locale` makes Mo converse in the storefront's language (LOCALE.md §1);
    // de (default) is byte-identical to before. Also sent as x-ms-locale below.
    var chatBody = { messages: toWire(messages), locale: LOCALE };
    if (opts.context) chatBody.context = opts.context;
    // Configurator thread (API_CONTRACT §2, type "configurator"): the page's
    // answers + plan ride on every turn — a typed page context never replaces
    // them; an explicit CTA / nudge context still wins.
    if (!opts.context || opts.context.source === 'page') {
      var cfgCtx = cfgContextForSend();
      if (cfgCtx) chatBody.context = cfgCtx;
    }
    // Signed-in: address the active thread so several conversations can live
    // under one stable session_id (CUSTOMER_ACCOUNT.md §7.6). OMITTED for
    // anonymous / email-only (activeConversationKey is always null for them) →
    // backend defaults to session_id, keeping tiers 1–2 byte-identical.
    if (auth.signedIn && activeConversationKey) chatBody.conversationKey = activeConversationKey;
    // Returning-customer memory: only after a successful capture in this
    // page's chat session (see the capturedEmail privacy gate). A new or
    // unverifiable email is ignored gracefully server-side.
    if (capturedEmail) chatBody.customer = { email: capturedEmail };
    // Campaign attribution (captureCampaignToken): the landing link's token
    // rides along on this tab session's first chat request — the context
    // greeting included — and is deleted only once the request was accepted,
    // so a failed first send retries it on the next turn. Re-validated on
    // read: sessionStorage is writable by anything on the page.
    var campaignToken = ssGet(CAMPAIGN_TOKEN_KEY);
    if (campaignToken && !CAMPAIGN_TOKEN_RE.test(campaignToken)) { ssDel(CAMPAIGN_TOKEN_KEY); campaignToken = null; }
    if (campaignToken) chatBody.campaignToken = campaignToken;

    var chatInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
      body: JSON.stringify(chatBody)
    };
    if (aborter) chatInit.signal = aborter.signal;
    fetch(API_BASE + '/api/chat', chatInit).then(function (res) {
      if (res.ok && campaignToken) ssDel(CAMPAIGN_TOKEN_KEY);
      if (cancelled) { try { if (res.body) res.body.cancel(); } catch (e) {} return; }
      if (!res.ok) {
        return res.json().catch(function () { return null; }).then(function (data) {
          if (cancelled) return;
          handleChatHttpError(res, data, rollback);
          state.streaming = false;
          updateInputState();
        });
      }
      // Stream the AI SDK v5 UI-message stream (SSE) body.
      var reader = streamReader = res.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';

      function pump() {
        return reader.read().then(function (r) {
          if (cancelled) return;
          if (r.done) {
            if (buffer.trim()) processLine(buffer);
            finalizeStream();
            return;
          }
          buffer += decoder.decode(r.value, { stream: true });
          var lines = buffer.split('\n');
          buffer = lines.pop(); // keep the (possibly partial) last line
          for (var i = 0; i < lines.length; i++) processLine(lines[i]);
          return pump();
        });
      }

      function processLine(line) {
        line = line.replace(/\r$/, '');
        if (!line) return;
        var payload = line;
        if (line.indexOf('data:') === 0) payload = line.slice(5).trim();
        else if (line.indexOf(':') === 0) return; // SSE comment / keep-alive
        if (!payload) return;
        if (payload === '[DONE]') { finalizeStream(); return; } // stream terminator
        var ev;
        try { ev = JSON.parse(payload); } catch (e) { return; } // ignore partial/non-JSON
        if (!ev || typeof ev !== 'object') return;
        if (Array.isArray(ev)) { ev.forEach(handleEvent); return; }
        handleEvent(ev);
      }

      // Translate an AI SDK v5 UI-message-stream event into the widget's
      // canonical part shape, then render/persist it.
      function handleEvent(ev) {
        if (cancelled) return;
        var type = ev.type;
        if (typeof type !== 'string') return;

        switch (type) {
          // --- Framing: no visible effect. ---
          case 'start':
          case 'start-step':
          case 'finish-step':
            return;
          case 'finish':
            finalizeStream();
            return;

          // --- Text run lifecycle (keyed by ev.id). ---
          case 'text-start':
            // Begin a fresh visible bubble for this run.
            ensureCtx().activeText = null;
            return;
          case 'text-delta': {
            var d = ev.delta != null ? ev.delta : (ev.text != null ? ev.text : '');
            if (!d) return;
            feedCanonical({ type: 'text', text: d });
            // Streaming voice mode: enqueue per-sentence audio AS the text
            // arrives so speech starts ~1 s in (API_CONTRACT.md §8). No-op
            // outside voice mode; falls back to play-after-complete on error.
            if (voiceMode) streamTtsFeed(d);
            return;
          }
          case 'text-end':
            // Run complete: re-render in full (flush any token withheld during
            // streaming), then close the run so the next text-start opens a new
            // bubble.
            if (ctx && ctx.activeText && ctx.activeText.node) {
              renderMarkdownInto(ctx.activeText.node, ctx.activeText.str, false);
            }
            if (ctx) ctx.activeText = null;
            return;

          // --- Tool call lifecycle (keyed by ev.toolCallId). ---
          case 'tool-input-start':
            if (ev.toolCallId && ev.toolName) toolNames[ev.toolCallId] = ev.toolName;
            return;
          case 'tool-input-delta':
            // Partial input args streaming; we only render once full input lands.
            return;
          case 'tool-input-available': {
            var name = ev.toolName || toolNames[ev.toolCallId];
            if (!name) return;
            if (ev.toolCallId) toolNames[ev.toolCallId] = name;
            // input may legitimately be {} for some tools; pass it through.
            feedCanonical({ type: 'tool-' + name, toolCallId: ev.toolCallId || null, input: ev.input != null ? ev.input : {} });
            return;
          }
          case 'tool-output-available':
            // The offer_email_summary RESULT carries the canonical consent
            // copy for the capture form (API_CONTRACT.md §2) — seed the
            // consent-copy cache so the card renders the exact served strings.
            // All other tool results carry nothing extra to render (the
            // widget hydrates its own product data). Consumed silently.
            if (ev.toolCallId && toolNames[ev.toolCallId] === 'offer_email_summary' &&
                ev.output && ev.output.consentCopy) {
              seedConsentCopy(ev.output.consentCopy);
            }
            // The showroom card shows only what the result says is there.
            if (ev.toolCallId && toolNames[ev.toolCallId] === 'suggest_showroom') showroomResult(ev.toolCallId, ev.output);
            // Persist the output on the accumulated HISTORY part (renderless
            // — accumulatePart only, never feedCanonical).
            if (ev.toolCallId && toolNames[ev.toolCallId]) {
              accumulatePart(asstParts, { type: 'tool-' + toolNames[ev.toolCallId], toolCallId: ev.toolCallId, output: ev.output != null ? ev.output : null });
            }
            return;
          case 'tool-output-error':
            if (ev.toolCallId && toolNames[ev.toolCallId] === 'suggest_showroom') showroomResult(ev.toolCallId, null);
            return;

          // --- Error event. ---
          case 'error':
            try { console.error('[ms-chat] stream error event', ev.errorText || ev.error || ev); } catch (e) {}
            streamErrored = true;
            return;

          default:
            // Custom data parts (data-*) and reasoning-* are not rendered here.
            if (type.indexOf('data-') === 0 || type.indexOf('reasoning') === 0) return;
            // Type only — never the event body: an unknown tool-* event can
            // carry tool input/output (e.g. order data), which must not reach
            // the console (CHAT_ORDER_STATUS.md "never log").
            try { console.debug('[ms-chat] unhandled stream event type:', type); } catch (e) {}
            return;
        }
      }

      return pump();
    }).catch(function (err) {
      if (cancelled) return; // our own abort (sign-out / erase) — not an error
      try { console.error('[ms-chat] chat request failed', err); } catch (e) {}
      removeTyping();
      if (gotContent) {
        // Keep partial content; just surface a soft error and re-enable.
        // (finalizeStream already shows the notice when an `error` chunk was
        // seen — don't double it.)
        var notified = streamErrored;
        streamBroken = true;
        finalizeStream();
        if (!notified) showMessageError(L('Es gab ein Problem. Bitte versuch es gleich nochmal.', 'Something went wrong. Please try again in a moment.'));
      } else {
        rollback();
        showMessageError(L('Es gab ein Problem. Bitte versuch es gleich nochmal.', 'Something went wrong. Please try again in a moment.'));
        state.streaming = false;
        updateInputState();
        if (voiceMode) restartVoiceLoop(); // keep the hands-free loop alive
      }
    });
  }

  function handleChatHttpError(res, data, rollback) {
    var code = data && data.error && data.error.code;
    removeTyping();

    if (res.status === 429 || code === 'rate_limited') {
      rollback();
      var retry = parseInt(res.headers.get('Retry-After'), 10);
      if (!isFinite(retry) || retry <= 0) retry = 30;
      lockRateLimit(retry);
      return;
    }
    if (res.status === 401 || code === 'unauthorized') {
      try { console.error('[ms-chat] 401 unauthorized — check ms_chat_shared_secret in theme settings.'); } catch (e) {}
      rollback();
      showMessageError(L('Chat ist gerade nicht verfügbar.', 'Chat is currently unavailable.'));
      return;
    }
    if (res.status === 403 || code === 'forbidden') {
      try { console.error('[ms-chat] 403 forbidden — origin not allowlisted on the backend (ALLOWED_ORIGINS).'); } catch (e) {}
      rollback();
      showMessageError(L('Chat ist gerade nicht verfügbar.', 'Chat is currently unavailable.'));
      return;
    }
    if (code === 'payload_too_large' || (res.status === 400 && code === 'payload_too_large')) {
      // 40-message cap hit. Offer a clean restart.
      rollback();
      showNotice('info', L('Dieser Chat ist ziemlich lang geworden. Starte einen neuen Chat, um weiterzumachen.', 'This chat has gotten quite long. Start a new chat to continue.'), L('Neuen Chat starten', 'Start new chat'), function () {
        startNewChat();
      });
      return;
    }
    // bad_request / internal_error / upstream_unavailable / anything else.
    try { console.error('[ms-chat] chat error', res.status, code); } catch (e) {}
    rollback();
    if (res.status >= 500 || code === 'internal_error' || code === 'upstream_unavailable') {
      showMessageError(L('Es gab ein Problem. Bitte versuch es gleich nochmal.', 'Something went wrong. Please try again in a moment.'));
    } else {
      showMessageError(L('Chat ist gerade nicht verfügbar.', 'Chat is currently unavailable.'));
    }
  }

  function lockRateLimit(seconds) {
    state.rateLocked = true;
    updateInputState();
    showNotice('warn', L('Zu viele Anfragen — bitte kurz warten.', 'Too many requests — please wait a moment.'));
    if (rateTimer) clearTimeout(rateTimer);
    rateTimer = setTimeout(function () {
      state.rateLocked = false;
      rateTimer = null;
      clearNotice();
      updateInputState();
      if (voiceMode) restartVoiceLoop(); // resume listening once the lock lifts
    }, seconds * 1000);
  }

  function startNewChat(fromConfigurator) {
    var hadCfg = !!cfgActive();
    // A reply still streaming belongs to the OLD thread: cancel it first, or
    // it would be drawn into (and, signed in, saved with) the new one.
    if (abortActiveStream) { abortActiveStream(); abortActiveStream = null; }
    endSpeaking(); // and stop any audio still queued for that reply
    removeTyping();
    clearNotice();
    if (auth.signedIn) {
      // Signed-in: the session id is the identity link (CUSTOMER_ACCOUNT.md §1),
      // so DON'T rotate it — just clear the active thread. Past conversations
      // stay in the customer's server-side history ("Neue Beratung"). Dropping
      // the local messages is enough to clear the per-request 40-message cap.
      lsDel(historyKey());
      messages = [];
      activeConversationId = null;
      // "Neue Beratung": keep session_id, start a FRESH thread key so prior
      // conversations stay in history and this becomes a new entry on first turn.
      activeConversationKey = uuid();
      saveConvKey();
    } else {
      rotateSession(); // anonymous/email-only: unchanged (rotates sid + history)
      activeConversationKey = null; // anonymous never sends a conversationKey
      // The panel stays open across the rotation (always so in the dock), so
      // openPanel() will not fire again: the new session is open from now on.
      if (state.open) track('chat_opened', {});
    }
    if (rateTimer) { clearTimeout(rateTimer); rateTimer = null; }
    state.rateLocked = false;
    state.streaming = false;
    updateInputState();
    renderAllMessages(); // empty -> welcome
    if (hadCfg && fromConfigurator !== true) {
      // The configurator thread ended: the page goes back to its first step.
      cfgStage = 'start';
      cfgApplyChrome();
      cfgEmit('reset', {});
    }
    if (!DOCK) { try { textarea.focus(); } catch (e) {} }
  }

  // ---------------------------------------------------------------------------
  // Contextual proactive nudge (engagement layer, WIDGET_SPEC §9c).
  //
  // A small dismissible speech bubble next to the launcher — NEVER a blocking
  // overlay/modal. Copy is tailored to the page/category (tone rule above);
  // it never asks for an email. Frequency: at most ONCE per session
  // (sessionStorage), never again once dismissed (localStorage), never if the
  // chat was already opened this session. Triggers: dwell on product/
  // collection pages, scroll near the bottom of a product page, exit intent
  // on desktop only (mobile = dwell/scroll only). First applicable wins.
  // ---------------------------------------------------------------------------
  var NUDGE_DISMISSED_KEY = 'ms-chat-nudge-dismissed'; // persists: dismissed once = never again
  var SS_NUDGE_SHOWN = 'ms-chat-nudge-shown';          // once per session
  var SS_CHAT_OPENED = 'ms-chat-opened';               // chat opened this session
  var SS_ATTN = 'ms-chat-attn-played';                 // launcher attention played this session
  var NUDGE_DWELL_MS = 24000;
  var nudgeEl = null;

  function prefersReducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  function nudgeEligible() {
    return !state.open &&
      lsGet(NUDGE_DISMISSED_KEY) !== '1' &&
      !ssGet(SS_NUDGE_SHOWN) &&
      !ssGet(SS_CHAT_OPENED);
  }

  // Copy priority: current product page -> current collection page -> a
  // category streak in the trail -> friendly generic offer. All variants are
  // grammar-safe for any product/category name (quoted, no gender/number
  // agreement) and reference only the page/category — never behavior.
  function nudgeCopy() {
    if (PAGE_CTX.type === 'product' && PAGE_CTX.productName) {
      return { text: L('Fragen zum Produkt „', 'Questions about "') + PAGE_CTX.productName + L('“? Ich helf dir gern weiter.', '"? I\'m happy to help.'), contextual: true };
    }
    if (PAGE_CTX.type === 'collection' && PAGE_CTX.category) {
      return { text: L('Unsicher, was aus „', 'Not sure which "') + PAGE_CTX.category + L('“ zu dir passt? Lass es uns klären.', '" suits you? Let\'s figure it out.'), contextual: true };
    }
    var streak = trailCategoryStreak();
    if (streak) {
      return { text: L('Du schaust dir ein paar Produkte aus „', 'You\'re looking at a few products from "') + streak + L('“ an — soll ich beim Vergleich helfen?', '" — shall I help you compare?'), contextual: true };
    }
    return { text: L('Hi, ich bin Mo! Wenn du Fragen hast, helfe ich dir gern bei der Auswahl.', 'Hi, I\'m Mo! If you have any questions, I\'m happy to help you choose.'), contextual: false };
  }

  function removeNudge() {
    if (nudgeEl && nudgeEl.parentNode) nudgeEl.parentNode.removeChild(nudgeEl);
    nudgeEl = null;
  }

  function showNudge(trigger) {
    if (!nudgeEligible() || nudgeEl) return;
    var copy = nudgeCopy();
    ssSet(SS_NUDGE_SHOWN, '1');
    nudgeEl = el('div', { class: 'ms-chat-nudge' });
    var msg = el('button', { class: 'ms-chat-nudge-msg', type: 'button', text: copy.text });
    msg.addEventListener('click', function () {
      track('nudge_clicked', { pageType: PAGE_CTX.type, contextual: copy.contextual });
      removeNudge();
      var fresh = !messages.length;
      openPanel();
      // Fresh conversation: hand the page/trail context to the backend for a
      // streamed contextual greeting (messages: [] — API_CONTRACT.md §2).
      // With existing history (or no usable context) just open as before.
      if (fresh && !state.streaming && !state.rateLocked) {
        var ctx = null;
        if (PAGE_CTX.type === 'product' && (PAGE_CTX.productHandle || PAGE_CTX.productId)) {
          ctx = { type: 'product', productId: PAGE_CTX.productHandle || PAGE_CTX.productId };
          if (PAGE_CTX.productName) ctx.productTitle = PAGE_CTX.productName;
          var rv = recentlyViewedPayload();
          if (rv) ctx.recentlyViewed = rv;
        } else {
          ctx = browsingContext(null);
        }
        if (ctx) ctx.source = 'nudge'; // API_CONTRACT §2: the nudge path
        if (ctx) sendContextGreeting(ctx);
      }
    });
    var x = el('button', { class: 'ms-chat-nudge-x', type: 'button', 'aria-label': L('Hinweis schließen', 'Dismiss') });
    x.appendChild(icon('close'));
    x.addEventListener('click', function () {
      lsSet(NUDGE_DISMISSED_KEY, '1'); // dismissed once -> persists, never shown again
      track('nudge_dismissed', { pageType: PAGE_CTX.type, contextual: copy.contextual });
      removeNudge();
    });
    nudgeEl.appendChild(msg);
    nudgeEl.appendChild(x);
    root.appendChild(nudgeEl);
    track('nudge_shown', { pageType: PAGE_CTX.type, contextual: copy.contextual, trigger: trigger || '' });
  }

  // Behavior-based triggers; the first applicable one fires, then all are
  // torn down. showNudge() re-checks eligibility, so a chat opened after
  // arming silently wins.
  function initNudgeTriggers() {
    if (DOCK || !nudgeEligible()) return; // the docked chat needs no nudge
    var fired = false;
    var dwellTimer = null;
    var scrollBound = false;
    var mouseBound = false;

    function cleanup() {
      if (dwellTimer) { clearTimeout(dwellTimer); dwellTimer = null; }
      if (scrollBound) { window.removeEventListener('scroll', onScroll); scrollBound = false; }
      if (mouseBound) { document.removeEventListener('mouseout', onMouseOut); mouseBound = false; }
    }
    function fire(trigger) {
      if (fired) return;
      fired = true;
      cleanup();
      showNudge(trigger);
    }
    // Dwell on a product/collection page.
    if (PAGE_CTX.type === 'product' || PAGE_CTX.type === 'collection') {
      dwellTimer = setTimeout(function () { fire('dwell'); }, NUDGE_DWELL_MS);
    }
    // Scroll near the bottom of a product page.
    function onScroll() {
      var doc = document.documentElement;
      var max = Math.max(doc.scrollHeight || 0, document.body ? document.body.scrollHeight : 0);
      if (max <= window.innerHeight) return;
      if ((window.pageYOffset + window.innerHeight) / max >= 0.85) fire('scroll');
    }
    if (PAGE_CTX.type === 'product') {
      window.addEventListener('scroll', onScroll, { passive: true });
      scrollBound = true;
    }
    // Exit intent — DESKTOP only (pointer leaving toward the top/URL bar).
    // Mobile has no pointer-out: dwell/scroll above are the mobile triggers.
    function onMouseOut(e) {
      if (e.relatedTarget) return;
      if (typeof e.clientY === 'number' && e.clientY > 16) return;
      fire('exit');
    }
    if (isDesktop()) {
      document.addEventListener('mouseout', onMouseOut);
      mouseBound = true;
    }
  }

  // ---------------------------------------------------------------------------
  // One-time launcher attention animation: a single gentle bounce shortly
  // after load, then rest. Once per SESSION (not per navigation), skipped
  // entirely under prefers-reduced-motion (the CSS also freezes it as a
  // belt-and-braces guard).
  // ---------------------------------------------------------------------------
  function playLauncherAttention() {
    if (DOCK || ssGet(SS_ATTN)) return;
    ssSet(SS_ATTN, '1'); // consumed even when skipped — never replays this session
    if (prefersReducedMotion()) return;
    setTimeout(function () {
      if (state.open || !launcher) return;
      launcher.classList.add('ms-chat-launcher--attn');
      launcher.addEventListener('animationend', function onEnd(e) {
        if (e.target !== launcher) return; // ignore bubbled orb/halo animation ends
        launcher.classList.remove('ms-chat-launcher--attn');
        launcher.removeEventListener('animationend', onEnd);
      });
      track('launcher_attention_played', {});
    }, 1400);
  }

  // ---------------------------------------------------------------------------
  // Public API: product-page CTA (feature 2). Opens the panel and sends a
  // product-primed message so the assistant advises about that product. This
  // works whether the conversation is fresh or already going — it appends a
  // normal turn, so existing history is never wiped. The `context` field is
  // also sent (the backend may use it); ids/title only — never a page scrape.
  // ---------------------------------------------------------------------------
  function openWithProduct(id, title) {
    try {
      openPanel();
      var pid = id != null ? String(id) : '';
      var ptitle = title != null ? String(title) : '';
      track('product_cta_opened', { productId: pid }); // numeric id stays for KPI
      if (state.streaming || state.rateLocked) return; // busy -> just open; user can ask
      // Context grounding: the catalog's ids are handles/slugs, so a numeric
      // Shopify product.id is dropped server-side. The CTA only renders on
      // its own product page, where the snippet injects productHandle — use
      // it (guarded against a CTA for a different product), falling back to
      // the passed id only when no handle is present.
      var ctxId = pid;
      if (PAGE_CTX.type === 'product' && PAGE_CTX.productHandle &&
          (!pid || !PAGE_CTX.productId || pid === PAGE_CTX.productId)) {
        ctxId = PAGE_CTX.productHandle;
      }
      var context = { type: 'product', productId: ctxId, productTitle: ptitle };
      var rv = recentlyViewedPayload();
      if (rv) context.recentlyViewed = rv;
      context.source = 'cta'; // API_CONTRACT §2: the CTA path, never held out
      // Deliberately a primer user message, NOT the messages:[] greeting:
      // the primer carries the title in text, so the consultation works even
      // if the context's productId were ever dropped server-side. (With
      // existing history this is also the contract's pivot path.)
      var prompt = ptitle
        ? (L('Ich interessiere mich für „', 'I\'m interested in "') + ptitle + L('". Kannst du mich zu diesem Produkt beraten?', '". Can you advise me on this product?'))
        : L('Kannst du mich zu diesem Produkt beraten?', 'Can you advise me on this product?');
      sendMessage(prompt, context);
    } catch (e) {
      try { console.error('[ms-chat] openWithProduct failed', e); } catch (e2) {}
    }
  }

  // ---------------------------------------------------------------------------
  // Share-icon entry point: open the panel and drop the SAME email-capture form
  // into the message area so the user can request the summary at any time. If a
  // not-yet-submitted capture card is already on screen, reuse it (don't stack).
  // ---------------------------------------------------------------------------
  var lastCaptureRow = null;
  function openCaptureForm() {
    try {
      openPanel();
      if (lastCaptureRow && lastCaptureRow.parentNode === messagesEl) {
        var existing = lastCaptureRow.querySelector('input[type="email"]');
        lastCaptureRow.scrollIntoView({ block: 'nearest' });
        if (existing) { try { existing.focus(); } catch (e) {} }
        return;
      }
      clearWelcome();
      var ar = assistantRow();
      ar.content.appendChild(buildCaptureCard({ message: CONSENT_COPY.intro, productIds: null }));
      messagesEl.appendChild(ar.row);
      lastCaptureRow = ar.row;
      scrollToBottom();
      setTimeout(function () {
        try { ar.row.querySelector('input[type="email"]').focus(); } catch (e) {}
      }, 60);
    } catch (e) {
      try { console.error('[ms-chat] openCaptureForm failed', e); } catch (e2) {}
    }
  }

  // ---------------------------------------------------------------------------
  // Feedback entry point (footer link). Drops the short feedback card into the
  // message area. Available to all tiers; reuses a still-open card instead of
  // stacking. POSTs to /api/feedback (API_CONTRACT.md §9).
  // ---------------------------------------------------------------------------

  // Coarse, telemetry-grade tier hint the widget already knows — never derived
  // from anything sensitive. Signed-in > captured email-only > anonymous.
  function feedbackTier() {
    if (auth.signedIn) return 'signed-in';
    if (capturedEmail) return 'email';
    return 'anonymous';
  }

  // Contact email for THIS comment — only for an already-identified user. We
  // attach the email a contact captured in this page session; a signed-in
  // customer is linked by the session id, so we never send their address here.
  function identifiedEmail() {
    return capturedEmail || null;
  }

  // The storefront path the user is on (context only; backend caps the length).
  function currentPagePath() {
    try { return (window.location && window.location.pathname) || null; } catch (e) { return null; }
  }

  function buildFeedbackCard() {
    var card = el('div', { class: 'ms-chat-card ms-chat-feedback' });
    var body = el('div', { class: 'ms-chat-card-body' });

    var head = el('div', { class: 'ms-chat-card-head' });
    head.appendChild(icon('feedback'));
    head.appendChild(el('span', { text: FEEDBACK_COPY.title }));
    body.appendChild(head);

    body.appendChild(el('div', { class: 'ms-chat-card-text', text: FEEDBACK_COPY.intro }));

    var form = el('form', { class: 'ms-chat-form', novalidate: 'novalidate' });

    var taId = 'msc-fb-' + Math.random().toString(36).slice(2, 8);
    var input = el('textarea', { id: taId, name: 'feedback', rows: '3', maxlength: '4000', placeholder: FEEDBACK_COPY.placeholder, 'aria-label': FEEDBACK_COPY.title });
    var field = el('div', { class: 'ms-chat-field' });
    field.appendChild(input);
    form.appendChild(field);

    var errEl = el('div', { class: 'ms-chat-form-error', style: 'display:none' });
    form.appendChild(errEl);
    function showError(m) { errEl.textContent = m; errEl.style.display = 'block'; }
    function clearError() { errEl.style.display = 'none'; }

    var actions = el('div', { class: 'ms-chat-feedback-actions' });
    var cancelBtn = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary', text: FEEDBACK_COPY.cancel });
    var submit = el('button', { type: 'submit', class: 'ms-chat-btn ms-chat-btn--primary', text: FEEDBACK_COPY.submit });
    actions.appendChild(cancelBtn);
    actions.appendChild(submit);
    form.appendChild(actions);
    body.appendChild(form);
    card.appendChild(body);

    cancelBtn.addEventListener('click', function () {
      var row = card.closest ? card.closest('.ms-chat-row') : null;
      if (row && row.parentNode) row.parentNode.removeChild(row);
      if (row && row === lastFeedbackRow) lastFeedbackRow = null;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();
      var msg = (input.value || '').trim();
      if (!msg) { showError(FEEDBACK_COPY.errEmpty); try { input.focus(); } catch (er) {} return; }
      if (msg.length > 4000) { showError(FEEDBACK_COPY.errLong); return; }

      // Only `message` is required; everything else is optional context — send
      // what we have, omit the rest (API_CONTRACT.md §9). No PII unless the
      // user is already identified.
      var payload = { message: msg };
      if (sid) payload.sessionId = sid;
      if (auth.signedIn && activeConversationKey) payload.conversationId = activeConversationKey;
      var tier = feedbackTier();
      if (tier) payload.tier = tier;
      var contactEmail = identifiedEmail();
      if (contactEmail) payload.email = contactEmail;
      var page = currentPagePath();
      if (page) payload.page = page;

      submit.disabled = true;
      cancelBtn.disabled = true;
      submit.textContent = FEEDBACK_COPY.sending;

      function reenable() {
        submit.disabled = false;
        cancelBtn.disabled = false;
        submit.textContent = FEEDBACK_COPY.submit;
      }

      fetch(API_BASE + '/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (res.ok) {
          var ok = el('div', { class: 'ms-chat-form-success' });
          ok.appendChild(icon('check'));
          ok.appendChild(el('h3', { text: FEEDBACK_COPY.thanksTitle }));
          ok.appendChild(el('p', { text: FEEDBACK_COPY.thanks }));
          body.replaceChildren(head, ok);
          var row = card.closest ? card.closest('.ms-chat-row') : null;
          if (row && row === lastFeedbackRow) lastFeedbackRow = null; // a thanked card can't be reused
          scrollToBottom();
          return;
        }
        // Keep the comment so the user can retry.
        return res.json().catch(function () { return null; }).then(function (data) {
          var code = data && data.error && data.error.code;
          if (res.status === 413 || code === 'payload_too_large') {
            showError(FEEDBACK_COPY.errLong);
            reenable();
            return;
          }
          if (res.status === 429 || code === 'rate_limited') {
            var retry = parseInt(res.headers.get('Retry-After'), 10);
            if (!isFinite(retry) || retry <= 0) retry = 30;
            showError(FEEDBACK_COPY.errRate);
            submit.textContent = FEEDBACK_COPY.submit;
            // Honor the window — re-enable only once it elapses.
            setTimeout(function () { submit.disabled = false; cancelBtn.disabled = false; }, retry * 1000);
            return;
          }
          showError(FEEDBACK_COPY.errGeneric);
          reenable();
        });
      }).catch(function () {
        showError(FEEDBACK_COPY.errGeneric);
        reenable();
      });
    });

    return card;
  }

  var lastFeedbackRow = null;
  function openFeedbackCard() {
    try {
      openPanel();
      if (lastFeedbackRow && lastFeedbackRow.parentNode === messagesEl) {
        lastFeedbackRow.scrollIntoView({ block: 'nearest' });
        var existing = lastFeedbackRow.querySelector('textarea');
        if (existing) { try { existing.focus(); } catch (e) {} }
        return;
      }
      clearWelcome();
      var ar = assistantRow();
      ar.content.appendChild(buildFeedbackCard());
      messagesEl.appendChild(ar.row);
      lastFeedbackRow = ar.row;
      scrollToBottom();
      setTimeout(function () {
        try { ar.row.querySelector('textarea').focus(); } catch (e) {}
      }, 60);
    } catch (e) {
      try { console.error('[ms-chat] openFeedbackCard failed', e); } catch (e2) {}
    }
  }

  // ---------------------------------------------------------------------------
  // Signed-in summary download (CUSTOMER_ACCOUNT.md §11). Guarded XHR to
  // GET /api/account/summary?conversationKey=… → a branded PDF document
  // (Content-Type: application/pdf, since 10E-1), saved as a file client-side
  // (a plain <a download> can't send the x-ms-chat-key + session guards). The
  // response body is taken as a Blob and saved behind the button. On-demand and
  // may make one AI call, so it can take a moment — we show a busy state and
  // impose NO short timeout.
  // ---------------------------------------------------------------------------
  var DOWNLOAD_COPY = {
    notFound: 'Für diese Beratung gibt es noch keine Zusammenfassung.',
    error: 'Zusammenfassung konnte gerade nicht erstellt werden — bitte später erneut versuchen.'
  };
  if (LOCALE === 'en') Object.assign(DOWNLOAD_COPY, {
    notFound: 'There\'s no summary for this consultation yet.',
    error: 'The summary couldn\'t be created right now — please try again later.'
  });
  var downloadingSummary = false;
  function downloadSummary() {
    if (!auth.signedIn || !activeConversationKey || downloadingSummary) return;
    downloadingSummary = true;
    downloadBtn.disabled = true;
    downloadBtn.classList.add('ms-chat-download--busy');
    clearNotice();
    track('summary_download_started', {});
    var key = activeConversationKey;
    var reqSid = sid;
    fetch(API_BASE + '/api/account/summary?conversationKey=' + encodeURIComponent(key), {
      method: 'GET', headers: accountHeaders()
    }).then(function (res) {
      if (accountReplyStale(reqSid)) throw 0; // signed out meanwhile: never save it
      if (res.status === 401) { accountUnauthorized(); throw 0; }   // session gone
      if (res.status === 404) throw 404;                            // not this customer's / unknown
      if (!res.ok) throw new Error('summary ' + res.status);
      return res.blob();
    }).then(function (blob) {
      if (accountReplyStale(reqSid)) return;
      // The endpoint returns a PDF (10E-1); save it with a .pdf name so the OS
      // opens it correctly. The Blob already carries application/pdf from the
      // response, so no explicit re-typing is needed.
      var url = URL.createObjectURL(blob);
      var a = el('a', { href: url, download: 'motionsports-zusammenfassung.pdf' });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) {} }, 1000);
      track('summary_downloaded', {});
    }).catch(function (e) {
      if (e === 0) return; // 401 already dropped to anonymous
      if (e === 404) showNotice('info', DOWNLOAD_COPY.notFound);
      else showNotice('warn', DOWNLOAD_COPY.error);
    }).then(function () {
      downloadingSummary = false;
      if (downloadBtn) { downloadBtn.disabled = false; downloadBtn.classList.remove('ms-chat-download--busy'); }
    });
  }

  // ---------------------------------------------------------------------------
  // Customer Account tier-3 UI: sign-in card, header affordances, signed-in
  // welcome greeting, and the ChatGPT-style conversation-history drawer.
  // All of this is gated on `auth.signedIn` (or settled-but-anonymous), so it
  // is inert for visitors who never sign in.
  // ---------------------------------------------------------------------------

  // The benefits card (task §2): NOT a fake login form — auth is Shopify's
  // hosted page; one button initiates the redirect and returns here.
  function buildSignInCard() {
    var card = el('div', { class: 'ms-chat-card ms-chat-signin-card' });
    var body = el('div', { class: 'ms-chat-card-body' });
    var head = el('div', { class: 'ms-chat-card-head' });
    head.appendChild(icon('user'));
    head.appendChild(el('span', { text: ACCOUNT_COPY.signInTitle }));
    body.appendChild(head);
    body.appendChild(el('div', { class: 'ms-chat-card-text', text: ACCOUNT_COPY.signInIntro }));
    var ul = el('ul', { class: 'ms-chat-signin-benefits' });
    ACCOUNT_COPY.benefits.forEach(function (b) {
      var li = el('li');
      li.appendChild(icon('check'));
      li.appendChild(el('span', { text: b }));
      ul.appendChild(li);
    });
    body.appendChild(ul);
    var btn = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--primary ms-chat-signin-cta' }, [ACCOUNT_COPY.signInBtn]);
    btn.addEventListener('click', function () { initiateLogin(); });
    body.appendChild(btn);
    // Reassurance line: the card must sell the account without gatekeeping the
    // chat — anonymous visitors should feel free to just start typing.
    body.appendChild(el('div', { class: 'ms-chat-signin-hint', text: ACCOUNT_COPY.signInHint }));
    card.appendChild(body);
    // Served reward teaser (SPEC B2): the card renders at once as before; the
    // teaser + terms join above the CTA only if the copy answers while this
    // card is still the live one in the welcome slot (updateWelcomeAuth
    // replaces it on every auth change), for the same anonymous session.
    // A sid change elsewhere while the copy loads (another tab signed out /
    // rotated) refetches once for the new sid — the copy is per session.
    function addTeaser(retry) {
      var reqSid = sid;
      fetchSignInConsentCopy().then(function (c) {
        if (sid !== reqSid) { if (retry) addTeaser(false); return; }
        var r = signInTeaserReward(c);
        if (!r || !auth.settled || auth.signedIn) return;
        if (!welcomeAuthEl || card.parentNode !== welcomeAuthEl || btn.parentNode !== body) return;
        body.insertBefore(rewardTeaser(r), btn);
        body.insertBefore(rewardTerms(r), btn);
      }).catch(function () {}); // no copy -> the card stays as it is
    }
    addTeaser(true);
    return card;
  }

  // At-sign-in marketing opt-in (CONSENT_FLOW.md §2). UI CHROME only (German) —
  // every LEGAL string (headline framing, the marketing label, the shared
  // footer, the consentTextShown audit text, imprint/privacy targets) is
  // backend-served and rendered verbatim; nothing consent-related is hard-coded
  // here.
  var OPTIN_COPY = {
    loading: 'Einwilligungstext wird geladen…',
    submit: 'Ja, Angebote aktivieren',
    sending: 'Wird gesendet…',
    // An explicit "no" — remembered on this device like the popup's
    // decline (MKT_DECLINE_SNOOZE_MS), so it says so instead of "not now".
    decline: 'Nein, danke',
    errRate: 'Zu viele Anfragen — bitte kurz warten.',
    errUpstream: 'Anmeldung gerade nicht möglich — bitte später erneut versuchen.',
    errGeneric: 'Anmeldung fehlgeschlagen. Bitte versuch es erneut.',
    successTitle: 'Fast geschafft!',
    // DOI: ticking only sends a confirmation mail — not subscribed until clicked.
    successPending: 'Bitte bestätige die Anmeldung über den Link in der E-Mail an deine hinterlegte Adresse.',
    // 422 no_verified_email -> fall back to the typed-email capture form.
    noEmail: 'Für dein Konto ist keine bestätigte E-Mail-Adresse hinterlegt.',
    noEmailBtn: 'E-Mail-Adresse eingeben'
  };
  if (LOCALE === 'en') Object.assign(OPTIN_COPY, {
    loading: 'Loading consent text…',
    submit: 'Yes, activate offers',
    sending: 'Sending…',
    decline: 'No, thanks',
    errRate: 'Too many requests — please wait a moment.',
    errUpstream: 'Sign-up isn\'t possible right now — please try again later.',
    errGeneric: 'Sign-up failed. Please try again.',
    successTitle: 'Almost there!',
    successPending: 'Please confirm your subscription via the link in the email sent to your registered address.',
    noEmail: 'There\'s no confirmed email address on file for your account.',
    noEmailBtn: 'Enter email address'
  });

  // Session-scoped guard: once the customer answers the opt-in (popup or
  // inline card) or dismisses the popup (Esc/backdrop) we stop re-presenting
  // it for the rest of this browser session (it is an optional benefit
  // surface, not a nag). Never persisted across sessions — the longer
  // device memory of an explicit decline is MKT_DECISION_KEY.
  var OPTIN_DONE_KEY = 'ms-chat-optin-done';
  var signInOptInDone = (ssGet(OPTIN_DONE_KEY) === '1');
  // The signed-in consent ask was SHOWN this browser session — by the inline
  // card or the popup, which are one surface ('signin'). Shared by both so
  // consent_gate_shown {surface:'signin'} counts once per session
  // (API_CONTRACT.md §5), and so the popup never asks again after an inline
  // card that went unanswered (e.g. left on screen, then the page reloaded —
  // the card is not persisted, and asking twice in a visit would be a nag).
  var OPTIN_SHOWN_KPI_KEY = 'ms-chat-optin-ask-shown';
  function markOptInDone() { signInOptInDone = true; ssSet(OPTIN_DONE_KEY, '1'); }
  // Show the at-sign-in opt-in ONLY when the backend says there's no marketing
  // decision on record yet (auth.optInActionable, CUSTOMER_ACCOUNT.md §6.1) —
  // so a customer who already opted in / out (or whose prior consent carried
  // forward) is never re-asked. On top of the backend flag, two LOCAL guards
  // shared by the popup and the inline card: `signInOptInDone` (answered or
  // dismissed this browser session) and the device's decision memory
  // (mktDecisionQuiet: accepted, or declined within the last 30 days — the
  // backend records no "Nein", so optInActionable stays true after one).
  function optInActionable() {
    return auth.signedIn && auth.optInActionable === true && !signInOptInDone && !mktDecisionQuiet();
  }

  // The opt-in card (CONSENT_FLOW.md §2, mechanic updated to match the consent
  // gate): the SAME double-opt-in as the capture form, minus the "type your
  // email" step. Renders the served v3 copy verbatim — a benefit headline
  // (framing, NOT consent text), the served consent STATEMENT fully visible,
  // the served footer, and imprint/privacy links. The affirmative act is an
  // explicit tap on the accept button directly beneath the shown text
  // (button-consent — never pre-decided, never auto-submitted); it runs the
  // existing DOI path (POST /api/account/marketing-opt-in) echoing
  // consentTextShown verbatim. The equally prominent "Nein, danke" POSTs
  // nothing (it is remembered on the device only) and blocks nothing. KPI:
  // consent_gate_shown / _accepted / _declined with { surface: 'signin',
  // placement, variant? }, the same funnel as the popup (the card has no
  // dismiss path). `placement` is 'signin_return' from presentSignInOptIn(),
  // 'value_moment' from maybeValueMomentAsk() — which passes the approved copy
  // it already checked as `preCopy` (rendered at once, no second fetch).
  function buildMarketingOptInCard(placement, preCopy) {
    placement = placement || 'signin_return';
    // Captured with the RENDERED copy (CONSENT_CONTRACT §3.2): the variant the
    // shopper saw and the session it was rendered for.
    var variant = null;
    var renderSid = sid;
    // The served reward as rendered on this card (null: none) — drives the
    // `reward` KPI flag and the after-accept line.
    var reward = null;
    var card = el('div', { class: 'ms-chat-card ms-chat-optin-card' });
    var body = el('div', { class: 'ms-chat-card-body' });
    card.appendChild(body);
    body.appendChild(el('div', { class: 'ms-chat-consent-loading', text: OPTIN_COPY.loading }));

    var copy = null;
    var errEl = el('div', { class: 'ms-chat-form-error', style: 'display:none' });
    function showError(m) { errEl.textContent = m; errEl.style.display = 'block'; }
    function clearError() { errEl.textContent = ''; errEl.style.display = 'none'; }

    // The card's explicit decline: quiet for this session AND, like the
    // popup's "Nein, danke", for MKT_DECLINE_SNOOZE_MS on this device.
    // The card is the same signed-in consent ask as the popup, so it feeds the
    // same consent_gate_* funnel (API_CONTRACT.md §5, CONSENT_FLOW.md §3).
    function decline() {
      recordMktDecision('declined');
      markOptInDone();
      track('consent_gate_declined', consentGateData(placement, variant, !!reward));
      card.remove();
    }

    function renderForm(c) {
      copy = c;
      variant = servedVariant(c);
      renderSid = sid;
      reward = c.lawyerApproved === true ? servedReward(c) : null;
      body.replaceChildren();
      // Value-moment lead (served, SPEC B3) — this placement only, on top.
      var vm = placement === 'value_moment' ? servedValueMoment(c) : null;
      if (vm) body.appendChild(el('div', { class: 'ms-chat-vm-lead', text: vm.lead }));
      // Reward badge above the headline (CONSENT_CONTRACT §3.1 extended).
      if (reward) body.appendChild(rewardBadge(reward));
      // Benefit framing ABOVE the consent statement — explicitly NOT part of
      // consentTextShown (CONSENT_FLOW.md §2.1); rendered as served.
      if (typeof c.headline === 'string' && c.headline) {
        body.appendChild(el('div', { class: 'ms-chat-optin-headline', text: c.headline }));
      }
      // Served benefit bullets (v5), all or nothing — never widget text.
      var cardBenefits = servedBenefits(c);
      if (cardBenefits) body.appendChild(benefitsList(cardBenefits, 'ms-chat-optin-benefits'));
      // Served reward terms right after the framing, before the consent
      // statement — never part of consentTextShown.
      if (reward) body.appendChild(rewardTerms(reward));
      var form = el('form', { class: 'ms-chat-form ms-chat-optin', novalidate: 'novalidate' });

      // The SERVED consent statement, fully visible (never truncated) directly
      // above the accept button — tapping that button IS the clear affirmative
      // act for exactly this text (Planet49-safe: nothing is pre-decided, and
      // there is no code path that submits without the tap).
      form.appendChild(el('div', { class: 'ms-chat-gate-consent', text: c.marketingLabel }));

      // Shared footer beneath the statement — backend-served, part of the audit text.
      if (typeof c.consentFooter === 'string' && c.consentFooter) {
        form.appendChild(el('div', { class: 'ms-chat-consent-footer', text: c.consentFooter }));
      }
      // Imprint / privacy links nearby (compliance), between the footer and the
      // buttons as CONSENT_CONTRACT §3.1 orders them — targets backend-served.
      var legal = el('div', { class: 'ms-chat-legal-links' });
      var impHref = safeHref(c.imprintUrl);
      var privHref = safeHref(c.privacyUrl);
      if (impHref) legal.appendChild(el('a', { href: impHref, target: '_blank', rel: 'noopener noreferrer', text: L('Impressum', 'Imprint') }));
      if (privHref) legal.appendChild(el('a', { href: privHref, target: '_blank', rel: 'noopener noreferrer', text: L('Datenschutz', 'Privacy') }));
      form.appendChild(legal);

      form.appendChild(errEl);

      var submit = el('button', { type: 'submit', class: 'ms-chat-btn ms-chat-btn--primary' }, [OPTIN_COPY.submit]);
      form.appendChild(submit);
      // The decline is a REAL, equally reachable choice (CONSENT_FLOW.md §1:
      // no visual burying) — a full button right below accept, exactly like
      // the popup's ms-chat-gate-decline, never a muted link under the legal row.
      var skip = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary ms-chat-optin-decline' }, [OPTIN_COPY.decline]);
      skip.addEventListener('click', decline);
      form.appendChild(skip);

      body.appendChild(form);
      // Counted only once the served copy is really on screen (a card that
      // removes itself for missing / unapproved copy was never "shown"), and
      // once per browser session for this surface (API_CONTRACT.md §5) — a
      // reload re-rendering an unanswered card is not a second impression.
      if (ssGet(OPTIN_SHOWN_KPI_KEY) !== '1') {
        ssSet(OPTIN_SHOWN_KPI_KEY, '1');
        track('consent_gate_shown', consentGateData(placement, variant, !!reward));
      }

      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        clearError();
        if (!copy) return; // served copy not loaded -> cannot submit
        var payload = {
          marketingConsent: true,   // the user's explicit accept tap on the shown text
          // The served audit string, echoed back VERBATIM (Art. 7 proof) — never
          // recomposed or hard-coded client-side.
          consentTextShown: copy.consentTextShown,
          // Stored with the consent record + carried to the DOI email; matches
          // the locale the sign-in consent copy was served in (LOCALE.md §1).
          locale: LOCALE,
          // Telemetry echo (ACCOUNT_CONTRACT §6.2): where the ask was shown and
          // which served framing variant — body only, never a header.
          placement: placement
        };
        if (variant) payload.variant = variant;
        submit.disabled = true;
        submit.textContent = OPTIN_COPY.sending;
        // Guarded account XHR (CONSENT_FLOW.md §2.2): same headers as /api/auth/me.
        fetch(API_BASE + '/api/account/marketing-opt-in', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
          body: JSON.stringify(payload)
        }).then(function (res) {
          if (res.status === 401) { accountUnauthorized(); card.remove(); return; }
          if (res.ok) {
            return res.json().catch(function () { return null; }).then(function (data) {
              // "Check your inbox" only when a DOI mail really went out
              // (marketingOutcome); already subscribed -> nothing to do.
              var outcome = marketingOutcome(data && data.marketing);
              var ok = el('div', { class: 'ms-chat-form-success' });
              ok.appendChild(icon('check'));
              ok.appendChild(el('h3', { text: outcome === 'pending' ? OPTIN_COPY.successTitle
                : (outcome === 'already' ? MKT_RESULT_COPY.alreadyTitle : MKT_RESULT_COPY.otherTitle) }));
              ok.appendChild(el('p', { text: outcome === 'pending' ? OPTIN_COPY.successPending
                : (outcome === 'already' ? MKT_RESULT_COPY.already : MKT_RESULT_COPY.other) }));
              // Served reward note — never for 'already' (no new sign-up).
              if (reward && reward.afterAccept && outcome !== 'already') {
                ok.appendChild(el('p', { class: 'ms-chat-reward-after', text: reward.afterAccept }));
              }
              body.replaceChildren(ok);
              markOptInDone();
              recordMktDecision('accepted'); // quiets the consent gate on this device
              // Same place the popup counts it: the accept tap's POST succeeded —
              // and only under the session the ask was rendered for (a late 2xx
              // after a sign-out / rotation must not land on the next session).
              if (sid === renderSid) track('consent_gate_accepted', consentGateData(placement, variant, !!reward));
              scrollToBottom();
            });
          }
          return res.json().catch(function () { return null; }).then(function (data) {
            var code = data && data.error && data.error.code;
            if (res.status === 422 || code === 'no_verified_email') {
              // Rare: no verified email on file -> fall back to the typed-email
              // capture form (the existing, unchanged path).
              body.replaceChildren();
              body.appendChild(el('div', { class: 'ms-chat-card-text', text: OPTIN_COPY.noEmail }));
              var b = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary' }, [OPTIN_COPY.noEmailBtn]);
              b.addEventListener('click', function () { markOptInDone(); card.remove(); openCaptureForm(); });
              body.appendChild(b);
              return;
            }
            if (res.status === 429 || code === 'rate_limited') {
              var retry = parseInt(res.headers.get('Retry-After'), 10);
              if (!isFinite(retry) || retry <= 0) retry = 30;
              showError(OPTIN_COPY.errRate);
              submit.textContent = OPTIN_COPY.submit;
              setTimeout(function () { submit.disabled = false; }, retry * 1000);
              return;
            }
            var msg = (data && data.error && data.error.message) || '';
            if (res.status === 502 || res.status === 503 || code === 'upstream_unavailable') msg = OPTIN_COPY.errUpstream;
            else if (!msg) msg = OPTIN_COPY.errGeneric;
            showError(msg);
            submit.disabled = false;
            submit.textContent = OPTIN_COPY.submit;
          });
        }).catch(function () {
          showError(OPTIN_COPY.errUpstream);
          submit.disabled = false;
          submit.textContent = OPTIN_COPY.submit;
        });
      });
    }

    // preCopy: the caller already fetched + approved it for this sid.
    if (preCopy) { renderForm(preCopy); return card; }

    fetchSignInConsentCopy().then(function (c) {
      // lawyerApproved:false means the copy is not yet legally signed off — do
      // not launch the surface to real users (CONSENT_FLOW.md §1).
      if (c.lawyerApproved !== true) { card.remove(); return; }
      renderForm(c);
    }).catch(function () {
      // No served copy -> render nothing (we never hard-code consent text).
      card.remove();
    });

    return card;
  }

  // Present the opt-in INLINE after a sign-in moment when the welcome state
  // (which renders it in its own auth slot) isn't on screen — e.g. signing in
  // mid-conversation. Non-stacking and session-gated; never shown to anonymous.
  var lastOptInRow = null;
  function presentSignInOptIn() {
    try {
      if (!optInActionable()) return;
      // Welcome on screen -> stay clean (orb only); the first-message popup
      // asks once the customer actually starts chatting.
      if (welcomeEl && welcomeEl.parentNode === messagesEl) return;
      if (lastOptInRow && lastOptInRow.parentNode === messagesEl) {
        lastOptInRow.scrollIntoView({ block: 'nearest' });
        return;
      }
      var ar = assistantRow();
      ar.content.appendChild(buildMarketingOptInCard('signin_return'));
      messagesEl.appendChild(ar.row);
      lastOptInRow = ar.row;
      scrollToBottom();
    } catch (e) {
      try { console.error('[ms-chat] presentSignInOptIn failed', e); } catch (e2) {}
    }
  }

  // Value-moment ask (SPEC B3b, placement 'value_moment'): when the served
  // copy carries `valueMoment`, the signed-in opt-in waits for a finished
  // live product turn instead of the first-message popup, and is appended
  // as its own Mo row (never part of asstParts, so never saved to history).
  // Only finalizeStream calls this — restored history never asks. At most
  // once per tab session (OPTIN_SHOWN_KPI_KEY, set when the card renders);
  // vmAskState stops a second turn from arming it while the copy is fetched.
  var VM_PARTS = { 'tool-show_product': 1, 'tool-compare_products': 1, 'tool-add_to_cart': 1 };
  var vmAskState = 0; // 0 idle, 1 copy in flight, 2 shown (this page view)
  function hasValueMomentPart(parts) {
    for (var i = 0; parts && i < parts.length; i++) {
      if (parts[i] && VM_PARTS[parts[i].type] === 1) return true;
    }
    return false;
  }
  // Re-run after the copy fetch: anything may have changed meanwhile.
  function valueMomentEligible(forSid) {
    if (sid !== forSid || !state.open || voiceMode || gateEl || state.rateLocked) return false;
    // A newer turn already streaming: the card would land inside it.
    if (state.streaming) return false;
    // Never while a dialog is open — the history drawer counts too.
    if (historyEl && historyEl.classList.contains('ms-chat-history--open')) return false;
    if (!auth.settled || !auth.signedIn || !optInActionable()) return false;
    if (ssGet(OPTIN_SHOWN_KPI_KEY) === '1') return false;
    // Back from the chat's own sign-in in this tab: they keep the immediate
    // ask (popup / signin_return card, SPEC B3a) — even when a fast product
    // turn finishes before the first-message popup's 700 ms timer.
    if (ssGet(SIGNIN_RETURNED_SS_KEY) === '1') return false;
    // A pending opt-in card already is the ask — never stack a second one.
    if (lastOptInRow && lastOptInRow.parentNode === messagesEl &&
        lastOptInRow.querySelector('.ms-chat-optin-card')) return false;
    return true;
  }
  function maybeValueMomentAsk(parts, forSid, opts) {
    if (vmAskState !== 0) return;
    // A live user turn only (not the nudge's context greeting).
    if (!opts || !opts.userMsg || !hasValueMomentPart(parts)) return;
    if (!valueMomentEligible(forSid)) return;
    vmAskState = 1;
    fetchSignInConsentCopy().then(function (c) {
      vmAskState = 0;
      if (!c || c.lawyerApproved !== true || !servedValueMoment(c)) return;
      if (!valueMomentEligible(forSid)) return;
      var ar = assistantRow();
      ar.content.appendChild(buildMarketingOptInCard('value_moment', c));
      messagesEl.appendChild(ar.row);
      lastOptInRow = ar.row;
      vmAskState = 2;
      scrollToBottom();
    }).catch(function () { vmAskState = 0; }); // no copy -> no ask, silent
  }

  // ---------------------------------------------------------------------------
  // First-message gate — ONE popup per browser session, right after the user
  // sends their first message (the reply streams in behind it, so the visitor
  // has already invested in the conversation — the value moment). The reply
  // is NEVER conditional on the choice: every button, the backdrop and Esc
  // all land in the same answered chat. Which popup appears depends on the
  // resolved auth tier:
  //   * ANONYMOUS (auth settled, not signed in) -> the sign-in motivation
  //     popup (presentLoginGate). Pure UI chrome — the same benefit copy as the
  //     welcome sign-in card (ACCOUNT_COPY), no consent text, no backend copy
  //     needed, so it always renders. "Anmelden" runs the normal top-level
  //     sign-in redirect; the conversation survives it (see presentLoginGate).
  //   * SIGNED IN + optInActionable -> the marketing consent gate
  //     (presentConsentGate): served surface=signin copy rendered verbatim
  //     (never hard-coded, lawyerApproved-gated — same rules as every consent
  //     surface), POST /api/account/marketing-opt-in (account email, no email
  //     field).
  //
  // Marketing-gate consent mechanic: a CLEAR AFFIRMATIVE ACT. The served
  // marketingLabel + consentFooter are fully visible above the buttons and
  // only an explicit "Accept" tap sends marketingConsent:true (button-consent,
  // like a cookie dialog — never pre-decided, never auto-submitted).
  //
  // Anti-nag rules, chosen deliberately (GDPR "freely given" + the
  // no-dark-patterns golden rule), shared by both popups:
  //   * at most ONE popup per browser session (GATE_SS_KEY is shared, so a
  //     visitor never sees the login popup and the opt-in in the same visit),
  //   * never in voice mode, only while the panel is open, never stacked,
  //   * marketing: shown ONLY when /api/auth/me says signedIn +
  //     marketing.optInActionable === true (a shop subscriber reads
  //     confirmed / false and is never asked); an ACCEPT is remembered forever
  //     (device + backend record), an explicit DECLINE ("Nein, danke" here or
  //     on the inline card) quiets both surfaces for 30 days on this device,
  //   * login: "Später" snoozes the login popup for 24h under its OWN key — it
  //     says nothing about marketing, so it never touches the marketing
  //     decision memory,
  //   * backdrop/Esc = "later": quiet for this browser session only (popup
  //     and inline card alike), the next session may ask again.
  // ---------------------------------------------------------------------------
  var GATE_COPY = {
    aria: 'Angebote aktivieren',
    // No benefit bullets here: the consent popup's framing (headline +
    // `benefits`) is SERVED copy (API_CONTRACT §0 rule 11, CONSENT_CONTRACT §1).
    accept: 'Ja, Angebote aktivieren',
    decline: 'Nein, danke',
    sending: 'Wird gesendet…',
    successTitle: 'Fast geschafft!',
    successPending: 'Bitte bestätige deine Anmeldung über den Link in der E-Mail — erst danach bekommst du unsere Angebote.',
    continueBtn: 'Weiter zur Antwort',
    errRate: 'Zu viele Anfragen — bitte kurz warten.',
    errUpstream: 'Gerade nicht möglich — bitte versuch es später erneut.',
    errGeneric: 'Das hat leider nicht geklappt. Bitte versuch es erneut.',
    // Login popup chrome (anonymous). Headline, intro and bullets are reused
    // from ACCOUNT_COPY so the popup and the welcome card always say the same
    // thing — no new promises live here.
    loginAria: 'Anmelden',
    loginLater: 'Später',
    // Factual reassurance: the chat is kept in this browser under the same
    // session id and the redirect returns to this page (initiateLogin).
    loginKeep: 'Dein bisheriger Chat bleibt nach der Anmeldung erhalten.',
    loginWaiting: 'Antwort wird noch geladen…'
  };
  if (LOCALE === 'en') Object.assign(GATE_COPY, {
    aria: 'Activate offers',
    accept: 'Yes, activate offers',
    decline: 'No, thanks',
    sending: 'Sending…',
    successTitle: 'Almost there!',
    successPending: 'Please confirm via the link in the email — only then will you receive our offers.',
    continueBtn: 'Back to the answer',
    errRate: 'Too many requests — please wait a moment.',
    errUpstream: 'Not possible right now — please try again later.',
    errGeneric: 'That didn\'t work. Please try again.',
    loginAria: 'Sign in',
    loginLater: 'Not now',
    loginKeep: 'Your chat so far is kept after you sign in.',
    loginWaiting: 'Finishing the answer…'
  });

  // Device-local marketing-decision memory. This is UX memory ONLY (when to
  // stop asking) — never proof of consent; the backend's DOI record stays the
  // single legal source of truth. Accepted -> never ask again on this device;
  // declined -> quiet for MKT_DECLINE_SNOOZE_MS. 30 days because the backend
  // records no "Nein" (CUSTOMER_ACCOUNT.md §6.1: optInActionable stays true
  // after a decline), so without it a customer would be asked at every
  // sign-in; a month keeps the ask rare without making a "no" permanent.
  var MKT_DECISION_KEY = 'ms-chat-mkt-decision';
  var MKT_DECLINE_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
  var GATE_SS_KEY = 'ms-chat-gate-shown'; // ONE first-message popup per browser session
  // Login popup "Später" snooze (device-local timestamp). Its own key on
  // purpose: declining to sign in is not a marketing decision.
  var LOGIN_GATE_SNOOZE_KEY = 'ms-chat-login-gate-snooze';
  var LOGIN_GATE_SNOOZE_MS = 24 * 60 * 60 * 1000;
  // An accept recorded on this device keeps every signin ask quiet this long
  // (SPEC C, HARDENING-2): a second tab whose /api/auth/me predates the
  // accept must not ask again and trigger a second DOI mail.
  var MKT_ACCEPT_QUIET_MS = 24 * 60 * 60 * 1000;
  // A chat sign-in return completed in this tab session (handleAuthReturn).
  // In value-moment mode those visitors keep the immediate ask (SPEC B3a).
  var SIGNIN_RETURNED_SS_KEY = 'ms-chat-signin-returned';

  function loadMktDecision() {
    try {
      var raw = lsGet(MKT_DECISION_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      return (d && typeof d === 'object' && typeof d.state === 'string') ? d : null;
    } catch (e) { return null; }
  }
  function recordMktDecision(state) {
    try { lsSet(MKT_DECISION_KEY, JSON.stringify({ state: state, at: Date.now() })); } catch (e) {}
  }
  // True while this device remembers a DECLINE within MKT_DECLINE_SNOOZE_MS
  // (the backend does not record a "Nein", CUSTOMER_ACCOUNT.md §6.1), or an
  // ACCEPT within MKT_ACCEPT_QUIET_MS. The accept window is short on purpose:
  // after it the backend answers optInActionable:false anyway, and when a DOI
  // link expires unconfirmed it deliberately turns true again so the ask may
  // be offered once more — a lasting device "accepted" flag would override
  // that; 24 h only covers another tab with a stale /api/auth/me. Read by
  // optInActionable(), so the popup and the inline cards follow one memory.
  function mktDecisionQuiet() {
    var d = loadMktDecision();
    if (!d) return false;
    var age = Date.now() - Number(d.at);
    if (!(age >= 0)) return false;
    if (d.state === 'declined') return age < MKT_DECLINE_SNOOZE_MS;
    if (d.state === 'accepted') return age < MKT_ACCEPT_QUIET_MS;
    return false;
  }

  var gateEl = null; // the open gate overlay (at most one)
  var gateCloseFn = null; // its close(), for a session reset from another tab

  // Rules shared by both popups: one per session, never stacked, never in the
  // hands-free voice loop, and only once the auth tier is known (an unsettled
  // tier just waits for the next turn — the session key is set only on show).
  function gateBaseEligible() {
    if (gateEl || ssGet(GATE_SS_KEY)) return false;
    if (voiceMode) return false;      // never interrupt the hands-free loop
    if (cfgGateHold()) return false;  // configurator: not before Mo's plan
    if (!auth.settled) return false;  // unknown tier -> wait for the next turn
    return true;
  }

  // Marketing opt-in gate — signed-in customers only.
  function consentGateEligible() {
    if (!gateBaseEligible() || !auth.signedIn) return false;
    // The inline opt-in card (presentSignInOptIn) is still on screen and
    // unanswered -> it already IS the ask; never stack the same marketing
    // question a second time. (Answering or dismissing it marks the opt-in
    // done, and a card without served copy removes itself, so checking for
    // the card inside the row keeps the gate quiet only while it's pending.)
    if (lastOptInRow && lastOptInRow.parentNode === messagesEl &&
        lastOptInRow.querySelector('.ms-chat-optin-card')) return false;
    // The inline card already asked this session (answered or not) -> the
    // popup stays quiet; the next session may ask again.
    if (ssGet(OPTIN_SHOWN_KPI_KEY) === '1') return false;
    // The backend knows the real decision state — trust it; optInActionable()
    // adds the local memory (answered/dismissed this session, declined on
    // this device within 30 days, accepted).
    return optInActionable();
  }

  // Sign-in motivation popup — anonymous visitors only.
  function loginGateEligible() {
    if (!gateBaseEligible() || auth.signedIn) return false;
    var snoozedAt = parseInt(lsGet(LOGIN_GATE_SNOOZE_KEY), 10);
    if (isFinite(snoozedAt) && (Date.now() - snoozedAt) < LOGIN_GATE_SNOOZE_MS) return false;
    return true;
  }

  // Called (debounced by the session guard) right after a user message is
  // sent. Anonymous -> the login popup, synchronously (no copy to fetch).
  // Signed in -> the marketing copy is fetched BEFORE anything is shown: no
  // served copy or lawyerApproved !== true -> no gate at all (fail-closed,
  // silent).
  // `tries` (internal): the auth tier usually settles within a few hundred ms
  // of the panel opening, but a deep link / product-CTA send can beat it.
  // Rather than silently skipping to the next turn, poll briefly (≤5s) for
  // the tier to settle, then decide.
  function maybeShowConsentGate(tries, userMsg) {
    if (!state.open) return;
    // The send that scheduled us failed and was rolled back (network/5xx/429):
    // there is no conversation to "keep", and the error notice must not be
    // covered — keep the one popup of this session for a successful turn.
    if (userMsg && messages.indexOf(userMsg) === -1) return;
    if (state.rateLocked) return;
    tries = tries || 0;
    if (!auth.settled && tries < 10) {
      setTimeout(function () { maybeShowConsentGate(tries + 1, userMsg); }, 500);
      return;
    }
    if (loginGateEligible()) { presentLoginGate(userMsg); return; }
    if (!consentGateEligible()) return;
    fetchSignInConsentCopy().then(function (c) {
      if (!c || c.lawyerApproved !== true) return;
      // Value-moment mode (SPEC B3a): the ask waits for a product turn
      // (maybeValueMomentAsk) — no popup, no GATE_SS_KEY, no KPI here. A
      // visitor back from the chat's own sign-in keeps the immediate ask.
      if (servedValueMoment(c) && ssGet(SIGNIN_RETURNED_SS_KEY) !== '1') return;
      // Re-check after the async fetch (a second send, sign-out, voice mode…).
      if (!consentGateEligible() || !state.open) return;
      presentConsentGate(c);
    }).catch(function () {}); // surface not served -> render nothing
  }

  // Shared dialog shell for both popups: overlay over the chat panel (the
  // reply keeps streaming behind the dimmed backdrop), minimal focus trap, and
  // backdrop/Esc = "later" (`onDefer` records the KPI; no snooze is stored —
  // the session guard alone keeps this visit quiet). Closing always reveals
  // the streamed answer and hands focus back to the composer. `onClose`
  // (optional) runs on every close, e.g. to cancel pending work.
  function openGateDialog(ariaLabel, onDefer, onClose) {
    var wrap = el('div', { class: 'ms-chat-gate', role: 'dialog', 'aria-modal': 'true', 'aria-label': ariaLabel });
    var backdrop = el('div', { class: 'ms-chat-gate-backdrop' });
    var card = el('div', { class: 'ms-chat-gate-card', tabindex: '-1' });
    wrap.appendChild(backdrop);
    wrap.appendChild(card);

    function close() {
      if (onClose) { try { onClose(); } catch (e) {} }
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      if (gateEl === wrap) { gateEl = null; gateCloseFn = null; }
      scrollToBottom(); // reveal the streamed answer behind the gate
      // The popup usually closes while the reply is still streaming, when the
      // composer is disabled (updateInputState) and focus() on it is a no-op —
      // focus would fall to <body> and keyboard users would lose their place.
      // Park focus on the message list instead (programmatic-only tabindex).
      try {
        if (textarea.disabled) {
          if (!messagesEl.hasAttribute('tabindex')) messagesEl.setAttribute('tabindex', '-1');
          messagesEl.focus({ preventScroll: true });
        } else {
          textarea.focus();
        }
      } catch (e) {}
    }
    function defer() { onDefer(); close(); }
    backdrop.addEventListener('click', defer);
    wrap.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') { ev.stopPropagation(); defer(); return; }
      // Minimal focus trap: keep Tab cycling inside the dialog. Disabled
      // controls (login popup "waiting" state, consent gate setBusy) are
      // skipped — focus() on them is a no-op and would let Tab escape. Focus
      // on the card itself (where show() puts it) or anywhere outside the
      // card counts as an edge, so Shift+Tab right after opening can't fall
      // through to the panel controls behind the aria-modal backdrop.
      if (ev.key === 'Tab') {
        var f = Array.prototype.filter.call(card.querySelectorAll('button, input, a[href]'),
          function (n) { return !n.disabled; });
        if (!f.length) { ev.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1], a = document.activeElement;
        if (ev.shiftKey && (a === first || a === card || !card.contains(a))) { ev.preventDefault(); last.focus(); }
        else if (!ev.shiftKey && (a === last || !card.contains(a))) { ev.preventDefault(); first.focus(); }
      }
    });

    // The brand orb keeps the dialog friendly — this is Mo asking, not a
    // legal wall.
    card.appendChild(logoEl('ms-chat-gate-logo'));

    return {
      card: card,
      close: close,
      show: function () {
        gateEl = wrap;
        gateCloseFn = close;
        panel.appendChild(wrap);
        try { card.focus(); } catch (e) {}
      }
    };
  }

  // Anonymous sign-in motivation popup. It has its OWN KPI names — the
  // consent_gate_* events are the documented MARKETING-consent metrics
  // (surface 'signin' | 'chat'); a sign-in click must never be counted as a
  // consent acceptance by an aggregate that doesn't filter on surface:
  //   login_gate_shown           — popup rendered
  //   login_gate_signin_clicked  — "Anmelden" tapped (initiateLogin then also
  //                                sends account_signin_started {source:'login_gate'})
  //   login_gate_declined        — "Später" (24h snooze)
  //   login_gate_dismissed       — backdrop / Esc (this session only)
  // Nothing personal is sent — event names only (login_gate_shown adds
  // teaser:true + the served variant id only when the reward teaser rendered).
  //
  // The served reward teaser (SPEC B2) needs the signin copy: it is fetched
  // first, but the popup waits for it at most LOGIN_GATE_COPY_WAIT_MS and
  // then shows without it. loginGatePending keeps a second send's gate check
  // from presenting a second popup during that wait (GATE_SS_KEY is set only
  // at show time).
  var LOGIN_GATE_COPY_WAIT_MS = 1200;
  var loginGatePending = false;
  // The latest send that asked for the popup during the wait: if the first
  // send is rolled back, a later one still gets its popup.
  var loginGateLatestMsg = null;
  function presentLoginGate(userMsg) {
    loginGateLatestMsg = userMsg || null;
    if (loginGatePending) return;
    loginGatePending = true;
    var reqSid = sid;
    var timer = null;
    var copyP = fetchSignInConsentCopy().catch(function () { return null; });
    var waitP = new Promise(function (resolve) {
      timer = setTimeout(function () { resolve(null); }, LOGIN_GATE_COPY_WAIT_MS);
    });
    Promise.race([copyP, waitP]).then(function (c) {
      clearTimeout(timer);
      loginGatePending = false;
      // Re-check after the wait: same session, panel open, no dialog, still
      // anonymous + eligible — plus maybeShowConsentGate's own send guards
      // (a rolled-back or rate-limited send keeps its error notice visible).
      if (sid !== reqSid || !state.open || gateEl || !loginGateEligible()) return;
      var liveMsg = (userMsg && messages.indexOf(userMsg) !== -1) ||
        (loginGateLatestMsg && messages.indexOf(loginGateLatestMsg) !== -1);
      if ((userMsg || loginGateLatestMsg) && !liveMsg) return;
      if (state.rateLocked) return;
      showLoginGate(c);
    });
  }
  function showLoginGate(c) {
    var teaser = signInTeaserReward(c);
    var shownData = {};
    if (teaser) {
      shownData.teaser = true;
      var tv = servedVariant(c);
      if (tv) shownData.variant = tv;
    }
    ssSet(GATE_SS_KEY, '1');
    track('login_gate_shown', shownData);

    var waitTimer = null;
    var dlg = openGateDialog(GATE_COPY.loginAria,
      function () { track('login_gate_dismissed', {}); },
      function () { if (waitTimer) { clearInterval(waitTimer); waitTimer = null; } });
    var card = dlg.card;

    card.appendChild(el('div', { class: 'ms-chat-gate-headline', text: ACCOUNT_COPY.signInTitle }));
    card.appendChild(el('div', { class: 'ms-chat-gate-intro', text: ACCOUNT_COPY.signInIntro }));
    var ul = el('ul', { class: 'ms-chat-gate-benefits' });
    ACCOUNT_COPY.benefits.forEach(function (b) {
      var li = el('li');
      li.appendChild(icon('check'));
      li.appendChild(el('span', { text: b }));
      ul.appendChild(li);
    });
    card.appendChild(ul);
    // Served reward teaser + its terms under the (widget) benefits.
    if (teaser) {
      card.appendChild(rewardTeaser(teaser));
      card.appendChild(rewardTerms(teaser));
    }

    var signIn = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--primary ms-chat-gate-accept' }, [ACCOUNT_COPY.signInHeader]);
    // "Später" is a REAL, equally reachable choice (same size, right below).
    var later = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary ms-chat-gate-decline' }, [GATE_COPY.loginLater]);
    card.appendChild(signIn);
    card.appendChild(later);
    card.appendChild(el('div', { class: 'ms-chat-signin-hint ms-chat-gate-hint', text: GATE_COPY.loginKeep }));

    later.addEventListener('click', function () {
      lsSet(LOGIN_GATE_SNOOZE_KEY, String(Date.now()));
      track('login_gate_declined', {});
      dlg.close();
    });

    // The popup appears ~0.7s after the first send, so the reply is usually
    // still streaming. The assistant message is only written to the local
    // history when its stream finishes (finalizeStream -> saveHistory), so
    // redirecting mid-stream would come back to the question WITHOUT its
    // answer. Wait for the stream to settle (capped, so a hung stream can't
    // trap the visitor), then run the same redirect as the welcome card and
    // header button: same sid + history in localStorage, return_url = this
    // page, panel re-opened on return.
    var WAIT_STEP_MS = 200;
    var WAIT_MAX_MS = 20000;
    signIn.addEventListener('click', function () {
      track('login_gate_signin_clicked', {});
      if (!state.streaming) { initiateLogin('login_gate'); return; }
      signIn.disabled = true;
      signIn.textContent = GATE_COPY.loginWaiting;
      var waited = 0;
      waitTimer = setInterval(function () {
        waited += WAIT_STEP_MS;
        if (state.streaming && waited < WAIT_MAX_MS) return;
        clearInterval(waitTimer);
        waitTimer = null;
        initiateLogin('login_gate');
      }, WAIT_STEP_MS);
    });

    dlg.show();
  }

  // Signed-in marketing opt-in gate (surface=signin).
  function presentConsentGate(c) {
    var placement = 'popup';
    // Captured from the RENDERED copy object (CONSENT_CONTRACT §3.2), together
    // with the session the ask is rendered for.
    var variant = servedVariant(c);
    var renderSid = sid;
    // The served reward (null: none) — rendered all or nothing; flags every
    // consent_gate_* of this ask with reward:true (SPEC B1).
    var reward = c.lawyerApproved === true ? servedReward(c) : null;
    // acceptStarted: the accept POST went out — from then on Esc / backdrop
    // (also on the success view) is no "dismiss" (it was a decision).
    // dialogClosed: the dialog is gone — a late POST answer must not reopen
    // anything (no capture form, no error text on a detached card).
    var acceptStarted = false;
    var dialogClosed = false;
    ssSet(GATE_SS_KEY, '1');
    if (ssGet(OPTIN_SHOWN_KPI_KEY) !== '1') { // shared with the inline card
      ssSet(OPTIN_SHOWN_KPI_KEY, '1');
      track('consent_gate_shown', consentGateData(placement, variant, !!reward));
    }

    var dlg = openGateDialog(GATE_COPY.aria, function () {
      // Esc/backdrop: quiet for this browser session, the inline card
      // included. Counted as consent_gate_dismissed only while no decision
      // was made — after the accept tap it behaves like „Weiter zur Antwort“.
      markOptInDone();
      if (!acceptStarted) track('consent_gate_dismissed', consentGateData(placement, variant, !!reward));
    }, function () { dialogClosed = true; });
    var card = dlg.card;
    var close = dlg.close;

    // Reward badge between the logo and the headline (§3.1 extended).
    if (reward) card.appendChild(rewardBadge(reward));
    // Served benefit headline (framing, NOT part of consentTextShown).
    if (typeof c.headline === 'string' && c.headline) {
      card.appendChild(el('div', { class: 'ms-chat-gate-headline', text: c.headline }));
    }
    // Served benefit bullets (v5, CONSENT_CONTRACT §3.1): verbatim, all or
    // nothing — never bullets of the widget's own (§0 rule 11).
    var gateBenefits = servedBenefits(c);
    if (gateBenefits) card.appendChild(benefitsList(gateBenefits, 'ms-chat-gate-benefits'));
    // Served reward terms after the framing, before the consent statement.
    if (reward) card.appendChild(rewardTerms(reward));

    var errEl = el('div', { class: 'ms-chat-form-error', style: 'display:none' });
    function showError(m) { errEl.textContent = m; errEl.style.display = 'block'; }
    function clearError() { errEl.textContent = ''; errEl.style.display = 'none'; }

    // The SERVED consent statement — fully visible above the buttons; tapping
    // "Accept" IS the affirmative act for exactly this text. Echoed back as
    // consentTextShown verbatim (Art. 7 audit record).
    card.appendChild(el('div', { class: 'ms-chat-gate-consent', text: c.marketingLabel }));
    if (typeof c.consentFooter === 'string' && c.consentFooter) {
      card.appendChild(el('div', { class: 'ms-chat-consent-footer', text: c.consentFooter }));
    }
    var legal = el('div', { class: 'ms-chat-legal-links' });
    var impHref = safeHref(c.imprintUrl);
    var privHref = safeHref(c.privacyUrl);
    if (impHref) legal.appendChild(el('a', { href: impHref, target: '_blank', rel: 'noopener noreferrer', text: L('Impressum', 'Imprint') }));
    if (privHref) legal.appendChild(el('a', { href: privHref, target: '_blank', rel: 'noopener noreferrer', text: L('Datenschutz', 'Privacy') }));
    card.appendChild(legal);

    card.appendChild(errEl);

    var accept = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--primary ms-chat-gate-accept' }, [GATE_COPY.accept]);
    // Decline is a REAL, equally reachable choice (same size, right below) —
    // quieter styling is allowed, a hidden/hard decline is not.
    var decline = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--secondary ms-chat-gate-decline' }, [GATE_COPY.decline]);
    card.appendChild(accept);
    card.appendChild(decline);

    decline.addEventListener('click', function () {
      recordMktDecision('declined');
      markOptInDone(); // also quiets the welcome opt-in card this session
      track('consent_gate_declined', consentGateData(placement, variant, !!reward));
      close();
    });

    // `outcome` from marketingOutcome(): the inbox line only for a DOI mail
    // that really went out; an already-subscribed answer says so instead.
    function showSuccess(outcome) {
      var ok = el('div', { class: 'ms-chat-form-success' });
      ok.appendChild(icon('check'));
      ok.appendChild(el('h3', { text: outcome === 'pending' ? GATE_COPY.successTitle
        : (outcome === 'already' ? MKT_RESULT_COPY.alreadyTitle : MKT_RESULT_COPY.otherTitle) }));
      ok.appendChild(el('p', { text: outcome === 'pending' ? GATE_COPY.successPending
        : (outcome === 'already' ? MKT_RESULT_COPY.already : MKT_RESULT_COPY.other) }));
      // Served reward note — never for 'already' (no new sign-up).
      if (reward && reward.afterAccept && outcome !== 'already') {
        ok.appendChild(el('p', { class: 'ms-chat-reward-after', text: reward.afterAccept }));
      }
      var cont = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--primary' }, [GATE_COPY.continueBtn]);
      cont.addEventListener('click', close);
      card.replaceChildren(ok, cont);
      try { cont.focus(); } catch (e) {}
    }

    function setBusy(busy) {
      accept.disabled = busy;
      decline.disabled = busy;
      accept.textContent = busy ? GATE_COPY.sending : GATE_COPY.accept;
    }

    function handleFailure(res, data) {
      var code = data && data.error && data.error.code;
      if (res && (res.status === 429 || code === 'rate_limited')) {
        var retry = parseInt(res.headers.get('Retry-After'), 10);
        if (!isFinite(retry) || retry <= 0) retry = 30;
        showError(GATE_COPY.errRate);
        decline.disabled = false;
        accept.textContent = GATE_COPY.accept;
        setTimeout(function () { accept.disabled = false; }, retry * 1000);
        return;
      }
      var msg = (data && data.error && data.error.message) || '';
      if (!res || res.status === 502 || res.status === 503 || code === 'upstream_unavailable') msg = GATE_COPY.errUpstream;
      else if (!msg) msg = GATE_COPY.errGeneric;
      showError(msg);
      setBusy(false);
    }

    accept.addEventListener('click', function () {
      clearError();
      setBusy(true);
      var payload = {
        marketingConsent: true, // the user's explicit Accept tap on the shown text
        // The served audit string, echoed back VERBATIM (Art. 7) — never
        // recomposed client-side.
        consentTextShown: c.consentTextShown,
        locale: LOCALE,
        // Telemetry echo (ACCOUNT_CONTRACT §6.2) — JSON body only, no header.
        placement: placement
      };
      if (variant) payload.variant = variant;
      acceptStarted = true;
      fetch(API_BASE + '/api/account/marketing-opt-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ms-chat-key': CHAT_KEY, 'x-ms-session': sid, 'x-ms-locale': LOCALE },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (res.status === 401) { accountUnauthorized(); if (!dialogClosed) close(); return; }
        if (res.status === 422) {
          // No verified account email -> fall back to the typed-email capture
          // form (the existing, unchanged path) — unless the shopper already
          // closed the dialog: then they left the ask deliberately.
          if (dialogClosed) return;
          close();
          openCaptureForm();
          return;
        }
        if (res.ok) {
          return res.json().catch(function () { return null; }).then(function (data) {
            recordMktDecision('accepted');
            markOptInDone();
            // Only under the session the ask was rendered for: a late 2xx
            // after a sign-out / rotation must not land on the next session
            // (the server's signin_optin events keep the record).
            if (sid === renderSid) track('consent_gate_accepted', consentGateData(placement, variant, !!reward));
            if (!dialogClosed) showSuccess(marketingOutcome(data && data.marketing));
          });
        }
        if (dialogClosed) return; // closed meanwhile: nothing visible, no KPI
        return res.json().catch(function () { return null; }).then(function (data) { handleFailure(res, data); });
      }).catch(function () { if (!dialogClosed) handleFailure(null, null); });
    });

    dlg.show();
  }

  // Reflect the resolved auth state across the chrome. Called on every auth
  // change; a no-op shape for anonymous (signInBtn shown, history hidden,
  // share button behaves exactly as before).
  function reflectAuthState() {
    if (signInBtn) signInBtn.classList.toggle('ms-chat-signin-btn--visible', auth.settled && !auth.signedIn);
    if (accountBtn) {
      // Signed in: the header shows the customer's name (task §2) in place of
      // the "Anmelden" button; tapping it opens their conversation history.
      accountBtn.classList.toggle('ms-chat-account-btn--shown', auth.signedIn);
      if (accountNameEl) accountNameEl.textContent = auth.name || L('Konto', 'Account');
    }
    updateShareBtn();          // hides the email-capture entry point when signed in
    updateWelcomeAuth();       // greeting / sign-in card inside the welcome state
    updateShopSignInBtn();     // "Mit Kundenkonto anmelden" for shop-only sign-ins
    if (!auth.signedIn) closeHistory();
  }

  // Welcome-state addition: the sign-in benefits card when settled-but-
  // anonymous. Signed-in customers get the bare orb (owner request: a clean
  // welcome) — no greeting and no opt-in card here; the first-message popup is
  // their single marketing ask. Lives in a container built into the welcome
  // element; emptied/filled here.
  function updateWelcomeAuth() {
    if (welcomeAuthEl) {
      welcomeAuthEl.replaceChildren();
      if (auth.settled && !auth.signedIn) welcomeAuthEl.appendChild(buildSignInCard());
    }
  }

  // --- Conversation-history drawer (signed-in only) ---------------------------
  var shopSignInBtn = null;
  function updateShopSignInBtn() {
    if (shopSignInBtn) shopSignInBtn.style.display = (auth.signedIn && authVia() === 'shop') ? '' : 'none';
  }
  function buildHistoryDrawer() {
    historyEl = el('div', { class: 'ms-chat-history', 'aria-hidden': 'true', role: 'dialog', 'aria-label': ACCOUNT_COPY.historyAria });

    var head = el('div', { class: 'ms-chat-history-head' });
    historyTitleEl = el('span', { class: 'ms-chat-history-title', text: ACCOUNT_COPY.historyTitle });
    var back = el('button', { class: 'ms-chat-iconbtn', type: 'button', 'aria-label': L('Verlauf schließen', 'Close history') });
    back.appendChild(icon('close'));
    back.addEventListener('click', closeHistory);
    head.appendChild(historyTitleEl);
    head.appendChild(back);
    historyEl.appendChild(head);

    var newBtn = el('button', { class: 'ms-chat-btn ms-chat-btn--primary ms-chat-history-new', type: 'button' });
    newBtn.appendChild(icon('plus'));
    newBtn.appendChild(el('span', { text: ACCOUNT_COPY.newChat }));
    newBtn.addEventListener('click', function () {
      track('account_new_consultation', {});
      startNewChat();                              // mints a fresh conversationKey
      addOptimisticConversation(activeConversationKey); // task §3: show it at once
      closeHistory();                              // reveal the empty chat to type in
    });
    historyEl.appendChild(newBtn);

    historyListEl = el('div', { class: 'ms-chat-history-list' });
    historyEl.appendChild(historyListEl);

    var foot = el('div', { class: 'ms-chat-history-foot' });
    // Recognised only through the shop's login (whoami): keep the chat's own
    // "Anmelden" reachable — the order status (get_order_status) needs that
    // sign-in once (CHAT_ORDER_STATUS.md). Hidden after a real chat sign-in;
    // updateShopSignInBtn() decides.
    shopSignInBtn = el('button', { class: 'ms-chat-history-link', type: 'button', text: L('Mit Kundenkonto anmelden', 'Sign in with your account') });
    shopSignInBtn.addEventListener('click', function () { initiateLogin(); });
    foot.appendChild(shopSignInBtn);
    updateShopSignInBtn();
    var outBtn = el('button', { class: 'ms-chat-history-link', type: 'button', text: ACCOUNT_COPY.logout });
    outBtn.addEventListener('click', signOut);
    foot.appendChild(outBtn);
    // GDPR data-rights pair, signed-in only (same gate as this drawer): export
    // (Art. 15/20) then the heavier erase (Art. 17).
    var exportWrap = el('div', { class: 'ms-chat-history-export' });
    buildExportControl(exportWrap);
    foot.appendChild(exportWrap);
    var eraseWrap = el('div', { class: 'ms-chat-history-erase' });
    buildEraseControl(eraseWrap);
    foot.appendChild(eraseWrap);
    historyEl.appendChild(foot);

    panel.appendChild(historyEl);
  }

  function openHistory() {
    if (!historyEl || !auth.signedIn) return;
    track('account_history_opened', {});
    historyTitleEl.textContent = auth.name ? (L('Hallo ', 'Hi ') + auth.name) : ACCOUNT_COPY.historyTitle;
    historyEl.classList.add('ms-chat-history--open');
    historyEl.setAttribute('aria-hidden', 'false');
    loadConversations();
  }
  function closeHistory() {
    if (!historyEl) return;
    historyEl.classList.remove('ms-chat-history--open');
    historyEl.setAttribute('aria-hidden', 'true');
  }

  // Task §1: surface the conversation history PROMINENTLY for signed-in
  // customers rather than leaving it behind a header control. When a signed-in
  // customer opens the chat onto the welcome state (no conversation in
  // progress), land them straight on their history list — past beratungen +
  // "Neue Beratung". A mid-conversation reopen (messages present) is left alone
  // so we never cover an active chat. The header name pill stays as the re-entry.
  // Armed on each panel open and flushed once detection has settled.
  var autoHistoryArmed = false;
  function maybeAutoOpenHistory() {
    if (!state.open || !auth.signedIn || DOCK) return;
    if (messages.length) return;
    if (!historyEl || historyEl.classList.contains('ms-chat-history--open')) return;
    openHistory();
  }
  function flushAutoHistory() {
    if (!autoHistoryArmed || !auth.settled) return; // wait for the probe; applyAuth re-calls
    autoHistoryArmed = false;
    maybeAutoOpenHistory();
  }

  // A /api/account/* answer that lands after the session changed under it
  // (sign-out / erase in this tab or another — every one of them rotates the
  // sid — or a failed probe) belongs to the PREVIOUS customer. Sign-out is
  // local only, so the old sid still resolves on the server and such a late
  // 200 would otherwise draw (and store under the fresh sid) that customer's
  // transcript or conversation titles for the next person. Callers capture
  // `sid` before the fetch and drop the answer entirely when this is true.
  function accountReplyStale(reqSid) { return sid !== reqSid || !auth.signedIn; }

  // 401 on any /api/account/* call means the session no longer resolves
  // (logged out / expired / erased) — fail closed and drop back to anonymous.
  function accountUnauthorized() {
    var wasSignedIn = auth.signedIn || lsGet(SIGNED_IN_KEY) === '1';
    applyAuth(null);
    closeHistory();
    if (wasSignedIn) endedSignInCleanup();
  }

  // The history list is rendered from a small local model so a freshly started
  // thread is shown IMMEDIATELY and never lost (task §3), reconciling with the
  // server once it has persisted (10E-1 lists fast + persists eagerly):
  //   * historyServerList — the last list the backend returned (sorted desc).
  //   * historyOptimistic  — threads we started locally ("Neue Beratung") that
  //                          may not be on the server yet; dropped once the
  //                          server reports the same conversationKey.
  var historyServerList = [];
  var historyOptimistic = [];
  var historyLoaded = false;

  // Optimistically show a just-started thread at the top of the list. Keyed by
  // its conversationKey so the server entry (same key, written on first send)
  // replaces it on the next fetch — no duplicate, no flash of "empty". Only one
  // pending entry is kept: starting another "Neue Beratung" supersedes a prior
  // one that was never sent (a persisted thread reappears from the server).
  function addOptimisticConversation(key) {
    if (!key) return;
    historyOptimistic = [{
      conversationKey: key,
      conversationId: null,
      title: ACCOUNT_COPY.newChat,
      messageCount: 0,
      updatedAt: new Date().toISOString(),
      optimistic: true
    }];
    renderHistoryList();
  }

  // Drop optimistic entries the server now knows about (matched by key).
  function reconcileOptimistic() {
    if (!historyOptimistic.length) return;
    var known = {};
    for (var i = 0; i < historyServerList.length; i++) {
      if (historyServerList[i].conversationKey) known[historyServerList[i].conversationKey] = true;
    }
    historyOptimistic = historyOptimistic.filter(function (c) { return !known[c.conversationKey]; });
  }

  function mergedConversations() {
    var seen = {};
    for (var i = 0; i < historyServerList.length; i++) {
      if (historyServerList[i].conversationKey) seen[historyServerList[i].conversationKey] = true;
    }
    var pending = historyOptimistic.filter(function (c) { return !seen[c.conversationKey]; });
    return pending.concat(historyServerList); // optimistic (newest) first, then server
  }

  function loadConversations() {
    // Render what we already have instantly; only the first-ever load with
    // nothing to show gets the skeleton (never a long blank — task §3).
    if (historyLoaded || historyOptimistic.length) renderHistoryList();
    else renderHistorySkeleton();
    var reqSid = sid;
    fetch(API_BASE + '/api/account/conversations', { method: 'GET', headers: accountHeaders() })
      .then(function (r) {
        if (accountReplyStale(reqSid)) throw 0;
        if (r.status === 401) { accountUnauthorized(); throw 0; }
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (accountReplyStale(reqSid)) return;
        historyServerList = (data && data.conversations) || [];
        historyLoaded = true;
        reconcileOptimistic();
        renderHistoryList();
      })
      .catch(function (e) {
        if (e === 0 || accountReplyStale(reqSid)) return;
        // Keep anything we can still show; only a truly empty list surfaces the error.
        if (mergedConversations().length) renderHistoryList();
        else historyListEl.replaceChildren(el('div', { class: 'ms-chat-history-empty', text: ACCOUNT_COPY.loadError }));
      });
  }

  // A few shimmer rows while the first list fetch is in flight.
  function renderHistorySkeleton() {
    historyListEl.replaceChildren();
    for (var i = 0; i < 5; i++) {
      var row = el('div', { class: 'ms-chat-conv-skel', 'aria-hidden': 'true' });
      row.appendChild(el('span', { class: 'ms-chat-conv-skel-title' }));
      row.appendChild(el('span', { class: 'ms-chat-conv-skel-meta' }));
      historyListEl.appendChild(row);
    }
  }

  function renderHistoryList() {
    var list = mergedConversations();
    historyListEl.replaceChildren();
    if (!list.length) {
      historyListEl.appendChild(el('div', { class: 'ms-chat-history-empty', text: ACCOUNT_COPY.empty }));
      return;
    }
    for (var i = 0; i < list.length; i++) historyListEl.appendChild(buildConversationItem(list[i]));
  }

  function convDate(c) {
    var iso = c.updatedAt || c.createdAt;
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(L('de-DE', 'en-GB'), { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) { return ''; }
  }

  // Is this row the thread currently loaded in the view? Match by the stable
  // conversationKey first (survives the optimistic→server reconcile), then the
  // numeric id for legacy/server-only rows.
  function isActiveConv(c) {
    if (activeConversationKey && c.conversationKey && c.conversationKey === activeConversationKey) return true;
    return activeConversationId != null && c.conversationId === activeConversationId;
  }

  function buildConversationItem(c) {
    var item = el('div', { class: 'ms-chat-conv' });
    if (isActiveConv(c)) item.classList.add('ms-chat-conv--active');

    var openBtn = el('button', { class: 'ms-chat-conv-open', type: 'button' });
    var titleEl = el('span', { class: 'ms-chat-conv-title', text: c.title || ACCOUNT_COPY.newChat });
    var meta = [ACCOUNT_COPY.msgCount(c.messageCount), convDate(c)].filter(Boolean).join(' · ');
    openBtn.appendChild(titleEl);
    openBtn.appendChild(el('span', { class: 'ms-chat-conv-meta', text: meta }));
    // An optimistic (not-yet-persisted) row IS the active new chat — opening it
    // just returns to that empty chat; there's no server transcript to fetch
    // and no row to rename/delete yet.
    if (c.optimistic) {
      openBtn.addEventListener('click', function () { closeHistory(); });
      item.appendChild(openBtn);
      return item;
    }
    openBtn.addEventListener('click', function () { openConversation(c.conversationId); });
    item.appendChild(openBtn);

    var acts = el('div', { class: 'ms-chat-conv-acts' });
    var renameBtn = el('button', { class: 'ms-chat-iconbtn ms-chat-conv-act', type: 'button', 'aria-label': ACCOUNT_COPY.rename, title: ACCOUNT_COPY.rename });
    renameBtn.appendChild(icon('pencil'));
    renameBtn.addEventListener('click', function () { startRename(item, c, titleEl); });
    var delBtn = el('button', { class: 'ms-chat-iconbtn ms-chat-conv-act', type: 'button', 'aria-label': ACCOUNT_COPY.del, title: ACCOUNT_COPY.del });
    delBtn.appendChild(icon('trash'));
    delBtn.addEventListener('click', function () { startDelete(item, c); });
    acts.appendChild(renameBtn);
    acts.appendChild(delBtn);
    item.appendChild(acts);
    return item;
  }

  // Inline rename (PATCH /api/account/conversations/{id}) — no native prompt.
  function startRename(item, c, titleEl) {
    if (item.querySelector('.ms-chat-conv-edit')) return;
    item.classList.add('ms-chat-conv--editing');
    var form = el('form', { class: 'ms-chat-conv-edit' });
    var input = el('input', { type: 'text', value: c.title || '', maxlength: '80', 'aria-label': ACCOUNT_COPY.rename });
    var save = el('button', { type: 'submit', class: 'ms-chat-conv-mini ms-chat-conv-mini--primary', text: ACCOUNT_COPY.save });
    var cancel = el('button', { type: 'button', class: 'ms-chat-conv-mini', text: ACCOUNT_COPY.cancel });
    form.appendChild(input);
    form.appendChild(save);
    form.appendChild(cancel);
    function done() { item.classList.remove('ms-chat-conv--editing'); if (form.parentNode) form.remove(); }
    cancel.addEventListener('click', done);
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var next = input.value.trim();
      if (!next) { try { input.focus(); } catch (er) {} return; }
      save.disabled = true;
      var reqSid = sid;
      fetch(API_BASE + '/api/account/conversations/' + encodeURIComponent(c.conversationId), {
        method: 'PATCH',
        headers: Object.assign({ 'Content-Type': 'application/json' }, accountHeaders()),
        body: JSON.stringify({ title: next })
      }).then(function (r) {
        if (accountReplyStale(reqSid)) throw 0;
        if (r.status === 401) { accountUnauthorized(); throw 0; }
        return r.ok ? r.json() : null;
      }).then(function (data) {
        if (data && data.ok) { c.title = data.title; titleEl.textContent = data.title; track('conversation_renamed', {}); }
        done();
      }).catch(function (er) { if (er === 0) return; save.disabled = false; });
    });
    item.appendChild(form);
    setTimeout(function () { try { input.focus(); input.select(); } catch (e) {} }, 30);
  }

  // Inline two-step delete (DELETE /api/account/conversations/{id}). Honest
  // wording per CUSTOMER_ACCOUNT.md §7.4 — "this chat", not "all data".
  function startDelete(item, c) {
    if (item.querySelector('.ms-chat-conv-confirm')) return;
    item.classList.add('ms-chat-conv--confirm');
    var bar = el('div', { class: 'ms-chat-conv-confirm' });
    bar.appendChild(el('span', { class: 'ms-chat-conv-confirm-text', text: ACCOUNT_COPY.deleteConfirm }));
    var yes = el('button', { type: 'button', class: 'ms-chat-conv-mini ms-chat-conv-mini--danger', text: ACCOUNT_COPY.deleteYes });
    var no = el('button', { type: 'button', class: 'ms-chat-conv-mini', text: ACCOUNT_COPY.cancel });
    bar.appendChild(yes);
    bar.appendChild(no);
    function done() { item.classList.remove('ms-chat-conv--confirm'); if (bar.parentNode) bar.remove(); }
    no.addEventListener('click', done);
    yes.addEventListener('click', function () {
      yes.disabled = true;
      var reqSid = sid;
      fetch(API_BASE + '/api/account/conversations/' + encodeURIComponent(c.conversationId), { method: 'DELETE', headers: accountHeaders() })
        .then(function (r) {
          if (accountReplyStale(reqSid)) throw 0;
          if (r.status === 401) { accountUnauthorized(); throw 0; }
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          if (data && data.deleted) {
            track('conversation_deleted', {});
            // Drop it from the model so it can't reappear before the next fetch.
            historyServerList = historyServerList.filter(function (x) { return x.conversationId !== c.conversationId; });
            historyOptimistic = historyOptimistic.filter(function (x) { return !x.conversationKey || x.conversationKey !== c.conversationKey; });
            // If the active thread was deleted, clear the local view too.
            if (isActiveConv(c)) {
              lsDel(historyKey()); messages = []; activeConversationId = null; clearConvKey(); renderAllMessages();
            }
            item.remove();
            if (!historyListEl.querySelector('.ms-chat-conv')) renderHistoryList();
          } else { done(); }
        })
        .catch(function (er) { if (er === 0) return; yes.disabled = false; });
    });
    item.appendChild(bar);
  }

  // Open a past conversation: fetch the transcript and load it into the local
  // view as the active thread (GET /api/account/conversations/{id}). The
  // transcript carries readable turns only (§7.2); we render them as text
  // bubbles and persist under the current session so a reload keeps them.
  function transcriptToMessages(conv) {
    var out = [];
    var arr = (conv && conv.messages) || [];
    for (var i = 0; i < arr.length; i++) {
      var m = arr[i];
      if (!m || (m.role !== 'user' && m.role !== 'assistant')) continue;
      out.push({ id: m.role.charAt(0) + '-' + uuid(), role: m.role, parts: [{ type: 'text', text: m.content || '' }] });
    }
    return out;
  }
  function openConversation(id) {
    var reqSid = sid;
    fetch(API_BASE + '/api/account/conversations/' + encodeURIComponent(id), { method: 'GET', headers: accountHeaders() })
      .then(function (r) {
        if (accountReplyStale(reqSid)) throw 0;
        if (r.status === 401) { accountUnauthorized(); throw 0; }
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (accountReplyStale(reqSid)) throw 0;
        var conv = data && data.conversation;
        if (!conv) throw new Error('no conversation');
        // Same as startNewChat(): a reply still streaming belongs to the
        // thread on screen, never to the one being opened.
        if (abortActiveStream) { abortActiveStream(); abortActiveStream = null; }
        endSpeaking();
        removeTyping();
        // A past thread knows nothing of this page: the next typed message on
        // a product page carries its context once more.
        ssDel(CTX_LAST_KEY);
        track('conversation_opened', {});
        messages = transcriptToMessages(conv).slice(-40);
        activeConversationId = conv.conversationId;
        // Adopt this thread's key so the next /api/chat turn appends to it
        // (CUSTOMER_ACCOUNT.md §7.6) and the summary download targets it (§8).
        activeConversationKey = conv.conversationKey || null;
        saveConvKey();
        saveHistory();
        if (rateTimer) { clearTimeout(rateTimer); rateTimer = null; }
        state.rateLocked = false;
        state.streaming = false;
        updateInputState();
        renderAllMessages();
        closeHistory();
        scrollToBottom();
      })
      .catch(function (e) {
        if (e === 0) return;
        closeHistory();
        showMessageError(ACCOUNT_COPY.openError);
      });
  }

  // Download ALL my data (GET /api/account/export, GDPR Art. 15/20). A safe
  // read, so no confirm step (unlike erase). Mirrors the summary download
  // (downloadSummary): guarded fetch (accountHeaders → x-ms-chat-key +
  // x-ms-session, Origin auto) → Blob → a temporary <a download> click →
  // revokeObjectURL, with a small loading state and a friendly German error.
  var exportingData = false;
  function buildExportControl(wrap) {
    wrap.replaceChildren();
    var btn = el('button', { class: 'ms-chat-history-link ms-chat-history-link--quiet', type: 'button', text: ACCOUNT_COPY.exportData });
    var msg = el('div', { class: 'ms-chat-export-msg', style: 'display:none' });
    btn.addEventListener('click', function () {
      if (exportingData) return;
      exportingData = true;
      btn.disabled = true;
      btn.textContent = ACCOUNT_COPY.exportBusy;
      msg.style.display = 'none';
      track('account_export_started', {});
      var reqSid = sid;
      fetch(API_BASE + '/api/account/export', { method: 'GET', headers: accountHeaders() })
        .then(function (res) {
          // Signed out meanwhile: the previous customer's data must not land
          // in this (possibly shared) browser's downloads.
          if (accountReplyStale(reqSid)) throw 0;
          if (res.status === 401) { accountUnauthorized(); throw 0; } // session gone → anonymous
          if (!res.ok) throw new Error('export ' + res.status);       // 503 etc. → friendly error
          return res.blob();
        })
        .then(function (blob) {
          if (accountReplyStale(reqSid)) return;
          // 200 application/json (Content-Disposition attachment). Save the
          // returned bytes under the contract filename; the Blob already carries
          // its type from the response, so no explicit re-typing is needed.
          var url = URL.createObjectURL(blob);
          var a = el('a', { href: url, download: 'motionsports-meine-daten.json' });
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) {} }, 1000);
          track('account_exported', {});
        })
        .catch(function (e) {
          if (e === 0) return; // 401 already dropped to anonymous (drawer closed)
          msg.textContent = ACCOUNT_COPY.exportError;
          msg.style.display = '';
        })
        .then(function () {
          exportingData = false;
          if (btn) { btn.disabled = false; btn.textContent = ACCOUNT_COPY.exportData; }
        });
    });
    wrap.appendChild(btn);
    wrap.appendChild(msg);
  }

  // "Meine Daten löschen" copy — GET /api/consent-copy?surface=erase
  // (CUSTOMER_ACCOUNT.md §7.5, API_CONTRACT.md §7.4). confirmBody is served per
  // backend config: it names the shop customer account only when the erasure
  // really reaches Shopify (SHOPIFY_ERASURE_SYNC). So the widget never
  // hard-codes it and has NO fallback text — without served copy there is no
  // confirmation and therefore no erase. Fetched fresh on every open (no
  // cache): a rare action, and the backend switch may change in between.
  // Same headers as every account call; ?locale= like the other consent-copy
  // GETs (en on /en).
  function validEraseCopy(c) {
    return !!(c && typeof c === 'object' &&
      typeof c.confirmHeading === 'string' && c.confirmHeading &&
      typeof c.confirmBody === 'string' && c.confirmBody &&
      typeof c.confirmButton === 'string' && c.confirmButton);
  }
  function fetchEraseCopy() {
    return fetch(API_BASE + '/api/consent-copy?surface=erase&locale=' + LOCALE, { method: 'GET', headers: accountHeaders() })
      .then(function (res) {
        if (!res.ok) throw new Error('consent-copy erase ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!validEraseCopy(data)) throw new Error('consent-copy erase: invalid payload');
        return data;
      });
  }

  // Delete ALL my data (POST /api/account/erase, §7.5) — distinct, heavier than
  // a per-chat delete. The quiet footer link opens an inline confirmation in
  // the drawer footer, built ONLY from the served copy: confirmHeading +
  // confirmBody, the destructive confirmButton and a cancel. Keyboard: focus
  // moves to the heading on open, Esc or "Abbrechen" closes it and returns
  // focus to the link. No widget KPI: `account_erased` is SERVER-ONLY
  // (API_CONTRACT.md §5, emitted by the erase endpoint itself).
  var erasingData = false;
  var eraseSeq = 0; // ids for aria-labelledby / aria-describedby
  function buildEraseControl(wrap) {
    wrap.replaceChildren();
    var btn = el('button', { class: 'ms-chat-history-link ms-chat-history-link--quiet', type: 'button', text: ACCOUNT_COPY.erase });
    btn.addEventListener('click', function () { openEraseConfirm(wrap); });
    wrap.appendChild(btn);
    return btn;
  }

  function openEraseConfirm(wrap) {
    var n = ++eraseSeq;
    var box = el('div', { class: 'ms-chat-erase-confirm', role: 'group' });
    var msg = el('div', { class: 'ms-chat-erase-msg', role: 'alert', style: 'display:none' });
    var busy = false;

    function cancel() {
      if (busy) return;
      var b = buildEraseControl(wrap);
      try { b.focus(); } catch (e) {}
    }
    box.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') { ev.stopPropagation(); cancel(); }
    });
    function cancelBtn() {
      var no = el('button', { type: 'button', class: 'ms-chat-conv-mini', text: ACCOUNT_COPY.cancel });
      no.addEventListener('click', cancel);
      return no;
    }

    // 1) Loading the served copy.
    function renderLoading() {
      var t = el('div', { class: 'ms-chat-erase-text', tabindex: '-1', text: ACCOUNT_COPY.loading });
      var row = el('div', { class: 'ms-chat-erase-actions' });
      row.appendChild(cancelBtn());
      box.replaceChildren(t, row);
      try { t.focus(); } catch (e) {}
      fetchEraseCopy().then(renderConfirm, renderCopyError);
    }

    // 1b) No served copy: a short chrome message + retry — never a fallback
    // confirmation text, and no way to erase without the served one.
    function renderCopyError() {
      if (!box.parentNode) return; // cancelled meanwhile
      var t = el('div', { class: 'ms-chat-erase-msg', role: 'alert', tabindex: '-1', text: ACCOUNT_COPY.eraseCopyError });
      var row = el('div', { class: 'ms-chat-erase-actions' });
      var again = el('button', { type: 'button', class: 'ms-chat-conv-mini', text: ACCOUNT_COPY.retry });
      again.addEventListener('click', renderLoading);
      row.appendChild(again);
      row.appendChild(cancelBtn());
      box.replaceChildren(t, row);
      try { again.focus(); } catch (e) {}
    }

    // 2) The confirmation, verbatim from the served copy.
    function renderConfirm(copy) {
      if (!box.parentNode) return; // cancelled meanwhile
      var hid = 'ms-chat-erase-h' + n, bid = 'ms-chat-erase-b' + n;
      var heading = el('div', { class: 'ms-chat-erase-heading', id: hid, tabindex: '-1', text: copy.confirmHeading });
      var bodyEl = el('div', { class: 'ms-chat-erase-text', id: bid, text: copy.confirmBody });
      box.setAttribute('aria-labelledby', hid);
      box.setAttribute('aria-describedby', bid);
      var row = el('div', { class: 'ms-chat-erase-actions' });
      var yes = el('button', { type: 'button', class: 'ms-chat-conv-mini ms-chat-conv-mini--danger ms-chat-erase-yes', text: copy.confirmButton });
      var no = cancelBtn();
      row.appendChild(yes);
      row.appendChild(no);
      msg.style.display = 'none';
      box.replaceChildren(heading, bodyEl, row, msg);
      try { heading.focus(); } catch (e) {}

      function setBusy(b) {
        busy = b;
        yes.disabled = b; no.disabled = b;
        yes.textContent = b ? ACCOUNT_COPY.eraseBusy : copy.confirmButton;
      }
      yes.addEventListener('click', function () {
        if (erasingData) return;
        erasingData = true;
        setBusy(true);
        msg.style.display = 'none';
        fetch(API_BASE + '/api/account/erase', { method: 'POST', headers: accountHeaders() })
          .then(function (r) {
            if (r.status === 401) return 'gone';    // session already gone
            if (r.status === 503) return 'failed';  // nothing was erased -> retry
            if (!r.ok) return 'error';
            // Shape unchanged: { ok, erased, deletedConversations }.
            return r.json().catch(function () { return null; })
              .then(function (d) { return (d && d.erased === true) ? 'erased' : 'error'; });
          }, function () { return 'error'; })
          .then(function (result) {
            erasingData = false;
            if (result === 'erased') {
              busy = false;
              clearAfterErase(wrap);
              showEraseDone(copy);
              return;
            }
            if (result === 'gone') {
              // 401: the session no longer resolves (logged out, expired or
              // already erased) — nothing left to confirm; just leave no
              // signed-in state or stored history behind.
              busy = false;
              clearAfterErase(wrap);
              try { textarea.focus(); } catch (e) {} // the drawer (and its focus) is gone
              return;
            }
            // 503 says nothing was deleted (served failedBody); anything else
            // gets the chrome error. Both keep the confirmation for a retry.
            setBusy(false);
            msg.textContent = (result === 'failed' && typeof copy.failedBody === 'string' && copy.failedBody)
              ? copy.failedBody : ACCOUNT_COPY.eraseError;
            msg.style.display = '';
            try { yes.focus(); } catch (e) {}
          });
      });
    }

    wrap.replaceChildren(box);
    renderLoading();
  }

  // After a 200 (or a 401): the backend session no longer resolves
  // (/api/auth/me answers signedIn:false, /api/account/* 401), so drop every
  // signed-in state at once, then remove this session's stored chat history
  // the same way sign-out does (dropSessionHistory: history + thread key
  // gone, fresh session id, attribution token reset). The cached
  // conversation list of the erased account is emptied too, so a later
  // sign-in on this page never flashes it.
  function clearAfterErase(wrap) {
    // Same as signOut: the shop's whoami must not re-sign the visitor in.
    ssSet(WHOAMI_SS_KEY, '1');
    moBlankCart(); // before the rotation drops the cached token (erased or 401)
    closeHistory();
    applyAuth(null);
    dropSessionHistory(); // also clears the loaded conversation list
    buildEraseControl(wrap); // a fresh footer for a later sign-in
  }

  // The served doneHeading + doneBody, shown over the (now anonymous) chat in
  // the shared gate dialog shell (focus trap, Esc/backdrop/button close) —
  // the drawer that held the confirmation is already closed.
  function showEraseDone(copy) {
    var heading = (typeof copy.doneHeading === 'string' && copy.doneHeading) ? copy.doneHeading : '';
    var body = (typeof copy.doneBody === 'string' && copy.doneBody) ? copy.doneBody : '';
    if (!heading && !body) return;
    if (gateEl) return; // never stack dialogs
    var dlg = openGateDialog(heading || body, function () {});
    var card = dlg.card;
    if (heading) card.appendChild(el('div', { class: 'ms-chat-gate-headline', text: heading }));
    if (body) card.appendChild(el('div', { class: 'ms-chat-gate-intro', text: body }));
    var ok = el('button', { type: 'button', class: 'ms-chat-btn ms-chat-btn--primary' }, [ACCOUNT_COPY.eraseDoneOk]);
    ok.addEventListener('click', dlg.close);
    card.appendChild(ok);
    dlg.show();
    try { ok.focus(); } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Campaign link attribution (?mo_c=<token>, API_CONTRACT.md §2 "Optional
  // campaignToken", §11.2). A Mo CTA in a campaign e-mail lands here with the
  // send's token appended. It is read on EVERY load (not only with mo=open)
  // and BEFORE handleMoDeepLink strips the mo* params, then stripped itself so
  // a reload or a copied/shared URL never counts the send for someone else.
  // Each strip re-reads window.location, so it composes with the ms_auth and
  // mo* strips in any order. The token goes into sessionStorage ONLY (never
  // localStorage, cookies, KPI payloads or logs) and is handed back exactly
  // once as campaignToken on this tab session's next POST /api/chat
  // (startStream), which deletes it on res.ok. A malformed value is dropped
  // silently — the backend would ignore it anyway — but still leaves the URL.
  // ---------------------------------------------------------------------------
  var CAMPAIGN_TOKEN_KEY = 'ms_mo_c';
  // Page context already sent for this page in this session (pageContextForSend):
  // '<sid>|p:<handle>' / '<sid>|c:<collectionHandle>'. sessionStorage only,
  // chat-functional, never in a KPI payload.
  var CTX_LAST_KEY = 'ms-chat-ctx-last';
  var CAMPAIGN_TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;
  function captureCampaignToken() {
    try {
      // The theme's <head> script may already have moved it off the address
      // bar (earlyParam, see readAuthReturn); the URL is the fallback.
      var early = earlyParam('mo_c');
      if (early != null) {
        var etok = String(early).trim();
        if (CAMPAIGN_TOKEN_RE.test(etok)) ssSet(CAMPAIGN_TOKEN_KEY, etok);
      }
      var u = new URL(window.location.href);
      if (!u.searchParams.has('mo_c')) return;
      var tok = String(u.searchParams.get('mo_c') || '').trim();
      if (early == null && CAMPAIGN_TOKEN_RE.test(tok)) ssSet(CAMPAIGN_TOKEN_KEY, tok);
      u.searchParams.delete('mo_c');
      window.history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Campaign deep link (?mo=open / #mo-open, WIDGET_SPEC §9d). The backend's
  // campaign emails end in a Mo CTA linking here (CAMPAIGN_MO_DEEPLINK_URL),
  // so the click lands in an already-open consultation. Purely client-side:
  // no network calls, no greeting change — the open is exactly a launcher
  // click on a fully initialized widget. Optional modifiers, only read when
  // mo=open is present:
  //   mo_new=1            fresh consultation — don't resume the stored thread
  //   mo_view=fullscreen  open expanded (the desktop modal mode; mobile is
  //                       always true fullscreen)
  // All three params (+ the #mo-open hash) are stripped via replaceState —
  // the same cleanup idiom as ?ms_auth= in handleAuthReturn — so a reload or
  // copied URL doesn't re-trigger the auto-open. utm_* params are left
  // untouched (the shop's analytics, not ours). The campaign token (mo_c) is
  // NOT handled here: captureCampaignToken() already read and stripped it at
  // the top of init(), with or without mo=open. Fail-silent by design: the
  // deep link must never break widget init.
  // ---------------------------------------------------------------------------
  function handleMoDeepLink() {
    var wantsOpen = false, wantsNew = false, wantsFullscreen = false;
    try {
      var u = new URL(window.location.href);
      var hashOpen = u.hash === '#mo-open';
      wantsOpen = u.searchParams.get('mo') === 'open' || hashOpen;
      if (!wantsOpen) return; // no deep link -> byte-identical behaviour
      wantsNew = u.searchParams.get('mo_new') === '1';
      wantsFullscreen = u.searchParams.get('mo_view') === 'fullscreen';
      u.searchParams.delete('mo');
      u.searchParams.delete('mo_new');
      u.searchParams.delete('mo_view');
      if (hashOpen) u.hash = '';
      window.history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
    } catch (e) { return; }
    try {
      if (wantsNew) {
        // Fresh consultation — startNewChat()'s two branches, but decided by
        // the local signed-in hint because auth is NOT settled yet at init:
        // rotating the sid would sever a signed-in identity link
        // (CUSTOMER_ACCOUNT.md §1). A possibly-signed-in visitor keeps the
        // sid and just drops the local thread — the first turn then mints a
        // fresh conversationKey (maybeMintConversationKey), so this becomes
        // its own history entry. A pure-anonymous visitor rotates exactly
        // like "Neuen Chat starten".
        if (shouldProbeAuth()) {
          lsDel(historyKey());
          messages = [];
          activeConversationId = null;
          clearConvKey();
        } else {
          rotateSession();
          activeConversationKey = null;
        }
        renderAllMessages(); // empty -> welcome (init rendered the old thread)
      }
      if (wantsFullscreen) {
        // The widget's expanded state is the desktop modal mode (§4; mobile
        // is always true fullscreen). Applied to the still-closed panel so
        // the open animates straight into it — no sidebar -> modal flicker.
        // Deliberately NOT persisted to VIEW_MODE_KEY: the campaign link
        // must not overwrite the customer's saved layout preference.
        state.viewMode = 'modal';
        applyViewMode();
      }
      if (typeof openPanel === 'function') openPanel();
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Init.
  // ---------------------------------------------------------------------------
  function init() {
    // Campaign token first: it must be read before ANY strip of the address
    // bar (ms_auth / mo*) and before anything could copy the URL elsewhere.
    captureCampaignToken();
    // Then the sign-in return (?ms_auth / one-time ?ms_code): stripped now,
    // processed by handleAuthReturn below once the widget is built.
    readAuthReturn();
    // Configurator page: dock the panel into the page (cfgInitDock below).
    try { DOCK = document.querySelector('[data-ms-chat-dock]'); } catch (e) { DOCK = null; }
    buildShell();
    autoGrow(); // size the input to its clean single-line height up front
    renderAllMessages();
    updateInputState();
    // Server-rendered orb spans (the product-page CTA renders an empty
    // .ms-chat-logo span in the template) get the same blob SVG as the
    // JS-built ones.
    try {
      var orbs = document.querySelectorAll('.ms-chat-logo');
      for (var oi = 0; oi < orbs.length; oi++) {
        if (!orbs[oi].firstElementChild) orbs[oi].appendChild(logoBlobs());
      }
    } catch (e) {}
    window.MS_CHAT = window.MS_CHAT || {};
    window.MS_CHAT.openWithProduct = openWithProduct;
    window.MS_CHAT.openEmailSummary = openCaptureForm;
    // version 2 (since 2026-10-27): the note (answers.note / meta.note) and
    // the product rules are supported — a version-1 widget drops the note, so
    // the page offers its step 04 only from version 2 on.
    window.MS_CHAT.configurator = {
      version: 2,
      dock: !!DOCK,
      start: cfgStart,
      update: cfgUpdate,
      restart: cfgRestart,
      track: cfgTrackFromPage,
      state: function () {
        var c = cfgActive();
        return c ? { answers: c.answers, partner: c.partner || null, stage: cfgStage, busy: state.streaming || state.rateLocked } : null;
      }
    };
    bindProductCtas();
    cfgInitDock();
    // Engagement layer: record this page in the local browsing trail, arm the
    // contextual nudge triggers, play the one-time launcher attention motion.
    recordTrail();
    initNudgeTriggers();
    playLauncherAttention();
    // Order attribution: arm the consent listener + once-per-page-load
    // re-stamp of the live cart (only when a cached token already exists).
    initAttribution();
    // Follow a session rotation (sign-out / erase / new chat) in another tab.
    window.addEventListener('storage', onSidChangedElsewhere);
    // Customer Account: process a sign-in/logout return marker (?ms_auth=…),
    // re-hydrating identity and re-opening the panel onto the SAME conversation.
    handleAuthReturn();
    // Campaign deep link (?mo=open / #mo-open): auto-open LAST, on the fully
    // initialized widget, with the modifiers applied before the open.
    handleMoDeepLink();
  }

  // Delegated handler for storefront product-page CTAs. Reading product id +
  // title from data-* attributes (not an inline onclick) keeps it robust no
  // matter what quotes/apostrophes a product title contains.
  function bindProductCtas() {
    document.addEventListener('click', function (e) {
      var t = e.target;
      var btn = t && t.closest ? t.closest('.ms-chat-product-cta') : null;
      if (!btn) return;
      e.preventDefault();
      openWithProduct(btn.getAttribute('data-ms-chat-product-id'), btn.getAttribute('data-ms-chat-product-title'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
